import { useState, useEffect, useLayoutEffect, useCallback, useRef, createContext, useContext, Fragment } from "react";

// Set browser title
document.title = "CLAS CRM Suite";
// Force favicon refresh
const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
link.rel = 'icon'; link.href = '/favicon.ico?' + Date.now();
document.head.appendChild(link);

// Mobile: render at real device width instead of a zoomed-out desktop page.
(() => {
  let vp = document.querySelector("meta[name=viewport]");
  if (!vp) { vp = document.createElement("meta"); vp.name = "viewport"; document.head.appendChild(vp); }
  vp.content = "width=device-width, initial-scale=1, viewport-fit=cover";
})();

// Mobile: stop anything from making the page wider than the screen (which leaves a
// blank strip down the right-hand side). Inline styles can't contain overflow at the
// html/body level, so this is done with real CSS.
(() => {
  const st = document.createElement("style");
  st.textContent = `
    @media (max-width: 767px) {
      html, body, #root { overflow-x: hidden !important; max-width: 100% !important; width: 100% !important; margin: 0; }
      #root * { max-width: 100%; }
      /* things that legitimately scroll sideways keep doing so, inside their own box */
      #root table { max-width: none; }
      img, svg, canvas { max-width: 100%; height: auto; }
    }
  `;
  document.head.appendChild(st);
})();

// Freeze table headers (like Excel's Freeze Panes). Each list scrolls inside its
// own box sized to the screen; the header row stays on top while you scroll down,
// and moves sideways with the columns. Lists marked "freeze-cols" (Call Tracker,
// Leads) also keep their first two columns (tick-box + Ticket # / Enq #) in view
// when scrolling right.
(() => {
  const st = document.createElement("style");
  st.textContent = `
    @media (min-width: 768px) {
      #root div:has(> table) { max-height: calc(100vh - 150px); overflow: auto !important; }
    }
    #root table thead th {
      position: sticky; top: 0; z-index: 3;
      background: #f8fafc;
      box-shadow: inset 0 -2px 0 #e2e8f0;
    }
    #root table.freeze-cols tbody tr { background-color: #fff; }
    #root table.freeze-cols th:nth-child(1), #root table.freeze-cols td:nth-child(1) {
      position: sticky; left: 0; z-index: 2;
      width: 44px; min-width: 44px; max-width: 44px; box-sizing: border-box; padding-left: 8px; padding-right: 8px;
    }
    #root table.freeze-cols th:nth-child(2), #root table.freeze-cols td:nth-child(2) {
      position: sticky; left: 44px; z-index: 2;
    }
    #root table.freeze-cols td:nth-child(1), #root table.freeze-cols td:nth-child(2) { background-color: inherit; background-clip: padding-box; }
    #root table.freeze-cols thead th:nth-child(1), #root table.freeze-cols thead th:nth-child(2) { z-index: 4; background: #f8fafc; }
  `;
  document.head.appendChild(st);
})();

// Security: disable right-click, F12, Ctrl+U, Ctrl+Shift+I in production
if (window.location.hostname !== "localhost") {
  // Block right-click on the page, but ALLOW it inside text fields, and ALLOW it
  // whenever text is selected — so copy/paste (only reachable via the long-press
  // menu on mobile) works on both Android and iOS, for inputs and displayed text.
  document.addEventListener("contextmenu", (e) => {
    const t = e.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
    if (window.getSelection && window.getSelection().toString().trim().length > 0) return;
    e.preventDefault();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "F12") { e.preventDefault(); return false; }
    if (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J" || e.key === "C")) { e.preventDefault(); return false; }
    if (e.ctrlKey && e.key === "u") { e.preventDefault(); return false; }
  });
}

// ============================================================================
// CONTEXT & AUTH
// ============================================================================
const AuthContext = createContext(null);
const API = "/api";

const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) { const err = new Error(data.message || "Request failed"); err.status = res.status; err.data = data; throw err; }
  return data;
};

// ============================================================================
// DATE UTILITIES
// ============================================================================
const formatDateDMY = (d) => {
  if (!d) return "";
  const iso = toISODate(String(d));
  if (!iso) return "";
  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

// Big amounts in lakh / crore so they fit a card: ₹40.22 L, ₹1.25 Cr (full value as tooltip)
const formatINRShort = (v) => {
  const n = parseFloat(v) || 0, a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};
const toISODate = (dmyStr) => {
  if (!dmyStr) return "";
  const s = String(dmyStr);
  // Handle full ISO datetime from database (e.g., 2026-03-31T18:30:00.000Z)
  // Convert to local date to avoid timezone shift
  if (s.includes("T")) {
    const dt = new Date(s);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  // Already ISO format (YYYY-MM-DD)
  if (s.match(/^\d{4}-\d{2}-\d{2}/)) return s.slice(0, 10);
  // DD/MM/YYYY format
  if (s.includes("/")) {
    const parts = s.split("/");
    if (parts.length === 3 && parts[2].length === 4) return `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}`;
  }
  return "";
};

const formatCurrencyINR = (amt) => {
  if (!amt) return "₹0.00";
  const n = parseFloat(amt);
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(n);
};

// CSV Download Helper
const downloadCSV = (csvContent, filename) => {
  const BOM = "\uFEFF";
  const sep = "sep=,\n";
  const blob = new Blob([BOM + sep + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Build proper CSV row with quoting
const csvRow = (fields) => fields.map(f => {
  const s = (f === null || f === undefined) ? "" : String(f);
  return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
}).join(",");

// Confirm cancel if form has data
const confirmCancel = (form, defaults = {}) => {
  const hasData = Object.keys(form).some(k => {
    if (k.startsWith("_") || k === "customer_products" || k === "customer_product_id") return false;
    const val = form[k];
    const def = defaults[k] || "";
    return val && val !== def && val !== "Open" && val !== "New" && val !== "Warm";
  });
  if (!hasData) return true;
  return window.confirm("You have unsaved changes. Exit without saving?");
};

// Convert 24-hour time to 12-hour AM/PM format
const formatTime12hr = (timeStr) => {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  let h = parseInt(parts[0]);
  const m = parts[1] || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h = h - 12;
  return `${h}:${m} ${ampm}`;
};

// Arrow-key scrolling for a focusable scroll box: click/tab into it, then
// Arrow keys move up/down/left/right, PageUp/PageDown jump a screenful.
// Compact contact helpers — phones from the customer master (main + contact numbers),
// and the customer's emails. Values may be comma-separated (multiple entries).
const contactNums = (r) => [r.customer_phone || r.phone || r.client_phone, r.contact_phone].filter(Boolean).join(", ") || "—";
const contactMails = (r) => [r.customer_email || r.email || r.client_email, r.contact_email].filter(Boolean).join(", ") || "—";
const contactPersons = (r) => r.contact_person || "—";

// ---- Excel-style multi-level sorting ----
// Click a header to sort by it; click more headers to add further sort levels
// (①②③ …), click an active header again to flip its direction. Values are
// compared by type: dates chronologically, numbers numerically, text A-Z
// (case-insensitive); blanks always sort last.
const useMultiSort = () => {
  const [sorts, setSorts] = useState([]); // [{ key, dir }]
  const toggleSort = (key) => {
    setSorts(prev => {
      const i = prev.findIndex(s => s.key === key);
      if (i === -1) return [...prev, { key, dir: "asc" }];
      const next = [...prev];
      if (next[i].dir === "asc") { next[i] = { key, dir: "desc" }; return next; }
      return next.filter(s => s.key !== key); // third click removes this level
    });
  };
  const clearSort = () => setSorts([]);
  return { sorts, toggleSort, clearSort };
};

const sortValue = (v) => {
  if (v === null || v === undefined || v === "" || v === "—") return null;
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) return parseFloat(s);              // plain number
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(s);                          // ISO date
  if (iso) return new Date(s).getTime();
  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s);                  // DD/MM/YYYY
  if (dmy) return new Date(`${dmy[3]}-${dmy[2]}-${dmy[1]}`).getTime();
  return s.toLowerCase();
};

const applyMultiSort = (rows, sorts, accessors = {}) => {
  if (!sorts || sorts.length === 0) return rows;
  const get = (row, key) => (accessors[key] ? accessors[key](row) : row[key]);
  return [...rows].sort((a, b) => {
    for (const s of sorts) {
      const av = sortValue(get(a, s.key)), bv = sortValue(get(b, s.key));
      if (av === bv) continue;
      if (av === null) return 1;          // blanks last, regardless of direction
      if (bv === null) return -1;
      const cmp = av < bv ? -1 : 1;
      return s.dir === "asc" ? cmp : -cmp;
    }
    return 0;
  });
};

// Clickable header cell showing its sort level and direction.
const SortTh = ({ label, sortKey, sorts, onToggle, style }) => {
  const idx = sorts.findIndex(s => s.key === sortKey);
  const active = idx !== -1;
  return (
    <th onClick={() => onToggle(sortKey)} title="Click to sort · click again to reverse · click a third time to remove"
      style={{ ...style, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
      {label}
      {active && <span style={{ marginLeft: 4, color: colors.primary, fontWeight: 700, fontSize: 10 }}>
        {sorts.length > 1 ? `${idx + 1}` : ""}{sorts[idx].dir === "asc" ? "▲" : "▼"}
      </span>}
    </th>
  );
};

// Small "Sorted by … / Clear" strip shown above a sorted table.
const SortBar = ({ sorts, clearSort, labels = {} }) => {
  if (!sorts.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: colors.textLight, marginBottom: 6 }}>
      <span>Sorted by: {sorts.map((s, i) => `${i + 1}. ${labels[s.key] || s.key} ${s.dir === "asc" ? "▲" : "▼"}`).join("  ·  ")}</span>
      <button onClick={clearSort} style={{ border: `1px solid ${colors.border}`, background: "#fff", borderRadius: 6, cursor: "pointer", padding: "1px 8px", fontSize: 11 }}>Clear sort</button>
    </div>
  );
};

// ---- Mobile: show a wide table as tap-to-open cards ----
// rows: data; primary: main line (e.g. customer); badge: {text,color};
// meta: [[label, value], ...] shown as compact label/value pairs; onOpen(row).
// On phones, hide the filter panel behind a toggle so the list is visible immediately.
const MobileFilters = ({ children, count }) => {
  const [open, setOpen] = useState(false);
  if (!isMobileNow()) return children;
  return (
    <div style={{ marginBottom: 10 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#f8fafc", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "11px 14px",
          fontSize: 14, fontWeight: 700, color: colors.text, cursor: "pointer", minHeight: 44 }}>
        <span>🔍 Search &amp; Filters{count ? ` (${count} active)` : ""}</span>
        <span style={{ color: colors.textLight }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
};

const MobileCards = ({ rows, primary, secondary, badge, meta, onOpen, empty }) => {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: 24, textAlign: "center", color: colors.textLight, fontSize: 13 }}>{empty || "No records found."}</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r, i) => {
        const b = badge ? badge(r) : null;
        return (
          <div key={r.id || i} onClick={() => onOpen && onOpen(r)}
            style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: "10px 12px", background: "#fff",
              cursor: onOpen ? "pointer" : "default", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: colors.text, lineHeight: 1.3 }}>{primary(r)}</div>
              {b && <span style={{ flexShrink: 0, background: b.color + "22", color: b.color, padding: "2px 9px", borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{b.text}</span>}
            </div>
            {secondary && <div style={{ fontSize: 12, color: colors.textLight, marginTop: 2 }}>{secondary(r)}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 10px", marginTop: 8 }}>
              {meta(r).filter(m => m && m[1] && m[1] !== "—").map(([label, value], mi) => (
                <div key={mi} style={{ fontSize: 11.5, minWidth: 0 }}>
                  <span style={{ color: colors.textLight }}>{label}: </span>
                  <span style={{ color: colors.text, fontWeight: 600, wordBreak: "break-word" }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Full postal address for a customer, from the customer master.
const custAddress = (clientId, masters) => {
  const c = masters?.clients?.find(x => String(x.id) === String(clientId));
  if (!c) return "";
  return [c.address_line1, c.address_line2, c.city, c.state, c.pincode].filter(Boolean).join(", ");
};
const mapsLink = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

// Admin view: who is currently using the CRM. Handy before restarting the server
// for an update — you can see whether anyone is mid-work.
const WhoIsOnline = ({ user }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => { try { const r = await api("/call-logs/active-users"); setList(r.data || []); } catch (e) {} };
  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "manager") return;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  if (user?.role !== "admin" && user?.role !== "manager") return null;

  const ago = (sec) => sec < 60 ? "just now" : `${Math.floor(sec / 60)} min ago`;
  return (
    <div style={{ ...styles.card, padding: 0, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "10px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 700 }}>
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: list.length ? colors.success : colors.textLight, display: "inline-block" }} />
          Online now <span style={{ color: colors.textLight, fontWeight: 400 }}>({list.length} active in the last 5 min)</span>
        </div>
        <span style={{ color: colors.textLight, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${colors.border}`, padding: "8px 14px 12px" }}>
          {list.length === 0 ? (
            <div style={{ fontSize: 12.5, color: colors.textLight }}>Nobody is using the CRM right now — a safe moment to deploy an update.</div>
          ) : (
            <>
              {list.map(u => (
                <div key={u.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "3px 0", borderBottom: `1px solid ${colors.border}` }}>
                  <span><b>{u.full_name || u.username}</b> <span style={{ color: colors.textLight }}>· {u.role}</span></span>
                  <span style={{ color: colors.textLight }}>{ago(u.seconds_ago)}</span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: colors.textLight, marginTop: 6 }}>Ask these users to pause before restarting the server.</div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Fixed option lists shared by the customer forms (quick create, master, lead conversion)
const IND_OPTS = ["IT/Software", "Manufacturing", "Retail", "Healthcare", "FMCG", "Pharma", "Automobile", "Banking/Finance", "Education", "Telecom", "Real Estate", "Logistics", "Agriculture", "Textiles", "E-commerce", "Others"].map(d => ({ value: d, label: d }));
const CT_OPTS = ["Pvt Ltd", "Ltd (Public)", "LLP", "Partnership", "Proprietorship", "Trust", "Society", "Government", "Others"].map(d => ({ value: d, label: d }));
const ST_OPTS = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu & Kashmir","Ladakh"].map(x => ({ value: x, label: x }));

const clientsCreateFields = (masters) => [
      { key: "company_name", label: "Company Name *", required: true },
      { key: "group_id", label: "Group (sister concerns sharing a licence)", type: "searchable_select", options: masters.groups?.map(g => ({ value: g.id.toString(), label: g.name })) || [], createLabel: "Create Group", createEndpoint: "customer-products/groups", createFields: [{ key: "name", label: "Group Name", required: true }] },
      { key: "new_product_id", label: "Product (for the serial below) *", type: "searchable_select", options: masters.products?.map(pr => ({ value: pr.id.toString(), label: pr.name })) || [] },
      { key: "new_license_number", label: "Product Serial / Licence Number *" },
      { key: "new_user_type", label: "User Type (Single / Multi)", type: "select", options: [{ value: "Single User", label: "Single User" }, { value: "Multi User", label: "Multi User" }] },
      { key: "new_subscription_expiry", label: "Subscription / Licence Expiry", type: "date" },
      { key: "parent_principal_id", label: "Principal Company", type: "searchable_select", options: masters.principals?.map(p => ({ value: p.id.toString(), label: p.company_name })) || [] },
      { key: "relationship_manager_id", label: "Handled By (Executive)", type: "searchable_select", options: masters.executives?.map(e => ({ value: e.id.toString(), label: e.name })) || [] },
      { key: "contact_person", label: "Contact Person(s) — separate multiple with commas", type: "textarea" },
      { key: "contact_phone", label: "Contact Number(s) — separate multiple with commas", type: "textarea" },
      { key: "phone", label: "Mobile Number(s) — separate multiple with commas", type: "textarea" },
      { key: "email", label: "Email(s) — separate multiple with commas", type: "textarea" },
      { key: "contact_email", label: "Contact Email(s) — separate multiple with commas", type: "textarea" },
      { key: "contact_designation", label: "Contact Designation(s)", type: "textarea" },
      { key: "gst_registration", label: "GST Registration *", type: "select", options: [{ value: "Registered", label: "Registered" }, { value: "Unregistered", label: "Unregistered" }] },
      { key: "gstin", label: "GSTIN (required if Registered)" },
      { key: "industry", label: "Industry / Sector", type: "select", options: IND_OPTS },
      { key: "company_type", label: "Company Type", type: "select", options: CT_OPTS },
      { key: "address_line1", label: "Address Line 1" }, { key: "address_line2", label: "Address Line 2" },
      { key: "city", label: "City" }, { key: "state", label: "State", type: "select", options: ST_OPTS },
      { key: "pincode", label: "Pincode" }, { key: "website", label: "Website" },
      { key: "pan", label: "PAN" }, { key: "cin", label: "CIN" },
      { key: "partner_id", label: "Billing Partner", type: "searchable_select", options: masters.partners?.map(pt => ({ value: pt.id.toString(), label: pt.name })) || [] },
      { key: "annual_revenue", label: "Annual Revenue Range", type: "select", options: ["< 1 Cr", "1-5 Cr", "5-25 Cr", "25-100 Cr", "> 100 Cr"].map(d => ({ value: d, label: d })) },
      { key: "employee_count", label: "No. of Employees", type: "number" },
      { key: "payment_terms", label: "Payment Terms" },
      { key: "credit_limit", label: "Credit Limit", type: "number" },
      { key: "notes", label: "Notes", type: "textarea", fullWidth: true },
    ];

// ---- Lead conversion ----
// After a lead is marked Converted: find out whether this company is already a
// customer, record WHAT was sold, and either add the sale to the existing
// customer or create a new customer from the lead's details.
const LeadConversion = ({ lead, masters, loadMasters, onDone }) => {
  const [matches, setMatches] = useState([]);
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState("");            // "existing" | "new"
  const [clientId, setClientId] = useState("");
  const [saleTypeId, setSaleTypeId] = useState("");
  const [productId, setProductId] = useState("");
  const [serial, setSerial] = useState("");
  const [userType, setUserType] = useState("Single User");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // Escape (and the mobile Close button) should close only the TOP window:
  // the customer form if it is open, otherwise this one. A ref is used so the
  // handler always sees the current state, never a stale copy.
  const showCreateRef = useRef(false);
  useEffect(() => { showCreateRef.current = showCreate; }, [showCreate]);
  useEffect(() => {
    const onCloseModal = () => {
      if (showCreateRef.current) { showCreateRef.current = false; setShowCreate(false); }
      else onDone();
    };
    document.addEventListener("close-modal", onCloseModal);
    return () => document.removeEventListener("close-modal", onCloseModal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Is this company already a customer? Check name, phone and e-mail.
  useEffect(() => {
    (async () => {
      const found = new Map();
      for (const term of [lead.prospect_company, lead.phone, lead.email].filter(Boolean)) {
        try {
          const r = await api(`/clients?search=${encodeURIComponent(String(term).trim())}&limit=20`);
          (r.data || []).forEach(c => found.set(c.id, c));
        } catch (e) {}
      }
      const list = Array.from(found.values());
      setMatches(list);
      setMode(list.length ? "existing" : "new");
      if (list.length) setClientId(String(list[0].id));
      setChecking(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  const saleType = masters.saleTypes?.find(t => String(t.id) === String(saleTypeId));
  const needsSerial = !!saleType?.needs_serial;

  // Record the sale against an existing customer
  const saveExisting = async () => {
    if (!clientId) { setErr("Please choose the customer."); return; }
    if (!saleTypeId) { setErr("Please choose what was sold."); return; }
    if (needsSerial && (!productId || !serial.trim())) { setErr("This sale type needs a product and serial / licence number."); return; }
    setBusy(true); setErr("");
    try {
      if (needsSerial) {
        // Does this customer already hold this product? (warn, do not block)
        try {
          const ex = await api(`/customer-products/customer/${clientId}`);
          const same = (ex.data || []).find(cp => String(cp.product_id) === String(productId) && cp.is_active);
          if (same && !window.confirm(`This customer already has ${same.product_name || "this product"}${same.license_number ? ` (${same.license_number})` : ""}.\n\nAdd another licence anyway?`)) {
            setBusy(false); return;
          }
        } catch (e) {}
        // The server blocks a serial already used by a customer outside this group
        await api(`/customer-products`, { method: "POST", body: {
          client_id: clientId, product_id: productId, license_number: serial.trim(),
          user_type: userType || "Single User", is_active: true,
          notes: note || `From lead ${lead.enquiry_number}` } });
      }
      await api(`/leads/${lead.id}`, { method: "PUT", body: {
        ...lead, status: "Converted", converted_client_id: clientId, sale_type_id: saleTypeId } });
      loadMasters && loadMasters();
      onDone(`Sale recorded against ${matches.find(m => String(m.id) === String(clientId))?.company_name || "the customer"}.`);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  // Link a freshly created customer back to the lead
  const afterCreate = async (newClient) => {
    try {
      await api(`/leads/${lead.id}`, { method: "PUT", body: {
        ...lead, status: "Converted", converted_client_id: newClient.id, sale_type_id: saleTypeId || null } });
    } catch (e) {}
    loadMasters && loadMasters();
    onDone(`Customer "${newClient.company_name}" created from this lead.`);
  };

  const fieldRow = { marginBottom: 12 };

  return (
    <>
      {/* Hidden while the customer form is open, so only one window shows at a time */}
      {!showCreate && (
      <div style={{ ...styles.modal, zIndex: 11000 }} onClick={() => onDone()}>
        <div style={{ ...styles.modalContent, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
          <h3 style={{ marginTop: 0 }}>✓ Lead converted — what was sold?</h3>
          <p style={{ fontSize: 12.5, color: colors.textLight, marginTop: 0 }}>
            <b>{lead.prospect_company}</b>{lead.contact_person ? ` · ${lead.contact_person}` : ""}{lead.phone ? ` · ${lead.phone}` : ""}
          </p>

          {checking ? <div style={{ padding: 20, textAlign: "center", color: colors.textLight }}>Checking whether this company is already a customer…</div> : (<>
            {matches.length > 0 && (
              <div style={{ border: `1px solid #f59e0b`, background: "#fffbeb", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: "#b45309", fontSize: 13, marginBottom: 6 }}>This company already exists as a customer</div>
                <select style={styles.select} value={clientId} onChange={e => setClientId(e.target.value)}>
                  {matches.map(m => <option key={m.id} value={m.id}>{m.company_name}{m.city ? ` — ${m.city}` : ""} (ID {m.id})</option>)}
                </select>
              </div>
            )}

            <div style={{ display: "flex", gap: 14, marginBottom: 14, fontSize: 13 }}>
              {matches.length > 0 && (
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" checked={mode === "existing"} onChange={() => setMode("existing")} /> Add this sale to the existing customer
                </label>
              )}
              <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                <input type="radio" checked={mode === "new"} onChange={() => setMode("new")} /> Create a new customer
              </label>
            </div>

            <div style={fieldRow}>
              <label style={styles.label}>What was sold? *</label>
              <select style={styles.select} value={saleTypeId} onChange={e => { setSaleTypeId(e.target.value); setErr(""); }}>
                <option value="">Select…</option>
                {masters.saleTypes?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            {mode === "existing" && needsSerial && (
              <div style={styles.grid(2)}>
                <div><label style={styles.label}>Product *</label>
                  <select style={styles.select} value={productId} onChange={e => setProductId(e.target.value)}>
                    <option value="">Select product…</option>
                    {masters.products?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div><label style={styles.label}>Serial / Licence Number *</label>
                  <input style={styles.input} value={serial} onChange={e => setSerial(e.target.value)} placeholder="e.g. 794080854" />
                </div>
                <div><label style={styles.label}>User Type</label>
                  <select style={styles.select} value={userType} onChange={e => setUserType(e.target.value)}>
                    <option>Single User</option><option>Multi User</option>
                  </select>
                </div>
                <div><label style={styles.label}>Note</label>
                  <input style={styles.input} value={note} onChange={e => setNote(e.target.value)} placeholder="optional" />
                </div>
              </div>
            )}

            {mode === "new" && (
              <div style={{ fontSize: 12.5, color: colors.textLight, marginBottom: 12 }}>
                The new customer form will open pre-filled from this lead — company, contact, phone, e-mail and city.
              </div>
            )}

            {err && <div style={{ background: "#fef2f2", color: colors.danger, padding: "8px 12px", borderRadius: 6, fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost")} onClick={() => onDone()}>Skip for now</button>
              {mode === "existing"
                ? <button style={styles.btn("primary")} onClick={saveExisting} disabled={busy}>{busy ? "Saving…" : "Record sale"}</button>
                : <button style={styles.btn("primary")} onClick={() => { if (!saleTypeId) { setErr("Please choose what was sold."); return; } setShowCreate(true); }}>Create customer →</button>}
            </div>
          </>)}
        </div>
      </div>
      )}

      {showCreate && (
        <FullCreate title="Customer" endpoint="clients" level={1}
          fields={clientsCreateFields(masters)}
          initialForm={{
            company_name: lead.prospect_company || "",
            contact_person: lead.contact_person || "",
            phone: lead.phone || "",
            email: lead.email || "",
            city: lead.city || "",
            location_id: lead.location_id ? String(lead.location_id) : "",
            relationship_manager_id: lead.attended_by_id ? String(lead.attended_by_id) : "",
            new_product_id: lead.product_id ? String(lead.product_id) : "",
          }}
          onCreated={afterCreate}
          onClose={() => setShowCreate(false)} />   /* back to the "what was sold?" step */
      )}
    </>
  );
};

// ---- Advanced filters (Excel-style, any field) ----
// The user adds filter rows one at a time: pick a field, then a value. Master
// fields cascade — choosing a Principal narrows the Customer list, choosing a
// Customer narrows the Licence list.
const callFilterFields = (masters, rows) => {
  const pick = (k) => rows.find(r => r.field === k)?.value;
  const principal = pick("principal_id");
  const client = pick("client_id");
  const clientOpts = (masters.clients || [])
    .filter(c => !principal || String(c.parent_principal_id) === String(principal))
    .map(c => ({ value: String(c.id), label: c.company_name }));
  const licenceOpts = Object.entries(masters.clientSerials || {})
    .filter(([cid]) => !client || String(cid) === String(client))
    .flatMap(([, sns]) => sns).filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i).slice(0, 500)
    .map(sn => ({ value: sn, label: sn }));
  return [
    // "Executive" means whoever is doing the work — the assignee if the call is
    // assigned, otherwise the executive on the call. This matches how the
    // dashboard and reports credit calls, so the numbers agree.
    { key: "handler_id", label: "Executive (handling the call)", type: "select", options: (masters.executives || []).map(e => ({ value: String(e.id), label: e.name })) },
    { key: "assigned_to_id", label: "Assigned To (only)", type: "select", options: (masters.executives || []).map(e => ({ value: String(e.id), label: e.name })) },
    { key: "executive_id", label: "Logged By (executive on the call)", type: "select", options: (masters.executives || []).map(e => ({ value: String(e.id), label: e.name })) },
    { key: "principal_id", label: "Principal Company", type: "select", options: (masters.principals || []).map(p => ({ value: String(p.id), label: p.company_name })) },
    { key: "client_id", label: "Customer" + (principal ? " (in this principal)" : ""), type: "select", options: clientOpts },
    { key: "group_id", label: "Customer Group", type: "select", options: (masters.groups || []).map(g => ({ value: String(g.id), label: g.name })) },
    { key: "license_number", label: "Licence / Serial No." + (client ? " (this customer)" : ""), type: licenceOpts.length ? "select" : "text", options: licenceOpts },
    { key: "product_id", label: "Product", type: "select", options: (masters.products || []).map(p => ({ value: String(p.id), label: p.name })) },
    { key: "call_type_id", label: "Call Type", type: "select", options: (masters.callTypes || []).map(c => ({ value: String(c.id), label: c.name })) },
    { key: "problem_id", label: "Problem Faced", type: "select", options: (masters.problems || []).map(p => ({ value: String(p.id), label: p.name })) },
    { key: "call_status", label: "Status", type: "select", options: ["Open","In Progress","Completed","Escalated","On Hold","Cancelled"].map(v => ({ value: v, label: v })) },
    { key: "location_id", label: "Location", type: "select", options: (masters.locations || []).map(l => ({ value: String(l.id), label: l.name })) },
    { key: "billed_through", label: "Billed Through / Partner", type: "select", options: (masters.partners || []).map(p => ({ value: String(p.id), label: p.name })) },
    { key: "tdl_type", label: "TDL Type", type: "text" },
    { key: "contact_person_called", label: "Contact Person Spoke To", type: "text" },
    { key: "software_version", label: "Release / Version", type: "text" },
    { key: "regarding", label: "Subject contains", type: "text" },
    { key: "physical_days", label: "Physical Days", type: "text" },
    { key: "__date_call", label: "Call Date (range)", type: "daterange", from: "date_from", to: "date_to" },
    { key: "__date_log", label: "Log Date (range)", type: "daterange", from: "log_from", to: "log_to" },
    { key: "__date_next", label: "Next Action Date (range)", type: "daterange", from: "next_from", to: "next_to" },
    { key: "__date_expiry", label: "Subscription Expiry (range)", type: "daterange", from: "expiry_from", to: "expiry_to" },
    // ---- every remaining Call Tracker form field ----
    { key: "call_details", label: "Call Details contains", type: "text" },
    { key: "problem_observed", label: "Problem Observed contains", type: "text" },
    { key: "solution_provided", label: "Solution Provided contains", type: "text" },
    { key: "min_hours", label: "Hours at least", type: "text" },
    { key: "rescheduled", label: "Rescheduled", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
    { key: "amc_call", label: "AMC call", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
    { key: "checked_in", label: "Visit checked in", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
  ];
};

// Field list for LEAD advanced filters
const leadFilterFields = (masters) => [
  { key: "attended_by_id", label: "Attended By", type: "select", options: (masters.executives || []).map(e => ({ value: String(e.id), label: e.name })) },
  { key: "assigned_to_id", label: "Assigned To", type: "select", options: (masters.executives || []).map(e => ({ value: String(e.id), label: e.name })) },
  { key: "assigned_by_id", label: "Assigned By", type: "select", options: (masters.executives || []).map(e => ({ value: String(e.id), label: e.name })) },
  { key: "lead_source", label: "Lead Source", type: "select", options: (masters.leadSources || []).map(l => ({ value: l.name, label: l.name })) },
  { key: "status", label: "Status", type: "select",
    options: (masters.leadStatuses || []).length
      ? (masters.leadStatuses || []).map(s => ({ value: s.name, label: s.name }))
      : ["New","Contacted","Demo Scheduled","Demo Completed","Proposal Sent","Negotiation","Converted","Lost","On Hold","Closed"].map(v => ({ value: v, label: v })) },
  { key: "priority", label: "Priority", type: "select", options: ["Hot","Warm","Cold"].map(v => ({ value: v, label: v })) },
  { key: "product_id", label: "Product", type: "select", options: (masters.products || []).map(p => ({ value: String(p.id), label: p.name })) },
  { key: "location_id", label: "Location", type: "select", options: (masters.locations || []).map(l => ({ value: String(l.id), label: l.name })) },
  { key: "sale_type_id", label: "Sale Type (converted)", type: "select", options: (masters.saleTypes || []).map(t => ({ value: String(t.id), label: t.name })) },
  { key: "prospect_company", label: "Prospect Company contains", type: "text" },
  { key: "contact_person", label: "Contact Person contains", type: "text" },
  { key: "city", label: "City contains", type: "text" },
  { key: "__date_enquiry", label: "Enquiry Date (range)", type: "daterange", from: "date_from", to: "date_to" },
  { key: "__date_followup", label: "Follow-up Date (range)", type: "daterange", from: "fu_from", to: "fu_to" },
  { key: "__date_closed", label: "Converted Date (range)", type: "daterange", from: "conv_from", to: "conv_to" },
  { key: "__date_activity", label: "Closed / Last Activity Date (range)", type: "daterange", from: "act_from", to: "act_to" },
  // ---- every remaining Lead form field ----
  { key: "phone_has", label: "Phone contains", type: "text" },
  { key: "email_has", label: "Email contains", type: "text" },
  { key: "designation_has", label: "Designation contains", type: "text" },
  { key: "__date_demo", label: "Demo Date (range)", type: "daterange", from: "demo_from", to: "demo_to" },
  { key: "min_value", label: "Estimated Value at least (₹)", type: "text" },
  { key: "min_deal", label: "Deal Value (closed) at least (₹)", type: "text" },
  { key: "min_users", label: "No. of Users at least", type: "text" },
  { key: "next_action", label: "Next Action", type: "select", options: ["Call Back", "Send Brochure", "Schedule Demo", "Send Proposal", "Site Visit", "Follow Up Email", "Negotiate", "Close Deal", "No Action"].map(v => ({ value: v, label: v })) },
  { key: "lost_reason", label: "Lost Reason", type: "select", options: lostReasonOptions(masters) },
  { key: "latest_outcome", label: "Latest Outcome (last follow-up)", type: "select", options: ["Interested", "Not Ready", "Callback Requested", "Proposal Sent", "Negotiating", "No Response", "Converted", "Lost"].map(v => ({ value: v, label: v })) },
  { key: "competitor_has", label: "Competitor Info contains", type: "text" },
  { key: "remarks_has", label: "Remarks contains", type: "text" },
  { key: "referral_client_id", label: "Referred By (customer)", type: "select", options: (masters.clients || []).map(c => ({ value: String(c.id), label: c.company_name })) },
];

// Field list for the Licences & Renewals report
const licenceFilterFields = (masters) => [
  { key: "client_id", label: "Customer", type: "select", options: (masters.clients || []).map(c => ({ value: String(c.id), label: c.company_name })) },
  { key: "principal_id", label: "Principal Company", type: "select", options: (masters.principals || []).map(p => ({ value: String(p.id), label: p.company_name })) },
  { key: "group_id", label: "Customer Group", type: "select", options: (masters.groups || []).map(g => ({ value: String(g.id), label: g.name })) },
  { key: "product_id", label: "Product", type: "select", options: (masters.products || []).map(p => ({ value: String(p.id), label: p.name })) },
  { key: "user_type", label: "User Type", type: "select", options: ["Single User","Multi User"].map(v => ({ value: v, label: v })) },
  { key: "license_number", label: "Licence / Serial contains", type: "text" },
];

const AdvancedFilters = ({ masters, screen = "calls", rows, setRows, onApply, fieldsFn, title = "Advanced filters" }) => {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [savedId, setSavedId] = useState("");
  const fields = (fieldsFn || callFilterFields)(masters, rows);
  const fieldOf = (k) => fields.find(f => f.key === k);

  const loadSaved = async () => { try { const r = await api(`/call-logs/saved-filters?screen=${screen}`); setSaved(r.data || []); } catch (e) {} };
  useEffect(() => { loadSaved(); /* eslint-disable-next-line */ }, []);

  const addRow = () => setRows([...rows, { field: "", value: "", from: "", to: "" }]);
  const setRow = (i, patch) => setRows(rows.map((r, n) => n === i ? { ...r, ...patch } : r));
  const delRow = (i) => setRows(rows.filter((_, n) => n !== i));

  const saveSet = async () => {
    const active = rows.filter(r => r.field && (r.value || r.from || r.to));
    if (!active.length) return;
    const name = window.prompt("Save this filter set as:", saved.find(s => String(s.id) === savedId)?.name || "");
    if (!name || !name.trim()) return;
    try {
      const r = await api(`/call-logs/saved-filters`, { method: "POST", body: { name: name.trim(), screen, definition: active } });
      await loadSaved(); setSavedId(String(r.data.id));
    } catch (e) {}
  };
  const applySaved = (id) => {
    setSavedId(id);
    const set = saved.find(s => String(s.id) === String(id));
    if (!set) return;
    const d = typeof set.definition === "string" ? JSON.parse(set.definition) : set.definition;
    setRows(d || []); setOpen(true);
  };
  const delSaved = async () => {
    const set = saved.find(s => String(s.id) === savedId);
    if (!set || !window.confirm(`Delete filter set "${set.name}"?`)) return;
    try { await api(`/call-logs/saved-filters/${set.id}`, { method: "DELETE" }); setSavedId(""); loadSaved(); } catch (e) {}
  };

  const activeCount = rows.filter(r => r.field && (r.value || r.from || r.to)).length;

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 12, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", flexWrap: "wrap" }}>
        <button onClick={() => setOpen(o => !o)} style={{ ...styles.btn("ghost", "sm"), fontWeight: 700 }}>
          {open ? "▲" : "▼"} ➕ {title}{activeCount ? ` (${activeCount} active)` : ""}
        </button>
        <select style={{ ...styles.select, maxWidth: 240 }} value={savedId} onChange={e => applySaved(e.target.value)}>
          <option value="">— Saved filter sets —</option>
          {saved.map(sv => <option key={sv.id} value={sv.id}>{sv.name}{sv.created_by_name ? ` · ${sv.created_by_name}` : ""}</option>)}
        </select>
        {activeCount > 0 && <button style={styles.btn("ghost", "sm")} onClick={saveSet}>💾 Save set</button>}
        {savedId && <button style={{ ...styles.btn("ghost", "sm"), color: colors.danger }} onClick={delSaved}>🗑</button>}
        {activeCount > 0 && <button style={styles.btn("ghost", "sm")} onClick={() => { setRows([]); setSavedId(""); onApply([]); }}>Clear all</button>}
      </div>

      {open && (
        <div style={{ borderTop: `1px solid ${colors.border}`, padding: "10px 12px" }}>
          {rows.length === 0 && <div style={{ fontSize: 12.5, color: colors.textLight, marginBottom: 8 }}>No filters yet — click “Add filter” and choose any field.</div>}
          {rows.map((r, i) => {
            const f = fieldOf(r.field);
            return (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
                <select style={{ ...styles.select, maxWidth: 240 }} value={r.field} onChange={e => setRow(i, { field: e.target.value, value: "", from: "", to: "" })}>
                  <option value="">Choose a field…</option>
                  {fields.filter(x => x.key === r.field || !rows.some(rr => rr.field === x.key)).map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                </select>
                {f?.type === "select" && (
                  <select style={{ ...styles.select, maxWidth: 260 }} value={r.value} onChange={e => setRow(i, { value: e.target.value })}>
                    <option value="">Any</option>
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
                {f?.type === "text" && (
                  <input style={{ ...styles.input, maxWidth: 260 }} value={r.value} onChange={e => setRow(i, { value: e.target.value })} placeholder="contains…" />
                )}
                {f?.type === "daterange" && (<>
                  <DateInput value={r.from} onChange={v => setRow(i, { from: v })} />
                  <span style={{ color: colors.textLight, fontSize: 12 }}>to</span>
                  <DateInput value={r.to} onChange={v => setRow(i, { to: v })} />
                </>)}
                <button style={{ ...styles.btn("ghost", "sm"), color: colors.danger }} onClick={() => delRow(i)}>✕</button>
              </div>
            );
          })}
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button style={styles.btn("ghost", "sm")} onClick={addRow}>➕ Add filter</button>
            <button style={styles.btn("primary", "sm")} onClick={() => onApply(rows)}>Apply filters</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Turn filter rows into query parameters
const advParams = (rows, fields) => {
  const out = {};
  (rows || []).forEach(r => {
    const f = fields.find(x => x.key === r.field);
    if (!f) return;
    if (f.type === "daterange") { if (r.from) out[f.from] = r.from; if (r.to) out[f.to] = r.to; }
    else if (r.value) out[r.field] = r.value;
  });
  return out;
};

const scrollByKey = (e) => {
  const el = e.currentTarget;
  const step = 60;
  const keys = { ArrowDown: () => el.scrollTop += step, ArrowUp: () => el.scrollTop -= step, ArrowRight: () => el.scrollLeft += step, ArrowLeft: () => el.scrollLeft -= step, PageDown: () => el.scrollTop += el.clientHeight * 0.85, PageUp: () => el.scrollTop -= el.clientHeight * 0.85, Home: () => el.scrollTop = 0, End: () => el.scrollTop = el.scrollHeight };
  if (keys[e.key]) { keys[e.key](); e.preventDefault(); }
};

// Wraps a wide table: page scrolls vertically as normal; horizontal scrolling is
// done with ◀ ▶ buttons or Arrow keys (after clicking into the box). Pair with a
// sticky first column so the Customer name stays visible while scrolling right.
const ScrollBox = ({ children, minWidth }) => {
  const ref = useRef(null);
  const nudge = (dx) => { if (ref.current) ref.current.scrollBy({ left: dx, behavior: "smooth" }); };
  return (
    <div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
        <button onClick={() => nudge(-320)} style={{ border: `1px solid ${colors.border}`, background: "#fff", borderRadius: 6, cursor: "pointer", padding: "2px 10px", fontWeight: 700 }}>◀</button>
        <button onClick={() => nudge(320)} style={{ border: `1px solid ${colors.border}`, background: "#fff", borderRadius: 6, cursor: "pointer", padding: "2px 10px", fontWeight: 700 }}>▶</button>
        <span style={{ fontSize: 11, color: colors.textLight }}>scroll sideways (or click the table and use arrow keys)</span>
      </div>
      <div ref={ref} tabIndex={0} onKeyDown={scrollByKey} style={{ overflow: "auto", maxHeight: isMobileNow() ? "60vh" : "68vh", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0, outline: "none", border: `1px solid ${colors.border}`, borderRadius: 8 }}>
        {children}
      </div>
    </div>
  );
};

// Parse time from any format to 24-hour HH:MM
const parseTimeTo24hr = (timeStr) => {
  if (!timeStr) return "";
  const s = timeStr.trim().toUpperCase();
  // Already 24hr format like 14:30
  if (s.match(/^\d{1,2}:\d{2}$/) && !s.includes("AM") && !s.includes("PM")) {
    const [h, m] = s.split(":");
    return `${h.padStart(2, "0")}:${m}`;
  }
  // 12hr format like 2:30 PM or 02:30 PM
  const match = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (match) {
    let h = parseInt(match[1]);
    const m = match[2];
    const ampm = match[3];
    if (ampm === "PM" && h < 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${m}`;
  }
  // Try to extract just numbers like 1430 or 230
  const digits = s.replace(/[^\d]/g, "");
  if (digits.length === 4) return `${digits.slice(0,2)}:${digits.slice(2)}`;
  if (digits.length === 3) return `0${digits[0]}:${digits.slice(1)}`;
  return timeStr;
};

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================
const colors = {
  primary: "#1a56db",
  primaryLight: "#e8eefb",
  secondary: "#64748b",
  success: "#059669",
  warning: "#d97706",
  danger: "#dc2626",
  bg: "#f1f5f9",
  card: "#ffffff",
  border: "#e2e8f0",
  text: "#1e293b",
  textLight: "#64748b",
  hot: "#dc2626",
  warm: "#d97706",
  cold: "#3b82f6",
};

// ---- Mobile support ----
// Screens narrower than this are treated as phones. Desktop layout is untouched.
const MOBILE_BP = 768;
const isMobileNow = () => (typeof window !== "undefined" && window.innerWidth < MOBILE_BP);
const useIsMobile = () => {
  const [mob, setMob] = useState(isMobileNow());
  useEffect(() => {
    const onResize = () => setMob(isMobileNow());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("orientationchange", onResize); };
  }, []);
  return mob;
};

// The style objects below read isMobileNow() when they are evaluated. Crossing the
// mobile/desktop boundary (rotating a phone, resizing a window) needs a re-render
// so those styles are recalculated — this forces one, debounced.
const useResponsiveStyles = () => {
  const [, bump] = useState(0);
  useEffect(() => {
    let last = isMobileNow(), t = null;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => { const now = isMobileNow(); if (now !== last) { last = now; bump(n => n + 1); } }, 150);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); window.removeEventListener("orientationchange", onResize); };
  }, []);
};

const styles = {
  page: { minHeight: "100vh", background: colors.bg, fontFamily: "'Segoe UI', -apple-system, sans-serif", color: colors.text, ...(isMobileNow() ? { overflowX: "hidden", width: "100%", maxWidth: "100vw" } : {}) },
  sidebar: { width: 250, background: "#0f172a", color: "#fff", position: "fixed", top: 0, left: 0, bottom: 0, overflowY: "auto", zIndex: 100, transition: "transform 0.3s" },
  sidebarHeader: { padding: "20px 16px", borderBottom: "1px solid #1e293b", fontSize: 15, fontWeight: 700, letterSpacing: 0.5 },
  navGroup: { padding: "12px 0" },
  navLabel: { padding: "6px 16px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "#64748b" },
  navItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", fontSize: 13, cursor: "pointer", background: active ? "#1e293b" : "transparent", color: active ? "#60a5fa" : "#94a3b8", borderLeft: active ? "3px solid #60a5fa" : "3px solid transparent", transition: "all 0.15s" }),
  main: { marginLeft: isMobileNow() ? 0 : 250, padding: isMobileNow() ? "12px 10px 24px" : "24px 32px", paddingTop: isMobileNow() ? 64 : 24, ...(isMobileNow() ? { width: "100%", maxWidth: "100vw", boxSizing: "border-box", overflowX: "hidden" } : {}) },  topbar: { display: "flex", justifyContent: "space-between", alignItems: isMobileNow() ? "flex-start" : "center", flexDirection: isMobileNow() ? "column" : "row", gap: isMobileNow() ? 10 : 0, marginBottom: isMobileNow() ? 14 : 24 },
  h1: { fontSize: isMobileNow() ? 19 : 22, fontWeight: 700, margin: 0 },
  card: { background: colors.card, borderRadius: 10, border: `1px solid ${colors.border}`, padding: isMobileNow() ? 12 : 24, marginBottom: isMobileNow() ? 12 : 20, ...(isMobileNow() ? { maxWidth: "100%", boxSizing: "border-box", overflowX: "hidden" } : {}) },
  statCard: (color) => ({ background: colors.card, borderRadius: 10, border: `1px solid ${colors.border}`, padding: isMobileNow() ? "12px 14px" : "20px 24px", flex: 1, minWidth: isMobileNow() ? 140 : 0, borderLeft: `4px solid ${color}` }),
  btn: (variant = "primary", size = "md") => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, border: "none", borderRadius: 7, cursor: "pointer", fontWeight: 600,
    fontSize: isMobileNow() ? (size === "sm" ? 13 : 14) : (size === "sm" ? 12 : 13),
    padding: isMobileNow() ? (size === "sm" ? "9px 14px" : "12px 18px") : (size === "sm" ? "6px 12px" : "9px 18px"),
    minHeight: isMobileNow() ? (size === "sm" ? 38 : 44) : "auto", whiteSpace: "nowrap",
    background: variant === "primary" ? colors.primary : variant === "success" ? colors.success : variant === "danger" ? colors.danger : variant === "warning" ? colors.warning : "#e2e8f0",
    color: variant === "ghost" ? colors.text : "#fff",
    transition: "opacity 0.15s", opacity: 1,
  }),
  input: { width: "100%", padding: isMobileNow() ? "11px 12px" : "9px 12px", border: `1px solid ${colors.border}`, borderRadius: 7, fontSize: isMobileNow() ? 16 : 13, outline: "none", boxSizing: "border-box" },
  select: { width: "100%", padding: isMobileNow() ? "11px 12px" : "9px 12px", border: `1px solid ${colors.border}`, borderRadius: 7, fontSize: isMobileNow() ? 16 : 13, outline: "none", boxSizing: "border-box", background: "#fff" },
  label: { display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4, color: colors.textLight },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "10px 12px", borderBottom: `2px solid ${colors.border}`, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: colors.textLight },
  td: { padding: "10px 12px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "middle" },
  badge: (color) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: color + "18", color }),
  grid: (cols) => ({ display: "grid", gridTemplateColumns: isMobileNow() ? "1fr" : `repeat(${cols}, 1fr)`, gap: isMobileNow() ? 12 : 16 }),
  flexRow: { display: "flex", gap: isMobileNow() ? 8 : 12, alignItems: isMobileNow() ? "stretch" : "center", ...(isMobileNow() ? { flexWrap: "wrap", width: "100%" } : {}) },
  filterBar: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: isMobileNow() ? "stretch" : "flex-end", marginBottom: 16, width: "100%", ...(isMobileNow() ? { flexDirection: "column" } : {}) },
  modal: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: isMobileNow() ? "flex-start" : "center", justifyContent: "center", zIndex: 1000, padding: isMobileNow() ? "8px 0" : 0, overflowY: isMobileNow() ? "auto" : "hidden" },
  modalContent: { background: "#fff", borderRadius: isMobileNow() ? 10 : 12, padding: isMobileNow() ? 14 : 28, width: isMobileNow() ? "96%" : "90%", maxWidth: 700, maxHeight: isMobileNow() ? "92vh" : "85vh", overflowY: "auto" },
  toast: (type) => ({
    position: "fixed", top: 20, right: 20, padding: "12px 24px", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, zIndex: 9999,
    background: type === "success" ? colors.success : type === "error" ? colors.danger : colors.warning,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)", animation: "slideIn 0.3s ease",
  }),
};

// Toast notification
const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return <div style={styles.toast(type)}>{message}</div>;
};

// Date Input — custom DD/MM/YYYY format with calendar popup
const DateInput = ({ value, onChange, ...props }) => {
  const isoVal = value ? toISODate(value) : "";
  const displayFromIso = isoVal ? (() => { const [y,m,d] = isoVal.split("-"); return `${d}/${m}/${y}`; })() : "";
  const [typing, setTyping] = useState(false);
  const [typedVal, setTypedVal] = useState("");

  const handleTextChange = (e) => {
    let v = e.target.value.replace(/[^\d/]/g, "");
    const digits = v.replace(/\//g, "");
    if (digits.length >= 2 && v.split("/").length < 2) v = digits.slice(0, 2) + "/" + digits.slice(2);
    if (digits.length >= 4 && v.split("/").length < 3) { const p = v.split("/"); v = p[0] + "/" + p[1].slice(0, 2) + "/" + (p[1].slice(2) + (p[2] || "")); }
    if (v.length > 10) v = v.slice(0, 10);
    setTypedVal(v);
    setTyping(true);
    const parts = v.split("/");
    if (parts.length === 3 && parts[2].length === 4) {
      const [d, m, y] = parts;
      const day = parseInt(d), mon = parseInt(m), year = parseInt(y);
      if (day >= 1 && day <= 31 && mon >= 1 && mon <= 12 && year >= 1900 && year <= 2100) {
        onChange(`${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`);
      }
    }
  };

  const handleBlur = () => { setTyping(false); setTypedVal(""); };
  const calRef = { current: null };

  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <input
        style={{ ...styles.input, paddingRight: 36 }}
        value={typing ? typedVal : displayFromIso}
        onChange={handleTextChange}
        onFocus={() => { setTyping(true); setTypedVal(displayFromIso); }}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          // Prevent arrow keys from doing anything in date field — let Tab work naturally
          if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
        }}
        placeholder="DD/MM/YYYY"
        maxLength={10}
        {...props}
      />
      <span style={{ position: "absolute", right: 8, fontSize: 18, cursor: "pointer", color: colors.textLight, zIndex: 1 }} onClick={() => { calRef.current?.showPicker?.(); }}>📅</span>
      <input
        ref={el => calRef.current = el}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        style={{ position: "absolute", right: 0, top: 0, width: 0, height: 0, opacity: 0, pointerEvents: "none", overflow: "hidden" }}
        value={isoVal}
        onChange={(e) => { onChange(e.target.value); setTyping(false); }}
      />
    </div>
  );
};

// Pagination
const Pagination = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.pages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
      <button style={styles.btn("ghost", "sm")} onClick={() => onPageChange(pagination.page - 1)} disabled={pagination.page <= 1}>← Prev</button>
      <span style={{ padding: "6px 12px", fontSize: 12, color: colors.textLight }}>Page {pagination.page} of {pagination.pages} ({pagination.total} records)</span>
      <button style={styles.btn("ghost", "sm")} onClick={() => onPageChange(pagination.page + 1)} disabled={pagination.page >= pagination.pages}>Next →</button>
    </div>
  );
};

// Custom Time Input with AM/PM - always shows 12-hour format
const TimeInput = ({ value, onChange }) => {
  // value is in 24hr format "HH:MM" e.g. "14:30"
  const parse24 = (v) => {
    if (!v) return { hour: "", minute: "", period: "AM" };
    const [h, m] = v.split(":");
    let hr = parseInt(h) || 0;
    const period = hr >= 12 ? "PM" : "AM";
    if (hr === 0) hr = 12;
    else if (hr > 12) hr -= 12;
    return { hour: hr.toString(), minute: (m || "00").padStart(2, "0"), period };
  };
  const to24 = (hour, minute, period) => {
    if (!hour) return "";
    let h = parseInt(hour);
    if (period === "AM" && h === 12) h = 0;
    else if (period === "PM" && h !== 12) h += 12;
    return `${h.toString().padStart(2, "0")}:${minute || "00"}`;
  };
  const { hour, minute, period } = parse24(value);
  const update = (h, m, p) => { const v = to24(h || hour, m || minute, p || period); if (v) onChange(v); };
  const selStyle = { ...styles.select, width: "auto", minWidth: 55, padding: "6px 4px", fontSize: 14 };
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <select style={selStyle} value={hour} onChange={(e) => update(e.target.value, null, null)}>
        <option value="">HH</option>
        {[12,1,2,3,4,5,6,7,8,9,10,11].map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span style={{ fontWeight: 700 }}>:</span>
      <select style={selStyle} value={minute} onChange={(e) => update(null, e.target.value, null)}>
        <option value="">MM</option>
        {Array.from({length: 60}, (_, i) => i.toString().padStart(2, "0")).map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <select style={{ ...selStyle, minWidth: 55 }} value={period} onChange={(e) => update(null, null, e.target.value)}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

// Searchable Select Dropdown with typeahead
// ============================================================================
// FastText — a text box that keeps the typing to itself. Letters appear at once;
// the form is told about the new text 250 ms after you pause, when you leave the
// box, or just before saving (flushFastText). Without this, every letter redrew
// the whole Call Tracker (form + list of calls), which made typing lag.
// ============================================================================
const __fastPending = new Set();          // boxes with typing not yet passed to the form
const flushFastText = () => {             // pass on any pending typing; returns true if there was some
  const fns = [...__fastPending]; __fastPending.clear();
  fns.forEach(fn => fn());
  return fns.length > 0;
};
// Safety net for keyboard shortcuts pressed within 250 ms of typing (Alt+S, Esc, …):
// pass the just-typed text to its form first, then replay the same key a moment
// later, once the form has it. (Clicking a button needs nothing: leaving the box
// hands the text over before the click.)
const __fastReplayed = new WeakSet();
if (typeof window !== "undefined") {
  window.addEventListener("keydown", (e) => {
    if (__fastReplayed.has(e) || !(e.key === "Escape" || e.altKey)) return;
    if (!__fastPending.size) return;
    if (!flushFastText()) return;
    e.preventDefault(); e.stopImmediatePropagation();
    const target = document.activeElement || document.body;
    setTimeout(() => {
      const again = new KeyboardEvent("keydown", { key: e.key, code: e.code, altKey: e.altKey, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey, bubbles: true, cancelable: true });
      __fastReplayed.add(again);
      target.dispatchEvent(again);
    }, 60);
  }, true);   // capture: runs before every screen's own shortcut handling
}
const FastText = ({ value, onCommit, multiline = false, onBlur, ...rest }) => {
  const [v, setV] = useState(value ?? "");
  const last = useRef(value ?? "");        // the value the form currently has
  const timer = useRef(null);
  const flushRef = useRef(null);
  useEffect(() => {                        // the form changed it (e.g. voice minutes, reset) → show that
    if ((value ?? "") !== last.current) { last.current = value ?? ""; setV(value ?? ""); }
  }, [value]);
  const commit = (nv) => {
    clearTimeout(timer.current);
    if (flushRef.current) { __fastPending.delete(flushRef.current); flushRef.current = null; }
    if (nv !== last.current) { last.current = nv; onCommit(nv); }
  };
  useEffect(() => () => { clearTimeout(timer.current); if (flushRef.current) __fastPending.delete(flushRef.current); }, []);
  const change = (e) => {
    const nv = e.target.value;
    setV(nv);
    clearTimeout(timer.current);
    if (flushRef.current) __fastPending.delete(flushRef.current);
    flushRef.current = () => commit(nv);
    __fastPending.add(flushRef.current);
    timer.current = setTimeout(() => commit(nv), 250);
  };
  const Tag = multiline ? "textarea" : "input";
  return <Tag {...rest} value={v} onChange={change} onBlur={(e) => { commit(e.target.value); if (onBlur) onBlur(e); }} />;
};

// ============================================================================
// SearchInput — a search box that keeps the typing to itself: letters appear at
// once, and the search runs 300 ms after you pause (or on Enter / leaving the box).
// Before, every letter re-ran the search and redrew the whole screen.
// Esc clears it (first press), like before.
// ============================================================================
const SearchInput = ({ value, onSearch, onEnter, delay = 300, ...rest }) => {
  const [v, setV] = useState(value || "");
  const sent = useRef(value || "");
  const timer = useRef(null);
  useEffect(() => { if ((value || "") !== sent.current) { sent.current = value || ""; setV(value || ""); } }, [value]);   // cleared from outside (Clear button)
  useEffect(() => () => clearTimeout(timer.current), []);
  const send = (nv) => { clearTimeout(timer.current); if (nv !== sent.current) { sent.current = nv; onSearch(nv); } };
  return (
    <input {...rest} value={v}
      onChange={e => { const nv = e.target.value; setV(nv); clearTimeout(timer.current); timer.current = setTimeout(() => send(nv), delay); }}
      onBlur={() => send(v)}
      onKeyDown={e => {
        if (e.key === "Escape" && v) { e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); setV(""); send(""); return; }
        if (e.key === "Enter") { send(v); if (onEnter) setTimeout(onEnter, 60); }   // let the new search text reach the screen first
      }} />
  );
};

// One shared, fast comparer for dropdown lists (case-insensitive; "Item 9" before "Item 10")
const __ssCollator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
const SearchableSelect = ({ options, value, onChange, placeholder, onCreate, createLabel }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  // Sort the list only when it actually changes. It used to be re-sorted on EVERY
  // redraw — i.e. on every key typed anywhere in the form — and with thousands of
  // customers that made typing in the Call Tracker lag.
  const sortCache = useRef({ key: null, list: [] });
  const optKey = options.length + "|" + options.map(o => o.value + "\u0001" + o.label).join("\u0002");
  if (sortCache.current.key !== optKey) {
    sortCache.current = { key: optKey, list: [...options].sort((a, b) => __ssCollator.compare(String(a.label ?? ""), String(b.label ?? ""))) };
  }
  const sorted = sortCache.current.list;
  // Match on WORD STARTS, not anywhere in the text: typing "ar in" matches
  // "AR Infomatrix" but not "beralmar india". Best matches are listed first.
  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    const terms = q.split(/\s+/).filter(Boolean);
    const score = (label) => {
      const l = String(label || "").toLowerCase();
      const words = l.split(/[^a-z0-9]+/).filter(Boolean);
      // every typed word must start some word in the label (or the number must contain it)
      const ok = terms.every(t => words.some(w => w.startsWith(t)) || (/^\d+$/.test(t) && l.includes(t)));
      if (!ok) return -1;
      if (l.startsWith(q)) return 0;          // best: label begins with what was typed
      if (words[0] && words[0].startsWith(terms[0])) return 1;   // first word matches
      return 2;                                // other word-start matches
    };
    return sorted
      .map(o => ({ o, s: score(o.label) }))
      .filter(x => x.s >= 0)
      .sort((a, b) => a.s - b.s)
      .map(x => x.o);
  })();
  const selected = options.find(o => o.value == value);
  const listRef = { current: null };

  const scrollToItem = (idx) => {
    if (listRef.current) {
      const items = listRef.current.querySelectorAll('[data-option]');
      if (items[idx]) items[idx].scrollIntoView({ block: "nearest" });
    }
  };

  const selectItem = (val) => {
    onChange(val);
    setSearch("");
    setOpen(false);
    setHighlightIdx(-1);
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={{ ...styles.input, display: "flex", alignItems: "center", padding: 0, cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <input
          style={{ flex: 1, border: "none", outline: "none", padding: "8px 12px", fontSize: 13, background: "transparent" }}
          value={open ? search : (selected?.label || "")}
          onChange={(e) => { setSearch(e.target.value); setHighlightIdx(0); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setHighlightIdx(-1); }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              if (open || search) {
                e.preventDefault(); e.stopPropagation();
                setSearch(""); setOpen(false); setHighlightIdx(-1);
              }
              // If dropdown is already closed and no search text, let Escape bubble up to close the form
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!open) { setOpen(true); setHighlightIdx(0); }
              else { const next = Math.min(highlightIdx + 1, filtered.length - 1); setHighlightIdx(next); scrollToItem(next); }
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              if (highlightIdx > 0) { const prev = highlightIdx - 1; setHighlightIdx(prev); scrollToItem(prev); }
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (open && highlightIdx >= 0 && highlightIdx < filtered.length) { selectItem(filtered[highlightIdx].value); }
              else if (open && filtered.length === 1) { selectItem(filtered[0].value); }
            } else if (e.key === "Tab") {
              // On Tab: if dropdown is open and something is highlighted, select it, then let Tab move naturally
              if (open && highlightIdx >= 0 && highlightIdx < filtered.length) {
                selectItem(filtered[highlightIdx].value);
              } else if (open) {
                setSearch(""); setOpen(false); setHighlightIdx(-1);
              }
              // Don't preventDefault — let Tab move to next field naturally
            }
          }}
          placeholder={placeholder || "Search..."}
        />
        {value && <span style={{ padding: "0 8px", cursor: "pointer", color: colors.textLight, fontSize: 14 }} tabIndex={-1} onClick={(e) => { e.stopPropagation(); onChange(""); setSearch(""); }}>✕</span>}
        <span style={{ padding: "0 10px", color: colors.textLight, fontSize: 10 }} tabIndex={-1}>▼</span>
      </div>
      {open && (
        <div ref={el => listRef.current = el} style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 8, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          <div style={{ padding: "4px 8px", cursor: "pointer", fontSize: 12, color: colors.textLight, background: "#f8fafc" }} onMouseDown={(e) => { e.preventDefault(); selectItem(""); }}>
            — None —
          </div>
          {filtered.map((o, idx) => (
            <div key={o.value} data-option style={{ padding: "6px 12px", cursor: "pointer", fontSize: 12,
              background: idx === highlightIdx ? colors.primary : (o.value == value ? colors.primaryLight : "transparent"),
              color: idx === highlightIdx ? "#fff" : "inherit" }}
              onMouseEnter={() => setHighlightIdx(idx)}
              onMouseDown={(e) => { e.preventDefault(); selectItem(o.value); }}>
              {o.label}
            </div>
          ))}
          {filtered.length === 0 && <div style={{ padding: "8px 12px", fontSize: 12, color: colors.textLight }}>No results found</div>}
          {onCreate && (
            <div style={{ padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: colors.primary, borderTop: `1px solid ${colors.border}`, background: "#f0f4ff" }}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); onCreate(); }}>
              + {createLabel || "Create New"}
            </div>
          )}
        </div>
      )}
      {open && <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => { setOpen(false); setSearch(""); setHighlightIdx(-1); }} />}
    </div>
  );
};

// Full Create Modal for masters — opens complete form on top of everything
const FullCreate = ({ title, fields, endpoint, onCreated, onClose, level = 0, initialForm }) => {
  const [form, setForm] = useState(initialForm || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [nestedCreate, setNestedCreate] = useState(null);
  const zBase = 2000 + (level * 1000);

  // Customers: same duplicate rules as the full master, checked live
  const isCust = endpoint === "clients";
  const { dupes, similar } = useCustomerDupes(form, null, isCust);
  // Picking an existing customer here simply selects it for the call
  const useExisting = (rec) => {
    if (!window.confirm(`Use the existing customer "${rec.company_name}" instead of creating a new one?`)) return;
    onCreated(rec); onClose();
  };

  const custErrors = () => {
    if (!isCust) return [];
    const e = []; const has = (v) => String(v || "").trim().length > 0;
    if (!has(form.new_license_number)) e.push("Product serial / licence number is required for a new customer.");
    if (has(form.new_license_number) && !has(form.new_product_id)) e.push("Please choose the product this serial number belongs to.");
    if (form.gst_registration === "Registered") {
      const g = String(form.gstin || "").replace(/\s/g, "");
      if (!g) e.push("GSTIN is required when the customer is GST Registered.");
      else if (g.length !== 15) e.push("GSTIN must be 15 characters.");
    }
    return e;
  };

  const handleSave = async () => {
    if (isCust) {
      const errs = custErrors();
      if (errs.length) { setError(errs[0]); return; }
      if (dupes.length) {
        setError(`Cannot save — ${dupes[0].label} already used by "${dupes[0].rec.company_name}" (ID ${dupes[0].rec.id}).`);
        return;
      }
    } else {
      // Other masters (product, executive, designation…): same name, mobile or
      // e-mail as an existing record is blocked — as on the master screens.
      const nameKey = fields.find(f => f.required)?.key || "name";
      const nn = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const em = (v) => String(v || "").trim().toLowerCase();
      const ph = ["phone", "alternate_phone", "mobile"].filter(k => fields.some(f => f.key === k));
      const ems = ["email"].filter(k => fields.some(f => f.key === k));
      const typedPh = ph.flatMap(k => splitVals(form[k]).map(normPhone)).filter(p => p.length >= 10);
      const typedEm = ems.flatMap(k => splitVals(form[k]).map(em)).filter(m => m.includes("@"));
      if (nn(form[nameKey]) || typedPh.length || typedEm.length) {
        try {
          const all = (await api(`/${endpoint}?limit=100000`)).data || [];
          const tag = (d) => `"${d[nameKey] || d.name || "ID " + d.id}" (ID ${d.id}${d.is_active === false ? ", inactive — reactivate it instead" : ""})`;
          let clash = null;
          const byName = nn(form[nameKey]) && all.find(d => nn(d[nameKey]) === nn(form[nameKey]));
          if (byName) clash = `this name is already used by ${tag(byName)}`;
          for (const p of typedPh) { if (clash) break; const d = all.find(o => ph.some(k => splitVals(o[k]).map(normPhone).includes(p))); if (d) clash = `mobile ${p} is already used by ${tag(d)}`; }
          for (const m of typedEm) { if (clash) break; const d = all.find(o => ems.some(k => splitVals(o[k]).map(em).includes(m))); if (d) clash = `e-mail ${m} is already used by ${tag(d)}`; }
          if (clash) { setError(`Cannot save — ${clash}.`); return; }
        } catch (e) { /* list not available — let the server decide */ }
      }
    }
    setSaving(true); setError("");
    try {
      // These two are form-only helpers for creating the customer's first licence
      const { new_product_id, new_license_number, new_user_type, new_subscription_expiry, ...payload } = form;
      const r = await api(`/${endpoint}`, { method: "POST", body: payload });
      if (isCust && r.data?.id && new_license_number && new_product_id) {
        try {
          await api(`/customer-products`, { method: "POST", body: {
            client_id: r.data.id, product_id: new_product_id,
            license_number: String(new_license_number).trim(),
            user_type: new_user_type || "Single User", subscription_expiry: new_subscription_expiry || null, is_active: true } });
        } catch (e) { /* customer is saved; the licence can be added in the next step */ }
      }
      onCreated(r.data);
      onClose();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  // Capture Escape and Alt+S — only when no nested create is open
  useEffect(() => {
    const handleKey = (e) => {
      if (nestedCreate) return; // Let nested handle it
      if (e.key === "Escape") { e.stopImmediatePropagation(); e.preventDefault(); onClose(); }
      if (e.altKey && e.key.toLowerCase() === "s") { e.stopImmediatePropagation(); e.preventDefault(); handleSave(); }
    };
    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: zBase, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 30 + level * 20, overflowY: "auto" }} onClick={(e) => { e.stopPropagation(); if (!nestedCreate) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: "24px 28px", width: "90%", maxWidth: 750, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 8px 30px rgba(0,0,0,0.25)", zIndex: zBase + 1 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Create {title}</h3>
          <button style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: colors.textLight }} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ padding: "8px 12px", background: "#fef2f2", color: colors.danger, borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={styles.grid(2)} ref={el => { if (el && !el.dataset.focused) { el.dataset.focused = "1"; const inp = el.querySelector("input[type='text'],input:not([type])"); if (inp) setTimeout(() => inp.focus(), 150); } }}>
          {fields.map(f => (
            <div key={f.key} style={f.fullWidth ? { gridColumn: "1 / -1" } : {}}>
              <label style={styles.label}>{f.label}{f.required ? " *" : ""}</label>
              {(f.type === "searchable_select") ? (
                <SearchableSelect options={f.options || []} value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} placeholder={`Search ${f.label.toLowerCase()}...`}
                  onCreate={f.createLabel ? () => setNestedCreate({ title: f.createLabel.replace("Create ", ""), endpoint: f.createEndpoint || f.key.replace("_id", "s"), targetField: f.key, fields: f.createFields || [{ key: "name", label: `${f.label} Name`, required: true }] }) : undefined}
                  createLabel={f.createLabel} />
              ) : f.type === "select" ? (
                <select style={styles.select} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}>
                  <option value="">Select...</option>
                  {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <FastText multiline style={{ ...styles.input, minHeight: 70 }} value={form[f.key] || ""} onCommit={(t) => setForm(fm => ({ ...fm, [f.key]: t }))} />
              ) : f.type === "date" ? (
                <DateInput value={form[f.key] || ""} onChange={v => setForm({ ...form, [f.key]: v })} />
              ) : (
                <FastText style={styles.input} type={f.type || "text"} value={form[f.key] || ""} onCommit={(t) => setForm(fm => ({ ...fm, [f.key]: t }))} placeholder={f.placeholder} />
              )}
              {isCust && <DupeMsg dupes={dupes} field={f.key === "contact_phone" ? "phone" : f.key === "contact_email" ? "email" : f.key} onOpen={useExisting} />}
              {isCust && f.key === "company_name" && <SimilarList similar={similar} onOpen={useExisting} />}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
          <button style={styles.btn("ghost")} onClick={onClose}>Cancel <span style={{ fontSize: 9, color: colors.textLight, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
          <button style={{ ...styles.btn("primary"), ...(isCust && dupes.length ? { opacity: 0.5, cursor: "not-allowed" } : {}) }} onClick={handleSave} disabled={saving || (isCust && dupes.length > 0)}>{saving ? "Creating..." : "Create"} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span></button>
        </div>
      </div>

      {/* Nested FullCreate */}
      {nestedCreate && (
        <FullCreate title={nestedCreate.title} endpoint={nestedCreate.endpoint} fields={nestedCreate.fields} level={level + 1}
          onCreated={(newItem) => { setForm(f => ({ ...f, [nestedCreate.targetField]: newItem.id?.toString() })); window.dispatchEvent(new Event("masters-refresh")); }}
          onClose={() => setNestedCreate(null)} />
      )}
    </div>
  );
};

// Label with underlined shortcut key
const ShortcutLabel = ({ text, shortcutChar }) => {
  if (!shortcutChar) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(shortcutChar.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return <span>{text.slice(0, idx)}<span style={{ textDecoration: "underline", fontWeight: 700 }}>{text[idx]}</span>{text.slice(idx + 1)}</span>;
};

// Status Badge
const StatusBadge = ({ status }) => {
  const colorMap = {
    Open: colors.warning, "In Progress": colors.primary, Completed: colors.success,
    Escalated: colors.danger, "On Hold": colors.secondary,
    New: colors.primary, Contacted: colors.warning, "Demo Scheduled": "#8b5cf6",
    "Demo Completed": "#6366f1", "Proposal Sent": "#0891b2", Negotiation: colors.warning,
    Converted: colors.success, Lost: colors.danger,
    Hot: colors.hot, Warm: colors.warm, Cold: colors.cold,
    Draft: colors.secondary, Sent: colors.primary, Accepted: colors.success,
    Rejected: colors.danger, Cancelled: colors.secondary,
  };
  return <span style={styles.badge(colorMap[status] || colors.secondary)}>{status}</span>;
};

// ============================================================================
// LOGIN SCREEN
// ============================================================================
// ============================================================================
// Quick-create from any dropdown: ready-made forms for the common masters, and a
// small host that opens the create window, fills the field with the new item and
// refreshes every screen's dropdowns.
//   const [qc, setQc] = useState(null);
//   <SearchableSelect ... onCreate={() => setQc({ kind: "customer", apply: id => setForm(f => ({ ...f, client_id: id })) })} />
//   <QuickCreateModal qc={qc} masters={masters} onClose={() => setQc(null)} />
// ============================================================================
const QC_PRODUCT_CATS = ["Software License", "Hardware", "Service", "AMC/Support", "Customization", "Training", "Subscription", "Consulting", "Others"].map(d => ({ value: d, label: d }));
const qcConfig = (kind, masters) => {
  const desig = masters.designations?.map(d => ({ value: d.id.toString(), label: d.name })) || [];
  const dept = masters.departments?.map(d => ({ value: d.id.toString(), label: d.name })) || [];
  if (kind === "customer") return { title: "Customer", endpoint: "clients", fields: clientsCreateFields(masters) };
  if (kind === "lostReason") return { title: "Lost Reason", endpoint: "lost-reasons", fields: [{ key: "name", label: "Reason", required: true }] };
  if (kind === "product") return { title: "Product", endpoint: "products", fields: [
    { key: "name", label: "Product Name", required: true }, { key: "category", label: "Category", type: "select", options: QC_PRODUCT_CATS },
    { key: "description", label: "Description", type: "textarea", fullWidth: true },
    { key: "hsn_sac_code", label: "HSN/SAC Code" }, { key: "default_gst_rate", label: "GST Rate (%)", type: "number" },
    { key: "unit_price", label: "Unit Price (INR)", type: "number" },
  ]};
  if (kind === "executive") return { title: "Executive", endpoint: "executives", fields: [
    { key: "name", label: "Full Name", required: true }, { key: "employee_id", label: "Employee ID" },
    { key: "designation_id", label: "Designation", type: "searchable_select", options: desig, createLabel: "Create Designation", createEndpoint: "designations", createFields: [{ key: "name", label: "Designation Name", required: true }] },
    { key: "department_id", label: "Department", type: "searchable_select", options: dept, createLabel: "Create Department", createEndpoint: "departments", createFields: [{ key: "name", label: "Department Name", required: true }] },
    { key: "email", label: "Email", type: "email" }, { key: "phone", label: "Phone" },
  ]};
  return null;
};
const QuickCreateModal = ({ qc, masters, onClose }) => {
  if (!qc) return null;
  const cfg = qcConfig(qc.kind, masters);
  if (!cfg) return null;
  return (
    <FullCreate title={cfg.title} endpoint={cfg.endpoint} fields={cfg.fields}
      onCreated={(item) => { if (item?.id) qc.apply(item.id.toString(), item); window.dispatchEvent(new Event("masters-refresh")); }}
      onClose={onClose} />
  );
};

// ---- First login / after a password reset: the user must choose their own password ----
const ForcePasswordChange = ({ user, onDone, onLogout }) => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setErr("");
    if (!f.current_password) return setErr("Enter the password you just logged in with.");
    if (f.new_password.length < 8) return setErr("New password must be at least 8 characters.");
    if (f.new_password === f.current_password) return setErr("New password must be different from the current one.");
    if (f.new_password !== f.confirm_password) return setErr("The two new passwords do not match.");
    setBusy(true);
    try {
      await api("/auth/change-password", { method: "PUT", body: { current_password: f.current_password, new_password: f.new_password } });
      onDone();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const box = { ...styles.input, width: "100%", boxSizing: "border-box" };
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9", padding: 16 }}>
      <div style={{ ...styles.card, width: "100%", maxWidth: 420 }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 20 }}>🔐 Set your own password</h2>
        <div style={{ fontSize: 13, color: colors.textLight, marginBottom: 16, lineHeight: 1.5 }}>
          Welcome, {user.full_name || user.username}. You logged in with a password the admin gave you — please choose your own before continuing.
        </div>
        <div style={{ marginBottom: 12 }}><label style={styles.label}>Current (temporary) password</label>
          <input type="password" style={box} value={f.current_password} onChange={e => setF({ ...f, current_password: e.target.value })} autoFocus /></div>
        <div style={{ marginBottom: 12 }}><label style={styles.label}>New password (at least 8 characters)</label>
          <input type="password" style={box} value={f.new_password} onChange={e => setF({ ...f, new_password: e.target.value })} /></div>
        <div style={{ marginBottom: 12 }}><label style={styles.label}>Type the new password again</label>
          <input type="password" style={box} value={f.confirm_password} onChange={e => setF({ ...f, confirm_password: e.target.value })}
            onKeyDown={e => { if (e.key === "Enter") submit(); }} /></div>
        {err && <div style={{ color: colors.danger, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <button style={{ ...styles.btn("primary"), width: "100%" }} disabled={busy} onClick={submit}>{busy ? "Saving…" : "Save and continue"}</button>
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <span style={{ fontSize: 12, color: colors.textLight, cursor: "pointer", textDecoration: "underline" }} onClick={onLogout}>Sign out</span>
        </div>
      </div>
    </div>
  );
};

const LoginScreen = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      localStorage.setItem("token", data.token);
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobileNow() ? 16 : 0, background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: isMobileNow() ? "32px 22px" : "48px 40px", width: isMobileNow() ? "100%" : 380, maxWidth: 380, boxSizing: "border-box", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: colors.primary, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 24, color: "#fff", fontWeight: 800 }}>CT</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Call Tracker & CRM</h1>
          <p style={{ fontSize: 13, color: colors.textLight, margin: "4px 0 0" }}>Sign in to your account</p>
        </div>
        <div>
          {error && <div style={{ background: "#fef2f2", color: colors.danger, padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <div style={{ marginBottom: 16 }}>
            <label style={styles.label}>Username</label>
            <input style={styles.input} value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter username" />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={styles.label}>Password</label>
            <input style={styles.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" />
          </div>
          <button style={{ ...styles.btn("primary"), width: "100%", justifyContent: "center", padding: "12px 18px" }} onClick={handleSubmit} disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </div>
    </div>
  );
};

// LEAD SUMMARY BUILDER
const LeadSummaryBuilder = ({ dateFrom, dateTo, masters, leadAdv = {} }) => {
  const [row1, setRow1] = useState("source");
  const [row2, setRow2] = useState("");
  const [colBy, setColBy] = useState("status");
  const [measure, setMeasure] = useState("count");
  const [pivotFilters, setPivotFilters] = useState({});
  const [pivotData, setPivotData] = useState(null);
  const [generated, setGenerated] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchPivot = async () => {
    try {
      const params = new URLSearchParams({ row1, row2, col_by: colBy, measure });
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      Object.entries(leadAdv).forEach(([k, v]) => { if (v) params.append(k, v); });   // dashboard lead filters
      Object.entries(pivotFilters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api(`/dashboard/lead-pivot?${params}`);
      // Process pivot data
      const rows = r.data;
      const cols = [...new Set(rows.map(r => r.col_label))].sort();
      const data = {};
      rows.forEach(r => {
        const r1Key = r.row1_label;
        if (!data[r1Key]) data[r1Key] = { _total: {}, _row2s: {} };
        if (r.row2_label) {
          if (!data[r1Key]._row2s[r.row2_label]) data[r1Key]._row2s[r.row2_label] = {};
          data[r1Key]._row2s[r.row2_label][r.col_label] = parseFloat(r.measure_val);
          data[r1Key]._total[r.col_label] = (data[r1Key]._total[r.col_label] || 0) + parseFloat(r.measure_val);
        } else {
          data[r1Key]._total[r.col_label] = parseFloat(r.measure_val);
        }
      });
      setPivotData({ cols, data, row1s: Object.keys(data).sort() });
      setGenerated(true);
    } catch (e) { console.error(e); }
  };

  // Blank until Generate is pressed; changing any selector/filter blanks it again.
  useEffect(() => { setGenerated(false); setPivotData(null); }, [row1, row2, colBy, measure, pivotFilters, dateFrom, dateTo, JSON.stringify(leadAdv)]);

  const exportPivotCSV = () => {
    if (!pivotData || pivotData.row1s.length === 0) return;
    const r1Label = row1.charAt(0).toUpperCase() + row1.slice(1);
    let csv = `${r1Label},${pivotData.cols.join(",")},Total\n`;
    pivotData.row1s.forEach(r1Key => {
      let total = 0;
      pivotData.cols.forEach(c => { total += pivotData.data[r1Key]._total[c] || 0; });
      csv += `"${r1Key}",${pivotData.cols.map(c => pivotData.data[r1Key]._total[c] || 0).join(",")},${measure === "pipeline" ? total.toFixed(2) : total}\n`;
    });
    downloadCSV(csv, `lead_summary_${row1}_by_${colBy}.csv`);
  };

  const pivot = pivotData || { cols: [], data: {}, row1s: [] };

  return (
    <div style={{ ...styles.card, marginTop: 20, borderTop: `3px solid ${colors.success}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Lead Summary Builder</h3>
        {pivot.row1s.length > 0 && <button style={styles.btn("success", "sm")} onClick={exportPivotCSV}>📊 Export to Excel</button>}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div><label style={styles.label}>Row</label>
          <select style={styles.select} value={row1} onChange={e => setRow1(e.target.value)}>
            <option value="source">Source</option><option value="status">Status</option><option value="priority">Priority</option>
            <option value="executive">Executive</option><option value="product">Product</option><option value="month">Month</option>
          </select>
        </div>
        <div><label style={styles.label}>Sub Row</label>
          <select style={styles.select} value={row2} onChange={e => setRow2(e.target.value)}>
            <option value="">None</option><option value="source">Source</option><option value="status">Status</option>
            <option value="priority">Priority</option><option value="executive">Executive</option><option value="product">Product</option>
          </select>
        </div>
        <div><label style={styles.label}>Column</label>
          <select style={styles.select} value={colBy} onChange={e => setColBy(e.target.value)}>
            <option value="status">Status</option><option value="priority">Priority</option><option value="source">Source</option>
            <option value="executive">Executive</option><option value="month">Month</option>
          </select>
        </div>
        <div><label style={styles.label}>Measure</label>
          <select style={styles.select} value={measure} onChange={e => setMeasure(e.target.value)}>
            <option value="count">Count</option><option value="pipeline">Pipeline Value</option>
          </select>
        </div>
        <button style={{ ...styles.btn("ghost", "sm"), alignSelf: "flex-end" }} onClick={() => setPivotFilters({})}>Clear Filters</button>
        <button style={{ ...styles.btn("primary", "sm"), alignSelf: "flex-end" }} onClick={fetchPivot}>Generate</button>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div><label style={styles.label}>Filter Source</label>
          <select style={styles.select} value={pivotFilters.filter_source || ""} onChange={e => setPivotFilters({ ...pivotFilters, filter_source: e.target.value })}>
            <option value="">All</option>{(masters?.leadSources || []).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Filter Status</label>
          <select style={styles.select} value={pivotFilters.filter_status || ""} onChange={e => setPivotFilters({ ...pivotFilters, filter_status: e.target.value })}>
            <option value="">All</option>{["New","Contacted","Demo/Presentation","Proposal Sent","Negotiation","Converted","Lost"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Filter Priority</label>
          <select style={styles.select} value={pivotFilters.filter_priority || ""} onChange={e => setPivotFilters({ ...pivotFilters, filter_priority: e.target.value })}>
            <option value="">All</option>{["Hot","Warm","Cold"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Filter Executive</label>
          <select style={styles.select} value={pivotFilters.filter_executive || ""} onChange={e => setPivotFilters({ ...pivotFilters, filter_executive: e.target.value })}>
            <option value="">All</option>{(masters?.executives || []).map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Filter Product</label>
          <select style={styles.select} value={pivotFilters.filter_product || ""} onChange={e => setPivotFilters({ ...pivotFilters, filter_product: e.target.value })}>
            <option value="">All</option>{(masters?.products || []).map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {!generated ? (
        <div style={{ padding: 30, textAlign: "center", color: colors.textLight, fontSize: 14 }}>Choose your Row / Column / Measure and click <b>Generate</b> to build the summary.</div>
      ) : pivot.row1s.length > 0 ? (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={{ ...styles.th, minWidth: 150 }}>{row1.charAt(0).toUpperCase() + row1.slice(1)}</th>
              {pivot.cols.map(c => <th key={c} style={{ ...styles.th, textAlign: "right", minWidth: 80 }}>{c}</th>)}
              <th style={{ ...styles.th, textAlign: "right", fontWeight: 800 }}>Total</th>
            </tr></thead>
            <tbody>
              {pivot.row1s.map(r1Key => {
                const r1Total = pivot.cols.reduce((s, c) => s + (pivot.data[r1Key]._total[c] || 0), 0);
                const hasRow2 = Object.keys(pivot.data[r1Key]._row2s).length > 0;
                return (
                  <Fragment key={r1Key}>
                    <tr style={{ background: "#f8fafc", cursor: hasRow2 ? "pointer" : "default", fontWeight: 600 }}
                      onClick={() => hasRow2 && setExpanded(p => ({ ...p, [r1Key]: !p[r1Key] }))}>
                      <td style={styles.td}>{hasRow2 ? (expanded[r1Key] ? "▼ " : "▶ ") : ""}{r1Key}</td>
                      {pivot.cols.map(c => <td key={c} style={{ ...styles.td, textAlign: "right" }}>{measure === "pipeline" ? formatCurrencyINR(pivot.data[r1Key]._total[c] || 0) : (pivot.data[r1Key]._total[c] || "")}</td>)}
                      <td style={{ ...styles.td, textAlign: "right", fontWeight: 800 }}>{measure === "pipeline" ? formatCurrencyINR(r1Total) : r1Total}</td>
                    </tr>
                    {hasRow2 && expanded[r1Key] && Object.keys(pivot.data[r1Key]._row2s).sort().map(r2Key => {
                      const r2Total = pivot.cols.reduce((s, c) => s + (pivot.data[r1Key]._row2s[r2Key][c] || 0), 0);
                      return (
                        <tr key={r2Key} style={{ fontSize: 12 }}>
                          <td style={{ ...styles.td, paddingLeft: 30, color: colors.textLight }}>{r2Key}</td>
                          {pivot.cols.map(c => <td key={c} style={{ ...styles.td, textAlign: "right", color: colors.textLight }}>{measure === "pipeline" ? formatCurrencyINR(pivot.data[r1Key]._row2s[r2Key][c] || 0) : (pivot.data[r1Key]._row2s[r2Key][c] || "")}</td>)}
                          <td style={{ ...styles.td, textAlign: "right", fontWeight: 600 }}>{measure === "pipeline" ? formatCurrencyINR(r2Total) : r2Total}</td>
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              })}
              <tr style={{ background: "#e8eefb", fontWeight: 800 }}>
                <td style={styles.td}>GRAND TOTAL</td>
                {pivot.cols.map(c => {
                  const colTotal = pivot.row1s.reduce((s, r) => s + (pivot.data[r]._total[c] || 0), 0);
                  return <td key={c} style={{ ...styles.td, textAlign: "right" }}>{measure === "pipeline" ? formatCurrencyINR(colTotal) : colTotal}</td>;
                })}
                <td style={{ ...styles.td, textAlign: "right" }}>{measure === "pipeline" ? formatCurrencyINR(pivot.row1s.reduce((s, r) => s + pivot.cols.reduce((s2, c) => s2 + (pivot.data[r]._total[c] || 0), 0), 0)) : pivot.row1s.reduce((s, r) => s + pivot.cols.reduce((s2, c) => s2 + (pivot.data[r]._total[c] || 0), 0), 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : <p style={{ fontSize: 12, color: colors.textLight, textAlign: "center", padding: 20 }}>No lead data available for the selected criteria.</p>}
    </div>
  );
};

// ============================================================================
// DASHBOARD
// ============================================================================
// Dashboard advanced filters, kept across screen navigation.
// "Rows" are what's being edited; "applied" is what the numbers are using.
let __dashCallRows = [], __dashCallApplied = [];
let __dashLeadRows = [], __dashLeadApplied = [];
// The card list a record was opened from — the Dashboard reopens it when you come
// back from that record (instead of dropping you on the plain Dashboard).
// Search box for the Dashboard's card lists. Typing stays inside this small box
// (instant); the list is told 200 ms after you pause. Before, every letter redrew
// the whole Dashboard and the open list, so the letters appeared slowly.
const ListSearchBox = ({ value, onSearch, placeholder }) => {
  const [v, setV] = useState(value || "");
  const sent = useRef(value || "");
  const timer = useRef(null);
  useEffect(() => { if ((value || "") !== sent.current) { sent.current = value || ""; setV(value || ""); } }, [value]);   // cleared / restored from outside
  useEffect(() => () => clearTimeout(timer.current), []);
  const send = (nv) => { clearTimeout(timer.current); sent.current = nv; onSearch(nv); };
  return (
    <input autoFocus style={{ ...styles.input, flex: 1, minWidth: 220 }} value={v} placeholder={placeholder}
      onChange={e => { const nv = e.target.value; setV(nv); clearTimeout(timer.current); timer.current = setTimeout(() => send(nv), 200); }}
      onKeyDown={e => {
        if (e.key === "Escape" && v) { e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); setV(""); send(""); }   // 1st Esc clears
        if (e.key === "Enter") send(v);
      }} />
  );
};

let __dashReturn = null;   // { title, kind, fetchFn, search, data }
let __dashStats = null;    // last figures shown — the Dashboard appears with these at once, then refreshes
const __ovTextCache = new WeakMap();   // searchable text of each list row, built once per row
const Dashboard = ({ masters, setActiveScreen, setPendingCall, setPendingLead, setPendingReturn, user }) => {
  const [callRows, setCallRowsState] = useState(__dashCallRows);
  const setCallRows = (v) => { __dashCallRows = v; setCallRowsState(v); };
  const [callApplied, setCallAppliedState] = useState(__dashCallApplied);
  const setCallApplied = (v) => { __dashCallApplied = v; setCallAppliedState(v); };
  const [leadRows, setLeadRowsState] = useState(__dashLeadRows);
  const setLeadRows = (v) => { __dashLeadRows = v; setLeadRowsState(v); };
  const [leadApplied, setLeadAppliedState] = useState(__dashLeadApplied);
  const setLeadApplied = (v) => { __dashLeadApplied = v; setLeadAppliedState(v); };
  // Advanced call filters as query parameters. On the Dashboard "executive_id"
  // already means "handling the call", so the advanced "Logged By" filter is sent
  // as logged_by_id instead.
  const callAdvParams = () => {
    const o = advParams(callApplied, callFilterFields(masters, callApplied));
    if (o.executive_id) { o.logged_by_id = o.executive_id; delete o.executive_id; }
    return o;
  };
  const leadAdvParams = () => advParams(leadApplied, leadFilterFields(masters));
  const callAdvKey = JSON.stringify(callApplied), leadAdvKey = JSON.stringify(leadApplied);
  const [stats, setStats] = useState(__dashStats);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(!__dashStats);   // blank "Loading" only the very first time
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dashFilter, setDashFilter] = useState({});
  const [drillDown, setDrillDown] = useState(null);
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [pivotData, setPivotData] = useState([]);
  const [pivotGenerated, setPivotGenerated] = useState(false);
  const [pivotRow1, setPivotRow1] = useState("principal");
  const [pivotRow2, setPivotRow2] = useState("client");
  const [pivotColBy, setPivotColBy] = useState("call_type");
  const [pivotMeasure, setPivotMeasure] = useState("hours");
  const [pivotFilters, setPivotFilters] = useState({});
  const [pivotExpanded, setPivotExpanded] = useState({});
  const [execDrill, setExecDrill] = useState(null);
  const [execDrillData, setExecDrillData] = useState([]);
  const [execDrillLoading, setExecDrillLoading] = useState(false);
  const [showTodayActions, setShowTodayActions] = useState(false);
  const [showTodayLeadFollowups, setShowTodayLeadFollowups] = useState(false);
  // Coming back from a record opened in a card's list → that list is open from the
  // very first frame (it used to appear a moment later, so the plain Dashboard flashed)
  const [overlay, setOverlay] = useState(() => {
    const r = __dashReturn;
    return (r && r.fetchFn) ? { title: r.title, data: r.data || [], loading: !r.data, kind: r.kind, fetchFn: r.fetchFn } : null;
  });

  // Unified overlay opener
  const openOverlay = async (title, fetchFn, kind) => {
    setOverlay({ title, data: [], loading: true, kind, fetchFn });
    try {
      const data = await fetchFn(false);
      setOverlay({ title, data, loading: false, kind, fetchFn });
    } catch (e) { setOverlay({ title, data: [], loading: false, kind, fetchFn }); }
  };
  // Export ALL matching records (ignores the 500 on-screen cap) as CSV.
  const [exportingAll, setExportingAll] = useState(false);
  const { sorts, toggleSort, clearSort } = useMultiSort();
  const ovAcc = { contacts: (r) => contactNums(r), emails: (r) => contactMails(r) };
  // ---- Search inside a card's list: every field of the records ----
  const [ovSearch, setOvSearch] = useState(() => (__dashReturn && __dashReturn.search) || "");   // set by ListSearchBox after a pause
  const [ovAll, setOvAll] = useState(() => (__dashReturn && __dashReturn.all) || null);          // all records, when the list was cut at 500
  const ovText = (r) => {
    if (r && typeof r === "object" && __ovTextCache.has(r)) return __ovTextCache.get(r);
    const parts = [];
    Object.values(r || {}).forEach(v => {
      if (v === null || v === undefined || typeof v === "object") return;
      const t = String(v);
      parts.push(t);
      if (/^\d{4}-\d{2}-\d{2}/.test(t)) parts.push(formatDateDMY(t));     // dates as shown on screen
      const digits = t.replace(/\D/g, "");
      if (digits.length >= 6 && digits !== t) parts.push(digits);         // phone numbers without spaces / dashes
    });
    const txt = parts.join(" ").toLowerCase();
    if (r && typeof r === "object") __ovTextCache.set(r, txt);
    return txt;
  };
  const ovTerms = ovSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const ovSource = (ovTerms.length && ovAll) ? ovAll : (overlay?.data || []);
  const ovFiltered = ovTerms.length ? ovSource.filter(r => { const t = ovText(r); return ovTerms.every(w => t.includes(w)); }) : ovSource;
  const ovRows = applyMultiSort(ovFiltered, sorts, ovAcc);
  // The list on screen stops at 500 — when searching such a list, search ALL its records
  useEffect(() => {
    if (!ovTerms.length || ovAll || !overlay?.fetchFn || overlay.loading || (overlay.data || []).length < 500) return;
    let gone = false;
    overlay.fetchFn(true).then(all => { if (!gone) setOvAll(all || []); }).catch(() => {});
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovSearch, overlay]);
  // Close the list for good (back to the plain Dashboard)
  const closeOverlay = () => { __dashReturn = null; setOverlay(null); setOvSearch(""); setOvAll(null); };
  // Open a record from the list: remember the list so we come back to it
  const rememberList = () => { if (overlay) __dashReturn = { title: overlay.title, kind: overlay.kind, fetchFn: overlay.fetchFn, search: ovSearch, data: overlay.data, all: ovAll }; };
  // License renewal-history popup (from a license drill row)
  const [licHistory, setLicHistory] = useState(null);
  const [licHistoryData, setLicHistoryData] = useState([]);
  const openLicHistory = async (row) => {
    setLicHistory(row); setLicHistoryData([]);
    try { const r = await api(`/customer-products/${row.customer_product_id}/renewals`); setLicHistoryData(r.data || []); } catch (e) {}
  };

  // Escape to close overlay
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape" && overlay) { e.stopPropagation(); closeOverlay(); } };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [overlay]);

  const fetchStats = async () => {
    if (!__dashStats) setLoading(true);   // later refreshes keep the screen (and any open list) in place
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (dashFilter.client_id) params.append("client_id", dashFilter.client_id);
      if (dashFilter.executive_id) params.append("executive_id", dashFilter.executive_id);
      if (dashFilter.search) params.append("search", dashFilter.search);
      Object.entries(callAdvParams()).forEach(([k, v]) => { if (v) params.append(k, v); });
      Object.entries(leadAdvParams()).forEach(([k, v]) => { if (v) params.append("l_" + k, v); });   // lead filters carry an l_ prefix
      const r = await api(`/dashboard/stats?${params}`);
      setStats(r.data); __dashStats = r.data;
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchPivot = async () => {
    try {
      const params = new URLSearchParams({ row1: pivotRow1, row2: pivotRow2, col_by: pivotColBy, measure: pivotMeasure });
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      Object.entries(callAdvParams()).forEach(([k, v]) => { if (v) params.append(k, v); });
      Object.entries(pivotFilters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api(`/dashboard/pivot?${params}`);
      setPivotData(r.data); setPivotGenerated(true);
    } catch (e) { console.error(e); }
  };

  const getDashParams = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.append("date_from", dateFrom);
    if (dateTo) p.append("date_to", dateTo);
    if (dashFilter.client_id) p.append("client_id", dashFilter.client_id);
    if (dashFilter.executive_id) p.append("executive_id", dashFilter.executive_id);
    if (dashFilter.search) p.append("search", dashFilter.search);
    Object.entries(callAdvParams()).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p;
  };

  // Parameters for any lead list opened from the Dashboard — the lead filters
  // and search box first, then whatever the clicked card adds (it wins).
  const leadDrillParams = (extra, forExport) => {
    const p = new URLSearchParams();
    Object.entries(leadAdvParams()).forEach(([k, v]) => { if (v) p.append(k, v); });
    if (dashFilter.search) p.append("search", dashFilter.search);
    Object.entries(extra || {}).forEach(([k, v]) => { if (v) p.append(k, v); });
    p.append("limit", forExport ? "100000" : "500");
    return p;
  };

  const handleDrillDown = async (status) => {
    openOverlay(`${status === "all" ? "All" : status} Calls`, async (forExport) => {
      const params = getDashParams();
      if (status !== "all") params.append("status", status);
      params.append("limit", forExport ? "100000" : "500");
      const r = await api(`/dashboard/drill-down?${params}`);
      return r.data;
    });
  };

  const openExecDrill = async (ex) => {
    openOverlay(`Calls by ${ex.label || "Executive"}`, async (forExport) => {
      const params = getDashParams();
      params.append("executive_id", ex.executive_id);
      params.append("limit", forExport ? "100000" : "500");
      const r = await api(`/dashboard/drill-down/executive?${params}`);
      return r.data;
    });
  };

  const openChartDrill = async (title, field, value) => {
    openOverlay(title, async (forExport) => {
      const params = getDashParams();
      if (field === "call_type") params.append("call_type_name", value);
      if (field === "executive") params.append("executive_name", value);
      if (field === "client") params.append("client_name", value);
      params.append("limit", forExport ? "100000" : "500");
      const r = await api(`/dashboard/drill-down?${params}`);
      return r.data;
    });
  };

  const openLeadsByFilter = async (title, filterObj) => {
    openOverlay(title, async (forExport) => {
      const r = await api(`/leads?${leadDrillParams(filterObj, forExport)}`);
      return r.data;
    });
  };

  useEffect(() => { fetchStats(); }, []);
  // Back from a record opened in a card's list → reopen that list (refreshed), with the same search
  useEffect(() => {
    const r = __dashReturn;
    if (r && r.fetchFn) {
      // the list is already on screen (see useState above) — refresh it in the background so an edit just made shows up
      r.fetchFn(false).then(data => {
        setOverlay(o => (o && o.title === r.title) ? { ...o, data: data || [], loading: false } : o);
        if (r.all) r.fetchFn(true).then(all => setOvAll(all || [])).catch(() => {});
      }).catch(() => setOverlay(o => o ? { ...o, loading: false } : o));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc closes whichever drill/overlay is open, returning to the Dashboard.
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") { setLicHistory(null); closeOverlay(); } };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  // Periodic auto-refresh (every 60s) — pauses while a drill/overlay is open or the
  // toggle is off, so it never interrupts someone mid-drill. Returning to the
  // Dashboard after an edit re-mounts it, so numbers also refresh after a save.
  useEffect(() => {
    if (!autoRefresh || overlay || execDrill || showTodayActions || showTodayLeadFollowups) return;
    const t = setInterval(() => { fetchStats(); }, 60000);
    return () => clearInterval(t);
  }, [autoRefresh, overlay, execDrill, showTodayActions, showTodayLeadFollowups, dateFrom, dateTo, dashFilter, callAdvKey, leadAdvKey]);

  // Auto-refresh when search/filter changes
  useEffect(() => {
    const timer = setTimeout(() => { fetchStats(); }, 500);
    return () => clearTimeout(timer);
  }, [dashFilter.search, dashFilter.client_id, dashFilter.executive_id, callAdvKey, leadAdvKey]);

  const applyDateFilter = () => { fetchStats(); setPivotGenerated(false); setPivotData([]); };
  const clearDateFilter = () => { setDateFrom(""); setDateTo(""); setDashFilter({}); setPivotGenerated(false); setPivotData([]); setTimeout(() => { fetchStats(); }, 100); };

  useEffect(() => { setPivotGenerated(false); setPivotData([]); }, [pivotRow1, pivotRow2, pivotColBy, pivotMeasure, pivotFilters]);

  if (loading && !stats) return <div style={{ padding: 40, textAlign: "center", color: colors.textLight }}>Loading dashboard...</div>;
  if (!stats) return <div style={{ padding: 40, textAlign: "center" }}>Dashboard data unavailable.</div>;

  const sc = stats.status_counts || {};

  // Build pivot table from flat data — supports 2 row levels
  const buildPivotTable = () => {
    if (pivotData.length === 0) return { row1s: [], colSet: [], data: {}, hasRow2: false };
    const colSet = new Set();
    const data = {}; // { row1: { _total: {}, _row2s: { row2: { col: val } } } }
    const hasRow2 = pivotRow2 && pivotRow2 !== '';

    pivotData.forEach(d => {
      const r1 = d.row1_label || 'Unknown';
      const r2 = d.row2_label || null;
      const col = d.col_label || 'Unknown';
      const val = parseFloat(d.value) || 0;
      colSet.add(col);

      if (!data[r1]) data[r1] = { _total: {}, _row2s: {} };
      data[r1]._total[col] = (data[r1]._total[col] || 0) + val;

      if (hasRow2 && r2) {
        if (!data[r1]._row2s[r2]) data[r1]._row2s[r2] = {};
        data[r1]._row2s[r2][col] = (data[r1]._row2s[r2][col] || 0) + val;
      }
    });

    const cols = Array.from(colSet).sort();
    const row1s = Object.keys(data).sort();
    return { row1s, cols, data, hasRow2 };
  };

  const pivot = buildPivotTable();

  // Export pivot to CSV
  const exportPivotExcel = () => {
    if (pivot.row1s.length === 0) return;
    const r1Label = pivotRow1.charAt(0).toUpperCase() + pivotRow1.slice(1);
    const r2Label = pivotRow2 ? pivotRow2.charAt(0).toUpperCase() + pivotRow2.slice(1) : '';
    let csv = pivot.hasRow2 ? `${r1Label},${r2Label}` : r1Label;
    pivot.cols.forEach(c => { csv += `,${c}`; });
    csv += ',Total\n';

    pivot.row1s.forEach(r1 => {
      const r1Total = pivot.cols.reduce((s, c) => s + (pivot.data[r1]._total[c] || 0), 0);
      csv += `"${r1}",${pivot.hasRow2 ? '"",' : ''}`;
      pivot.cols.forEach(c => { csv += `${pivot.data[r1]._total[c] ? (pivotMeasure === 'hours' ? parseFloat(pivot.data[r1]._total[c]).toFixed(2) : pivot.data[r1]._total[c]) : 0},`; });
      csv += `${pivotMeasure === 'hours' ? r1Total.toFixed(2) : r1Total}\n`;

      if (pivot.hasRow2) {
        Object.keys(pivot.data[r1]._row2s).sort().forEach(r2 => {
          const r2Total = pivot.cols.reduce((s, c) => s + (pivot.data[r1]._row2s[r2][c] || 0), 0);
          csv += `"","${r2}",`;
          pivot.cols.forEach(c => { csv += `${pivot.data[r1]._row2s[r2][c] ? (pivotMeasure === 'hours' ? parseFloat(pivot.data[r1]._row2s[r2][c]).toFixed(2) : pivot.data[r1]._row2s[r2][c]) : 0},`; });
          csv += `${pivotMeasure === 'hours' ? r2Total.toFixed(2) : r2Total}\n`;
        });
      }
    });

    // Grand total row
    csv += `"Grand Total",${pivot.hasRow2 ? '"",' : ''}`;
    pivot.cols.forEach(c => {
      const ct = pivot.row1s.reduce((s, r) => s + (pivot.data[r]._total[c] || 0), 0);
      csv += `${pivotMeasure === 'hours' ? ct.toFixed(2) : ct},`;
    });
    const gt = pivot.row1s.reduce((s, r) => s + pivot.cols.reduce((s2, c) => s2 + (pivot.data[r]._total[c] || 0), 0), 0);
    csv += `${pivotMeasure === 'hours' ? gt.toFixed(2) : gt}\n`;

    downloadCSV(csv, `summary_builder_${new Date().toISOString().slice(0,10)}.csv`);
  };

  // Simple bar chart renderer
  const BarChart = ({ data, labelKey = "label", valueKey = "hours", color = colors.primary, title, onItemClick }) => {
    if (!data || data.length === 0) return null;
    const maxVal = Math.max(...data.map(d => parseFloat(d[valueKey]) || 0));
    return (
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>{title}</h3>
        {data.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: onItemClick ? "pointer" : "default" }}
            onClick={() => onItemClick && onItemClick(d[labelKey])}>
            <div style={{ width: 120, fontSize: 11, color: onItemClick ? colors.primary : colors.textLight, textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d[labelKey]}</div>
            <div style={{ flex: 1, height: 22, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${maxVal > 0 ? (parseFloat(d[valueKey]) / maxVal * 100) : 0}%`, height: "100%", background: color, borderRadius: 4, minWidth: parseFloat(d[valueKey]) > 0 ? 4 : 0, transition: "width 0.5s ease" }} />
            </div>
            <div style={{ width: 60, fontSize: 12, fontWeight: 600, textAlign: "right", flexShrink: 0 }}>{parseFloat(d[valueKey]).toFixed(1)}h</div>
          </div>
        ))}
      </div>
    );
  };

  // Pie chart renderer
  const PieChart = ({ data, title, onItemClick }) => {
    if (!data || data.length === 0) return null;
    const total = data.reduce((s, d) => s + parseFloat(d.hours), 0);
    const pieColors = ["#1a56db", "#059669", "#d97706", "#dc2626", "#8b5cf6", "#0891b2", "#e11d48"];
    let cumPercent = 0;
    const slices = data.map((d, i) => {
      const percent = total > 0 ? parseFloat(d.hours) / total : 0;
      const startAngle = cumPercent * 360;
      cumPercent += percent;
      return { ...d, percent, color: pieColors[i % pieColors.length], startAngle, endAngle: cumPercent * 360 };
    });

    return (
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>{title}</h3>
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <svg width="140" height="140" viewBox="0 0 140 140">
            {slices.map((s, i) => {
              const startRad = (s.startAngle - 90) * Math.PI / 180;
              const endRad = (s.endAngle - 90) * Math.PI / 180;
              const largeArc = s.percent > 0.5 ? 1 : 0;
              const x1 = 70 + 60 * Math.cos(startRad), y1 = 70 + 60 * Math.sin(startRad);
              const x2 = 70 + 60 * Math.cos(endRad), y2 = 70 + 60 * Math.sin(endRad);
              if (s.percent >= 0.999) return <circle key={i} cx="70" cy="70" r="60" fill={s.color} style={{ cursor: onItemClick ? "pointer" : "default" }} onClick={() => onItemClick && onItemClick(s.label)} />;
              if (s.percent <= 0.001) return null;
              return <path key={i} d={`M70,70 L${x1},${y1} A60,60 0 ${largeArc},1 ${x2},${y2} Z`} fill={s.color} style={{ cursor: onItemClick ? "pointer" : "default" }} onClick={() => onItemClick && onItemClick(s.label)} />;
            })}
          </svg>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {slices.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: onItemClick ? "pointer" : "default" }}
                onClick={() => onItemClick && onItemClick(s.label)}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                <span style={{ color: onItemClick ? colors.primary : colors.textLight }}>{s.label}</span>
                <span style={{ fontWeight: 600, marginLeft: "auto" }}>{(s.percent * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const cardStyle = (color, isActive) => ({
    ...styles.statCard(color),
    cursor: "pointer",
    transition: "all 0.2s",
    transform: isActive ? "scale(1.02)" : "scale(1)",
    boxShadow: isActive ? `0 4px 12px ${color}33` : "none",
    outline: isActive ? `2px solid ${color}` : "none",
  });

  return (
    <div>
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Dashboard</h1>
        <MobileFilters count={Object.values(dashFilter).filter(Boolean).length + (dateFrom?1:0) + (dateTo?1:0)}>
        <div style={styles.flexRow}>
          <div><label style={styles.label}>Search</label><input style={{ ...styles.input, minWidth: isMobileNow() ? "100%" : 180 }} placeholder="Customer, Executive, Phone..." value={dashFilter.search || ""} onChange={e => setDashFilter({ ...dashFilter, search: e.target.value })} onKeyDown={(e) => { if (e.key === "Escape" && dashFilter.search) { e.stopPropagation(); setDashFilter({ ...dashFilter, search: "" }); } if (e.key === "Enter") { e.preventDefault(); applyDateFilter(); } }} /></div>
          <div><label style={styles.label}>From</label><DateInput value={dateFrom} onChange={setDateFrom} /></div>
          <div><label style={styles.label}>To</label><DateInput value={dateTo} onChange={setDateTo} /></div>
          <div><label style={styles.label}>Customer</label>
            <select style={{ ...styles.select, maxWidth: 200 }} value={dashFilter.client_id || ""} onChange={e => setDashFilter({ ...dashFilter, client_id: e.target.value })}>
              <option value="">All Customers</option>{masters.clients?.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Executive</label>
            <select style={{ ...styles.select, maxWidth: 150 }} value={dashFilter.executive_id || ""} onChange={e => setDashFilter({ ...dashFilter, executive_id: e.target.value })}>
              <option value="">All</option>{masters.executives?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <button style={styles.btn("primary", "sm")} onClick={applyDateFilter}>Apply</button>
          <button style={styles.btn("ghost", "sm")} onClick={() => { clearDateFilter(); setDashFilter({}); }}>Clear</button>
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: colors.textLight, cursor: "pointer", marginLeft: 4 }} title="Refresh the dashboard automatically every 60 seconds (pauses while you're viewing a drill-down)">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} /> Auto-refresh
          </label>
        </div>
        </MobileFilters>
      </div>

      {/* Advanced filters — calls drive the call cards/charts, leads drive the lead cards */}
      <AdvancedFilters masters={masters} screen="calls" title="Call filters" rows={callRows} setRows={setCallRows}
        onApply={(r) => { setCallRows(r); setCallApplied(r); setPivotGenerated(false); setPivotData([]); }} />
      <AdvancedFilters masters={masters} screen="leads" title="Lead filters" fieldsFn={leadFilterFields} rows={leadRows} setRows={setLeadRows}
        onApply={(r) => { setLeadRows(r); setLeadApplied(r); }} />

      {/* Status Cards - Clickable → opens overlay */}
      <WhoIsOnline user={user} />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {[
          { label: "All Calls", color: "#1e293b", count: sc.total_calls, hours: sc.total_hours, filter: {} },
          { label: "Open", color: colors.warning, count: sc.open_calls, hours: sc.open_hours, filter: { status: "Open" } },
          { label: "In Progress", color: colors.primary, count: sc.in_progress_calls, hours: sc.in_progress_hours, filter: { status: "In Progress" } },
          { label: "Completed", color: colors.success, count: sc.completed_calls, hours: sc.completed_hours, filter: { status: "Completed" } },
          { label: "Escalated", color: colors.danger, count: sc.escalated_calls, hours: sc.escalated_hours, filter: { status: "Escalated" } },
          { label: "On Hold", color: colors.secondary, count: sc.on_hold_calls, hours: sc.on_hold_hours, filter: { status: "On Hold" } },
        ].map(card => (
          <div key={card.label} style={cardStyle(card.color, false)} onClick={() => openOverlay(/calls$/i.test(card.label) ? card.label : `${card.label} Calls`, async (forExport) => {   // "All Calls", not "All Calls Calls"
            const p = getDashParams();
            Object.entries(card.filter).forEach(([k, v]) => p.append(k, v));
            p.append("limit", forExport ? "100000" : "500");
            const r = await api(`/dashboard/drill-down?${p}`); return r.data;
          })}>
            <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>{card.label} ↗</div>
            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 2 }}>{card.count || 0}</div>
            <div style={{ fontSize: 11, color: colors.textLight }}>{parseFloat(card.hours || 0).toFixed(1)} hours</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div style={{ ...styles.grid(3), marginTop: 20 }}>
        <div style={{ ...styles.card, maxHeight: 320, overflowY: "auto" }}><PieChart data={(stats.hours_by_call_type || []).slice(0, 10)} title="Hours by Call Type (Top 10)" onItemClick={(label) => openChartDrill(`Calls — ${label}`, "call_type", label)} /></div>
        <div style={{ ...styles.card, maxHeight: 320, overflowY: "auto" }}><BarChart data={(stats.hours_by_executive || []).slice(0, 10)} title="Hours by Executive (Top 10)" color={colors.primary} onItemClick={(label) => openChartDrill(`Calls — ${label}`, "executive", label)} /></div>
        <div style={{ ...styles.card, maxHeight: 320, overflowY: "auto" }}><BarChart data={(stats.hours_by_client || []).slice(0, 10)} title="Hours by Client (Top 10)" color={colors.success} onItemClick={(label) => openChartDrill(`Calls — ${label}`, "client", label)} /></div>
      </div>

      {/* Calls by Executive — clickable tiles */}
      {stats.calls_by_executive?.length > 0 && (
        <div style={{ ...styles.card, marginTop: 0 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>Calls by Executive (click a name for details · Esc to close)</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {stats.calls_by_executive.map((ex, i) => (
              <div key={i} onClick={() => openExecDrill(ex)} style={{ cursor: "pointer", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 12px", minWidth: 130, background: "#f8fafc" }}
                onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"} onMouseLeave={e => e.currentTarget.style.background = "#f8fafc"}>
                <div style={{ fontWeight: 600, color: colors.primary, fontSize: 13 }}>{ex.label || "Unassigned"}</div>
                <div style={{ fontSize: 11, color: colors.textLight, marginTop: 2 }}><b style={{ color: colors.text, fontSize: 15 }}>{ex.call_count}</b> calls</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      {stats.monthly_trend?.length > 0 && (
        <div style={{ ...styles.card, marginTop: 0 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 12 }}>Monthly Trend</h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 160, padding: "0 10px" }}>
            {stats.monthly_trend.map((m, i) => {
              const maxH = Math.max(...stats.monthly_trend.map(x => parseFloat(x.hours)));
              const barH = maxH > 0 ? (parseFloat(m.hours) / maxH * 130) : 0;
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{parseFloat(m.hours).toFixed(0)}h</div>
                  <div style={{ width: "100%", maxWidth: 50, height: barH, background: `linear-gradient(180deg, ${colors.primary}, #3b82f6)`, borderRadius: "4px 4px 0 0", transition: "height 0.5s" }} />
                  <div style={{ fontSize: 10, color: colors.textLight, textAlign: "center" }}>{m.label}</div>
                  <div style={{ fontSize: 9, color: colors.textLight }}>{m.call_count} calls</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dynamic Pivot Summary Builder */}
      <div style={{ ...styles.card, marginTop: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Summary Builder</h3>
          <button style={styles.btn("success", "sm")} onClick={exportPivotExcel} disabled={pivot.row1s.length === 0}>📊 Export to Excel</button>
        </div>

        {/* Row, Column, Measure selectors */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12, padding: "12px 16px", background: "#f8fafc", borderRadius: 8, border: `1px solid ${colors.border}` }}>
          <div><label style={styles.label}>Row 1 (Primary)</label>
            <select style={{ ...styles.select, width: 140 }} value={pivotRow1} onChange={(e) => setPivotRow1(e.target.value)}>
              <option value="principal">Principal</option><option value="client">Client</option>
              <option value="executive">Executive</option><option value="product">Product</option>
              <option value="location">Location</option><option value="call_type">Call Type</option>
              <option value="tdl_type">TDL Type</option><option value="status">Status</option>
            </select>
          </div>
          <div><label style={styles.label}>Row 2 (Sub-group)</label>
            <select style={{ ...styles.select, width: 140 }} value={pivotRow2} onChange={(e) => setPivotRow2(e.target.value)}>
              <option value="">— None —</option>
              <option value="principal">Principal</option><option value="client">Client</option>
              <option value="executive">Executive</option><option value="product">Product</option>
              <option value="location">Location</option><option value="call_type">Call Type</option>
              <option value="tdl_type">TDL Type</option><option value="status">Status</option>
            </select>
          </div>
          <div><label style={styles.label}>Columns</label>
            <select style={{ ...styles.select, width: 140 }} value={pivotColBy} onChange={(e) => setPivotColBy(e.target.value)}>
              <option value="call_type">Call Type</option><option value="month">Month</option>
              <option value="status">Status</option><option value="executive">Executive</option>
              <option value="principal">Principal</option><option value="client">Client</option>
              <option value="tdl_type">TDL Type</option>
            </select>
          </div>
          <div><label style={styles.label}>Measure</label>
            <select style={{ ...styles.select, width: 120 }} value={pivotMeasure} onChange={(e) => setPivotMeasure(e.target.value)}>
              <option value="hours">Hours</option><option value="count">Call Count</option>
            </select>
          </div>
        </div>

        {/* Filter bar */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16, padding: "10px 16px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: colors.warning, alignSelf: "center" }}>FILTERS:</span>
          <div><label style={styles.label}>Call Type</label>
            <select style={{ ...styles.select, width: 130 }} value={pivotFilters.filter_call_type || ""} onChange={(e) => setPivotFilters({ ...pivotFilters, filter_call_type: e.target.value })}>
              <option value="">All Types</option>
              {masters.callTypes?.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Status</label>
            <select style={{ ...styles.select, width: 120 }} value={pivotFilters.filter_status || ""} onChange={(e) => setPivotFilters({ ...pivotFilters, filter_status: e.target.value })}>
              <option value="">All</option>
              {["Open", "In Progress", "Completed", "Escalated", "On Hold"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Executive</label>
            <select style={{ ...styles.select, width: 130 }} value={pivotFilters.filter_executive || ""} onChange={(e) => setPivotFilters({ ...pivotFilters, filter_executive: e.target.value })}>
              <option value="">All</option>
              {masters.executives?.map(ex => <option key={ex.id} value={ex.name}>{ex.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Location</label>
            <select style={{ ...styles.select, width: 120 }} value={pivotFilters.filter_location || ""} onChange={(e) => setPivotFilters({ ...pivotFilters, filter_location: e.target.value })}>
              <option value="">All</option>
              {masters.locations?.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Product</label>
            <select style={{ ...styles.select, width: 130 }} value={pivotFilters.filter_product || ""} onChange={(e) => setPivotFilters({ ...pivotFilters, filter_product: e.target.value })}>
              <option value="">All</option>
              {masters.products?.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>TDL Type</label>
            <select style={{ ...styles.select, width: 130 }} value={pivotFilters.filter_tdl_type || ""} onChange={(e) => setPivotFilters({ ...pivotFilters, filter_tdl_type: e.target.value })}>
              <option value="">All</option>
              {["Bizom to Tally", "Tally to Bizom", "Both-way Sync"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button style={{ ...styles.btn("ghost", "sm"), alignSelf: "flex-end" }} onClick={() => setPivotFilters({})}>Clear Filters</button>
          <button style={{ ...styles.btn("primary", "sm"), alignSelf: "flex-end" }} onClick={fetchPivot}>Generate</button>
        </div>

        {/* Pivot Table */}
        {!pivotGenerated ? (
          <div style={{ padding: 30, textAlign: "center", color: colors.textLight, fontSize: 14 }}>Choose Row / Columns / Measure and click <b>Generate</b> to build the summary.</div>
        ) : pivot.row1s.length > 0 ? (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, position: "sticky", left: 0, background: "#1a56db", color: "#fff", zIndex: 1, minWidth: 180 }}>
                    {pivotRow1.charAt(0).toUpperCase() + pivotRow1.slice(1)}
                    {pivot.hasRow2 && ` / ${pivotRow2.charAt(0).toUpperCase() + pivotRow2.slice(1)}`}
                  </th>
                  {pivot.cols.map(c => <th key={c} style={{ ...styles.th, textAlign: "right", minWidth: 80, background: "#1a56db", color: "#fff" }}>{c}</th>)}
                  <th style={{ ...styles.th, textAlign: "right", background: "#0f172a", color: "#fff", fontWeight: 800, minWidth: 80 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {pivot.row1s.map(r1 => {
                  const r1Total = pivot.cols.reduce((s, c) => s + (pivot.data[r1]._total[c] || 0), 0);
                  const isExpanded = pivotExpanded[r1];
                  const r2Keys = Object.keys(pivot.data[r1]._row2s).sort();
                  return [
                    <tr key={r1} style={{ background: pivot.hasRow2 ? "#f0f4ff" : "transparent", cursor: pivot.hasRow2 ? "pointer" : "default" }}
                        onClick={() => pivot.hasRow2 && setPivotExpanded({ ...pivotExpanded, [r1]: !isExpanded })}>
                      <td style={{ ...styles.td, fontWeight: 700, position: "sticky", left: 0, background: pivot.hasRow2 ? "#f0f4ff" : "#fff", zIndex: 1 }}>
                        {pivot.hasRow2 && <span style={{ marginRight: 6, fontSize: 10 }}>{isExpanded ? "▼" : "▶"}</span>}
                        {r1}
                      </td>
                      {pivot.cols.map(c => <td key={c} style={{ ...styles.td, textAlign: "right", fontWeight: pivot.hasRow2 ? 600 : 400 }}>
                        {pivot.data[r1]._total[c] ? (pivotMeasure === "hours" ? parseFloat(pivot.data[r1]._total[c]).toFixed(1) : pivot.data[r1]._total[c]) : "—"}
                      </td>)}
                      <td style={{ ...styles.td, textAlign: "right", fontWeight: 700, background: "#f8fafc" }}>
                        {pivotMeasure === "hours" ? r1Total.toFixed(1) : r1Total}
                      </td>
                    </tr>,
                    ...(pivot.hasRow2 && isExpanded ? r2Keys.map(r2 => {
                      const r2Total = pivot.cols.reduce((s, c) => s + (pivot.data[r1]._row2s[r2]?.[c] || 0), 0);
                      return (
                        <tr key={`${r1}-${r2}`} style={{ background: "#fafbff" }}>
                          <td style={{ ...styles.td, paddingLeft: 36, fontSize: 12, color: colors.textLight, position: "sticky", left: 0, background: "#fafbff", zIndex: 1 }}>↳ {r2}</td>
                          {pivot.cols.map(c => <td key={c} style={{ ...styles.td, textAlign: "right", fontSize: 12 }}>
                            {pivot.data[r1]._row2s[r2]?.[c] ? (pivotMeasure === "hours" ? parseFloat(pivot.data[r1]._row2s[r2][c]).toFixed(1) : pivot.data[r1]._row2s[r2][c]) : "—"}
                          </td>)}
                          <td style={{ ...styles.td, textAlign: "right", fontWeight: 600, fontSize: 12, background: "#f8fafc" }}>
                            {pivotMeasure === "hours" ? r2Total.toFixed(1) : r2Total}
                          </td>
                        </tr>
                      );
                    }) : [])
                  ];
                })}
                <tr style={{ background: "#0f172a" }}>
                  <td style={{ ...styles.td, fontWeight: 800, color: "#fff", position: "sticky", left: 0, background: "#0f172a", zIndex: 1 }}>Grand Total</td>
                  {pivot.cols.map(c => {
                    const ct = pivot.row1s.reduce((s, r) => s + (pivot.data[r]._total[c] || 0), 0);
                    return <td key={c} style={{ ...styles.td, textAlign: "right", fontWeight: 700, color: "#fff" }}>{pivotMeasure === "hours" ? ct.toFixed(1) : ct}</td>;
                  })}
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: 800, color: "#fff" }}>
                    {pivotMeasure === "hours"
                      ? pivot.row1s.reduce((s, r) => s + pivot.cols.reduce((s2, c) => s2 + (pivot.data[r]._total[c] || 0), 0), 0).toFixed(1)
                      : pivot.row1s.reduce((s, r) => s + pivot.cols.reduce((s2, c) => s2 + (pivot.data[r]._total[c] || 0), 0), 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : <p style={{ fontSize: 13, color: colors.textLight, textAlign: "center", padding: 20 }}>Select options above to build your summary. Use Row 1 and Row 2 for grouping, Columns for cross-tab, and Filters to narrow data.</p>}
      </div>

      {/* Follow-ups & Recent */}
      <div style={{ ...styles.grid(2), marginTop: 0 }}>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Today's Follow-ups</h3>
          {stats.today_followups?.length > 0 ? stats.today_followups.map((f, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <div><strong>{f.prospect_company}</strong> — {f.attended_by_name || "Unassigned"}</div>
              <StatusBadge status={f.priority} />
            </div>
          )) : <p style={{ fontSize: 12, color: colors.textLight }}>No follow-ups today.</p>}
        </div>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Recent Calls</h3>
          {stats.recent_calls?.length > 0 ? stats.recent_calls.slice(0, 6).map((c, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", fontSize: 11 }}>
              <div><strong>#{c.ticket_number}</strong> {c.client_name} — {c.executive_name}</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span>{parseFloat(c.hours_spent || 0).toFixed(1)}h</span>
                <StatusBadge status={c.call_status} />
              </div>
            </div>
          )) : <p style={{ fontSize: 12, color: colors.textLight }}>No recent calls.</p>}
        </div>
      </div>

      {/* Lead Stats */}
      <div style={{ display: "flex", gap: 12, marginTop: 0, flexWrap: "wrap" }}>
        <div style={{ ...styles.statCard(colors.success), cursor: "pointer" }} onClick={() => openOverlay("Active Leads", async (forExport) => {
          const r = await api(`/leads?${leadDrillParams({ active_only: "true" }, forExport)}`); return r.data;
        })}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Active Leads ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.active_leads?.count || 0}</div>
          <div style={{ fontSize: 11, color: colors.textLight }}>Pipeline: {formatCurrencyINR(stats.active_leads?.pipeline_value)}</div>
        </div>
        <div style={{ ...styles.statCard(colors.danger), cursor: "pointer" }} onClick={() => openOverlay("Overdue Follow-ups", async (forExport) => {
          const r = await api(`/leads?${leadDrillParams({ overdue_only: "true" }, forExport)}`); return r.data;
        })}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Overdue Follow-ups ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: (parseInt(stats.overdue_followups) || 0) > 0 ? colors.danger : colors.text }}>{parseInt(stats.overdue_followups) || 0}</div>
        </div>
        <div style={{ ...styles.statCard(colors.primary), cursor: "pointer" }} onClick={() => openOverlay("Calls This Month", async (forExport) => {
          const p = getDashParams();
          p.append("this_month", "true");
          p.append("limit", forExport ? "100000" : "500");
          const r = await api(`/dashboard/drill-down?${p}`); return r.data;
        })}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Calls This Month ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.calls_this_month?.count || 0}</div>
          <div style={{ fontSize: 11, color: colors.textLight }}>{parseFloat(stats.calls_this_month?.hours || 0).toFixed(1)} hours</div>
        </div>
        <div style={{ ...styles.statCard("#f59e0b"), cursor: "pointer" }} onClick={() => openOverlay("Today's Call Actions & Overdue", async () => stats.today_actions || [])}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Today's Actions ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{parseInt(stats.today_actions_count) || 0}</div>
          <div style={{ fontSize: 11, color: colors.textLight }}>Overdue: {parseInt(stats.overdue_actions_count) || 0}</div>
        </div>
        <div style={{ ...styles.statCard("#8b5cf6"), cursor: "pointer" }} onClick={() => openOverlay("Today's Lead Follow-ups & Overdue", async () => stats.today_lead_followups || [])}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Today's Lead Follow-ups ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.today_lead_followups?.length || 0}</div>
        </div>
      </div>

      {/* Licenses & Renewals */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
        <div style={{ ...styles.statCard("#0891b2"), cursor: "pointer" }} onClick={() => openOverlay("Licenses Expiring (30 days)", async () => { const r = await api(`/reports/licenses?view=expiring&days=30`); return r.data; }, "licenses")}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Licenses Expiring (30d) ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{parseInt(stats.licenses_expiring_30) || 0}</div>
        </div>
        <div style={{ ...styles.statCard(colors.danger), cursor: "pointer" }} onClick={() => openOverlay("Expired Licenses", async () => { const r = await api(`/reports/licenses?view=expired`); return r.data; }, "licenses")}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Expired Licenses ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{parseInt(stats.licenses_expired) || 0}</div>
        </div>
        <div style={{ ...styles.statCard(colors.success), cursor: "pointer" }} onClick={() => openOverlay("Renewed This Month", async () => { const r = await api(`/reports/licenses?view=renewed&date_from=${new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10)}&date_to=${new Date().toISOString().slice(0,10)}`); return r.data; }, "licenses")}>
          <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, textTransform: "uppercase" }}>Renewed This Month ↗</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{parseInt(stats.renewed_this_month_count) || 0}</div>
          <div style={{ fontSize: 11, color: colors.success, fontWeight: 600 }}>{formatCurrencyINR(stats.renewed_this_month_amount || 0)}</div>
        </div>
      </div>

      {/* Charts */}

      {/* Leads / Enquiries Dashboard */}
      <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 24, marginBottom: 12, borderTop: `2px solid ${colors.primary}`, paddingTop: 16 }}>Leads / Enquiries Dashboard</h2>

      {/* Lead Pipeline by Status */}
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Lead Pipeline</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["New", "Contacted", "Demo", "Proposal", "Negotiation", "Converted", "Lost"].map(status => {
            const count = stats.lead_pipeline?.find(p => p.status === status)?.count || 0;
            const value = stats.lead_pipeline?.find(p => p.status === status)?.pipeline_value || 0;
            const statusColors = { New: "#3b82f6", Contacted: "#8b5cf6", Demo: "#f59e0b", Proposal: "#f97316", Negotiation: "#ef4444", Converted: "#10b981", Lost: "#6b7280" };
            return (
              <div key={status} onClick={() => openOverlay(`${status} Leads`, async (forExport) => {
                const r = await api(`/leads?${leadDrillParams({ status }, forExport)}`); return r.data;
              })} style={{ flex: 1, minWidth: 100, padding: "12px 14px", borderRadius: 10, background: "#f8fafc", border: `2px solid ${statusColors[status] || colors.border}`, textAlign: "center", cursor: "pointer", transition: "transform 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"} onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                <div style={{ fontSize: 24, fontWeight: 800, color: statusColors[status] }}>{count}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: statusColors[status] }}>{status} ↗</div>
                {value > 0 && <div style={{ fontSize: 10, color: colors.textLight, marginTop: 2 }}>{formatCurrencyINR(value)}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Leads by Source & Priority */}
      <div style={styles.grid(2)}>
        <div style={styles.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Leads by Source</h3>
            {stats.leads_by_source?.length > 0 && <button style={styles.btn("success", "sm")} onClick={() => {
              const headers = csvRow(["Source", "Count"]);
              const rows = stats.leads_by_source.map(s => csvRow([s.lead_source || "Unknown", s.count])).join("\n");
              downloadCSV(headers + "\n" + rows + "\n", `leads_by_source_${new Date().toISOString().slice(0,10)}.csv`);
            }}>📊</button>}
          </div>
          {stats.leads_by_source?.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
              {stats.leads_by_source.map((s, i) => (
                <div key={i} onClick={() => openLeadsByFilter(`Leads — ${s.lead_source || "Unknown"}`, { lead_source: s.lead_source || "" })} style={{ cursor: "pointer", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "6px 10px", background: "#f8fafc" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"} onMouseLeave={e => e.currentTarget.style.background = "#f8fafc"}>
                  <span style={{ fontSize: 12, color: colors.text }}>{s.lead_source || "Unknown"}</span>
                  <span style={{ fontWeight: 700, marginLeft: 6, color: colors.primary }}>{s.count}</span>
                </div>
              ))}
            </div>
          ) : <p style={{ fontSize: 12, color: colors.textLight }}>No lead data yet.</p>}
        </div>
        <div style={styles.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Leads by Priority</h3>
            {stats.leads_by_priority?.length > 0 && <button style={styles.btn("success", "sm")} onClick={() => {
              const headers = csvRow(["Priority", "Count", "Pipeline Value"]);
              const rows = stats.leads_by_priority.map(p => csvRow([p.priority || "Unset", p.count, p.pipeline_value])).join("\n");
              downloadCSV(headers + "\n" + rows + "\n", `leads_by_priority_${new Date().toISOString().slice(0,10)}.csv`);
            }}>📊</button>}
          </div>
          {stats.leads_by_priority?.length > 0 ? stats.leads_by_priority.map((p, i) => {
            const prioColors = { Hot: colors.danger, Warm: colors.warning, Cold: colors.primary };
            return (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: prioColors[p.priority] || colors.text }}>{p.priority || "Unset"}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontWeight: 700 }}>{p.count}</span>
                  {p.pipeline_value > 0 && <span style={{ fontSize: 10, color: colors.textLight }}>{formatCurrencyINR(p.pipeline_value)}</span>}
                </div>
              </div>
            );
          }) : <p style={{ fontSize: 12, color: colors.textLight }}>No lead data yet.</p>}
        </div>
      </div>

      {/* Conversion & Follow-up Stats */}
      <div style={styles.grid(3)}>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Conversion Rate</h3>
          {(() => {
            const total = stats.lead_pipeline?.reduce((s, p) => s + parseInt(p.count || 0), 0) || 0;
            const converted = parseInt(stats.lead_pipeline?.find(p => p.status === "Converted")?.count || 0);
            const rate = total > 0 ? ((converted / total) * 100).toFixed(1) : "0.0";
            return (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: 36, fontWeight: 800, color: colors.success }}>{rate}%</div>
                <div style={{ fontSize: 12, color: colors.textLight }}>{converted} converted out of {total} total</div>
              </div>
            );
          })()}
        </div>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Avg Follow-ups to Convert</h3>
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: colors.primary }}>{typeof stats.avg_followups_to_convert === "object" ? "—" : (stats.avg_followups_to_convert || "—")}</div>
            <div style={{ fontSize: 12, color: colors.textLight }}>follow-ups per conversion</div>
          </div>
        </div>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>Total Pipeline Value</h3>
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: colors.success }}>{formatCurrencyINR(stats.active_leads?.pipeline_value)}</div>
            <div style={{ fontSize: 12, color: colors.textLight }}>{stats.active_leads?.count || 0} active leads</div>
          </div>
        </div>
      </div>

      {/* Leads by Executive */}
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, marginBottom: 0 }}>Leads by Executive</h3>
          {stats.leads_by_executive?.length > 0 && <button style={styles.btn("success", "sm")} onClick={() => {
            const headers = csvRow(["Executive", "Total Leads", "Active", "Converted", "Lost", "Pipeline Value"]);
            const rows = stats.leads_by_executive.map(e => csvRow([e.executive_name || "Unassigned", e.total, e.active, e.converted, e.lost, e.pipeline_value])).join("\n");
            downloadCSV(headers + "\n" + rows + "\n", `leads_by_executive_${new Date().toISOString().slice(0,10)}.csv`);
          }}>📊 Export</button>}
        </div>
        {stats.leads_by_executive?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
            {stats.leads_by_executive.map((e, i) => (
              <div key={i} onClick={() => openOverlay(`Leads by ${e.executive_name || "Unassigned"}`, async (forExport) => {
                const r = await api(`/leads?${leadDrillParams({ attended_by_id: e.executive_id }, forExport)}`); return r.data;
              })} style={{ cursor: "pointer", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 12px", minWidth: 130, background: "#f8fafc" }}
                onMouseEnter={ev => ev.currentTarget.style.background = "#eef2ff"} onMouseLeave={ev => ev.currentTarget.style.background = "#f8fafc"}>
                <div style={{ fontWeight: 600, color: colors.primary, fontSize: 13 }}>{e.executive_name || "Unassigned"}</div>
                <div style={{ fontSize: 11, color: colors.textLight, marginTop: 2 }}><b style={{ color: colors.text, fontSize: 15 }}>{e.total}</b> leads</div>
              </div>
            ))}
          </div>
        ) : <p style={{ fontSize: 12, color: colors.textLight }}>No lead data yet.</p>}
      </div>

      {/* Lead Summary Builder */}
      <LeadSummaryBuilder dateFrom={dateFrom} dateTo={dateTo} masters={masters} leadAdv={leadAdvParams()} />

      {/* Unified Overlay Modal */}
      {overlay && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1500, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 30, overflowY: "auto" }} onClick={closeOverlay}>
          <div style={{ background: "#fff", borderRadius: 12, padding: "24px 28px", width: "95%", maxWidth: 1100, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 30px rgba(0,0,0,0.25)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{overlay.title} <span style={{ fontSize: 12, fontWeight: 400, color: colors.textLight }}>({overlay.data.length}{overlay.data.length >= 500 ? "+ shown — use Export all" : ""})</span></h2>
              <div style={{ display: "flex", gap: 8 }}>
                {overlay.kind !== "licenses" && overlay.fetchFn && <button style={styles.btn("success", "sm")} disabled={exportingAll} onClick={async () => {
                  setExportingAll(true);
                  try {
                    const all = await overlay.fetchFn(true);
                    if (all && all.length) {
                      const isCall = all[0]?.ticket_number !== undefined;
                      if (isCall) {
                        const headers = csvRow(["Ticket #", "Log Date", "Call Date", "Physical Days", "Executive", "Principal Company", "Customer", "Contact Person", "Contact Email", "License #", "Product", "TDL Type", "Start Time", "End Time", "Hours Spent", "Call Type", "Status", "Location", "Subscription Expiry", "Billed Through / Partner", "Assigned To", "Next Action Date", "Contact Person Spoke To", "Release / Version", "Subject", "Call Notes", "Problem Observed", "Solution Provided"]);
                        const rows = all.map(r => csvRow([r.ticket_number, r.log_date ? formatDateDMY(r.log_date) : "", formatDateDMY(r.call_date), r.physical_days || "", r.executive_name || "", r.principal_name || "", r.client_name || "", r.contact_person || "", r.contact_email || r.client_email || "", r.license_number || "", r.product_name || "", r.tdl_type || "", formatTime12hr(r.call_start_time), formatTime12hr(r.call_end_time), r.hours_spent || 0, r.call_type_name || "", r.call_status || "", r.location_name || "", r.subscription_expiry ? formatDateDMY(r.subscription_expiry) : "", r.billed_through_name || "", r.assigned_to_name || "", r.next_action_date ? formatDateDMY(r.next_action_date) : "", r.contact_person_called || "", r.software_version || "", r.regarding || "", r.call_details || "", r.problem_observed || "", r.solution_provided || ""])).join("\n");
                        downloadCSV(headers + "\n" + rows + "\n", `dashboard_${overlay.title.replace(/[^a-zA-Z0-9]/g, "_")}_all.csv`);
                      } else {
                        const headers = csvRow(["Enquiry #", "Date", "Prospect Company", "Contact Person", "Designation", "Phone", "Email", "City", "Location", "Lead Source", "Product", "No. of Users", "Estimated Deal Value", "Attended By", "Assigned To", "Status", "Priority", "Follow-up Date", "Next Action", "Remarks", "Total Follow-ups"]);
                        const rows = all.map(r => csvRow([r.enquiry_number, formatDateDMY(r.enquiry_date || r.follow_up_date), r.prospect_company || "", r.contact_person || "", r.designation || "", r.phone || "", r.email || "", r.city || "", r.location_name || "", r.lead_source || "", r.product_name || "", r.number_of_users || "", r.estimated_deal_value || "", r.executive_name || "", r.assigned_to_name || "", r.status || "", r.priority || "", r.follow_up_date ? formatDateDMY(r.follow_up_date) : "", r.next_action || "", r.remarks || "", r.total_followups || 0])).join("\n");
                        downloadCSV(headers + "\n" + rows + "\n", `dashboard_${overlay.title.replace(/[^a-zA-Z0-9]/g, "_")}_all.csv`);
                      }
                    }
                  } catch (e) {}
                  setExportingAll(false);
                }}>{exportingAll ? "Exporting…" : "⬇ Export all"}</button>}
                <button style={styles.btn("ghost", "sm")} onClick={closeOverlay}>✕ Close <span style={{ fontSize: 9, color: colors.textLight, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
              </div>
            </div>
            {overlay.kind !== "licenses" && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                <ListSearchBox value={ovSearch} onSearch={setOvSearch}
                  placeholder="🔍 Search any field — customer, ticket #, licence / serial #, mobile, e-mail, executive, notes, date…" />
                {ovTerms.length > 0 && (
                  <span style={{ fontSize: 12, color: colors.textLight }}>
                    {ovRows.length} match{ovRows.length === 1 ? "" : "es"}
                    {(overlay.data || []).length >= 500 ? (ovAll ? ` in all ${ovAll.length} records` : " — searching all records…") : ` of ${(overlay.data || []).length}`}
                    {" · "}<span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setOvSearch("")}>clear</span>
                  </span>
                )}
              </div>
            )}
            <SortBar sorts={sorts} clearSort={clearSort} labels={{ ticket_number: "Ticket#", call_date: "Date", executive_name: "Executive", client_name: "Customer", product_name: "Product", hours_spent: "Hours", call_status: "Status", next_action_date: "Next Action", enquiry_number: "Enquiry#", prospect_company: "Company", status: "Status", priority: "Priority" }} />
            {overlay.loading ? <p style={{ padding: 20, textAlign: "center", color: colors.textLight }}>Loading...</p> : (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
                {overlay.kind === "licenses" ? (
                  (() => {
                    const renewedView = overlay.data[0]?.renewed_date !== undefined;
                    const statusOf = (d) => { if (!d) return { label: "—", color: colors.textLight }; const days = Math.ceil((new Date(String(d).slice(0,10)) - new Date(new Date().toDateString())) / 86400000); if (days < 0) return { label: "Expired", color: colors.danger }; if (days <= 30) return { label: "Expiring", color: "#d97706" }; return { label: "Active", color: colors.success }; };
                    return (
                      <table style={{ ...styles.table, minWidth: 800 }}>
                        <thead><tr>
                          {(renewedView ? ["Customer", "Product", "License #", "New Expiry", "Renewed On", "Term", "Amount"] : ["Customer", "Product", "License #", "User Type", "Qty", "Expiry", "Days Left", "Status"]).map(h => <th key={h} style={styles.th}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {ovRows.map((r, i) => (
                            <tr key={i} onClick={() => openLicHistory(r)} style={{ cursor: "pointer", background: i % 2 === 0 ? "#fff" : "#f8fafc" }} onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"} onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#f8fafc"}>
                              {renewedView ? (<>
                                <td style={{ ...styles.td, fontWeight: 600 }}>{r.client_name}</td>
                                <td style={styles.td}>{r.product_name || "—"}</td>
                                <td style={styles.td}>{r.license_number || "—"}</td>
                                <td style={styles.td}>{r.new_expiry ? formatDateDMY(r.new_expiry) : "—"}</td>
                                <td style={styles.td}>{r.renewed_date ? formatDateDMY(r.renewed_date) : "—"}</td>
                                <td style={styles.td}>{r.term || "—"}</td>
                                <td style={{ ...styles.td, fontWeight: 600 }}>{r.amount ? formatCurrencyINR(r.amount) : "—"}</td>
                              </>) : (<>
                                <td style={{ ...styles.td, fontWeight: 600 }}>{r.client_name}</td>
                                <td style={styles.td}>{r.product_name || "—"}</td>
                                <td style={styles.td}>{r.license_number || "—"}</td>
                                <td style={styles.td}>{r.user_type || "—"}</td>
                                <td style={styles.td}>{r.quantity || "—"}</td>
                                <td style={styles.td}>{r.subscription_expiry ? formatDateDMY(r.subscription_expiry) : "—"}</td>
                                <td style={{ ...styles.td, fontWeight: 600, color: r.days_left < 0 ? colors.danger : r.days_left <= 30 ? "#d97706" : colors.text }}>{r.days_left}</td>
                                <td style={styles.td}>{(() => { const s = statusOf(r.subscription_expiry); return <span style={{ background: s.color + "22", color: s.color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{s.label}</span>; })()}</td>
                              </>)}
                            </tr>
                          ))}
                          {overlay.data.length === 0 && <tr><td colSpan={renewedView ? 7 : 8} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No records found.</td></tr>}
                        </tbody>
                      </table>
                    );
                  })()
                ) : overlay.data[0]?.ticket_number !== undefined ? (
                  <table style={{ ...styles.table, minWidth: 1000 }}>
                    <thead><tr>
                      {[["Ticket#","ticket_number"],["Date","call_date"],["Executive","executive_name"],["Customer","client_name"],["Product","product_name"],["License#","license_number"],["Start","call_start_time"],["End","call_end_time"],["Hours","hours_spent"],["Subject","regarding"],["Status","call_status"],["Next Action","next_action_date"]]
                        .map(([h, k]) => <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={styles.th} />)}
                    </tr></thead>
                    <tbody>
                      {ovRows.map((r, i) => (
                        <tr key={i} onClick={() => { rememberList(); setPendingReturn("dashboard"); setPendingCall(r); setOverlay(null); setActiveScreen("call-tracker"); }} style={{ cursor: "pointer", background: r.next_action_date && new Date(r.next_action_date) < new Date(new Date().toDateString()) ? "#fef2f2" : i % 2 === 0 ? "#fff" : "#f8fafc" }} onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"} onMouseLeave={e => e.currentTarget.style.background = (r.next_action_date && new Date(r.next_action_date) < new Date(new Date().toDateString()) ? "#fef2f2" : i % 2 === 0 ? "#fff" : "#f8fafc")}>
                          <td style={{ ...styles.td, fontWeight: 700, color: colors.primary }}>{r.ticket_number}</td>
                          <td style={styles.td}>{formatDateDMY(r.call_date)}</td>
                          <td style={styles.td}>{r.executive_name}</td>
                          <td style={styles.td}>{r.client_name}</td>
                          <td style={styles.td}>{r.product_name || "—"}</td>
                          <td style={{ ...styles.td, fontSize: 11 }}>{r.license_number || "—"}</td>
                          <td style={styles.td}>{formatTime12hr(r.call_start_time)}</td>
                          <td style={styles.td}>{formatTime12hr(r.call_end_time)}</td>
                          <td style={styles.td}>{parseFloat(r.hours_spent || 0).toFixed(2)}</td>
                          <td style={styles.td}>{r.regarding || "—"}</td>
                          <td style={styles.td}><StatusBadge status={r.call_status} /></td>
                          <td style={{ ...styles.td, fontSize: 11, fontWeight: 600, color: r.next_action_date && new Date(r.next_action_date) < new Date(new Date().toDateString()) ? colors.danger : colors.text }}>{r.next_action_date ? formatDateDMY(r.next_action_date) : "—"}</td>
                        </tr>
                      ))}
                      {overlay.data.length === 0 && <tr><td colSpan={12} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No records found.</td></tr>}
                    </tbody>
                  </table>
                ) : (
                  <table style={{ ...styles.table, minWidth: 900 }}>
                    <thead><tr>
                      {[["Enquiry#","enquiry_number"],["Date","enquiry_date"],["Company","prospect_company"],["Contact","contact_person"],["Phone","phone"],["Product","product_name"],["Deal Value","estimated_deal_value"],["Executive","executive_name"],["Follow-up","follow_up_date"],["Next Action","next_action"],["Priority","priority"],["Status","status"]]
                        .map(([h, k]) => <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={styles.th} />)}
                    </tr></thead>
                    <tbody>
                      {ovRows.map((r, i) => (
                        <tr key={i} onClick={() => { rememberList(); setPendingReturn("dashboard"); setPendingLead(r); setOverlay(null); setActiveScreen("leads"); }} style={{ cursor: "pointer", background: r.follow_up_date && new Date(r.follow_up_date) < new Date(new Date().toDateString()) ? "#fef2f2" : i % 2 === 0 ? "#fff" : "#f8fafc" }} onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"} onMouseLeave={e => e.currentTarget.style.background = (r.follow_up_date && new Date(r.follow_up_date) < new Date(new Date().toDateString()) ? "#fef2f2" : i % 2 === 0 ? "#fff" : "#f8fafc")}>
                          <td style={{ ...styles.td, fontWeight: 700, color: colors.primary }}>{r.enquiry_number}</td>
                          <td style={styles.td}>{formatDateDMY(r.enquiry_date)}</td>
                          <td style={styles.td}>{r.prospect_company}</td>
                          <td style={styles.td}>{r.contact_person || "—"}</td>
                          <td style={styles.td}>{r.phone || "—"}</td>
                          <td style={styles.td}>{r.product_name || "—"}</td>
                          <td style={styles.td}>{formatCurrencyINR(r.estimated_deal_value)}</td>
                          <td style={styles.td}>{r.executive_name || "—"}</td>
                          <td style={{ ...styles.td, fontWeight: 600, color: r.follow_up_date && new Date(r.follow_up_date) < new Date(new Date().toDateString()) ? colors.danger : colors.text }}>{r.follow_up_date ? formatDateDMY(r.follow_up_date) : "—"}</td>
                          <td style={styles.td}>{r.next_action || "—"}</td>
                          <td style={styles.td}>{r.priority}</td>
                          <td style={styles.td}><StatusBadge status={r.status} /></td>
                        </tr>
                      ))}
                      {overlay.data.length === 0 && <tr><td colSpan={12} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No records found.</td></tr>}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {licHistory && (
        <div style={styles.modal} onClick={() => setLicHistory(null)}>
          <div style={{ ...styles.modalContent, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: "#0891b2" }}>Renewal History</h3>
            <div style={{ fontSize: 13, color: colors.textLight, marginBottom: 10 }}><b>{licHistory.client_name}</b> · {licHistory.product_name || "—"}{licHistory.license_number ? ` · ${licHistory.license_number}` : ""}</div>
            {licHistoryData.length === 0 ? <p style={{ color: colors.textLight, fontSize: 13 }}>No renewals recorded for this license yet.</p> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>{["Renewed On", "Old → New Expiry", "Term", "Amount", "Note", "By"].map(h => <th key={h} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }}>{h}</th>)}</tr></thead>
                <tbody>{licHistoryData.map(h => (
                  <tr key={h.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={styles.td}>{formatDateDMY(h.renewed_date)}</td>
                    <td style={styles.td}>{h.old_expiry ? formatDateDMY(h.old_expiry) : "—"} → {h.new_expiry ? formatDateDMY(h.new_expiry) : "—"}</td>
                    <td style={styles.td}>{h.term || "—"}</td>
                    <td style={styles.td}>{h.amount ? formatCurrencyINR(h.amount) : "—"}</td>
                    <td style={styles.td}>{h.note || "—"}</td>
                    <td style={styles.td}>{h.renewed_by || "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            <div style={{ textAlign: "right", marginTop: 12 }}><button style={styles.btn("ghost", "sm")} onClick={() => setLicHistory(null)}>Close</button></div>
          </div>
        </div>
      )}

    </div>
  );
};

// ============================================================================
// GENERIC MASTER DATA CRUD SCREEN
// ============================================================================
// ---- Customer duplicate detection ----
// Values are checked against the server one at a time (search=), so the result is
// correct however many customers exist — no need to download the whole list.
// Phones are compared on their last 10 digits, so "+91 98450-12345", "98450 12345"
// and "9845012345" all match. Fields may hold several comma-separated values;
// ANY one matching counts as a duplicate.
const normPhone = (v) => { const d = String(v || "").replace(/[^\d]/g, ""); return d.length > 10 ? d.slice(-10) : d; };
const splitVals = (v) => String(v || "").split(",").map(x => x.trim()).filter(Boolean);
const normName = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const normGst = (v) => String(v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const searchClients = async (term) => {
  if (!term || String(term).trim().length < 3) return [];
  try { const r = await api(`/clients?search=${encodeURIComponent(String(term).trim())}&limit=50`); return r.data || []; }
  catch (e) { return []; }
};

// Live duplicate + similar-name checking for a customer form.
const useCustomerDupes = (form, selfId, enabled) => {
  const [dupes, setDupes] = useState([]);       // [{field,label,value,rec}]
  const [similar, setSimilar] = useState([]);   // records with a similar name
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!enabled) { setDupes([]); setSimilar([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setChecking(true);
      const found = [];
      const seen = new Set();
      const push = (field, label, value, rec) => {
        if (!rec || (selfId && rec.id === selfId)) return;
        const k = field + ":" + rec.id;
        if (seen.has(k)) return;
        seen.add(k); found.push({ field, label, value, rec });
      };

      // Company name — exact match blocks; near matches are only suggestions.
      // The lookup matches the typed text against company names AND product serial
      // (licence) numbers, and returns the licences held by each customer.
      const nameTyped = String(form.company_name || "").trim();
      let nameHits = [];
      if (nameTyped.length >= 3) {
        let rows = [];
        try { const r = await api(`/customer-products/lookup?q=${encodeURIComponent(nameTyped)}`); rows = r.data || []; } catch (e) {}
        // Group the rows by customer, collecting each customer's serial numbers
        const byClient = new Map();
        rows.forEach(row => {
          const cur = byClient.get(row.client_id) || { id: row.client_id, company_name: row.company_name, city: row.city, phone: row.phone, serials: [] };
          if (row.license_number && !cur.serials.includes(row.license_number)) cur.serials.push(row.license_number);
          byClient.set(row.client_id, cur);
        });
        nameHits = Array.from(byClient.values());
        const n = normName(nameTyped);
        nameHits.forEach(r => { if (normName(r.company_name) === n) push("company_name", "Company name", r.company_name, r); });
      }

      // Mobile numbers (main + contact)
      for (const raw of [...splitVals(form.phone), ...splitVals(form.contact_phone)]) {
        const p = normPhone(raw);
        if (p.length < 10) continue;
        const hits = await searchClients(p);
        hits.forEach(r => {
          const rp = [...splitVals(r.phone), ...splitVals(r.contact_phone)].map(normPhone);
          if (rp.includes(p)) push("phone", "Mobile number", raw, r);
        });
      }

      // E-mails (main + contact)
      for (const raw of [...splitVals(form.email), ...splitVals(form.contact_email)]) {
        const m = raw.toLowerCase();
        if (m.length < 5 || !m.includes("@")) continue;
        const hits = await searchClients(m);
        hits.forEach(r => {
          const rm = [...splitVals(r.email), ...splitVals(r.contact_email)].map(x => x.toLowerCase());
          if (rm.includes(m)) push("email", "Email", raw, r);
        });
      }

      // Product serial / licence number — unique across customers unless the
      // customers belong to the same group (sister concerns).
      const serial = String(form.new_license_number || "").trim();
      if (serial.length >= 4) {
        try {
          const params = new URLSearchParams({ serial });
          if (selfId) params.append("client_id", selfId);
          if (form.group_id) params.append("group_id", form.group_id);
          const r = await api(`/customer-products/serial-check?${params}`);
          (r.conflicts || []).forEach(c => push("new_license_number", "Serial / licence number", serial, { id: c.id, company_name: c.company_name + (c.group_name ? ` (group: ${c.group_name})` : "") }));
        } catch (e) {}
      }

      // GSTIN
      const g = normGst(form.gstin);
      if (g.length >= 10) {
        const hits = await searchClients(g);
        hits.forEach(r => { if (normGst(r.gstin) === g) push("gstin", "GSTIN", form.gstin, r); });
      }

      if (!cancelled) {
        setDupes(found);
        // The server search looks at phone/email/GSTIN too, so keep only records
        // whose COMPANY NAME actually matches what was typed.
        const typedN = normName(nameTyped);
        const exactIds = new Set(found.filter(d => d.field === "company_name").map(d => d.rec.id));
        const typedRaw = nameTyped.toLowerCase();
        setSimilar(
          nameHits
            .filter(r => r.id !== selfId && !exactIds.has(r.id))
            .filter(r => {
              const n = normName(r.company_name);
              const nameMatch = n && typedN && (n.includes(typedN) || typedN.includes(n));
              const serialMatch = (r.serials || []).some(sn => String(sn).toLowerCase().includes(typedRaw));
              return nameMatch || serialMatch;      // matched the name OR a serial number
            })
            .slice(0, 8)
        );
        setChecking(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selfId, form.company_name, form.phone, form.contact_phone, form.email, form.contact_email, form.gstin, form.new_license_number, form.group_id]);

  return { dupes, similar, checking };
};

// Red "already exists" line shown directly under the offending field.
const DupeMsg = ({ dupes, field, onOpen }) => {
  const hits = (dupes || []).filter(d => d.field === field);
  if (!hits.length) return null;
  return (
    <div style={{ marginTop: 4, fontSize: 11.5, color: colors.danger, fontWeight: 600 }}>
      {hits.slice(0, 2).map((d, i) => (
        <div key={i}>⛔ Already used by{" "}
          <span onClick={() => onOpen && onOpen(d.rec)} style={{ textDecoration: "underline", cursor: onOpen ? "pointer" : "default" }}>
            {d.rec.company_name}
          </span> (ID {d.rec.id})
        </div>
      ))}
    </div>
  );
};

// Type-ahead list of existing customers with a similar name.
const SimilarList = ({ similar, onOpen }) => {
  if (!similar || !similar.length) return null;
  return (
    <div style={{ marginTop: 4, border: `1px solid #f59e0b`, background: "#fffbeb", borderRadius: 6, padding: "6px 8px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#b45309", marginBottom: 3 }}>Existing customers (name or serial no.) — click to open</div>
      {similar.map(r => (
        <div key={r.id} onClick={() => onOpen && onOpen(r)}
          style={{ fontSize: 12, padding: "4px 4px", cursor: onOpen ? "pointer" : "default", borderRadius: 4, borderBottom: `1px solid #fde68a` }}
          onMouseEnter={e => e.currentTarget.style.background = "#fef3c7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          <b>{r.company_name}</b>
          {r.serials && r.serials.length > 0 && (
            <span style={{ color: "#b45309", fontWeight: 600 }}> — {r.serials.join(", ")}</span>
          )}
          <span style={{ color: colors.textLight }}>
            {r.city ? ` · ${r.city}` : ""}{r.phone ? ` · ${r.phone}` : ""} · ID {r.id}
          </span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Keep a list up to date with OTHER people's changes: every 60 seconds, and at once
// when you come back to the CRM tab / window. Quiet (no "Loading…"); paused while a
// form or window is open on this screen; never more than once in 15 seconds.
// ============================================================================
const useAutoRefresh = (refresh, paused) => {
  const [updatedAt, setUpdatedAt] = useState(() => new Date());
  const fn = useRef(refresh); fn.current = refresh;
  const pausedRef = useRef(paused); pausedRef.current = paused;
  const last = useRef(Date.now());
  const run = useCallback(async (force) => {
    if (!force && (pausedRef.current || document.hidden || Date.now() - last.current < 15000)) return;
    last.current = Date.now();
    try { await fn.current(); setUpdatedAt(new Date()); } catch (e) { /* next time */ }
  }, []);
  useEffect(() => {
    const t = setInterval(() => run(false), 60000);
    const back = () => { if (!document.hidden) run(false); };
    document.addEventListener("visibilitychange", back);
    window.addEventListener("focus", back);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", back); window.removeEventListener("focus", back); };
  }, [run]);
  return { updatedAt, refreshNow: () => run(true) };
};
const RefreshStamp = ({ updatedAt, onRefresh }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, fontSize: 11, color: colors.textLight, margin: "0 0 6px" }}
    title="The list refreshes by itself every minute, and when you come back to this tab">
    Updated {updatedAt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })} ·
    <span style={{ color: colors.primary, cursor: "pointer", textDecoration: "underline" }} onClick={onRefresh}>↻ Refresh</span>
  </div>
);

const MasterScreen = ({ title, endpoint, columns, formFields, linkConfig, renderExtra, extraButtons, pendingOpenId, setPendingOpen, pendingReturn, setActiveScreen }) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [quickCreateModal, setQuickCreateModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [linkedPrincipals, setLinkedPrincipals] = useState(new Set());
  const [linkedClients, setLinkedClients] = useState(new Set());

  // Close the form only after confirming if there are unsaved changes.
  // Used by Esc, clicking outside, and the Cancel button so a stray keypress /
  // right-click can't silently discard a half-filled record.
  const attemptClose = () => {
    if (showImport) { setShowImport(false); return; }
    if (showForm && !confirmCancel(form)) return;
    setShowForm(false); setEditItem(null);
  };
  const attemptCloseRef = useRef(attemptClose);
  attemptCloseRef.current = attemptClose;

  // Keyboard shortcuts for Master screens
  useEffect(() => {
    const closeModal = () => { attemptCloseRef.current(); };
    const addNew = () => { setEditItem(null); setForm({}); setShowForm(true); };
    const saveForm = () => { document.dispatchEvent(new Event("master-do-save")); };
    const handleScreenKeys = (e) => {
      if (!e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'x') { e.preventDefault(); exportToExcel(); }
      else if (k === 'b') { e.preventDefault(); blankExport(); }
      else if (k === 'm') { e.preventDefault(); document.querySelector('input[type="file"]')?.click(); }
    };
    document.addEventListener("close-modal", closeModal);
    document.addEventListener("shortcut-add-new", addNew);
    document.addEventListener("shortcut-save", saveForm);
    window.addEventListener("keydown", handleScreenKeys);
    return () => { document.removeEventListener("close-modal", closeModal); document.removeEventListener("shortcut-add-new", addNew); document.removeEventListener("shortcut-save", saveForm); window.removeEventListener("keydown", handleScreenKeys); };
  }, []);

  const fetchData = useCallback(async (page = 1, quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, ...(search && { search }) });
      const r = await api(`/${endpoint}?${params}`);
      let sortedData = r.data;
      if (sortBy) {
        sortedData = [...r.data].sort((a, b) => {
          const va = (a[sortBy] || "").toString().toLowerCase();
          const vb = (b[sortBy] || "").toString().toLowerCase();
          return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        });
      }
      setData(sortedData); setPagination(r.pagination);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  }, [endpoint, search, sortBy, sortDir]);

  const auto = useAutoRefresh(() => fetchData(pagination?.page || 1, true), showForm);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => { fetchData(); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ---- Live duplicate checking (customers) ----
  const isCustomerMaster = endpoint === "clients";
  const { dupes, similar } = useCustomerDupes(form, editItem?.id, isCustomerMaster && showForm);
  // Clicking a suggested/duplicate customer opens that record instead
  const openExisting = async (rec) => {
    if (!window.confirm(`Open "${rec.company_name}" instead of creating a new customer?`)) return;
    setShowForm(false); setEditItem(null);
    setTimeout(() => openEdit(rec), 100);
  };

  // Mandatory-field rules for customers
  const customerErrors = () => {
    if (!isCustomerMaster) return [];
    const e = [];
    const isNew = !editItem;
    const has = (v) => String(v || "").trim().length > 0;
    if (isNew && !has(form.new_license_number)) e.push("Product serial / licence number is required for a new customer.");
    if (isNew && has(form.new_license_number) && !has(form.new_product_id)) e.push("Please choose the product this serial number belongs to.");
    if (form.gst_registration === "Registered") {
      const g = String(form.gstin || "").replace(/\s/g, "");
      if (!g) e.push("GSTIN is required when the customer is GST Registered.");
      else if (g.length !== 15) e.push("GSTIN must be 15 characters.");
    }
    return e;
  };

  const handleSave = async () => {
    try {
      // Customers: mandatory fields and duplicate clashes BLOCK the save outright.
      if (isCustomerMaster) {
        const errs = customerErrors();
        if (errs.length) { setToast({ message: errs[0], type: "error" }); return; }
        if (dupes.length) {
          const c = dupes[0];
          setToast({ message: `Cannot save — ${c.label} already used by "${c.rec.company_name}" (ID ${c.rec.id}).`, type: "error" });
          return;
        }
      }

      // ---- Duplicate check: BLOCKS a save that repeats the name, a mobile number
      // or an e-mail of another record in this master (active or inactive).
      // On edit, only fields that were changed are checked, so older duplicates
      // can still be edited. Customers have their own, fuller check above.
      if (!isCustomerMaster) {
        const nameField = formFields.find(f => f.required)?.key || formFields[0]?.key;
        const phoneKeys = ["phone", "alternate_phone", "mobile", "contact_phone"].filter(k => formFields.some(f => f.key === k));
        const emailKeys = ["email", "contact_email"].filter(k => formFields.some(f => f.key === k));
        const nn = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const em = (v) => String(v || "").trim().toLowerCase();
        const changed = (k, norm) => !editItem || norm(form[k]) !== norm(editItem[k]);
        const typedName = nn(form[nameField]);
        const typedPhones = phoneKeys.filter(k => changed(k, normPhone)).flatMap(k => splitVals(form[k]).map(normPhone)).filter(p => p.length >= 10);
        const typedEmails = emailKeys.filter(k => changed(k, em)).flatMap(k => splitVals(form[k]).map(em)).filter(m => m.includes("@"));
        const checkName = typedName && changed(nameField, nn);
        if (checkName || typedPhones.length || typedEmails.length) {
          let allRecords = data;
          try { const allR = await api(`/${endpoint}?limit=100000`); allRecords = allR.data || data; } catch (e) {}
          const others = allRecords.filter(d => !editItem || d.id !== editItem.id);
          const label = (d) => `"${d[nameField] || d.name || d.company_name || "ID " + d.id}" (ID ${d.id}${d.is_active === false ? ", inactive — reactivate it instead" : ""})`;
          let clash = null;
          if (checkName) { const d = others.find(o => nn(o[nameField]) === typedName); if (d) clash = `this name is already used by ${label(d)}`; }
          if (!clash) for (const p of typedPhones) {
            const d = others.find(o => phoneKeys.some(k => splitVals(o[k]).map(normPhone).includes(p)));
            if (d) { clash = `mobile number ${p} is already used by ${label(d)}`; break; }
          }
          if (!clash) for (const m of typedEmails) {
            const d = others.find(o => emailKeys.some(k => splitVals(o[k]).map(em).includes(m)));
            if (d) { clash = `e-mail ${m} is already used by ${label(d)}`; break; }
          }
          if (clash) { setToast({ message: `Cannot save — ${clash}.`, type: "error" }); return; }
        }
      }

      let savedId;
      // new_product_id / new_license_number are form-only helpers used to create the
      // customer's first licence — they are not columns on the record itself.
      const { new_product_id, new_license_number, new_user_type, new_subscription_expiry, ...payload } = form;

      if (editItem) {
        await api(`/${endpoint}/${editItem.id}`, { method: "PUT", body: payload });
        savedId = editItem.id;
        setToast({ message: "Updated!", type: "success" });
      } else {
        const r = await api(`/${endpoint}`, { method: "POST", body: payload });
        savedId = r.data?.id;
        setToast({ message: "Created!", type: "success" });
        if (isCustomerMaster && savedId && new_license_number && new_product_id) {
          try {
            await api(`/customer-products`, { method: "POST", body: {
              client_id: savedId, product_id: new_product_id,
              license_number: String(new_license_number).trim(),
              user_type: new_user_type || "Single User", subscription_expiry: new_subscription_expiry || null, is_active: true } });
          } catch (e) { setToast({ message: `Customer saved, but the licence could not be added: ${e.message}`, type: "warning" }); }
        }
        if (linkConfig && savedId) await saveLinking(savedId);
        // If this master has an extra section (e.g. customer products), keep the
        // form open on the just-created record and jump to that section so the
        // user can map products/licenses immediately — no need to search again.
        if (renderExtra && r.data) {
          setEditItem(r.data);
          setForm({ ...r.data });
          fetchData();
          setTimeout(() => { document.getElementById("master-extra-section")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 250);
          return;
        }
      }
      if (linkConfig && savedId) await saveLinking(savedId);
      setShowForm(false); setEditItem(null); setForm({}); fetchData();
      window.dispatchEvent(new Event("masters-refresh"));   // other screens' dropdowns see it at once
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // Listen for Alt+S save shortcut
  useEffect(() => {
    const doSave = () => { if (showForm) handleSave(); };
    document.addEventListener("master-do-save", doSave);
    return () => document.removeEventListener("master-do-save", doSave);
  });

  const handleToggle = async (id) => {
    try { await api(`/${endpoint}/${id}/toggle`, { method: "PATCH" }); setToast({ message: "Status updated!", type: "success" }); fetchData(); window.dispatchEvent(new Event("masters-refresh")); }
    catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`⚠️ Delete "${name}"?\n\nThis permanently removes it. If any call, lead, licence or other record uses it, deletion is blocked and you'll be told what uses it.`)) return;
    try {
      await api(`/${endpoint}/${id}`, { method: "DELETE" });
      setToast({ message: `"${name}" deleted!`, type: "success" });
      fetchData();
    } catch (e) {
      // The server says exactly what uses it, e.g. "used by 120 call logs, 3 leads"
      setToast({ message: e.message || `Cannot delete "${name}" — other records use it. Deactivate it instead.`, type: "error" });
    }
  };

  const openEdit = async (item) => {
    setEditItem(item); setForm(item); setShowForm(true);
    if (linkConfig && item.id) {
      try {
        const [pRes, cRes] = await Promise.all([
          api(`/${endpoint}/${item.id}/principals`),
          api(`/${endpoint}/${item.id}/clients`)
        ]);
        setLinkedPrincipals(new Set(pRes.data.map(p => p.id)));
        setLinkedClients(new Set(cRes.data.map(c => c.id)));
      } catch (e) { setLinkedPrincipals(new Set()); setLinkedClients(new Set()); }
    }
  };
  const openNew = () => { setEditItem(null); setForm({}); setLinkedPrincipals(new Set()); setLinkedClients(new Set()); setShowForm(true); };

  // If asked to open a specific record (e.g. a customer from a license row),
  // fetch it and open its edit form, then return to the origin screen on close.
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => {
    if (!pendingOpenId) return;
    (async () => {
      let rec = (data || []).find(d => d.id === pendingOpenId);
      if (!rec) { try { const r = await api(`/${endpoint}/${pendingOpenId}`); rec = r.data; } catch (e) {} }
      if (rec) { openEdit(rec); setReturnTo(pendingReturn || null); }
      if (setPendingOpen) setPendingOpen(null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenId]);
  // Before anything is drawn: a form opened from another screen (e.g. a Dashboard list)
  // goes straight back there when it closes — no frame of this screen's own list in between
  useLayoutEffect(() => {
    if (!showForm && returnTo) { const t = returnTo; setReturnTo(null); if (setActiveScreen) setActiveScreen(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  const saveLinking = async (id) => {
    if (!linkConfig) return;
    try {
      await api(`/${endpoint}/${id}/principals`, { method: "PUT", body: { principal_ids: Array.from(linkedPrincipals) } });
      await api(`/${endpoint}/${id}/clients`, { method: "PUT", body: { client_ids: Array.from(linkedClients) } });
    } catch (e) { console.error("Linking error:", e); }
  };

  // Export all data to CSV — no limit
  const exportToExcel = async () => {
    try {
      setToast({ message: "Exporting all records... please wait.", type: "success" });
      const r = await api(`/${endpoint}?limit=100000&active_only=true`);
      const rows = r.data;
      if (rows.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
      const allCols = [{ key: "id", label: "#" }, ...formFields.map(f => ({ key: f.key, label: f.label }))];
      const headers = csvRow(allCols.map(c => c.label));
      const dataRows = rows.map(row => csvRow(allCols.map(c => {
        const val = row[c.key];
        if (c.key.includes("date") || c.key.includes("expiry") || c.key === "date_of_joining") return val ? formatDateDMY(val) : "";
        return val === null || val === undefined ? "" : String(val);
      }))).join("\n");
      downloadCSV(headers + "\n" + dataRows + "\n", `${endpoint}_export_${new Date().toISOString().slice(0,10)}.csv`);
      setToast({ message: `Exported ${rows.length} records!`, type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // Blank Export with import guide
  const blankExport = () => {
    const cleanLabel = (l) => l.replace(/[₹]/g, "INR").replace(/[^\x20-\x7E]/g, "");
    const allCols = formFields.map(f => cleanLabel(f.label));
    const headers = csvRow(allCols);
    const sampleRow = csvRow(formFields.map(f => {
      if (f.required) return `Sample ${f.label.replace(/[₹]/g, "")}`;
      if (f.type === "date") return "25/04/2026";
      if (f.type === "email") return "sample@company.com";
      if (f.type === "number") return "1000";
      if (f.type === "select" || f.type === "searchable_select") {
        if (f.key.includes("revenue")) return "1-5 Crore";
        if (f.key.includes("payment")) return "Net 30";
        if (f.key.includes("state")) return "Karnataka";
        return f.options?.[0]?.label || "";
      }
      if (f.key.includes("phone")) return "9876543210";
      if (f.key.includes("pincode")) return "560001";
      if (f.key.includes("gstin")) return "29AABCT1234F1Z5";
      if (f.key.includes("pan")) return "AABCT1234F";
      if (f.key.includes("city")) return "Bangalore";
      if (f.key.includes("website")) return "www.example.com";
      if (f.key.includes("address")) return "123 Main Road";
      if (f.key.includes("notes")) return "Sample notes";
      return "";
    }));
    const guide = [
      "# IMPORT GUIDE - Delete these # lines and the sample row before importing",
      "# Fill data below the header row. Save as CSV UTF-8.",
      "# Required fields are marked with * in the form.",
      "# Dates must be in DD/MM/YYYY format (e.g. 25/04/2026)",
      "# Dropdown fields must match master data names exactly.",
      "# ---- DELETE THIS SAMPLE ROW BELOW BEFORE IMPORTING ----",
    ].join("\n");
    downloadCSV(guide + "\n" + headers + "\n" + sampleRow + "\n", `${endpoint}_blank_template.csv`);
    setToast({ message: "Blank template with sample data downloaded!", type: "success" });
  };

  // Export selected records
  const exportSelected = () => {
    if (selectedIds.size === 0) { setToast({ message: "No records selected.", type: "warning" }); return; }
    const rows = data.filter(d => selectedIds.has(d.id));
    const allCols = [{ key: "code", label: "ID" }, ...formFields.map(f => ({ key: f.key, label: f.label }))];
    let csv = allCols.map(c => c.label).join(",") + "\n";
    rows.forEach(row => {
      csv += allCols.map(c => {
        const val = row[c.key];
        if (val === null || val === undefined) return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(",") + "\n";
    });
    downloadCSV(csv, `${endpoint}_selected_${new Date().toISOString().slice(0,10)}.csv`);
    setToast({ message: `Exported ${rows.length} selected records!`, type: "success" });
  };

  // Delete selected records
  const deleteSelected = async () => {
    if (selectedIds.size === 0) { setToast({ message: "No records selected.", type: "warning" }); return; }
    if (!window.confirm(`⚠️ Permanently delete ${selectedIds.size} selected record(s)?\n\nRecords with linked call logs will be skipped.`)) return;
    let success = 0, failed = 0;
    for (const id of selectedIds) {
      try { await api(`/${endpoint}/${id}`, { method: "DELETE" }); success++; }
      catch (e) { failed++; }
    }
    setToast({ message: `Deleted ${success}, Skipped ${failed} (linked records).`, type: success > 0 ? "success" : "error" });
    setSelectedIds(new Set());
    fetchData();
  };

  // Toggle selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === data.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(data.map(d => d.id)));
  };

  // Handle file upload for import (CSV or tab-separated)
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Check file extension
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'xlsx' || ext === 'xls') {
      setToast({ message: "Please save your Excel file as 'CSV UTF-8 (Comma delimited)' first, then import the CSV file.", type: "warning" });
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = async (evt) => {
      let text = evt.target.result;
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
      text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      // Detect separator: tab or comma
      // Filter out guide lines (# comments) and sep= directive
      const allLines = text.split("\n").filter(l => l.trim() && !l.trim().startsWith("#") && !l.trim().toLowerCase().startsWith("sep="));
      const firstLine = allLines[0] || "";
      const separator = firstLine.includes("\t") ? "\t" : ",";
      const lines = allLines;
      if (lines.length < 2) { setToast({ message: "File is empty or has only headers.", type: "error" }); return; }
      const headers = separator === "\t" ? lines[0].split("\t").map(h => h.replace(/"/g, "").trim()) : parseCSVLine(lines[0]);
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = separator === "\t" ? lines[i].split("\t").map(v => v.replace(/"/g, "").trim()) : parseCSVLine(lines[i]);
        if (vals.every(v => !v.trim())) continue;
        const row = {};
        formFields.forEach((f, idx) => {
          const headerIdx = headers.findIndex(h => h.toLowerCase().trim() === f.label.toLowerCase().trim());
          const useIdx = headerIdx >= 0 ? headerIdx : idx;
          row[f.key] = vals[useIdx]?.trim() || "";
        });
        rows.push(row);
      }
      if (rows.length === 0) { setToast({ message: "No data rows found in the file.", type: "error" }); return; }
      const categorized = await categorizeImportData(rows);
      setImportData(categorized);
      setShowImport(true);
    };
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  // Parse CSV line handling quoted fields
  const parseCSVLine = (line) => {
    const result = []; let current = ""; let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQuotes && line[i+1] === '"') { current += '"'; i++; } else { inQuotes = !inQuotes; } }
      else if (ch === ',' && !inQuotes) { result.push(current); current = ""; }
      else { current += ch; }
    }
    result.push(current);
    return result;
  };

  // Smart Import — categorize as NEW, UPDATE, or UNCHANGED
  const handleImport = async () => {
    setImporting(true);
    let created = 0, updated = 0, failed = 0;
    
    const newRecords = importData.filter(r => r._action === "new");
    const updateRecords = importData.filter(r => r._action === "update");
    
    // Import new records in parallel batches of 10
    if (newRecords.length > 0) {
      const concurrency = 10;
      for (let i = 0; i < newRecords.length; i += concurrency) {
        const chunk = newRecords.slice(i, i + concurrency);
        if (i % 100 === 0) setToast({ message: `Importing ${i}/${newRecords.length} (${created} created)...`, type: "success" });
        
        const results = await Promise.allSettled(chunk.map(row => {
          const body = {};
          formFields.forEach(f => { if (row[f.key] !== undefined && row[f.key] !== "") body[f.key] = row[f.key]; });
          return api(`/${endpoint}`, { method: "POST", body });
        }));
        
        results.forEach(r => { if (r.status === "fulfilled") created++; else failed++; });
      }
    }
    
    // Update records
    for (const row of updateRecords) {
      try {
        const body = {};
        formFields.forEach(f => { if (row[f.key] !== undefined && row[f.key] !== "") body[f.key] = row[f.key]; });
        await api(`/${endpoint}/${row._matchId}`, { method: "PUT", body: { ...row._existing, ...body } });
        updated++;
      } catch (e) { failed++; }
    }
    
    setImporting(false);
    setShowImport(false); setImportData([]);
    setToast({ message: `Import complete! ${created} created, ${updated} updated, ${failed} failed.`, type: (created + updated) > 0 ? "success" : "error" });
    fetchData();
  };

  // Categorize import data against existing records
  const categorizeImportData = async (rows) => {
    try {
      const allData = await api(`/${endpoint}?limit=100000`);
      const existing = allData.data || [];
      // Find the name/key field for matching
      const nameKey = formFields.find(f => f.required)?.key || formFields[0]?.key;

      const categorized = rows.map(row => {
        const rowName = (row[nameKey] || "").toLowerCase().trim();
        const match = existing.find(e => {
          const eName = (e[nameKey] || "").toLowerCase().trim();
          return eName === rowName && rowName !== "";
        });
        if (match) {
          // Check if any fields are different
          const hasChanges = formFields.some(f => {
            const newVal = (row[f.key] || "").toString().trim();
            const oldVal = (match[f.key] || "").toString().trim();
            return newVal && newVal !== oldVal;
          });
          return { ...row, _action: hasChanges ? "update" : "skip", _matchId: match.id, _existing: match, _matchName: match[nameKey] };
        }
        return { ...row, _action: "new" };
      });
      return categorized;
    } catch (e) { return rows.map(r => ({ ...r, _action: "new" })); }
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>{title}</h1>
        <div style={styles.flexRow}>
          <button style={styles.btn("primary")} onClick={openNew}>+ Add New <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+N)</span></button>
          <button style={styles.btn("success", "sm")} onClick={exportToExcel}>📊 Export <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+X)</span></button>
          <button style={styles.btn("ghost", "sm")} onClick={blankExport}>📄 Blank <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+B)</span></button>
          <label style={{ ...styles.btn("ghost", "sm"), cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
            📥 Import <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+M)</span> <input type="file" accept=".csv,.txt,.tsv" onChange={handleFileUpload} style={{ display: "none" }} />
          </label>
          {extraButtons}
        </div>
      </div>

      {/* Selection action bar */}
      {selectedIds.size > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", marginBottom: 8, background: "#e8eefb", borderRadius: 8, border: `1px solid ${colors.primary}` }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.primary }}>{selectedIds.size} record(s) selected</span>
          <button style={styles.btn("success", "sm")} onClick={exportSelected}>📊 Export Selected</button>
          <button style={styles.btn("ghost", "sm")} onClick={() => setSelectedIds(new Set())}>✕ Clear Selection</button>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-end" }}>
          <RefreshStamp updatedAt={auto.updatedAt} onRefresh={auto.refreshNow} />
          <SearchInput style={{ ...styles.input, maxWidth: 300 }} placeholder={`Search ${title.toLowerCase()}...`} value={search} onSearch={setSearch} />
          <div>
            <label style={styles.label}>Sort By</label>
            <select style={{ ...styles.select, width: 150 }} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="">Default</option>
              {columns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <button style={styles.btn("ghost", "sm")} onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}>
            {sortDir === "asc" ? "↑ A-Z" : "↓ Z-A"}
          </button>
        </div>
        {loading ? <p style={{ color: colors.textLight }}>Loading...</p> : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
          <table style={{ ...styles.table, minWidth: 800 }}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 36, textAlign: "center" }}>
                  <input type="checkbox" checked={data.length > 0 && selectedIds.size === data.length} onChange={toggleSelectAll} style={{ cursor: "pointer" }} />
                </th>
                {columns.map((c) => <th key={c.key} style={styles.th}>{c.label}</th>)}
                <th style={styles.th}>Status</th><th style={{ ...styles.th, minWidth: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.id} style={{ opacity: item.is_active === false ? 0.5 : 1, background: selectedIds.has(item.id) ? "#f0f4ff" : "transparent" }}>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)} style={{ cursor: "pointer" }} />
                  </td>
                  {columns.map((c) => <td key={c.key} style={styles.td}>{c.render ? c.render(item[c.key], item) : item[c.key]}</td>)}
                  <td style={styles.td}><StatusBadge status={item.is_active !== false ? "Active" : "Inactive"} /></td>
                  <td style={styles.td}>
                    <div style={{ ...styles.flexRow, flexWrap: "nowrap" }}>
                      <button style={styles.btn("primary", "sm")} onClick={() => openEdit(item)}>Edit</button>
                      <button style={styles.btn(item.is_active !== false ? "warning" : "success", "sm")} onClick={() => handleToggle(item.id)}>
                        {item.is_active !== false ? "Deactivate" : "Activate"}
                      </button>
                      <button style={styles.btn("danger", "sm")} onClick={() => handleDelete(item.id, item.code || item.name || item.company_name)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={columns.length + 3} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No records found.</td></tr>}
            </tbody>
          </table>
          </div>
        )}
        <Pagination pagination={pagination} onPageChange={fetchData} />
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div style={styles.modal} onClick={attemptClose}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>{editItem ? "Edit" : "Add"} {title.replace(/s$/, "")}</h2>

            <div style={styles.grid(2)} ref={el => { if (el && !el.dataset.focused) { el.dataset.focused = "1"; const inp = el.querySelector("input[type='text'],input:not([type])"); if (inp) setTimeout(() => inp.focus(), 100); } }}>
              {formFields.filter(f => !(f.newOnly && editItem)).map((f) => (
                <div key={f.key} style={f.fullWidth ? { gridColumn: "1 / -1" } : {}}>
                  <label style={styles.label}>{f.label}{f.required && " *"}</label>
                  {f.type === "searchable_select" ? (
                    <SearchableSelect options={f.options || []} value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} placeholder={`Search ${f.label.toLowerCase()}...`}
                      onCreate={f.createLabel ? () => setQuickCreateModal({ title: f.createLabel.replace("Create ", ""), endpoint: f.createEndpoint || f.key.replace("_id", "s"), targetField: f.key, fields: f.createFields || [{ key: "name", label: `${f.label} Name`, required: true }] }) : undefined}
                      createLabel={f.createLabel} />
                  ) : f.type === "select" ? (
                    <select style={styles.select} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                      <option value="">Select...</option>
                      {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <FastText multiline style={{ ...styles.input, minHeight: 80 }} value={form[f.key] || ""} onCommit={(t) => setForm(fm => ({ ...fm, [f.key]: t }))} />
                  ) : f.type === "date" ? (
                    <DateInput value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} />
                  ) : (
                    <FastText style={styles.input} type={f.type || "text"} value={form[f.key] || ""} onCommit={(t) => setForm(fm => ({ ...fm, [f.key]: t }))} placeholder={f.placeholder} />
                  )}
                  {isCustomerMaster && <DupeMsg dupes={dupes} field={f.key === "contact_phone" ? "phone" : f.key === "contact_email" ? "email" : f.key} onOpen={openExisting} />}
                  {isCustomerMaster && f.key === "company_name" && <SimilarList similar={similar} onOpen={openExisting} />}
                </div>
              ))}
            </div>

            {/* Executive-Company Linking */}
            {linkConfig && editItem && (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ padding: "12px 16px", background: "#f8fafc", borderRadius: 8, border: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Associated Principal Companies</div>
                    <div style={{ maxHeight: 180, overflowY: "auto" }}>
                      {linkConfig.principals?.map(p => (
                        <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12, cursor: "pointer" }}>
                          <input type="checkbox" checked={linkedPrincipals.has(p.id)} onChange={() => {
                            setLinkedPrincipals(prev => { const n = new Set(prev); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; });
                          }} />
                          {p.company_name}
                        </label>
                      ))}
                      {(!linkConfig.principals || linkConfig.principals.length === 0) && <span style={{ fontSize: 11, color: colors.textLight }}>No principals available</span>}
                    </div>
                  </div>
                  <div style={{ padding: "12px 16px", background: "#f8fafc", borderRadius: 8, border: `1px solid ${colors.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Associated Customers</div>
                    <div style={{ maxHeight: 180, overflowY: "auto" }}>
                      {linkConfig.clients?.map(c => (
                        <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12, cursor: "pointer" }}>
                          <input type="checkbox" checked={linkedClients.has(c.id)} onChange={() => {
                            setLinkedClients(prev => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; });
                          }} />
                          {c.company_name}
                        </label>
                      ))}
                      {(!linkConfig.clients || linkConfig.clients.length === 0) && <span style={{ fontSize: 11, color: colors.textLight }}>No clients available</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Custom extra section (e.g., customer products) */}
            {renderExtra && editItem && <div id="master-extra-section">{renderExtra(editItem)}</div>}

            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 24 }}>
              <div>
                {editItem && (
                  <button style={styles.btn("danger", "sm")} onClick={async () => {
                    const name = editItem.name || editItem.company_name || editItem.code || "this record";
                    handleDelete(editItem.id, name);
                    setShowForm(false); setEditItem(null);
                  }}>Delete Permanently</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button style={styles.btn("ghost")} onClick={attemptClose}>Cancel <span style={{ fontSize: 9, color: colors.textLight, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
                <button style={{ ...styles.btn("primary"), ...(isCustomerMaster && dupes.length ? { opacity: 0.5, cursor: "not-allowed" } : {}) }} disabled={isCustomerMaster && dupes.length > 0} onClick={handleSave}>{editItem ? "Update" : "Create"} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Smart Import Preview Modal */}
      {showImport && (
        <div style={styles.modal} onClick={() => setShowImport(false)}>
          <div style={{ ...styles.modalContent, maxWidth: 950 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>Import & Update Preview</h2>
            {(() => {
              const newRows = importData.filter(r => r._action === "new");
              const updateRows = importData.filter(r => r._action === "update");
              const skipRows = importData.filter(r => r._action === "skip");
              return (
                <div>
                  <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                    <div style={{ padding: "8px 16px", background: "#ecfdf5", borderRadius: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: colors.success }}>🆕 {newRows.length}</span> New records
                    </div>
                    <div style={{ padding: "8px 16px", background: "#fffbeb", borderRadius: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: colors.warning }}>📝 {updateRows.length}</span> Updates
                    </div>
                    <div style={{ padding: "8px 16px", background: "#f1f5f9", borderRadius: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: colors.textLight }}>⏭ {skipRows.length}</span> Unchanged (skip)
                    </div>
                  </div>
                  <div style={{ maxHeight: 400, overflowY: "auto", overflowX: "auto" }}>
                    <table style={styles.table}>
                      <thead><tr>
                        <th style={styles.th}>Action</th><th style={styles.th}>#</th>
                        {formFields.map(f => <th key={f.key} style={styles.th}>{f.label}</th>)}
                      </tr></thead>
                      <tbody>
                        {importData.map((row, i) => (
                          <tr key={i} style={{ background: row._action === "new" ? "#f0fdf4" : row._action === "update" ? "#fffbeb" : "#f8fafc", opacity: row._action === "skip" ? 0.5 : 1 }}>
                            <td style={styles.td}>
                              <select style={{ ...styles.select, width: 90, fontSize: 11, padding: "2px 4px" }} value={row._action} onChange={(e) => {
                                const updated = [...importData]; updated[i] = { ...updated[i], _action: e.target.value }; setImportData(updated);
                              }}>
                                <option value="new">🆕 New</option>
                                <option value="update">📝 Update</option>
                                <option value="skip">⏭ Skip</option>
                              </select>
                            </td>
                            <td style={styles.td}>{i + 1}</td>
                            {formFields.map(f => {
                              const isChanged = row._action === "update" && row._existing && row[f.key] && row[f.key] !== (row._existing[f.key] || "").toString();
                              return <td key={f.key} style={{ ...styles.td, fontWeight: isChanged ? 700 : 400, color: isChanged ? colors.primary : "inherit" }}>{row[f.key] || "—"}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 24 }}>
                    <div style={{ fontSize: 11, color: colors.textLight, alignSelf: "center" }}>
                      Changed fields shown in <span style={{ color: colors.primary, fontWeight: 700 }}>blue bold</span>. Change action per row using dropdown.
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button style={styles.btn("ghost")} onClick={() => setShowImport(false)}>Cancel <span style={{ fontSize: 9, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
                      {newRows.length > 0 && <button style={styles.btn("success")} onClick={() => { const updated = importData.map(r => r._action === "update" ? { ...r, _action: "skip" } : r); setImportData(updated); handleImport(); }} disabled={importing}>
                        Create {newRows.length} New Only
                      </button>}
                      {updateRows.length > 0 && <button style={styles.btn("warning")} onClick={() => { const updated = importData.map(r => r._action === "new" ? { ...r, _action: "skip" } : r); setImportData(updated); handleImport(); }} disabled={importing}>
                        Update {updateRows.length} Only
                      </button>}
                      <button style={styles.btn("primary")} onClick={handleImport} disabled={importing}>
                        {importing ? "Importing..." : `Import All (${newRows.length + updateRows.length})`} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Quick Create Modal for searchable_select fields */}
      {quickCreateModal && (
        <FullCreate title={quickCreateModal.title} endpoint={quickCreateModal.endpoint} fields={quickCreateModal.fields}
          onCreated={(newItem) => { setForm(f => ({ ...f, [quickCreateModal.targetField]: newItem.id?.toString() })); fetchData(); window.dispatchEvent(new Event("masters-refresh")); }}
          onClose={() => setQuickCreateModal(null)} />
      )}
    </div>
  );
};

// ============================================================================
// CALL TRACKER SCREEN
// ============================================================================
const AMCScreen = ({ masters, loadMasters, companyLogo, companyName }) => {
  const [qc, setQc] = useState(null);   // quick-create from a dropdown
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ date_from: "", date_to: "", status: "", search: "" });
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ telephonic_support: "Unlimited", status: "Active" });
  const [toast, setToast] = useState(null);
  const [visitModal, setVisitModal] = useState(null); // { contract, visits, vform }
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [startNumber, setStartNumber] = useState("");
  const [balanceOnly, setBalanceOnly] = useState(false); // drill-down: only contracts with visits remaining
  const [renewedModal, setRenewedModal] = useState(null); // { from, to, data, loading }

  const monthStartStr = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-01"; };
  const todayStr2 = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); };
  const loadRenewed = async (from, to) => {
    setRenewedModal(m => ({ ...(m || {}), from, to, loading: true, data: (m && m.data) || [] }));
    try {
      const p = new URLSearchParams({ renewed_only: "true", limit: 2000 });
      if (from) p.append("renewed_from", from);
      if (to) p.append("renewed_to", to);
      const r = await api(`/amc?${p}`);
      setRenewedModal(m => ({ ...(m || {}), from, to, loading: false, data: r.data || [] }));
    } catch (e) { setRenewedModal(m => ({ ...(m || {}), loading: false })); setToast({ message: e.message, type: "error" }); }
  };
  const openRenewedReport = () => { const f = monthStartStr(), t = todayStr2(); loadRenewed(f, t); };
  const renewedExcel = () => {
    const rows = (renewedModal?.data) || [];
    if (rows.length === 0) { setToast({ message: "No renewed contracts to export.", type: "warning" }); return; }
    const content = csvRow([...amcHeaders, "Renewed Date"]) + "\n" + rows.map(r => csvRow([...amcFields(r), r.renewed_date ? formatDateDMY(r.renewed_date) : ""])).join("\n") + "\n";
    downloadCSV(content, `amc_renewed_${renewedModal.from}_to_${renewedModal.to}.csv`);
    setToast({ message: `Exported ${rows.length} renewed contracts!`, type: "success" });
  };
  const renewedPDF = () => {
    const rows = (renewedModal?.data) || [];
    if (rows.length === 0) { setToast({ message: "No renewed contracts to export.", type: "warning" }); return; }
    const logoHtml = companyLogo ? `<img src="${companyLogo}" style="height:50px;margin-right:14px" />` : "";
    const cols = ["Contract", "Customer", "Contact", "Mobile", "Product", "License", "Renewed On", "End Date", "Value", "Managed By"];
    const head = cols.map(h => `<th style="padding:6px;border:1px solid #ddd;background:#7c3aed;color:#fff;text-align:left">${h}</th>`).join("");
    let body = "";
    rows.forEach(r => { body += `<tr><td style="padding:5px;border:1px solid #ddd;font-weight:600">${r.contract_number}</td><td style="padding:5px;border:1px solid #ddd">${r.client_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.contact_person||""}</td><td style="padding:5px;border:1px solid #ddd">${r.customer_phone||r.contact_phone||""}</td><td style="padding:5px;border:1px solid #ddd">${r.product_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.license_number||""}</td><td style="padding:5px;border:1px solid #ddd">${r.renewed_date?formatDateDMY(r.renewed_date):""}</td><td style="padding:5px;border:1px solid #ddd">${formatDateDMY(r.end_date)}</td><td style="padding:5px;border:1px solid #ddd;text-align:right">${r.amc_value?formatCurrencyINR(r.amc_value):""}</td><td style="padding:5px;border:1px solid #ddd">${r.managed_by_name||""}</td></tr>`; });
    const html = `<html><head><title>Renewed AMC Report</title><style>@page{size:landscape;margin:10mm}</style></head><body style="font-family:Arial,sans-serif;padding:18px">
      <div style="display:flex;align-items:center;margin-bottom:8px">${logoHtml}<h1 style="color:#7c3aed;margin:0;font-size:20px">${companyName || "CLAS CRM Suite"}</h1></div>
      <h2 style="color:#333;margin:8px 0 4px">Renewed AMC Contracts</h2>
      <p style="color:#666;font-size:11px">Renewed ${formatDateDMY(renewedModal.from)} to ${formatDateDMY(renewedModal.to)} | ${rows.length} contract(s)</p>
      <hr style="border:1px solid #ddd;margin:10px 0">
      <table style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); w.print();
    setToast({ message: "Renewed PDF ready!", type: "success" });
  };
  const [formError, setFormError] = useState("");        // error shown inside the form modal (always visible)

  const clientOpts = (masters.clients || []).map(c => ({ value: String(c.id), label: c.company_name }));
  const productOpts = (masters.products || []).map(p => ({ value: String(p.id), label: p.name }));
  const execOpts = (masters.executives || []).map(e => ({ value: String(e.id), label: e.name }));

  const fetchData = useCallback(async () => {
    try {
      const p = new URLSearchParams({ limit: 1000 });
      Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
      const r = await api(`/amc?${p}`);
      setData(r.data || []);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (filters.date_from) p.append("date_from", filters.date_from);
      if (filters.date_to) p.append("date_to", filters.date_to);
      const r = await api(`/amc/dashboard/stats?${p}`);
      setStats(r.data || {});
    } catch (e) {}
  }, [filters.date_from, filters.date_to]);

  const auto = useAutoRefresh(() => fetchData(), showForm || !!visitModal || !!renewedModal);
  useEffect(() => { fetchData(); fetchStats(); }, [fetchData, fetchStats]);

  const openNew = () => { setEditItem(null); setFormError(""); setForm({ telephonic_support: "Unlimited", status: "Active" }); setShowForm(true); };
  const openEdit = (row) => {
    setEditItem(row); setFormError("");
    setForm({ ...row, client_id: row.client_id ? String(row.client_id) : "", product_id: row.product_id ? String(row.product_id) : "",
      managed_by_id: row.managed_by_id ? String(row.managed_by_id) : "",
      start_date: toISODate(row.start_date), end_date: toISODate(row.end_date) });
    setShowForm(true);
  };

  const handleSave = async () => {
    setFormError("");
    if (!form.client_id || !form.start_date || !form.end_date) { setFormError("Customer, Start date and End date are required."); return; }
    if (toISODate(form.end_date) < toISODate(form.start_date)) { setFormError("End date cannot be earlier than the start date. Please correct the dates."); return; }
    if (form.total_onsite_visits === undefined || form.total_onsite_visits === "" || form.total_onsite_visits === null) { setFormError("Enter the number of onsite visits."); return; }
    try {
      if (editItem) await api(`/amc/${editItem.id}`, { method: "PUT", body: form });
      else await api("/amc", { method: "POST", body: form });
      setToast({ message: editItem ? "Contract updated!" : "AMC contract created!", type: "success" });
      setShowForm(false); setEditItem(null); fetchData(); fetchStats();
    } catch (e) { setFormError(e.message); }
  };

  // ---- Exports ----
  const displayData = balanceOnly ? data.filter(r => Number(r.visits_balance) > 0) : data;
  const amcHeaders = ["Contract #", "Customer", "Contact Person", "Mobile", "Contact Phone", "Email", "GSTIN", "City", "Product", "License #", "Release/Version", "Subscription Expiry", "User Type", "Qty", "Start Date", "End Date", "Total Visits", "Visits Used", "Balance", "Telephonic", "AMC Value", "Managed By", "Status", "Remarks"];
  const amcFields = (r) => [
    r.contract_number, r.client_name || "", r.contact_person || "", r.customer_phone || "", r.contact_phone || "", r.customer_email || "", r.gstin || "", r.city || "",
    r.product_name || "", r.license_number || "", r.latest_release || "", r.cp_subscription_expiry ? formatDateDMY(r.cp_subscription_expiry) : "", r.user_type || "", r.quantity || "",
    formatDateDMY(r.start_date), formatDateDMY(r.end_date),
    r.total_onsite_visits, r.visits_used, r.visits_balance,
    r.telephonic_support || "", r.amc_value || "", r.managed_by_name || "",
    r.computed_status || r.status || "", r.remarks || ""
  ];
  const amcRowCsv = (r) => csvRow(amcFields(r));
  const exportExcel = () => {
    if (displayData.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const content = csvRow(amcHeaders) + "\n" + displayData.map(amcRowCsv).join("\n") + "\n";
    downloadCSV(content, `amc_contracts_${new Date().toISOString().slice(0,10)}.csv`);
    setToast({ message: `Exported ${displayData.length} contracts!`, type: "success" });
  };
  const exportPDF = () => {
    if (displayData.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const today = new Date(); const todayDMY = `${String(today.getDate()).padStart(2,"0")}/${String(today.getMonth()+1).padStart(2,"0")}/${today.getFullYear()}`;
    const logoHtml = companyLogo ? `<img src="${companyLogo}" style="height:54px;margin-right:14px" />` : "";
    const filterDesc = (filters.date_from || filters.date_to) ? `Expiry ${filters.date_from || "…"} to ${filters.date_to || "…"}` : "All";
    const cols = `<th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">Contract</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">Customer</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">Contact</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Mobile</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">Product</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">License</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Release</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Start</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">End</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Used/Total</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Bal</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Value</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Status</th>`;
    let rows = "";
    displayData.forEach(r => { rows += `<tr><td style="padding:5px;border:1px solid #ddd;font-weight:600">${r.contract_number}</td><td style="padding:5px;border:1px solid #ddd">${r.client_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.contact_person||""}</td><td style="padding:5px;border:1px solid #ddd">${r.customer_phone||r.contact_phone||""}</td><td style="padding:5px;border:1px solid #ddd">${r.product_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.license_number||""}</td><td style="padding:5px;border:1px solid #ddd">${r.latest_release||""}</td><td style="padding:5px;border:1px solid #ddd">${formatDateDMY(r.start_date)}</td><td style="padding:5px;border:1px solid #ddd">${formatDateDMY(r.end_date)}</td><td style="padding:5px;border:1px solid #ddd;text-align:center">${r.visits_used}/${r.total_onsite_visits}</td><td style="padding:5px;border:1px solid #ddd;text-align:center">${r.visits_balance}</td><td style="padding:5px;border:1px solid #ddd;text-align:right">${r.amc_value?formatCurrencyINR(r.amc_value):""}</td><td style="padding:5px;border:1px solid #ddd">${r.computed_status||r.status||""}</td></tr>`; });
    const html = `<html><head><title>AMC Contracts Report</title><style>@page{size:landscape;margin:10mm}</style></head><body style="font-family:Arial,sans-serif;padding:18px">
      <div style="display:flex;align-items:center;margin-bottom:8px">${logoHtml}<h1 style="color:#1a56db;margin:0;font-size:20px">${companyName || "CLAS CRM Suite"}</h1></div>
      <h2 style="color:#333;margin:8px 0 4px">AMC Contracts Report</h2>
      <p style="color:#666;font-size:11px">Generated: ${todayDMY} | Filter: ${filterDesc} | ${displayData.length} contract(s)</p>
      <hr style="border:1px solid #ddd;margin:12px 0">
      <table style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr>${cols}</tr></thead><tbody>${rows}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); w.print();
    setToast({ message: "AMC PDF ready!", type: "success" });
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 's' && showForm) { e.preventDefault(); handleSave(); }
      else if (k === 'n' && !showForm && !visitModal) { e.preventDefault(); openNew(); }
      else if (k === 'x' && !showForm) { e.preventDefault(); exportExcel(); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  const toggleDelete = async (row) => {
    if (!window.confirm(`Remove contract ${row.contract_number}?`)) return;
    try { await api(`/amc/${row.id}/toggle`, { method: "PATCH" }); setToast({ message: "Removed.", type: "success" }); fetchData(); fetchStats(); }
    catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // ---- Visits ----
  const openVisits = async (contract) => {
    setVisitModal({ contract, visits: [], vform: { visit_date: "", executive_id: "", notes: "" }, loading: true });
    try { const r = await api(`/amc/${contract.id}/visits`); setVisitModal(m => m && ({ ...m, visits: r.data || [], loading: false })); }
    catch (e) { setVisitModal(m => m && ({ ...m, loading: false })); }
  };
  const addVisit = async () => {
    const { contract, vform } = visitModal;
    if (!vform.visit_date) { setToast({ message: "Pick a visit date.", type: "error" }); return; }
    try {
      await api(`/amc/${contract.id}/visits`, { method: "POST", body: vform });
      const r = await api(`/amc/${contract.id}/visits`);
      setVisitModal(m => m && ({ ...m, visits: r.data || [], vform: { visit_date: "", executive_id: "", notes: "" } }));
      setToast({ message: "Visit logged!", type: "success" }); fetchData(); fetchStats();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };
  const deleteVisit = async (visitId) => {
    try { await api(`/amc/visits/${visitId}`, { method: "DELETE" });
      const r = await api(`/amc/${visitModal.contract.id}/visits`);
      setVisitModal(m => m && ({ ...m, visits: r.data || [] })); fetchData(); fetchStats();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // ---- Settings ----
  const openSettings = async () => {
    try { const r = await api("/amc/settings"); setStartNumber(String(r.data?.start_number ?? 1001)); setSettingsOpen(true); } catch (e) {}
  };
  const saveSettings = async () => {
    try { await api("/amc/settings", { method: "PUT", body: { start_number: parseInt(startNumber) || 1001 } }); setSettingsOpen(false); setToast({ message: "Starting number saved.", type: "success" }); }
    catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const statusColor = (s) => s === "Expired" ? colors.danger : s === "Expiring" ? "#d97706" : s === "Renewed" ? "#7c3aed" : s === "Cancelled" ? colors.textLight : colors.success;
  const card = (label, value, color, sub, onClick) => (
    <div onClick={onClick} title={onClick ? "Click to view these contracts" : undefined}
      style={{ background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 10, padding: "14px 16px", minWidth: 150, flex: 1, cursor: onClick ? "pointer" : "default", transition: "box-shadow .15s" }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.10)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}>
      <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || colors.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: colors.textLight, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: colors.text }}>🛡️ AMC Contracts</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={styles.btn("primary", "sm")} onClick={openNew}>+ New AMC <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+N)</span></button>
          <button style={styles.btn("success", "sm")} onClick={exportExcel}>📊 Export <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+X)</span></button>
          <button style={{ ...styles.btn("ghost", "sm"), background: "#dc2626", color: "#fff" }} onClick={exportPDF}>📄 PDF</button>
          <button style={styles.btn("ghost", "sm")} onClick={openSettings}>⚙️ Series</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        {card("Active AMCs", stats.active_count ?? "—", colors.success, null, () => { setFilters(f => ({ ...f, status: "Active" })); setBalanceOnly(false); })}
        {card("Renewals Due", stats.renewals_due ?? "—", colors.primary, stats.renewals_value ? formatCurrencyINR(stats.renewals_value) : "in selected range", () => { setFilters(f => ({ ...f, status: "" })); setBalanceOnly(false); })}
        {card("Expiring ≤30 days", stats.expiring_count ?? "—", "#d97706", null, () => { setFilters(f => ({ ...f, status: "Expiring" })); setBalanceOnly(false); })}
        {card("Expired", stats.expired_count ?? "—", colors.danger, null, () => { setFilters(f => ({ ...f, status: "Expired" })); setBalanceOnly(false); })}
        {card("Renewed", stats.renewed_count ?? "—", "#7c3aed", "click for report", openRenewedReport)}
        {card("Onsite Visit Balance", stats.total_visit_balance ?? "—", colors.text, "with visits remaining", () => { setFilters(f => ({ ...f, status: "Active" })); setBalanceOnly(true); })}
      </div>

      <div style={styles.filterBar}>
        <div><label style={styles.label}>Expiry From</label><DateInput value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} /></div>
        <div><label style={styles.label}>Expiry To</label><DateInput value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} /></div>
        <div><label style={styles.label}>Status</label>
          <select style={styles.input} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All</option><option value="Active">Active</option><option value="Expiring">Expiring (≤30d)</option><option value="Expired">Expired</option><option value="Renewed">Renewed</option><option value="Cancelled">Cancelled</option>
          </select>
        </div>
        <div><label style={styles.label}>Search</label><SearchInput style={{ ...styles.input, minWidth: 200 }} placeholder="Contract #, customer, mobile, GSTIN, license..." value={filters.search} onSearch={(t) => setFilters(f => ({ ...f, search: t }))} /></div>
        {(filters.date_from || filters.date_to || filters.status || filters.search || balanceOnly) && (
          <button style={styles.btn("ghost", "sm")} onClick={() => { setFilters({ date_from: "", date_to: "", status: "", search: "" }); setBalanceOnly(false); }}>Clear</button>
        )}
      </div>

      <RefreshStamp updatedAt={auto.updatedAt} onRefresh={auto.refreshNow} />
      <div style={{ background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 10, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
            {["Contract #", "Customer", "Product", "Start", "End", "Visits", "Telephonic", "Value", "Status", ""].map(h => <th key={h} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {displayData.map(r => (
              <tr key={r.id} onClick={() => openEdit(r)} style={{ borderTop: `1px solid ${colors.border}`, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ ...styles.td, fontWeight: 700 }}>{r.contract_number}</td>
                <td style={styles.td}>{r.client_name}</td>
                <td style={styles.td}>{r.product_name || "—"}</td>
                <td style={styles.td}>{formatDateDMY(r.start_date)}</td>
                <td style={styles.td}>{formatDateDMY(r.end_date)}</td>
                <td style={styles.td}><b style={{ color: r.visits_balance <= 0 ? colors.danger : colors.success }}>{r.visits_balance}</b> / {r.total_onsite_visits} <span style={{ color: colors.textLight, fontSize: 11 }}>({r.visits_used} used)</span></td>
                <td style={styles.td}>{r.telephonic_support}</td>
                <td style={styles.td}>{r.amc_value ? formatCurrencyINR(r.amc_value) : "—"}</td>
                <td style={styles.td}><span style={{ background: statusColor(r.computed_status) + "22", color: statusColor(r.computed_status), padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{r.computed_status}</span></td>
                <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                  <span style={{ color: colors.primary, cursor: "pointer", marginRight: 10 }} onClick={(e) => { e.stopPropagation(); openVisits(r); }}>🛠 Visits</span>
                  <span style={{ color: colors.primary, cursor: "pointer", marginRight: 10 }} onClick={(e) => { e.stopPropagation(); openEdit(r); }}>✏️</span>
                  <span style={{ color: colors.danger, cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); toggleDelete(r); }}>🗑</span>
                </td>
              </tr>
            ))}
            {displayData.length === 0 && <tr><td colSpan={10} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 30 }}>No AMC contracts found.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }} onClick={() => setShowForm(false)}>
          <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 640, boxShadow: "0 12px 40px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}` }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{editItem ? <span>Edit AMC <span style={{ background: colors.primary, color: "#fff", padding: "3px 12px", borderRadius: 6, fontSize: 14 }}>{editItem.contract_number}</span></span> : "New AMC Contract"}</h2>
            </div>
            {formError && (
              <div style={{ margin: "14px 20px 0", padding: "10px 14px", background: "#fef2f2", border: `1px solid ${colors.danger}`, color: colors.danger, borderRadius: 8, fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                <span>⚠</span><span>{formError}</span>
              </div>
            )}
            <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div><label style={styles.label}>Contract # <span style={{ color: colors.textLight, fontWeight: 400 }}>(blank = auto)</span></label><FastText style={styles.input} value={form.contract_number || ""} onCommit={(t) => setForm(f => ({ ...f, contract_number: t }))} placeholder="Auto from series" /></div>
              <div><label style={styles.label}>Customer *</label><SearchableSelect options={clientOpts} value={form.client_id || ""} onChange={(v) => setForm({ ...form, client_id: v })} placeholder="Search customer..."
                onCreate={() => setQc({ kind: "customer", apply: id => setForm(f => ({ ...f, client_id: id })) })} createLabel="Create Customer" /></div>
              <div><label style={styles.label}>Product</label><SearchableSelect options={productOpts} value={form.product_id || ""} onChange={(v) => setForm({ ...form, product_id: v })} placeholder="Search product..."
                onCreate={() => setQc({ kind: "product", apply: id => setForm(f => ({ ...f, product_id: id })) })} createLabel="Create Product" /></div>
              <div><label style={styles.label}>Managed By</label><SearchableSelect options={execOpts} value={form.managed_by_id || ""} onChange={(v) => setForm({ ...form, managed_by_id: v })} placeholder="Executive..."
                onCreate={() => setQc({ kind: "executive", apply: id => setForm(f => ({ ...f, managed_by_id: id })) })} createLabel="Create Executive" />
                <QuickCreateModal qc={qc && qc.where !== "visit" ? qc : null} masters={masters} onClose={() => setQc(null)} /></div>
              <div><label style={styles.label}>Start Date *</label><DateInput value={form.start_date || ""} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
              <div><label style={styles.label}>End Date *</label><DateInput value={form.end_date || ""} onChange={(v) => setForm({ ...form, end_date: v })} />
                {form.start_date && form.end_date && toISODate(form.end_date) < toISODate(form.start_date) && <div style={{ color: colors.danger, fontSize: 11, marginTop: 4 }}>⚠ End date is before the start date.</div>}
              </div>
              <div><label style={styles.label}>Total Onsite Visits *</label><input style={styles.input} type="number" min="0" value={form.total_onsite_visits ?? ""} onChange={(e) => setForm({ ...form, total_onsite_visits: e.target.value })} placeholder="e.g. 12" /></div>
              <div><label style={styles.label}>Telephonic Support</label><FastText style={styles.input} value={form.telephonic_support || ""} onCommit={(t) => setForm(f => ({ ...f, telephonic_support: t }))} placeholder="Unlimited" /></div>
              <div><label style={styles.label}>AMC Value (₹)</label><input style={styles.input} type="number" value={form.amc_value ?? ""} onChange={(e) => setForm({ ...form, amc_value: e.target.value })} /></div>
              <div><label style={styles.label}>Status</label>
                <select style={styles.input} value={form.status || "Active"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="Active">Active</option><option value="Renewed">Renewed</option><option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1" }}><label style={styles.label}>Remarks</label><FastText multiline style={{ ...styles.input, minHeight: 60 }} value={form.remarks || ""} onCommit={(t) => setForm(f => ({ ...f, remarks: t }))} /></div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${colors.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button style={styles.btn("ghost", "sm")} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={styles.btn("primary", "sm")} onClick={handleSave}>{editItem ? "Update" : "Create"} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span></button>
            </div>
          </div>
        </div>
      )}

      {visitModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10500, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }} onClick={() => setVisitModal(null)}>
          <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 640, boxShadow: "0 12px 40px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>🛠 Onsite Visits — {visitModal.contract.contract_number} ({visitModal.contract.client_name})</span>
              <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: colors.textLight }} onClick={() => setVisitModal(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 8, fontSize: 13 }}>
                Balance: <b style={{ color: visitModal.contract.visits_balance <= 0 ? colors.danger : colors.success }}>{visitModal.contract.visits_balance}</b> of {visitModal.contract.total_onsite_visits} remaining
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto", gap: 8, alignItems: "end", marginBottom: 14 }}>
                <div><label style={styles.label}>Visit Date</label><DateInput value={visitModal.vform.visit_date} onChange={(v) => setVisitModal(m => m && ({ ...m, vform: { ...m.vform, visit_date: v } }))} /></div>
                <div><label style={styles.label}>Executive</label><SearchableSelect options={execOpts} value={visitModal.vform.executive_id} onChange={(v) => setVisitModal(m => m && ({ ...m, vform: { ...m.vform, executive_id: v } }))}
                  onCreate={() => setQc({ kind: "executive", where: "visit", apply: id => setVisitModal(m => m && ({ ...m, vform: { ...m.vform, executive_id: id } })) })} createLabel="Create Executive" placeholder="Who visited" /></div>
                <QuickCreateModal qc={qc && qc.where === "visit" ? qc : null} masters={masters} onClose={() => setQc(null)} />
                <div><label style={styles.label}>Notes</label><input style={styles.input} value={visitModal.vform.notes} onChange={(e) => { const val = e.target.value; setVisitModal(m => m && ({ ...m, vform: { ...m.vform, notes: val } })); }} placeholder="What was done" /></div>
                <button style={styles.btn("primary", "sm")} onClick={addVisit}>+ Log</button>
              </div>
              {(visitModal.visits || []).map((v, i) => (
                <div key={v.source + "-" + v.id} style={{ borderLeft: `3px solid ${v.source === "call" ? "#7c3aed" : colors.primary}`, paddingLeft: 12, marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      Visit {i + 1} · {formatDateDMY(v.visit_date)} · {v.executive_name || "—"}
                      {v.hours ? ` · ${Number(v.hours)}h` : ""}
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 8, background: v.source === "call" ? "#ede9fe" : "#e0f2fe", color: v.source === "call" ? "#7c3aed" : "#0369a1" }}>{v.source === "call" ? `CALL ${v.ticket_number || ""}${v.status ? " · " + v.status : ""}` : "MANUAL"}</span>
                    </div>
                    {v.notes && <div style={{ fontSize: 12, color: "#475569" }}>{v.notes}</div>}
                  </div>
                  {v.source === "manual" && <span style={{ color: colors.danger, cursor: "pointer", fontSize: 12 }} onClick={() => deleteVisit(v.id)}>🗑</span>}
                </div>
              ))}
              {(!visitModal.loading && (visitModal.visits || []).length === 0) && <div style={{ color: colors.textLight, fontSize: 13 }}>No visits logged yet.</div>}
            </div>
          </div>
        </div>
      )}

      {settingsOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setSettingsOpen(false)}>
          <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 380, padding: 20 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>AMC Number Series</h3>
            <p style={{ fontSize: 12, color: colors.textLight }}>New contracts auto-number from here (plain number, no prefix). Setting a higher number affects only future contracts.</p>
            <label style={styles.label}>Starting Number</label>
            <input style={styles.input} type="number" value={startNumber} onChange={(e) => setStartNumber(e.target.value)} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button style={styles.btn("ghost", "sm")} onClick={() => setSettingsOpen(false)}>Cancel</button>
              <button style={styles.btn("primary", "sm")} onClick={saveSettings}>Save</button>
            </div>
          </div>
        </div>
      )}

      {renewedModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10500, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }} onClick={() => setRenewedModal(null)}>
          <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 1050, boxShadow: "0 12px 40px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#7c3aed" }}>🔄 Renewed AMC Contracts</span>
              <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: colors.textLight }} onClick={() => setRenewedModal(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", marginBottom: 14 }}>
                <div><label style={styles.label}>Renewed From</label><DateInput value={renewedModal.from} onChange={(v) => loadRenewed(v, renewedModal.to)} /></div>
                <div><label style={styles.label}>Renewed To</label><DateInput value={renewedModal.to} onChange={(v) => loadRenewed(renewedModal.from, v)} /></div>
                <div style={{ flex: 1 }} />
                <button style={styles.btn("success", "sm")} onClick={renewedExcel}>📊 Excel</button>
                <button style={{ ...styles.btn("ghost", "sm"), background: "#dc2626", color: "#fff" }} onClick={renewedPDF}>📄 PDF</button>
              </div>
              <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 8 }}>{renewedModal.loading ? "Loading…" : `${(renewedModal.data || []).length} contract(s) renewed in this period.`} Contracts renewed before this feature was added won't have a renewed date and won't appear here.</div>
              <div style={{ overflowX: "auto", maxHeight: "55vh" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
                    {["Contract #", "Customer", "Contact", "Mobile", "Product", "License #", "Renewed On", "End Date", "Value", "Managed By"].map(h => <th key={h} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {(renewedModal.data || []).map(r => (
                      <tr key={r.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ ...styles.td, fontWeight: 700 }}>{r.contract_number}</td>
                        <td style={styles.td}>{r.client_name}</td>
                        <td style={styles.td}>{r.contact_person || "—"}</td>
                        <td style={styles.td}>{r.customer_phone || r.contact_phone || "—"}</td>
                        <td style={styles.td}>{r.product_name || "—"}</td>
                        <td style={styles.td}>{r.license_number || "—"}</td>
                        <td style={styles.td}>{r.renewed_date ? formatDateDMY(r.renewed_date) : "—"}</td>
                        <td style={styles.td}>{formatDateDMY(r.end_date)}</td>
                        <td style={styles.td}>{r.amc_value ? formatCurrencyINR(r.amc_value) : "—"}</td>
                        <td style={styles.td}>{r.managed_by_name || "—"}</td>
                      </tr>
                    ))}
                    {!renewedModal.loading && (renewedModal.data || []).length === 0 && <tr><td colSpan={10} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>No AMCs renewed in this date range.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: colors.textLight, marginTop: 8 }}>Excel export includes all customer fields (email, GSTIN, city, release/version, subscription expiry, etc.).</div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

const ScheduleView = ({ masters, defaultExecId, companyName }) => {
  const isMobile = useIsMobile();
  const todayStr = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); };
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [assignedTo, setAssignedTo] = useState(defaultExecId ? String(defaultExecId) : "");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ scheduled: "true", limit: 2000 });
      if (from) p.append("sched_from", from);
      if (to) p.append("sched_to", to);
      if (assignedTo) p.append("assigned_to_id", assignedTo);
      const r = await api(`/call-logs?${p}`);
      setData(r.data || []);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  }, [from, to, assignedTo]);
  useEffect(() => { load(); }, [load]);

  const groups = {};
  data.forEach(c => { const k = c.call_type_name || "Other"; (groups[k] = groups[k] || []).push(c); });
  const groupNames = Object.keys(groups).sort();
  const fmtDT = (d, t) => d ? (formatDateDMY(d) + (t ? " " + formatTime12hr(String(t).slice(0,5)) : "")) : "—";

  const exportExcel = () => {
    if (data.length === 0) { setToast({ message: "No scheduled visits to export.", type: "warning" }); return; }
    const headers = ["Call Type", "Ticket #", "Customer", "Next Action Date & Time", "Call Date", "Assigned By", "Status"];
    const rows = [];
    groupNames.forEach(g => groups[g].forEach(c => rows.push(csvRow([g, c.ticket_number, c.client_name || "", fmtDT(c.next_action_date, c.next_action_time), c.call_date ? formatDateDMY(c.call_date) : "", c.executive_name || "", c.call_status || ""]))));
    downloadCSV(csvRow(headers) + "\n" + rows.join("\n") + "\n", `schedule_${from}_to_${to}.csv`);
    setToast({ message: `Exported ${data.length} scheduled visits!`, type: "success" });
  };

  const exportPDF = () => {
    if (data.length === 0) { setToast({ message: "No scheduled visits to export.", type: "warning" }); return; }
    const execName = assignedTo ? (masters.executives?.find(e => String(e.id) === String(assignedTo))?.name || "") : "All executives";
    let body = "";
    groupNames.forEach(g => {
      body += `<h3 style="color:#16a34a;margin:16px 0 6px;border-bottom:2px solid #16a34a;padding-bottom:3px">${g} (${groups[g].length})</h3><table style="width:100%;border-collapse:collapse;font-size:11px"><thead><tr><th style="padding:5px;border:1px solid #ddd;background:#f0fdf4;text-align:left">Ticket</th><th style="padding:5px;border:1px solid #ddd;background:#f0fdf4;text-align:left">Customer</th><th style="padding:5px;border:1px solid #ddd;background:#f0fdf4">Next Action</th><th style="padding:5px;border:1px solid #ddd;background:#f0fdf4">Call Date</th><th style="padding:5px;border:1px solid #ddd;background:#f0fdf4;text-align:left">Assigned By</th><th style="padding:5px;border:1px solid #ddd;background:#f0fdf4">Status</th></tr></thead><tbody>`;
      groups[g].forEach(c => { body += `<tr><td style="padding:4px;border:1px solid #ddd;font-weight:600">${c.ticket_number}</td><td style="padding:4px;border:1px solid #ddd">${c.client_name||""}</td><td style="padding:4px;border:1px solid #ddd">${fmtDT(c.next_action_date, c.next_action_time)}</td><td style="padding:4px;border:1px solid #ddd">${c.call_date?formatDateDMY(c.call_date):""}</td><td style="padding:4px;border:1px solid #ddd">${c.executive_name||""}</td><td style="padding:4px;border:1px solid #ddd">${c.call_status||""}</td></tr>`; });
      body += `</tbody></table>`;
    });
    const html = `<html><head><title>Scheduled Visits</title></head><body style="font-family:Arial,sans-serif;padding:24px">
      <h1 style="color:#16a34a;margin:0;font-size:20px">${companyName || "CLAS CRM Suite"}</h1>
      <h2 style="color:#333;margin:8px 0 4px">Scheduled Visits</h2>
      <p style="color:#666;font-size:11px">${formatDateDMY(from)} to ${formatDateDMY(to)} | ${execName} | ${data.length} visit(s)</p>
      <hr style="border:1px solid #ddd;margin:10px 0">${body}</body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); w.print();
    setToast({ message: "Schedule PDF ready!", type: "success" });
  };

  const statusColor = (s) => s === "Completed" ? colors.success : s === "Cancelled" ? colors.textLight : s === "Escalated" ? colors.danger : "#d97706";

  return (
    <div style={styles.card}>
      <div style={styles.filterBar}>
        <div><label style={styles.label}>From</label><DateInput value={from} onChange={setFrom} /></div>
        <div><label style={styles.label}>To</label><DateInput value={to} onChange={setTo} /></div>
        <div><label style={styles.label}>Assigned To</label>
          <select style={styles.select} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">All executives</option>{masters.executives?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <button style={styles.btn("ghost", "sm")} onClick={() => { const t = todayStr(); setFrom(t); setTo(t); }}>Today</button>
        <div style={{ flex: 1 }} />
        <button style={styles.btn("success", "sm")} onClick={exportExcel}>📊 Excel</button>
        <button style={{ ...styles.btn("ghost", "sm"), background: "#dc2626", color: "#fff" }} onClick={exportPDF}>📄 PDF</button>
      </div>

      <div style={{ padding: "4px 8px 12px" }}>
        {loading ? <div style={{ padding: 20, color: colors.textLight }}>Loading…</div> : data.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: colors.textLight }}>No scheduled visits for this period.</div>
        ) : groupNames.map(g => (
          <div key={g} style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#16a34a", borderBottom: `2px solid #16a34a`, paddingBottom: 3, marginBottom: 6 }}>{g} <span style={{ color: colors.textLight, fontWeight: 400, fontSize: 12 }}>({groups[g].length})</span></div>
            {isMobile ? (
              <MobileCards rows={groups[g]}
                primary={c => c.client_name || "—"}
                secondary={c => `#${c.ticket_number}`}
                badge={c => ({ text: c.call_status, color: statusColor(c.call_status) })}
                meta={c => [["Scheduled", fmtDT(c.next_action_date, c.next_action_time)],
                            ["Call date", c.call_date ? formatDateDMY(c.call_date) : ""],
                            ["Address", custAddress(c.client_id, masters)],
                            ["Contacts", contactNums(c)], ["Spoke to", c.contact_person_called || c.contact_person],
                            ["Assigned by", c.executive_name], ["Email", contactMails(c)]]} />
            ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
                {["Ticket #", "Customer", "Address", "Contacts", "Email", "Spoke To", "Next Action Date & Time", "Call Date", "Call Type", "Assigned By", "Status"].map(h => <th key={h} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {groups[g].map(c => (
                  <tr key={c.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{c.ticket_number}</td>
                    <td style={styles.td}>{c.client_name || "—"}</td>
                    <td style={styles.td}>{(() => { const a = custAddress(c.client_id, masters); return a
                      ? <span>{a} <a href={mapsLink(a)} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: "none" }}>🗺️</a></span>
                      : "—"; })()}</td>
                    <td style={styles.td}>{contactNums(c)}</td>
                    <td style={styles.td}>{contactMails(c)}</td>
                    <td style={styles.td}>{c.contact_person_called || contactPersons(c)}</td>
                    <td style={styles.td}>{fmtDT(c.next_action_date, c.next_action_time)}</td>
                    <td style={styles.td}>{c.call_date ? formatDateDMY(c.call_date) : "—"}</td>
                    <td style={styles.td}>{c.call_type_name || g}</td>
                    <td style={styles.td}>{c.executive_name || "—"}</td>
                    <td style={styles.td}><span style={{ background: statusColor(c.call_status) + "22", color: statusColor(c.call_status), padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{c.call_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        ))}
      </div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

// After quick-creating a customer, add its products/licences before returning to the
// call form — so the licence dropdown is populated straight away. Skippable.
const QuickLicences = ({ client, masters, loadMasters, onDone }) => {
  const [qc, setQc] = useState(null);   // quick-create from a dropdown
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ product_id: "", license_number: "", user_type: "", quantity: "1", subscription_expiry: "" });
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { try { const r = await api(`/customer-products/customer/${client.id}`); setRows(r.data || []); } catch (e) {} };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client?.id]);

  const add = async () => {
    if (!form.product_id) { setToast({ message: "Please choose a product.", type: "warning" }); return; }
    setSaving(true);
    try {
      await api(`/customer-products`, { method: "POST", body: {
        client_id: client.id, product_id: form.product_id, license_number: form.license_number || null,
        user_type: form.user_type || null, quantity: parseInt(form.quantity) || 1,
        subscription_expiry: form.subscription_expiry || null, is_active: true } });
      setForm({ product_id: "", license_number: "", user_type: "", quantity: "1", subscription_expiry: "" });
      await load(); loadMasters && loadMasters();
      setToast({ message: "Licence added.", type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this licence?")) return;
    try { await api(`/customer-products/${id}`, { method: "DELETE" }); load(); } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  return (
    <div style={{ ...styles.modal, zIndex: 11000 }} onClick={onDone}>
      <div style={{ ...styles.modalContent, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Products &amp; Licences — <span style={{ color: colors.primary }}>{client.company_name}</span></h3>
        <p style={{ fontSize: 12.5, color: colors.textLight, marginTop: 0 }}>
          Customer created. Add their products and licence numbers now so they're available on this call. You can skip and add them later from the Customers screen.
        </p>

        {rows.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginBottom: 12 }}>
            <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
              {["Product", "Licence #", "User Type", "Qty", "Expiry", ""].map(h => <th key={h} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                  <td style={styles.td}>{r.product_name || "—"}</td>
                  <td style={styles.td}>{r.license_number || "—"}</td>
                  <td style={styles.td}>{r.user_type || "—"}</td>
                  <td style={styles.td}>{r.quantity || 1}</td>
                  <td style={styles.td}>{r.subscription_expiry ? formatDateDMY(r.subscription_expiry) : "—"}</td>
                  <td style={styles.td}><button style={styles.btn("danger", "sm")} onClick={() => remove(r.id)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={styles.grid(2)}>
          <div><label style={styles.label}>Product *</label>
            <SearchableSelect options={masters.products?.map(p => ({ value: p.id.toString(), label: p.name })) || []}
              value={form.product_id ? String(form.product_id) : ""} onChange={v => setForm({ ...form, product_id: v })} placeholder="Search product..."
              onCreate={() => setQc({ kind: "product", apply: id => setForm(f => ({ ...f, product_id: id })) })} createLabel="Create Product" />
            <QuickCreateModal qc={qc} masters={masters} onClose={() => setQc(null)} />
          </div>
          <div><label style={styles.label}>Licence Number</label>
            <input style={styles.input} value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} placeholder="e.g. 794080854" />
          </div>
          <div><label style={styles.label}>User Type</label>
            <input style={styles.input} value={form.user_type} onChange={e => setForm({ ...form, user_type: e.target.value })} placeholder="Single User / Multi User" />
          </div>
          <div><label style={styles.label}>Quantity</label>
            <input style={styles.input} type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
          </div>
          <div><label style={styles.label}>Subscription Expiry</label>
            <DateInput value={form.subscription_expiry} onChange={v => setForm({ ...form, subscription_expiry: v })} />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button style={styles.btn("success")} onClick={add} disabled={saving}>{saving ? "Adding…" : "+ Add Licence"}</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button style={styles.btn("ghost")} onClick={onDone}>{rows.length ? "Done" : "Skip for now"}</button>
          {rows.length > 0 && <button style={styles.btn("primary")} onClick={onDone}>Done — use on this call</button>}
        </div>
      </div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

let __callAdvRows = [];     // Call Tracker: advanced filters kept across screen navigation
let __callAdvApplied = {};  // ...and the parameters they resolve to
const CallTrackerScreen = ({ masters, loadMasters, user, pendingCall, setPendingCall, setActiveScreen, pendingReturn }) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const defaultExecId = user?.executive_id?.toString() || "";
  const [form, setForm] = useState({ call_status: "Open", executive_id: defaultExecId, log_date: new Date().toISOString().slice(0,10), call_date: new Date().toISOString().slice(0,10) });
  const ctFormRef = useRef(null);
  const [toast, setToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [callView, setCallView] = useState("all"); // "all" | "next_actions" | "assigned_to_me" | "assigned_by_me" | "schedule"
  // Open leads assigned to me — shown as a reminder on the "Assigned to Me" tab
  const [myLeadCount, setMyLeadCount] = useState(0);
  useEffect(() => {
    if (callView !== "assigned_to_me") return;
    const p = new URLSearchParams({ limit: "1", active_only: "true" });
    if (user?.role === "admin") p.append("assigned_only", "true");
    else if (defaultExecId) p.append("assigned_to_id", defaultExecId);
    else { setMyLeadCount(0); return; }
    api(`/leads?${p}`).then(r => setMyLeadCount(r.pagination?.total || 0)).catch(() => setMyLeadCount(0));
  }, [callView, defaultExecId]);
  const [amFrom, setAmFrom] = useState("");  // Assigned-to-me: Next Action date range
  const [amTo, setAmTo] = useState("");
  const [showDoneRenewals, setShowDoneRenewals] = useState(false);
  const [renSel, setRenSel] = useState(() => new Set());   // renewal follow-ups ticked for "Cancel selected"
  const cancelRenewals = async () => {
    const ids = [...renSel];
    if (!ids.length) return;
    const reason = window.prompt(`Cancel ${ids.length} renewal follow-up(s)?\n\nReason (added to each call's notes):`, "Not renewing");
    if (reason === null) return;   // Cancel pressed
    try {
      const r = await api("/call-logs/bulk-cancel", { method: "POST", body: { ids, reason } });
      setToast({ message: `${r.cancelled} follow-up(s) cancelled${r.threadCalls ? ` (and ${r.threadCalls} open follow-up call(s) in their threads)` : ""}.`, type: "success" });
      setRenSel(new Set());
      fetchData(pagination?.page || 1);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };
  // Renewals tab: one more follow-up call for a licence renewal — a NEW call, pre-filled,
  // in the renewal follow-up's thread (each attempt is its own record with its own time)
  const logRenewalCall = async (p, e) => {
    if (e) e.stopPropagation();
    const today = toISODate(new Date().toISOString());
    const rType = (masters.callTypes || []).find(t => /renew/i.test(t.name || ""));
    const n = (parseInt(p.thread_calls) || 0) + 1;
    setEditItem(null);
    setForm({
      call_status: "Completed", executive_id: defaultExecId, log_date: today, call_date: today,
      client_id: p.client_id, product_id: p.product_id, principal_id: p.principal_id, license_number: p.license_number,
      call_type_id: rType ? String(rType.id) : p.call_type_id,
      regarding: `${String(p.regarding || "License renewal").replace(/ — follow-up call \d+$/, "")} — follow-up call ${n}`,
      parent_call_id: String(p.id), renewal_parent_id: p.id, _renewalTicket: p.ticket_number,
      customer_products: [], customer_product_id: "",
    });
    setShowForm(true);
    if (p.client_id) {
      try {
        const r = await api(`/customer-products/customer/${p.client_id}`);
        const cps = r.data || []; const m = cps.find(cp => cp.license_number === p.license_number);
        setForm(f => f.renewal_parent_id === p.id ? { ...f, customer_products: cps, customer_product_id: m ? String(m.id) : "" } : f);
      } catch (err) {}
    }
  };
  const [advRows, setAdvRowsState] = useState(__callAdvRows);
  const setAdvRows = (v) => { __callAdvRows = v; setAdvRowsState(v); };
  const [advApplied, setAdvAppliedState] = useState(__callAdvApplied);   // params currently in force
  const setAdvApplied = (v) => { __callAdvApplied = v; setAdvAppliedState(v); };
  const { sorts, toggleSort, clearSort } = useMultiSort();
  const tabSortAcc = { contacts: (r) => contactNums(r), emails: (r) => contactMails(r), spoke_to: (r) => r.contact_person_called || r.contact_person, next_action: (r) => r.next_action_date };
  const isMobile = useIsMobile();
  const statusColorOf = (s) => s === "Completed" ? colors.success : s === "Cancelled" ? colors.textLight : s === "Escalated" ? colors.danger : "#d97706";
  const nextActionText = (c) => c.next_action_date ? (formatDateDMY(c.next_action_date) + (c.next_action_time ? " " + formatTime12hr(String(c.next_action_time).slice(0,5)) : "")) : "";
  const sortedRows = applyMultiSort(data, sorts, tabSortAcc);
  const tabSortLabels = { ticket_number: "Ticket #", client_name: "Customer", contacts: "Contacts", emails: "Email", spoke_to: "Spoke To", next_action: "Next Action", call_date: "Call Date", call_type_name: "Call Type", executive_name: "Assigned By", assigned_to_name: "Assigned To", call_status: "Status" };
  const [parentOptions, setParentOptions] = useState([]);   // recent tickets to continue
  const [amcOptions, setAmcOptions] = useState([]);         // AMC contracts to attach a visit to
  const [amcMap, setAmcMap] = useState({});                 // contract id -> contract (for auto-fill)
  const [rescheduleHistory, setRescheduleHistory] = useState(null); // { loading, rows }
  const [parentCalls, setParentCalls] = useState({});       // id -> call, for auto-fill
  const [threadModal, setThreadModal] = useState(null);     // { loading, rows, summary }

  const loadParentOptions = useCallback(async () => {
    try {
      const r = await api("/call-logs?limit=500");
      const map = {};
      const opts = (r.data || []).map(c => {
        map[String(c.id)] = c;
        return { value: String(c.id), label: `${c.ticket_number} · ${c.client_name || "—"}${c.regarding ? " · " + c.regarding : ""}` };
      });
      setParentCalls(map);
      setParentOptions(opts);
    } catch (e) {}
  }, []);
  useEffect(() => { if (showForm) loadParentOptions(); }, [showForm, loadParentOptions]);

  const loadAmcOptions = useCallback(async () => {
    try {
      const r = await api("/amc?limit=1000");
      const map = {};
      const opts = (r.data || []).map(a => {
        map[String(a.id)] = a;
        return { value: String(a.id), label: `${a.contract_number} · ${a.client_name || "—"}${a.product_name ? " · " + a.product_name : ""} (bal ${a.visits_balance}/${a.total_onsite_visits})` };
      });
      setAmcMap(map);
      setAmcOptions(opts);
    } catch (e) {}
  }, []);
  useEffect(() => { if (showForm) loadAmcOptions(); }, [showForm, loadAmcOptions]);

  const openRescheduleHistory = async (callId) => {
    setRescheduleHistory({ loading: true, rows: [] });
    try { const r = await api(`/call-logs/${callId}/reschedules`); setRescheduleHistory({ loading: false, rows: r.data || [] }); }
    catch (e) { setRescheduleHistory({ loading: false, rows: [] }); }
  };

  const openThread = async (callId) => {
    setThreadModal({ loading: true, rows: [], summary: null });
    try {
      const r = await api(`/call-logs/${callId}/thread`);
      setThreadModal({ loading: false, rows: r.data || [], summary: r.summary || null });
    } catch (e) {
      setThreadModal({ loading: false, rows: [], summary: null, error: e.message });
    }
  };

  // Keyboard shortcuts for Call Tracker
  ctFormRef.current = form;   // latest form for Esc (this handler is only set up when the window opens)
  useEffect(() => {
    const closeModal = () => {
      if (!showForm) return;
      const typedJustNow = flushFastText();   // typing not yet handed to the form still counts as a change
      if (typedJustNow ? window.confirm("You have unsaved changes. Exit without saving?") : confirmCancel(ctFormRef.current)) setShowForm(false);
    };
    const addNew = () => { setEditItem(null); setForm({ call_status: "Open", executive_id: defaultExecId, log_date: new Date().toISOString().slice(0,10), call_date: new Date().toISOString().slice(0,10) }); setShowForm(true); };
    const saveForm = () => { if (showForm) document.dispatchEvent(new Event("do-save")); };
    const handleScreenKeys = (e) => {
      if (!e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'b') { e.preventDefault(); blankExportCalls(); }
      else if (k === 'x') { e.preventDefault(); exportAllCalls(); }
    };
    document.addEventListener("close-modal", closeModal);
    document.addEventListener("shortcut-add-new", addNew);
    document.addEventListener("shortcut-save", saveForm);
    window.addEventListener("keydown", handleScreenKeys);
    return () => { document.removeEventListener("close-modal", closeModal); document.removeEventListener("shortcut-add-new", addNew); document.removeEventListener("shortcut-save", saveForm); window.removeEventListener("keydown", handleScreenKeys); };
  }, [showForm]);

  const [quickCreate, setQuickCreate] = useState(null);
  const [newClientForLicences, setNewClientForLicences] = useState(null);

  // Full field configurations for each master (same as master screens)
  const deptOpts = ["Sales", "Support", "Technical", "Admin", "Operations", "Finance", "HR", "Management"].map(d => ({ value: d, label: d }));
  const desigOpts = ["Manager", "Senior Engineer", "Engineer", "Consultant", "Team Lead", "Director", "VP", "Analyst", "Coordinator", "Trainee"].map(d => ({ value: d, label: d }));
  const indOpts = ["IT/Software", "Manufacturing", "Retail", "Healthcare", "FMCG", "Pharma", "Automobile", "Banking/Finance", "Education", "Telecom", "Real Estate", "Logistics", "Agriculture", "Textiles", "E-commerce", "Others"].map(d => ({ value: d, label: d }));
  const ctOpts = ["Pvt Ltd", "Ltd (Public)", "LLP", "Partnership", "Proprietorship", "Trust", "Society", "Government", "Others"].map(d => ({ value: d, label: d }));
  const catOpts = ["Software License", "Hardware", "Service", "AMC/Support", "Customization", "Training", "Subscription", "Consulting", "Others"].map(d => ({ value: d, label: d }));
  const regOpts = ["North", "South", "East", "West", "Central", "North-East"].map(d => ({ value: d, label: d }));
  const stOpts = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu & Kashmir","Ladakh"].map(s => ({ value: s, label: s }));

  const desigSelectOpts = masters.designations?.map(d => ({ value: d.id.toString(), label: d.name })) || [];
  const deptSelectOpts = masters.departments?.map(d => ({ value: d.id.toString(), label: d.name })) || [];
  const partnerSelectOpts = masters.partners?.map(p => ({ value: p.id.toString(), label: p.name })) || [];

  const masterFieldConfigs = {
    executives: { title: "Executive", endpoint: "executives", targetField: "executive_id", fields: [
      { key: "name", label: "Full Name", required: true }, { key: "employee_id", label: "Employee ID" },
      { key: "designation_id", label: "Designation", type: "searchable_select", options: desigSelectOpts, createLabel: "Create Designation", createEndpoint: "designations", createFields: [{ key: "name", label: "Designation Name", required: true }] },
      { key: "department_id", label: "Department", type: "searchable_select", options: deptSelectOpts, createLabel: "Create Department", createEndpoint: "departments", createFields: [{ key: "name", label: "Department Name", required: true }] },
      { key: "email", label: "Email", type: "email" }, { key: "phone", label: "Phone" },
      { key: "alternate_phone", label: "Alternate Phone" }, { key: "date_of_joining", label: "Date of Joining", type: "date" },
      { key: "address", label: "Address", type: "textarea", fullWidth: true },
    ]},
    principals: { title: "Principal Company", endpoint: "principals", targetField: "principal_id", fields: [
      { key: "company_name", label: "Company Name", required: true }, { key: "industry", label: "Industry", type: "select", options: indOpts },
      { key: "company_type", label: "Company Type", type: "select", options: ctOpts },
      { key: "address_line1", label: "Address Line 1" }, { key: "city", label: "City" },
      { key: "state", label: "State", type: "select", options: stOpts }, { key: "phone", label: "Phone" },
      { key: "email", label: "Email", type: "email" }, { key: "gstin", label: "GSTIN" },
      { key: "contact_person", label: "Contact Person" }, { key: "contact_phone", label: "Contact Phone" },
    ]},
    // Full customer master — same fields as the Customers screen, so a customer
    // created mid-call is just as complete as one created from Masters.
    clients: { title: "Customer", endpoint: "clients", targetField: "client_id", fields: clientsCreateFields(masters) },
    problems: { title: "Problem", endpoint: "call-logs/problems", targetField: "problem_id", fields: [
      { key: "name", label: "Problem (short description) *", required: true },
      { key: "category", label: "Category" },
      { key: "description", label: "Notes", type: "textarea", fullWidth: true },
    ]},
    products: { title: "Product", endpoint: "products", targetField: "product_id", fields: [
      { key: "name", label: "Product Name", required: true }, { key: "category", label: "Category", type: "select", options: catOpts },
      { key: "description", label: "Description", type: "textarea", fullWidth: true },
      { key: "hsn_sac_code", label: "HSN/SAC Code" }, { key: "default_gst_rate", label: "GST Rate (%)", type: "number" },
      { key: "unit_price", label: "Unit Price (INR)", type: "number" },
    ]},
    callTypes: { title: "Call Type", endpoint: "call-types", targetField: "call_type_id", fields: [
      { key: "name", label: "Call Type Name", required: true },
    ]},
    locations: { title: "Location", endpoint: "locations", targetField: "location_id", fields: [
      { key: "name", label: "Location Name", required: true },
      { key: "state", label: "State", type: "select", options: stOpts },
      { key: "region", label: "Region", type: "select", options: regOpts }, { key: "pincode", label: "Pincode" },
    ]},
    partners: { title: "Partner", endpoint: "partners", targetField: "billed_through", fields: [
      { key: "name", label: "Partner Name", required: true },
      { key: "phone", label: "Phone" }, { key: "email", label: "Email", type: "email" },
    ]},
  };

  const toggleSelect = (id) => { setSelectAllPages(false); setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const toggleSelectAll = () => { if (selectedIds.size === data.length) { setSelectedIds(new Set()); setSelectAllPages(false); } else { setSelectedIds(new Set(data.map(d => d.id))); } };

  const blankExportCalls = () => {
    const headers = csvRow(["Call Date (DD/MM/YYYY)", "Executive Name", "Principal Company", "Customer", "Product", "TDL Type", "Start Time (HH:MM)", "End Time (HH:MM)", "Call Type", "Subject", "Call Notes", "Call Status", "Location", "Subscription Expiry (DD/MM/YYYY)", "Billed Through", "Contact Person", "Release/Version"]);
    const sample = csvRow(["25/04/2026", "Priya", "GRB", "Karnataka State Minerals Corporation Limited", "Gold", "Tally to Bizom", "10:00 AM", "11:30 AM", "Support", "Data sync issue", "Resolved sync error in SKU mapping", "Completed", "Bangalore", "31/03/2027", "Direct", "Rajesh - IT Dept", "7.0"]);
    const guide = [
      "# IMPORT GUIDE - Delete these # lines and the sample row before importing",
      "# Call Date: DD/MM/YYYY (e.g. 25/04/2026)",
      "# Executive Name: Must match exactly as in Executives master",
      "# Principal Company: Optional. Must match exactly",
      "# Customer: Must match exactly as in Customers master",
      "# Product: Must match exactly as in Products master",
      "# TDL Type: Bizom to Tally / Tally to Bizom / Both-way Sync (or leave blank)",
      "# Start Time & End Time: Both formats accepted:",
      "#   24-hour: 14:30 (for 2:30 PM)",
      "#   12-hour: 02:30 PM or 2:30 PM (AM/PM required for 12-hour)",
      "# Call Type: Must match exactly as in Call Types master",
      "# Call Status: Open / In Progress / Completed / Escalated / On Hold",
      "# Subscription Expiry: DD/MM/YYYY. Optional",
      "# Billed Through: Direct or partner name. Optional",
      "# Contact Person: Name of person spoken to. Optional",
      "# Release/Version: Software version. Optional",
      "# ---- DELETE THIS SAMPLE ROW BELOW BEFORE IMPORTING ----",
    ].join("\n");
    downloadCSV(guide + "\n" + headers + "\n" + sample + "\n", "call_log_blank_template.csv");
    setToast({ message: "Blank template with sample data downloaded!", type: "success" });
  };

  const allCallHeaders = ["Ticket #", "Log Date", "Call Date", "Physical Days", "Executive", "Principal Company", "Customer", "License #", "Product", "TDL Type", "Start Time", "End Time", "Hours Spent", "Call Type", "Status", "Location", "Subscription Expiry", "Billed Through / Partner", "Assigned To", "Next Action Date", "Contact Person Spoke To", "Release / Version", "Customer Email", "Subject", "Call Notes", "Problem Observed", "Solution Provided", "Type", "Continuation Of (Ticket #)", "Thread Total Hours"];
  const callToRow = (r, threadHours = {}, amcCols = null) => {
    const root = r.parent_call_id || r.id;
    const base = [
    r.ticket_number, r.log_date ? formatDateDMY(r.log_date) : "", formatDateDMY(r.call_date), r.physical_days || "",
    r.executive_name || "", r.principal_name || "", r.client_name || "", r.license_number || "",
    r.product_name || "", r.tdl_type || "", formatTime12hr(r.call_start_time), formatTime12hr(r.call_end_time),
    r.hours_spent || 0, r.call_type_name || "", r.call_status || "", r.location_name || "",
    r.subscription_expiry ? formatDateDMY(r.subscription_expiry) : "", r.billed_through_name || r.billed_through || "",
    r.assigned_to_name || "", r.next_action_date ? formatDateDMY(r.next_action_date) : "",
    r.contact_person_called || "", r.software_version || "", r.customer_email || "",
    r.regarding || "", r.call_details || "", r.problem_observed || "", r.solution_provided || "",
    r.parent_call_id ? "Continuation" : "Original",
    r.parent_ticket_number || "",
    (threadHours[root] != null ? Number(threadHours[root]) : (Number(r.hours_spent) || 0)).toFixed(2)
    ];
    if (amcCols != null) {
      const fmtR = (s) => { if (!s) return ""; const parts = String(s).split(" "); return formatDateDMY(parts[0]) + (parts[1] ? " " + formatTime12hr(parts[1]) : ""); };
      const original = r.original_visit_date ? (formatDateDMY(r.original_visit_date) + (r.original_visit_time ? " " + formatTime12hr(String(r.original_visit_time).slice(0,5)) : "")) : "";
      base.push(original);
      const moves = r.reschedule_dates || [];
      for (let k = 0; k < amcCols; k++) base.push(fmtR(moves[k]));
    }
    return csvRow(base);
  };

  const exportCallsData = (rows) => {
    const threadHours = {};
    rows.forEach(r => { const root = r.parent_call_id || r.id; threadHours[root] = (threadHours[root] || 0) + (parseFloat(r.hours_spent) || 0); });
    const hasAmc = rows.some(r => r.amc_contract_id || r.original_visit_date);
    const maxResched = hasAmc ? rows.reduce((m, r) => Math.max(m, (r.reschedule_dates || []).length), 0) : 0;
    const amcCols = hasAmc ? maxResched : null;
    const fullHeaders = hasAmc
      ? [...allCallHeaders, "AMC Original Scheduled", ...Array.from({ length: maxResched }, (_, i) => `AMC Reschedule ${i + 1}`)]
      : allCallHeaders;
    const headers = csvRow(fullHeaders);
    const csvRows = rows.map(r => callToRow(r, threadHours, amcCols)).join("\n");
    downloadCSV(headers + "\n" + csvRows + "\n", `call_logs_export_${new Date().toISOString().slice(0,10)}.csv`);
    setToast({ message: `Exported ${rows.length} records!`, type: "success" });
  };

  const exportAllCalls = async () => {
    try {
      setToast({ message: "Exporting all records... please wait.", type: "success" });
      const params = new URLSearchParams({ limit: 100000 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      if (callView === "next_actions") params.append("next_actions_due", "true");
      // An executive whose login isn't linked to an executive record sees nothing here
      // (it used to show EVERY assigned call)
      if (callView === "assigned_to_me") { if (user?.role === "admin") params.append("assigned_only", "true"); else params.append("assigned_to_id", defaultExecId || "0"); }
      if (callView === "assigned_by_me") { params.append("assigned_by_me", "true"); }
      if (callView === "renewals") { params.append("renewals", "true"); if (showDoneRenewals) params.append("show_completed", "true"); if (user?.role !== "admin" && defaultExecId) params.append("assigned_to_id", defaultExecId); }
      const r = await api(`/call-logs?${params}`);
      if (!r.data || r.data.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
      exportCallsData(r.data);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const exportSelectedCalls = () => {
    if (selectedIds.size === 0) { setToast({ message: "No records selected.", type: "warning" }); return; }
    const rows = data.filter(d => selectedIds.has(d.id));
    exportCallsData(rows);
  };

  const handleUnlock = async (id) => {
    try {
      await api(`/call-logs/${id}/unlock`, { method: "PATCH", body: { hours: 24 } });
      setToast({ message: "Call unlocked for 24 hours!", type: "success" });
      fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const handleLock = async (id) => {
    try {
      await api(`/call-logs/${id}/lock`, { method: "PATCH" });
      setToast({ message: "Call locked!", type: "success" });
      fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // Live time spent calculation
  const calculateHoursSpent = (startTime, endTime) => {
    if (!startTime || !endTime) return "";
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    let startMinutes = sh * 60 + sm;
    let endMinutes = eh * 60 + em;
    if (endMinutes <= startMinutes) endMinutes += 1440; // handle midnight crossover
    const diff = endMinutes - startMinutes;
    const hours = (diff / 60).toFixed(2);
    return hours;
  };

  const hoursSpent = calculateHoursSpent(form.call_start_time, form.call_end_time);

  const fetchData = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      Object.entries(advApplied).forEach(([k, v]) => { if (v) params.append(k, v); });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      if (callView === "next_actions") params.append("next_actions_due", "true");
      if (callView === "assigned_to_me") { if (user?.role === "admin") { params.append("assigned_only", "true"); } else { params.append("assigned_to_id", defaultExecId || "0"); } if (amFrom) params.append("sched_from", amFrom); if (amTo) params.append("sched_to", amTo); }
      if (callView === "assigned_by_me") { params.append("assigned_by_me", "true"); if (amFrom) params.append("sched_from", amFrom); if (amTo) params.append("sched_to", amTo); }
      if (callView === "renewals") { params.append("renewals", "true"); if (showDoneRenewals) params.append("show_completed", "true"); if (user?.role !== "admin" && defaultExecId) params.append("assigned_to_id", defaultExecId); }
      const r = await api(`/call-logs?${params}`);
      setData(r.data);
      setPagination(r.pagination);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  }, [filters, callView, amFrom, amTo, showDoneRenewals, advApplied]);

  const auto = useAutoRefresh(() => fetchData(pagination?.page || 1), showForm);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Open a call in the edit form (loads the customer's products/licenses too)
  // Mirrors the server rule: an Open call stays editable; a Completed/Cancelled
  // one locks after a short grace period for executives.
  const BACKDATE_GRACE_DAYS = 2;
  const backdateLock = (row) => {
    if (!row) return null;
    if (user?.role === "admin" || user?.role === "manager") return null;
    const unlocked = row.unlock_until && new Date(row.unlock_until) > new Date();
    if (unlocked) return null;
    // Open / in-progress calls are live work and are always editable, whatever the
    // is_locked flag says (many historic records carry it from the old system).
    if (!["Completed", "Cancelled"].includes(row.call_status)) return null;
    // Age (from the LAST SAVE) decides, and is checked before the is_locked flag:
    // that flag is TRUE on most records by default, so checking it first wrongly
    // locked calls the executive had only just entered.
    const touched = new Date(row.updated_at || row.created_at || row.call_date);
    const age = Math.floor((Date.now() - touched.getTime()) / 86400000);
    if (age > BACKDATE_GRACE_DAYS) return `This call was last saved on ${formatDateDMY(touched)} and is now closed for editing (older than ${BACKDATE_GRACE_DAYS} days). Please ask an administrator to unlock it.`;
    return null;
  };

  const openEdit = async (row) => {
    const locked = backdateLock(row);
    if (locked) { setToast({ message: locked, type: "error" }); return; }
    // Open the form straight away (it used to wait for the licence list first)
    setEditItem(row);
    setForm({ ...row, customer_products: [], customer_product_id: row.customer_product_id ? String(row.customer_product_id) : "" });
    setShowForm(true);
    if (row.client_id) {
      try {
        const r = await api(`/customer-products/customer/${row.client_id}`);
        const custProducts = r.data || [];
        const matchedCp = row.license_number ? custProducts.find(cp => cp.license_number === row.license_number) : (custProducts.length === 1 ? custProducts[0] : null);
        setForm(f => (f.id === row.id) ? { ...f, customer_products: custProducts, customer_product_id: f.customer_product_id || (matchedCp?.id?.toString() || "") } : f);
      } catch (e) {}
    }
    // The list on screen may be a minute old — if someone changed this call since, show their version
    try {
      const fresh = (await api(`/call-logs/${row.id}`)).data;
      if (fresh && fresh.updated_at && String(fresh.updated_at) !== String(row.updated_at)) {
        setEditItem(fresh);
        setForm(f => (f.id === row.id) ? { ...fresh, customer_products: f.customer_products, customer_product_id: f.customer_product_id } : f);
        setToast({ message: "This call was updated by someone else — showing the latest version.", type: "info" });
      }
    } catch (e) {}
  };

  // If we arrived here from a Dashboard/Reports drill-down, open that call's edit
  // form, and return to the origin screen when the form is closed (save or cancel).
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => {
    if (pendingCall) { openEdit(pendingCall); setReturnTo(pendingReturn || "dashboard"); if (setPendingCall) setPendingCall(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCall]);
  // Before anything is drawn: a form opened from another screen (e.g. a Dashboard list)
  // goes straight back there when it closes — no frame of this screen's own list in between
  useLayoutEffect(() => {
    if (!showForm && returnTo) { const t = returnTo; setReturnTo(null); if (setActiveScreen) setActiveScreen(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  // ---- Field check-in / check-out (time + GPS) ----
  const [geoBusy, setGeoBusy] = useState(false);
  const mapLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
  const getLoc = () => new Promise((resolve) => {
    if (!navigator.geolocation) { resolve({ lat: null, lng: null }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
  const ensureConsent = () => {
    if (localStorage.getItem("geo_consent") === "yes") return true;
    const ok = window.confirm("This records your current location for this visit (at check-in and check-out). Do you consent?");
    if (ok) localStorage.setItem("geo_consent", "yes");
    return ok;
  };
  const doCheckIn = async () => {
    if (!editItem || !ensureConsent()) return;
    setGeoBusy(true);
    const loc = await getLoc();
    try {
      const r = await api(`/call-logs/${editItem.id}/checkin`, { method: "POST", body: loc });
      setForm(f => ({ ...f, checkin_time: r.data.checkin_time, checkin_lat: r.data.checkin_lat, checkin_lng: r.data.checkin_lng, call_start_time: r.data.call_start_time, updated_at: r.data.updated_at || f.updated_at }));
      setToast({ message: loc.lat ? "Checked in with location!" : "Checked in (location unavailable — time recorded).", type: "success" });
      fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setGeoBusy(false);
  };
  const doCheckOut = async () => {
    if (!editItem) return;
    setGeoBusy(true);
    const loc = await getLoc();
    try {
      const r = await api(`/call-logs/${editItem.id}/checkout`, { method: "POST", body: loc });
      setForm(f => ({ ...f, updated_at: r.data.updated_at || f.updated_at, checkout_time: r.data.checkout_time, checkout_lat: r.data.checkout_lat, checkout_lng: r.data.checkout_lng, call_end_time: r.data.call_end_time, hours_spent: r.data.hours_spent }));
      setToast({ message: loc.lat ? "Checked out with location!" : "Checked out (location unavailable — time recorded).", type: "success" });
      fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setGeoBusy(false);
  };
  const doUndoCheckIn = async () => {
    if (!editItem || !window.confirm("Undo check-in? This clears the check-in time, location, and the Start Time it set.")) return;
    try {
      const ru = await api(`/call-logs/${editItem.id}/undo-checkin`, { method: "POST", body: {} });
      setForm(f => ({ ...f, updated_at: ru?.data?.updated_at || f.updated_at, checkin_time: null, checkin_lat: null, checkin_lng: null, call_start_time: "" }));
      setToast({ message: "Check-in undone.", type: "success" });
      fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };
  const doUndoCheckOut = async () => {
    if (!editItem || !window.confirm("Undo check-out? This clears the check-out time, location, End Time, and Hours.")) return;
    try {
      const ru = await api(`/call-logs/${editItem.id}/undo-checkout`, { method: "POST", body: {} });
      setForm(f => ({ ...f, updated_at: ru?.data?.updated_at || f.updated_at, checkout_time: null, checkout_lat: null, checkout_lng: null, call_end_time: "", hours_spent: null }));
      setToast({ message: "Check-out undone.", type: "success" });
      fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // Debounce search — refetch 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => { fetchData(); }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const ctSaveRef = useRef(null);
  const handleSave = async () => {
    // Text still in a FastText box (typed less than 250 ms ago, e.g. Alt+S right
    // after typing): hand it to the form first, then save on the next tick.
    if (flushFastText()) { setTimeout(() => ctSaveRef.current && ctSaveRef.current(), 40); return; }
    try {
      // Call Date cannot be in the future
      const _today = (() => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); })();
      if (form.call_date && toISODate(form.call_date) > _today) {
        setToast({ message: `Call Date can't be in the future. Today is ${formatDateDMY(_today)} — please correct the Call Date.`, type: "error" });
        return;
      }
      // Time validation — end time must be after start time
      if (form.call_start_time && form.call_end_time && form.call_end_time <= form.call_start_time) {
        setToast({ message: "End Time cannot be earlier than or equal to Start Time. Please correct the time.", type: "error" });
        return;
      }
      // Field-visit rule (Option A): if the logged-in executive is the one this call is
      // assigned to, they must Check In to save, and Check In + Check Out to set a
      // leaving-site status. The assigner and admins are exempt (they can schedule freely).
      if (editItem && form.assigned_to_id && defaultExecId && String(form.assigned_to_id) === String(defaultExecId) && user?.role !== "admin") {
        if (!form.checkin_time) {
          setToast({ message: "Please Check In first (top of this form) — your time on site is recorded from check-in.", type: "error" });
          return;
        }
        if (["Completed", "On Hold", "Cancelled"].includes(form.call_status) && !form.checkout_time) {
          setToast({ message: `Please Check Out before setting this visit to ${form.call_status} — this records your full time on site (including waiting).`, type: "error" });
          return;
        }
      }
      // Overlapping-time warning: does this executive already have a call on this
      // date whose times clash? Overlaps can be legitimate (a quick remote call during
      // an onsite visit), so this warns rather than blocks.
      if (form.call_date && form.call_start_time && form.call_end_time) {
        const execId = form.assigned_to_id || form.executive_id;
        if (execId) {
          try {
            const d = toISODate(form.call_date);
            const p = new URLSearchParams({ date_from: d, date_to: d, limit: "300" });
            const r = await api(`/call-logs?${p}`);
            const mine = (r.data || []).filter(c =>
              String(c.assigned_to_id || c.executive_id) === String(execId) &&
              (!editItem || c.id !== editItem.id) && c.call_start_time && c.call_end_time);
            const s1 = String(form.call_start_time).slice(0,5), e1 = String(form.call_end_time).slice(0,5);
            const clash = mine.find(c => {
              const s2 = String(c.call_start_time).slice(0,5), e2 = String(c.call_end_time).slice(0,5);
              return s1 < e2 && s2 < e1;                       // the two periods intersect
            });
            if (clash) {
              const who = clash.assigned_to_name || clash.executive_name || "This executive";
              const ok = window.confirm(
                `⚠️ Overlapping times\n\n${who} already has ticket #${clash.ticket_number} (${clash.client_name || ""}) ` +
                `from ${formatTime12hr(String(clash.call_start_time).slice(0,5))} to ${formatTime12hr(String(clash.call_end_time).slice(0,5))} on ${formatDateDMY(form.call_date)}.\n\n` +
                `This call is ${formatTime12hr(s1)} to ${formatTime12hr(e1)}, which overlaps.\n\nSave anyway?`);
              if (!ok) return;
            }
          } catch (e) { /* the check is advisory — never block saving because of it */ }
        }
      }

      const { _voice, ...formBody } = form;
      const keepVoice = (callId) => { if (_voice && callId) api("/voice/notes", { method: "POST", body: { ..._voice, call_id: callId } }).catch(() => {}); };
      if (editItem) {
        try {
          await api(`/call-logs/${editItem.id}`, { method: "PUT", body: { ...formBody, expected_updated_at: form.updated_at } });
        } catch (e) {
          if (e.status !== 409) throw e;
          // Someone else saved this call after you opened it
          if (window.confirm(`${e.message}\n\nOK = save YOUR changes anyway (theirs will be overwritten)\nCancel = load THEIR latest version instead`)) {
            await api(`/call-logs/${editItem.id}`, { method: "PUT", body: { ...formBody, force_overwrite: true } });
          } else {
            const fresh = await api(`/call-logs/${editItem.id}`);
            setEditItem(fresh.data);
            setForm(f => ({ ...fresh.data, customer_products: f.customer_products, customer_product_id: f.customer_product_id }));
            setToast({ message: "Loaded the latest version — check it, then save again if needed.", type: "info" });
            return;
          }
        }
        keepVoice(editItem.id);
        setToast({ message: `Updated! Ticket# ${editItem.ticket_number}`, type: "success" });
      } else {
        const r = await api("/call-logs", { method: "POST", body: formBody });
        keepVoice(r.data?.id);
        const ticketNum = r.data?.ticket_number || "";
        setToast({ message: `Call logged successfully! Ticket# ${ticketNum}`, type: "success" });
      }
      setShowForm(false); setEditItem(null); setForm({ call_status: "Open", executive_id: defaultExecId, log_date: new Date().toISOString().slice(0,10), call_date: new Date().toISOString().slice(0,10) }); fetchData();
      window.dispatchEvent(new Event("reminders-refresh"));
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  ctSaveRef.current = handleSave;

  // Listen for Alt+S save shortcut
  useEffect(() => {
    const doSave = () => { if (showForm) handleSave(); };
    document.addEventListener("do-save", doSave);
    return () => document.removeEventListener("do-save", doSave);
  });

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Call Tracker</h1>
        <div style={styles.flexRow}>
          <button style={styles.btn("primary")} onClick={() => { setEditItem(null); setForm({ call_status: "Open", executive_id: defaultExecId, log_date: new Date().toISOString().slice(0,10), call_date: new Date().toISOString().slice(0,10) }); setShowForm(true); }}>+ Log New Call <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+N)</span></button>
          <button style={styles.btn("success", "sm")} onClick={exportAllCalls}>📊 Export <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+X)</span></button>
          <button style={styles.btn("ghost", "sm")} onClick={blankExportCalls}>📄 Blank <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+B)</span></button>
          <label style={{ ...styles.btn("ghost", "sm"), cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
            📥 Import CSV <input type="file" accept=".csv,.txt" onChange={async (e) => {
              const file = e.target.files[0]; if (!file) return;
              const reader = new FileReader();
              reader.onload = async (evt) => {
                let text = evt.target.result;
                if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
                text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
                const lines = text.split("\n").filter(l => l.trim() && !l.trim().startsWith("#") && !l.trim().toLowerCase().startsWith("sep="));
                if (lines.length < 2) { setToast({ message: "File empty or no data rows.", type: "error" }); return; }
                const parseCSV = (line) => { const r=[]; let c="",q=false; for(let i=0;i<line.length;i++){const ch=line[i]; if(ch==='"'){if(q&&line[i+1]==='"'){c+='"';i++}else q=!q} else if(ch===','&&!q){r.push(c.trim());c=""}else c+=ch} r.push(c.trim()); return r; };
                
                // Parse headers and map columns
                const headers = parseCSV(lines[0]).map(h => h.toLowerCase().replace(/[()]/g, "").trim());
                const col = (name) => {
                  const idx = headers.findIndex(h => h.includes(name));
                  return idx;
                };
                const iCall = col("call date"); const iExec = col("executive"); const iPrin = col("principal");
                const iClient = col("customer") >= 0 ? col("customer") : col("client");
                const iProd = col("product"); const iTdl = col("tdl"); const iStart = col("start time");
                const iEnd = col("end time"); const iType = col("call type"); const iReg = col("regarding");
                const iDetails = col("call detail"); const iStatus = col("status"); const iLoc = col("location");
                const iExpiry = col("subscription"); const iBilled = col("billed"); const iContact = col("contact person");
                const iVersion = col("release") >= 0 ? col("release") : col("version");

                let imported=0, failed=0, errors=[];
                for (let i=1; i<lines.length; i++) {
                  const vals = parseCSV(lines[i]);
                  if (vals.every(v => !v.trim())) continue;
                  try {
                    const get = (idx) => idx >= 0 && idx < vals.length ? vals[idx].trim() : "";
                    const callDate = get(iCall); const execName = get(iExec); const prinName = get(iPrin);
                    const clientName = get(iClient); const prodName = get(iProd); const tdlType = get(iTdl);
                    const startTime = get(iStart); const endTime = get(iEnd); const callType = get(iType);
                    const regarding = get(iReg); const details = get(iDetails); const status = get(iStatus);
                    const locName = get(iLoc); const subExpiry = get(iExpiry); const billedThrough = get(iBilled);
                    const contactPerson = get(iContact); const version = get(iVersion);

                    if (!callDate || !execName || !clientName || !callType) { failed++; errors.push(`Row ${i+1}: Missing required fields`); continue; }

                    const execMatch = masters.executives?.find(e => e.name.toLowerCase().trim() === execName.toLowerCase().trim());
                    const prinMatch = prinName ? masters.principals?.find(p => p.company_name.toLowerCase().trim() === prinName.toLowerCase().trim()) : null;
                    const clientMatch = masters.clients?.find(c => c.company_name.toLowerCase().trim() === clientName.toLowerCase().trim());
                    const prodMatch = prodName ? masters.products?.find(p => p.name.toLowerCase().trim() === prodName.toLowerCase().trim()) : null;
                    const ctMatch = masters.callTypes?.find(c => c.name.toLowerCase().trim() === callType.toLowerCase().trim());
                    const locMatch = locName ? masters.locations?.find(l => l.name.toLowerCase().trim() === locName.toLowerCase().trim()) : null;

                    if (!execMatch) { failed++; errors.push(`Row ${i+1}: Executive "${execName}" not found`); continue; }
                    if (!clientMatch) { failed++; errors.push(`Row ${i+1}: Customer "${clientName}" not found`); continue; }
                    if (!ctMatch) { failed++; errors.push(`Row ${i+1}: Call Type "${callType}" not found`); continue; }

                    const parseDate = (d) => {
                      if (!d) return null;
                      if (d.includes("/")) { const p = d.split("/"); if (p.length === 3 && p[2].length === 4) return `${p[2]}-${p[1].padStart(2,"0")}-${p[0].padStart(2,"0")}`; }
                      if (d.includes("-") && d.length >= 10) return d.slice(0, 10);
                      return d;
                    };

                    await api("/call-logs", { method: "POST", body: {
                      call_date: parseDate(callDate), executive_id: execMatch.id, principal_id: prinMatch?.id || null, client_id: clientMatch.id,
                      product_id: prodMatch?.id || null, tdl_type: tdlType || null,
                      call_start_time: parseTimeTo24hr(startTime), call_end_time: parseTimeTo24hr(endTime), call_type_id: ctMatch.id,
                      regarding: regarding || null, call_details: details || null, call_status: status || "Open", location_id: locMatch?.id || null,
                      subscription_expiry: parseDate(subExpiry), billed_through: billedThrough || null,
                      contact_person_called: contactPerson || null, software_version: version || null
                    }});
                    imported++;
                  } catch (err) { failed++; errors.push(`Row ${i+1}: ${err.message}`); }
                }
                const errMsg = errors.length > 0 ? `\n\nErrors:\n${errors.slice(0,5).join("\n")}${errors.length > 5 ? `\n...and ${errors.length-5} more` : ""}` : "";
                setToast({ message: `Import done! ${imported} created, ${failed} failed.${errMsg}`, type: imported > 0 ? "success" : "error" });
                fetchData();
              };
              reader.readAsText(file, "UTF-8"); e.target.value = "";
            }} style={{ display: "none" }} />
          </label>
        </div>
      </div>

      {/* Selection action bar */}
      {selectedIds.size > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 16px", marginBottom: 8, background: "#e8eefb", borderRadius: 8, border: `1px solid ${colors.primary}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: colors.primary }}>{selectAllPages ? `All ${pagination?.total || "all"} records selected` : `${selectedIds.size} record(s) selected on this page`}</span>
            <button style={styles.btn("success", "sm")} onClick={async () => {
              if (selectAllPages) {
                setToast({ message: "Fetching all records...", type: "success" });
                const params = new URLSearchParams({ limit: 100000 });
                Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
                const r = await api(`/call-logs?${params}`);
                exportCallsData(r.data);
              } else { exportSelectedCalls(); }
            }}>📊 Export {selectAllPages ? "All" : "Selected"}</button>
            <button style={styles.btn("ghost", "sm")} onClick={() => { setSelectedIds(new Set()); setSelectAllPages(false); }}>✕ Clear</button>
          </div>
          {!selectAllPages && selectedIds.size === data.length && pagination?.total > data.length && (
            <div style={{ fontSize: 12, color: colors.primary, cursor: "pointer", textDecoration: "underline" }}
              onClick={() => setSelectAllPages(true)}>
              Select all {pagination.total} records across all pages?
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 0, marginBottom: 12, ...(isMobileNow() ? { overflowX: "auto", WebkitOverflowScrolling: "touch", flexWrap: "nowrap", paddingBottom: 2, maxWidth: "100%", minWidth: 0 } : {}) }}>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: callView === "all" ? `3px solid ${colors.primary}` : "3px solid transparent", background: callView === "all" ? "#e8eefb" : "#f8fafc", color: callView === "all" ? colors.primary : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setCallView("all")}>📋 All Calls</button>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: callView === "next_actions" ? `3px solid ${colors.danger}` : "3px solid transparent", background: callView === "next_actions" ? "#fef2f2" : "#f8fafc", color: callView === "next_actions" ? colors.danger : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setCallView("next_actions")}>🔔 Next Actions Due</button>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: callView === "assigned_to_me" ? `3px solid #7c3aed` : "3px solid transparent", background: callView === "assigned_to_me" ? "#f5f3ff" : "#f8fafc", color: callView === "assigned_to_me" ? "#7c3aed" : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setCallView("assigned_to_me")}>👤 {user?.role === "admin" ? "Assigned Calls" : "Assigned to Me"}</button>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: callView === "assigned_by_me" ? `3px solid #0891b2` : "3px solid transparent", background: callView === "assigned_by_me" ? "#ecfeff" : "#f8fafc", color: callView === "assigned_by_me" ? "#0891b2" : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setCallView("assigned_by_me")}>📤 Assigned by Me</button>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: callView === "schedule" ? `3px solid #16a34a` : "3px solid transparent", background: callView === "schedule" ? "#f0fdf4" : "#f8fafc", color: callView === "schedule" ? "#16a34a" : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setCallView("schedule")}>🗓️ Schedule</button>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: callView === "renewals" ? `3px solid #0891b2` : "3px solid transparent", background: callView === "renewals" ? "#ecfeff" : "#f8fafc", color: callView === "renewals" ? "#0891b2" : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setCallView("renewals")}>🔄 License &amp; Subscription Renewals</button>
      </div>

      {callView === "schedule" ? (
        <ScheduleView masters={masters} defaultExecId={defaultExecId} />
      ) : callView === "assigned_to_me" ? (
        <div style={styles.card}>
          {user?.role !== "admin" && !defaultExecId && (
            <div style={{ padding: "10px 14px", marginBottom: 10, borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: 13, fontWeight: 600 }}>
              ⚠ Your login is not linked to an executive, so nothing assigned to you can be shown. Ask the admin to link it in User Management, then sign out and sign in again.
            </div>
          )}
          {myLeadCount > 0 && (
            <div onClick={() => { __leadView = "assigned_to_me"; setActiveScreen("leads"); }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", marginBottom: 10, borderRadius: 8, background: "#f5f3ff", border: "1px solid #c4b5fd", color: "#6d28d9", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              📌 {user?.role === "admin" ? `${myLeadCount} open lead${myLeadCount === 1 ? " is" : "s are"} assigned to executives` : `You also have ${myLeadCount} open lead${myLeadCount === 1 ? "" : "s"} assigned to you`}
              <span style={{ marginLeft: "auto", textDecoration: "underline" }}>Open Leads →</span>
            </div>
          )}
          <div style={styles.filterBar}>
            <div><label style={styles.label}>Search</label><SearchInput style={{ ...styles.input, minWidth: 180 }} placeholder="Ticket#, Customer, Phone, Email..." value={filters.search || ""} onSearch={(t) => setFilters(f => ({ ...f, search: t }))} /></div>
            <div><label style={styles.label}>Next Action From</label><DateInput value={amFrom} onChange={setAmFrom} /></div>
            <div><label style={styles.label}>Next Action To</label><DateInput value={amTo} onChange={setAmTo} /></div>
            {(amFrom || amTo) && <button style={styles.btn("ghost", "sm")} onClick={() => { setAmFrom(""); setAmTo(""); }}>Clear</button>}
          </div>
          <SortBar sorts={sorts} clearSort={clearSort} labels={tabSortLabels} />
          {isMobile ? (
            <MobileCards rows={sortedRows} onOpen={openEdit}
              primary={c => c.client_name || "—"}
              secondary={c => `#${c.ticket_number} · ${c.call_type_name || "—"}`}
              badge={c => ({ text: c.call_status, color: statusColorOf(c.call_status) })}
              meta={c => [["Next action", nextActionText(c)], ["Call date", formatDateDMY(c.call_date)],
                          ["Contacts", contactNums(c)], ["Spoke to", c.contact_person_called || c.contact_person],
                          ["Address", custAddress(c.client_id, masters)], ["Email", contactMails(c)],
                          user?.role === "admin" ? ["Assigned to", c.assigned_to_name] : ["Assigned by", c.executive_name]]}
              empty={`No calls assigned to you${(amFrom || amTo) ? " in this date range" : ""}.`} />
          ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
                {(user?.role === "admin"
                  ? [["Ticket #","ticket_number"],["Customer","client_name"],["Contacts","contacts"],["Email","emails"],["Spoke To","spoke_to"],["Assigned By","executive_name"],["Assigned To","assigned_to_name"],["Next Action Date & Time","next_action"],["Call Date","call_date"],["Call Type","call_type_name"],["Status","call_status"]]
                  : [["Ticket #","ticket_number"],["Customer","client_name"],["Contacts","contacts"],["Email","emails"],["Spoke To","spoke_to"],["Next Action Date & Time","next_action"],["Call Date","call_date"],["Call Type","call_type_name"],["Assigned By","executive_name"],["Status","call_status"]]
                ).map(([h, k]) => <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }} />)}
              </tr></thead>
              <tbody>
                {sortedRows.map(c => (
                  <tr key={c.id} onClick={() => openEdit(c)} style={{ borderTop: `1px solid ${colors.border}`, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{c.ticket_number}</td>
                    <td style={styles.td}>{c.client_name || "—"}</td>
                    <td style={styles.td}>{contactNums(c)}</td>
                    <td style={styles.td}>{contactMails(c)}</td>
                    <td style={styles.td}>{c.contact_person_called || contactPersons(c)}</td>
                    {user?.role === "admin" ? (<>
                      <td style={styles.td}>{c.executive_name || "—"}</td>
                      <td style={{ ...styles.td, fontWeight: 600, color: "#7c3aed" }}>{c.assigned_to_name || "—"}</td>
                      <td style={styles.td}>{c.next_action_date ? (formatDateDMY(c.next_action_date) + (c.next_action_time ? " " + formatTime12hr(String(c.next_action_time).slice(0,5)) : "")) : "—"}</td>
                      <td style={styles.td}>{formatDateDMY(c.call_date)}</td>
                      <td style={styles.td}>{c.call_type_name || "—"}</td>
                    </>) : (<>
                      <td style={styles.td}>{c.next_action_date ? (formatDateDMY(c.next_action_date) + (c.next_action_time ? " " + formatTime12hr(String(c.next_action_time).slice(0,5)) : "")) : "—"}</td>
                      <td style={styles.td}>{formatDateDMY(c.call_date)}</td>
                      <td style={styles.td}>{c.call_type_name || "—"}</td>
                      <td style={styles.td}>{c.executive_name || "—"}</td>
                    </>)}
                    <td style={styles.td}><span style={{ background: (c.call_status === "Completed" ? colors.success : c.call_status === "Cancelled" ? colors.textLight : "#d97706") + "22", color: c.call_status === "Completed" ? colors.success : c.call_status === "Cancelled" ? colors.textLight : "#d97706", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{c.call_status}</span></td>
                  </tr>
                ))}
                {data.length === 0 && <tr><td colSpan={user?.role === "admin" ? 11 : 10} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>{user?.role === "admin" ? "No assigned calls" : "No calls assigned to you"}{(amFrom || amTo) ? " in this date range" : ""}.</td></tr>}
              </tbody>
            </table>
          </div>
          )}
          <Pagination pagination={pagination} onPageChange={fetchData} />
        </div>
      ) : callView === "assigned_by_me" ? (
        <div style={styles.card}>
          <div style={styles.filterBar}>
            <div><label style={styles.label}>Search</label><SearchInput style={{ ...styles.input, minWidth: 180 }} placeholder="Ticket#, Customer, Phone, Email..." value={filters.search || ""} onSearch={(t) => setFilters(f => ({ ...f, search: t }))} /></div>
            <div><label style={styles.label}>Next Action From</label><DateInput value={amFrom} onChange={setAmFrom} /></div>
            <div><label style={styles.label}>Next Action To</label><DateInput value={amTo} onChange={setAmTo} /></div>
            {(amFrom || amTo) && <button style={styles.btn("ghost", "sm")} onClick={() => { setAmFrom(""); setAmTo(""); }}>Clear</button>}
          </div>
          <SortBar sorts={sorts} clearSort={clearSort} labels={tabSortLabels} />
          {isMobile ? (
            <MobileCards rows={sortedRows} onOpen={openEdit}
              primary={c => c.client_name || "—"}
              secondary={c => `#${c.ticket_number} · ${c.call_type_name || "—"}`}
              badge={c => ({ text: c.call_status, color: statusColorOf(c.call_status) })}
              meta={c => [["Assigned to", c.assigned_to_name], ["Next action", nextActionText(c)],
                          ["Call date", formatDateDMY(c.call_date)], ["Contacts", contactNums(c)],
                          ["Address", custAddress(c.client_id, masters)],
                          ["Spoke to", c.contact_person_called || c.contact_person], ["Email", contactMails(c)]]}
              empty={`You haven't assigned any calls${(amFrom || amTo) ? " in this date range" : ""}.`} />
          ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
                {[["Ticket #","ticket_number"],["Customer","client_name"],["Contacts","contacts"],["Email","emails"],["Spoke To","spoke_to"],["Assigned To","assigned_to_name"],["Next Action Date & Time","next_action"],["Call Date","call_date"],["Call Type","call_type_name"],["Status","call_status"]]
                  .map(([h, k]) => <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }} />)}
              </tr></thead>
              <tbody>
                {sortedRows.map(c => (
                  <tr key={c.id} onClick={() => openEdit(c)} style={{ borderTop: `1px solid ${colors.border}`, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{c.ticket_number}</td>
                    <td style={styles.td}>{c.client_name || "—"}</td>
                    <td style={styles.td}>{contactNums(c)}</td>
                    <td style={styles.td}>{contactMails(c)}</td>
                    <td style={styles.td}>{c.contact_person_called || contactPersons(c)}</td>
                    <td style={{ ...styles.td, fontWeight: 600, color: "#0891b2" }}>{c.assigned_to_name || "—"}</td>
                    <td style={styles.td}>{c.next_action_date ? (formatDateDMY(c.next_action_date) + (c.next_action_time ? " " + formatTime12hr(String(c.next_action_time).slice(0,5)) : "")) : "—"}</td>
                    <td style={styles.td}>{formatDateDMY(c.call_date)}</td>
                    <td style={styles.td}>{c.call_type_name || "—"}</td>
                    <td style={styles.td}><span style={{ background: (c.call_status === "Completed" ? colors.success : c.call_status === "Cancelled" ? colors.textLight : "#d97706") + "22", color: c.call_status === "Completed" ? colors.success : c.call_status === "Cancelled" ? colors.textLight : "#d97706", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{c.call_status}</span></td>
                  </tr>
                ))}
                {data.length === 0 && <tr><td colSpan={10} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>You haven't assigned any calls{(amFrom || amTo) ? " in this date range" : ""}.</td></tr>}
              </tbody>
            </table>
          </div>
          )}
          <Pagination pagination={pagination} onPageChange={fetchData} />
        </div>
      ) : callView === "renewals" ? (
        <div style={styles.card}>
          <div style={{ ...styles.filterBar, alignItems: "center" }}>
            <div><label style={styles.label}>Search</label><SearchInput style={{ ...styles.input, minWidth: 200 }} placeholder="Ticket#, Customer, License#..." value={filters.search || ""} onSearch={(t) => setFilters(f => ({ ...f, search: t }))} /></div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: colors.textLight, cursor: "pointer", alignSelf: "flex-end", paddingBottom: 8 }}>
              <input type="checkbox" checked={showDoneRenewals} onChange={e => setShowDoneRenewals(e.target.checked)} /> Show completed
            </label>
            <div style={{ fontSize: 12, color: colors.textLight, alignSelf: "flex-end", paddingBottom: 8 }}>Renewal follow-ups {user?.role === "admin" ? "(all executives)" : "assigned to you"}. Closed automatically when the licence is renewed.</div>
          </div>
          <SortBar sorts={sorts} clearSort={clearSort} labels={tabSortLabels} />
          {isMobile ? (
            <MobileCards rows={sortedRows} onOpen={openEdit}
              primary={c => c.client_name || "—"}
              secondary={c => (c.regarding ? c.regarding.replace(/^License renewal [—-] /, "") : (c.license_number || `#${c.ticket_number}`))}
              badge={c => ({ text: c.call_status, color: statusColorOf(c.call_status) })}
              meta={c => [["Renewal due", c.next_action_date ? formatDateDMY(c.next_action_date) : ""],
                          ["Ticket", c.ticket_number], ["Contacts", contactNums(c)], ["Email", contactMails(c)],
                          user?.role === "admin" ? ["Assigned to", c.assigned_to_name] : ["Assigned by", c.executive_name]]}
              empty={`No renewal follow-ups${user?.role === "admin" ? "" : " assigned to you"}.`} />
          ) : (
          <ScrollBox>
            {(() => {
              const openIds = sortedRows.filter(c => !["Completed", "Cancelled"].includes(c.call_status)).map(c => c.id);
              const allTicked = openIds.length > 0 && openIds.every(id => renSel.has(id));
              return openIds.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "8px 12px", marginBottom: 8, background: renSel.size ? "#fef2f2" : "#f8fafc", border: `1px solid ${renSel.size ? "#fecaca" : colors.border}`, borderRadius: 8, fontSize: 13 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={allTicked} onChange={() => setRenSel(allTicked ? new Set() : new Set(openIds))} />
                    Select all shown ({openIds.length})
                  </label>
                  {renSel.size > 0 && (<>
                    <b style={{ color: colors.danger }}>{renSel.size} selected</b>
                    <button style={styles.btn("danger", "sm")} onClick={cancelRenewals}>✕ Cancel selected</button>
                    <button style={styles.btn("ghost", "sm")} onClick={() => setRenSel(new Set())}>Clear</button>
                  </>)}
                </div>
              );
            })()}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 800 }}>
              <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
                {(user?.role === "admin"
                  ? [["Ticket #","ticket_number"],["Customer","client_name"],["Contacts","contacts"],["Email","emails"],["License / Product","regarding"],["Next Call / Due","next_action"],["Assigned By","executive_name"],["Assigned To","assigned_to_name"],["Status","call_status"]]
                  : [["Ticket #","ticket_number"],["Customer","client_name"],["Contacts","contacts"],["Email","emails"],["License / Product","regarding"],["Next Call / Due","next_action"],["Assigned By","executive_name"],["Status","call_status"]]
                ).map(([h, k], hi) => <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={{ ...styles.td, fontWeight: 700, color: colors.textLight, position: "sticky", top: 0, background: "#f8fafc", zIndex: 3, ...(hi === 1 ? { left: 0, zIndex: 4, minWidth: 150 } : {}) }} />)}
                <th style={{ ...styles.td, position: "sticky", top: 0, background: "#f8fafc", zIndex: 3 }}></th>
              </tr></thead>
              <tbody>
                {sortedRows.map(c => (
                  <tr key={c.id} onClick={() => openEdit(c)} style={{ borderTop: `1px solid ${colors.border}`, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ ...styles.td, fontWeight: 700, whiteSpace: "nowrap" }} onClick={e => e.stopPropagation()}>
                      {!["Completed", "Cancelled"].includes(c.call_status) && (
                        <input type="checkbox" style={{ marginRight: 8, verticalAlign: "middle" }} checked={renSel.has(c.id)}
                          onChange={() => setRenSel(prev => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })} />
                      )}
                      <span style={{ cursor: "pointer" }} onClick={() => openEdit(c)}>{c.ticket_number}</span>
                    </td>
                    <td style={{ ...styles.td, position: "sticky", left: 0, background: "#fff", zIndex: 1, minWidth: 150 }}>{c.client_name || "—"}</td>
                    <td style={styles.td}>{contactNums(c)}</td>
                    <td style={styles.td}>{contactMails(c)}</td>
                    <td style={styles.td}>{c.regarding ? c.regarding.replace(/^License renewal [—-] /, "") : (c.license_number || "—")}
                      {parseInt(c.thread_calls) > 0 && (
                        <div style={{ fontSize: 11, color: colors.textLight, marginTop: 3, maxWidth: 300 }}>
                          📞 <b>{c.thread_calls} call{parseInt(c.thread_calls) === 1 ? "" : "s"}</b>
                          {c.last_thread_call ? ` · last ${formatDateDMY(c.last_thread_call.date)}${c.last_thread_call.by ? " (" + c.last_thread_call.by + ")" : ""}: "${String(c.last_thread_call.note || "").slice(0, 80)}"` : ""}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{c.next_action_date ? formatDateDMY(c.next_action_date) : "—"}</td>
                    <td style={styles.td}>{c.executive_name || "—"}</td>
                    {user?.role === "admin" && <td style={{ ...styles.td, fontWeight: 600, color: "#0891b2" }}>{c.assigned_to_name || "—"}</td>}
                    <td style={styles.td}><span style={{ background: (c.call_status === "Completed" ? colors.success : c.call_status === "Cancelled" ? colors.textLight : "#d97706") + "22", color: c.call_status === "Completed" ? colors.success : c.call_status === "Cancelled" ? colors.textLight : "#d97706", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{c.call_status}</span></td>
                  
                    <td style={styles.td} onClick={e => e.stopPropagation()}>
                      {!["Completed", "Cancelled"].includes(c.call_status) && (
                        <button style={{ ...styles.btn("primary", "sm"), whiteSpace: "nowrap" }} onClick={(e) => logRenewalCall(c, e)}
                          title="Log one more call to this customer about this renewal — saved as its own call in this thread">📞 Log follow-up call</button>
                      )}
                    </td>
                  </tr>
                ))}
                {data.length === 0 && <tr><td colSpan={user?.role === "admin" ? 10 : 9} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>No renewal follow-ups{user?.role === "admin" ? "" : " assigned to you"}. Generate them from Reports → Licenses &amp; Renewals.</td></tr>}
              </tbody>
            </table>
          </ScrollBox>
          )}
          <Pagination pagination={pagination} onPageChange={fetchData} />
        </div>
      ) : (
      <div style={styles.card}>
        <RefreshStamp updatedAt={auto.updatedAt} onRefresh={auto.refreshNow} />
        <AdvancedFilters masters={masters} screen="calls" rows={advRows} setRows={setAdvRows}
          onApply={(r) => { setAdvRows(r); setAdvApplied(advParams(r, callFilterFields(masters, r))); }} />
        <MobileFilters count={Object.values(filters).filter(Boolean).length}>
        <div style={styles.filterBar}>
          <div><label style={styles.label}>Search</label><SearchInput style={{ ...styles.input, minWidth: isMobileNow() ? "100%" : 180 }} placeholder="Ticket#, Customer, Phone, License#, GST..." value={filters.search || ""} onSearch={(t) => setFilters(f => ({ ...f, search: t }))} /></div>
          <div><label style={styles.label}>From</label><DateInput value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} /></div>
          <div><label style={styles.label}>To</label><DateInput value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} /></div>
          <div><label style={styles.label}>Executive</label>
            <select style={styles.select} value={filters.executive_id || ""} onChange={(e) => setFilters({ ...filters, executive_id: e.target.value })}>
              <option value="">All</option>{masters.executives?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Client</label>
            <select style={styles.select} value={filters.client_id || ""} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}>
              <option value="">All</option>{masters.clients?.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Call Type</label>
            <select style={styles.select} value={filters.call_type_id || ""} onChange={(e) => setFilters({ ...filters, call_type_id: e.target.value })}>
              <option value="">All</option>{masters.callTypes?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Status</label>
            <select style={styles.select} value={filters.call_status || ""} onChange={(e) => setFilters({ ...filters, call_status: e.target.value })}>
              <option value="">All</option>
              {["Open", "In Progress", "Completed", "Escalated", "On Hold"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button style={styles.btn("ghost", "sm")} onClick={() => setFilters({})}>Clear</button>
        </div>
        </MobileFilters>

        {isMobile ? (
          <MobileCards rows={data} onOpen={openEdit}
            primary={c => c.client_name || "—"}
            secondary={c => `#${c.ticket_number} · ${c.call_type_name || "—"} · ${c.executive_name || ""}`}
            badge={c => ({ text: c.call_status, color: statusColorOf(c.call_status) })}
            meta={c => [["Call date", formatDateDMY(c.call_date)], ["Hours", c.hours_spent],
                        ["Product", c.product_name], ["Licence", c.license_number],
                        ["Next action", nextActionText(c)], ["Contacts", contactNums(c)]]}
            empty={callView === "next_actions" ? "No actions due!" : "No call records found."} />
        ) : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
        <table className="freeze-cols" style={{ ...styles.table, minWidth: 1100 }}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 36, textAlign: "center" }}>
                <input type="checkbox" checked={data.length > 0 && selectedIds.size === data.length} onChange={toggleSelectAll} style={{ cursor: "pointer" }} />
              </th>
              <th style={styles.th}>Ticket #</th><th style={styles.th}>Date</th><th style={styles.th}>Executive</th>
              <th style={styles.th}>Client</th><th style={styles.th}>Product</th><th style={styles.th}>License #</th><th style={styles.th}>Type</th>
              <th style={styles.th}>Start</th><th style={styles.th}>End</th>
              <th style={styles.th}>Hours</th><th style={styles.th}>Status</th><th style={styles.th}>Next Action</th><th style={{ ...styles.th, minWidth: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.id} style={{ background: selectedIds.has(row.id) ? "#f0f4ff" : "#fff", cursor: "pointer" }}
                onClick={async (e) => {
                  if (e.target.type === "checkbox" || e.target.tagName === "BUTTON" || e.target.closest("button")) return;
                  let custProducts = [];
                  if (row.client_id) { try { const r = await api(`/customer-products/customer/${row.client_id}`); custProducts = r.data; } catch(e) {} }
                  const matchedCp = row.license_number ? custProducts.find(cp => cp.license_number === row.license_number) : (custProducts.length === 1 ? custProducts[0] : null);
                  setEditItem(row); setForm({ ...row, customer_products: custProducts, customer_product_id: matchedCp?.id?.toString() || "" }); setShowForm(true);
                }}>
                <td style={{ ...styles.td, textAlign: "center" }}>
                  <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)} style={{ cursor: "pointer" }} />
                </td>
                <td style={{ ...styles.td, fontWeight: 700 }}>
                  {row.ticket_number}
                  {row.parent_ticket_number && (
                    <div style={{ fontSize: 10, fontWeight: 400, color: colors.textLight }}>↳ cont. of {row.parent_ticket_number}</div>
                  )}
                  <div style={{ fontSize: 10, fontWeight: 400, color: colors.primary, cursor: "pointer", textDecoration: "underline" }} onClick={() => openThread(row.id)}>thread</div>
                </td>
                <td style={styles.td}>{formatDateDMY(row.call_date)}</td>
                <td style={styles.td}>{row.executive_name}</td>
                <td style={styles.td}>{row.client_name}</td>
                <td style={styles.td}>{row.product_name || "—"}</td>
                <td style={{ ...styles.td, fontSize: 11 }}>{row.license_number || "—"}</td>
                <td style={styles.td}>{row.call_type_name}</td>
                <td style={styles.td}>{formatTime12hr(row.call_start_time)}</td>
                <td style={styles.td}>{formatTime12hr(row.call_end_time)}</td>
                <td style={styles.td}>{parseFloat(row.hours_spent || 0).toFixed(2)}</td>
                <td style={styles.td}><StatusBadge status={row.call_status} /></td>
                <td style={{ ...styles.td, fontSize: 11, color: row.next_action_date && new Date(row.next_action_date) < new Date(new Date().toDateString()) ? colors.danger : colors.text, fontWeight: row.next_action_date ? 600 : 400 }}>{row.next_action_date ? formatDateDMY(row.next_action_date) : "—"}</td>
                <td style={styles.td}>
                  <div style={styles.flexRow}>
                    {(row.is_locked || backdateLock(row)) ? <span title={backdateLock(row) || "Locked"} style={{ fontSize: 14 }}>🔒</span> : <span title="Unlocked" style={{ fontSize: 14 }}>🔓</span>}
                    {(user?.role === 'admin' || !row.is_locked) && (
                      <button style={styles.btn("primary", "sm")} onClick={async () => {
                        let custProducts = [];
                        if (row.client_id) {
                          try { const r = await api(`/customer-products/customer/${row.client_id}`); custProducts = r.data; } catch(e) {}
                        }
                        // Find matching customer_product by license_number
                        const matchedCp = row.license_number ? custProducts.find(cp => cp.license_number === row.license_number) : (custProducts.length === 1 ? custProducts[0] : null);
                        setEditItem(row);
                        setForm({ ...row, customer_products: custProducts, customer_product_id: matchedCp?.id?.toString() || "" });
                        setShowForm(true);
                      }}>Edit</button>
                    )}
                    {user?.role === 'admin' && row.is_locked && (
                      <button style={styles.btn("warning", "sm")} onClick={() => handleUnlock(row.id)}>Unlock</button>
                    )}
                    {user?.role === 'admin' && !row.is_locked && (
                      <button style={styles.btn("ghost", "sm")} onClick={() => handleLock(row.id)}>Lock</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={14} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>{callView === "next_actions" ? "No actions due!" : "No call records found."}</td></tr>}
          </tbody>
        </table>
        </div>
        )}
        <Pagination pagination={pagination} onPageChange={fetchData} />
      </div>
      )}

      {showForm && (
        <div style={styles.modal} onClick={() => setShowForm(false)}>
          <div style={{ ...styles.modalContent, maxWidth: 850 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>{editItem ? <span>Edit Call <span style={{ background: colors.primary, color: "#fff", padding: "3px 12px", borderRadius: 6, fontSize: 14 }}>{editItem.ticket_number}</span></span> : "Log New Call"}</h2>
            {!editItem && form.renewal_parent_id && (
              <div style={{ padding: "10px 14px", marginBottom: 12, borderRadius: 8, background: "#ecfeff", border: "1px solid #a5f3fc", fontSize: 12.5, color: "#155e75", lineHeight: 1.5 }}>
                📞 <b>Follow-up call for renewal ticket #{form._renewalTicket}</b>. Fill in the call's start / end time and what the customer said.
                Set <b>Next Action Date</b> to when you'll call next — it moves onto the renewal follow-up in the Renewals tab.
                This call never e-mails the customer.
              </div>
            )}
            {editItem && (
              <div style={{ border: `1px solid #16a34a`, borderRadius: 8, padding: 12, background: "#f0fdf4", marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#16a34a", marginBottom: 8 }}>📍 Field Check-In / Check-Out</div>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
                  {form.checkin_time ? (
                    <div style={{ fontSize: 12.5 }}>✅ Checked in{form.call_start_time ? " at " + formatTime12hr(String(form.call_start_time).slice(0,5)) : ""}
                      {form.checkin_lat ? <a href={mapLink(form.checkin_lat, form.checkin_lng)} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: colors.primary }}>View on map</a> : <span style={{ marginLeft: 8, color: colors.textLight }}>(no location)</span>}
                      {!form.checkout_time && <span onClick={doUndoCheckIn} style={{ marginLeft: 10, color: colors.danger, cursor: "pointer", textDecoration: "underline" }}>Undo</span>}
                    </div>
                  ) : (
                    <button type="button" style={styles.btn("success", "sm")} onClick={doCheckIn} disabled={geoBusy}>{geoBusy ? "Locating…" : "📍 Check In"}</button>
                  )}
                  {form.checkin_time && (form.checkout_time ? (
                    <div style={{ fontSize: 12.5 }}>🏁 Checked out{form.call_end_time ? " at " + formatTime12hr(String(form.call_end_time).slice(0,5)) : ""}
                      {form.checkout_lat ? <a href={mapLink(form.checkout_lat, form.checkout_lng)} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: colors.primary }}>View on map</a> : <span style={{ marginLeft: 8, color: colors.textLight }}>(no location)</span>}
                      {form.hours_spent ? <span style={{ marginLeft: 8, fontWeight: 600 }}>· {form.hours_spent}h</span> : null}
                      <span onClick={doUndoCheckOut} style={{ marginLeft: 10, color: colors.danger, cursor: "pointer", textDecoration: "underline" }}>Undo</span>
                    </div>
                  ) : (
                    <button type="button" style={styles.btn("primary", "sm")} onClick={doCheckOut} disabled={geoBusy}>{geoBusy ? "Locating…" : "📍 Check Out"}</button>
                  ))}
                </div>
                <div style={{ fontSize: 10.5, color: colors.textLight, marginTop: 6 }}>Your location is recorded for this visit at check-in and check-out. Check-in sets Start Time; check-out sets End Time and Hours.</div>
              </div>
            )}
            <div style={styles.grid(3)} ref={el => { if (el && !el.dataset.focused) { el.dataset.focused = "1"; const inp = el.querySelector("input"); if (inp) setTimeout(() => inp.focus(), 100); } }}>
              <div><label style={styles.label}>Log Date</label>
                {user?.role === "admin" ? (
                  <DateInput value={form.log_date || ""} onChange={(v) => setForm({ ...form, log_date: v })} />
                ) : (
                  <div style={{ ...styles.input, background: "#f0f4ff", fontWeight: 600, color: colors.primary }}>{form.log_date ? formatDateDMY(form.log_date) : "—"}</div>
                )}
              </div>
              <div><label style={styles.label}>Call Date *</label><DateInput value={form.call_date} onChange={(v) => setForm({ ...form, call_date: v })} />
                {(() => { const t = (() => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); })(); return (form.call_date && toISODate(form.call_date) > t) ? <div style={{ color: colors.danger, fontSize: 11, marginTop: 4 }}>⚠ Call date can't be in the future (today is {formatDateDMY(t)}).</div> : null; })()}
              </div>
              <div><label style={styles.label}>Executive *</label>
                <SearchableSelect options={masters.executives?.map(e => ({ value: e.id.toString(), label: e.name })) || []}
                  value={form.executive_id || ""} onChange={(v) => setForm({ ...form, executive_id: v })} placeholder="Search executive..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.executives)} createLabel="Create Executive" />
              </div>
              <div><label style={styles.label}>Principal Company</label>
                <SearchableSelect options={masters.principals?.map(p => ({ value: p.id.toString(), label: p.company_name })) || []}
                  value={form.principal_id || ""} onChange={(v) => setForm({ ...form, principal_id: v })} placeholder="Search principal..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.principals)} createLabel="Create Principal" />
              </div>
              <div><label style={styles.label}>Customer *</label>
                <SearchableSelect options={masters.clients?.map(c => { const sn = masters.clientSerials?.[c.id.toString()] || []; return { value: c.id.toString(), label: sn.length ? `${c.company_name} — ${sn.join(", ")}` : c.company_name }; }) || []}
                  value={form.client_id || ""} onChange={async (v) => {
                    const client = masters.clients?.find(c => c.id == v);
                    let custProducts = [];
                    if (v) {
                      try { const r = await api(`/customer-products/customer/${v}`); custProducts = r.data; } catch(e) {}
                    }
                    const autoProduct = custProducts.length === 1 ? custProducts[0] : null;
                    setForm({ ...form, client_id: v, customer_products: custProducts,
                      product_id: autoProduct ? autoProduct.product_id?.toString() : (form.product_id || ""),
                      customer_product_id: autoProduct ? autoProduct.id?.toString() : "",
                      subscription_expiry: autoProduct?.subscription_expiry || client?.subscription_expiry || form.subscription_expiry || "",
                      license_number: autoProduct?.license_number || "",
                      customer_phone: client?.phone || "", customer_email: client?.email || "",
                      contact_person: client?.contact_person || "", contact_phone: client?.contact_phone || "", contact_email: client?.contact_email || "",
                      billed_through: client?.partner_id?.toString() || form.billed_through || "" });
                  }} placeholder="Search customer..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.clients)} createLabel="Create Customer" />
              </div>
              {/* The selected customer's group — read-only, for reference */}
              <div><label style={styles.label}>Group</label>
                <input style={{ ...styles.input, background: "#f8fafc", color: colors.textLight }} readOnly
                  value={(() => {
                    const c = masters.clients?.find(x => String(x.id) === String(form.client_id));
                    if (!c) return "";
                    const g = masters.groups?.find(gr => String(gr.id) === String(c.group_id));
                    return g ? g.name : "— not in a group —";
                  })()} placeholder="Select a customer first" />
              </div>
              {form.client_id && (form.customer_phone || form.contact_person || form.contact_phone || form.customer_email || form.contact_email) && (
                <div style={{ gridColumn: "1 / -1", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 12px", background: "#f8fafc", fontSize: 12.5 }}>
                  <div style={{ fontWeight: 700, color: colors.textLight, marginBottom: 4 }}>📇 Customer Contacts</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px" }}>
                    {form.customer_phone && <div><b>Phone:</b> {form.customer_phone}</div>}
                    {form.contact_person && <div><b>Contact Person:</b> {form.contact_person}</div>}
                    {form.contact_phone && <div><b>Contact No.:</b> {form.contact_phone}</div>}
                    {form.customer_email && <div><b>Email:</b> {form.customer_email}</div>}
                    {form.contact_email && <div><b>Contact Email:</b> {form.contact_email}</div>}
                  </div>
                </div>
              )}
              {/* Customer address from the customer master — always current */}
              {form.client_id && custAddress(form.client_id, masters) && (
                <div style={{ gridColumn: "1 / -1", border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 12px", background: "#f8fafc", fontSize: 12.5 }}>
                  <div style={{ fontWeight: 700, color: colors.textLight, marginBottom: 4 }}>📍 Customer Address</div>
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                    <span>{custAddress(form.client_id, masters)}</span>
                    <a href={mapsLink(custAddress(form.client_id, masters))} target="_blank" rel="noopener noreferrer"
                      style={{ color: colors.primary, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>🗺️ View on map</a>
                  </div>
                </div>
              )}
              <div><label style={styles.label}>License Number</label>
                {form.customer_products && form.customer_products.length > 0 ? (
                  <SearchableSelect options={form.customer_products.map(cp => ({ value: cp.id.toString(), label: cp.license_number || "No License" }))}
                    onCreate={() => { const c = masters.clients?.find(x => String(x.id) === String(form.client_id)); if (c) setNewClientForLicences(c); }} createLabel="Add Licence for this customer"
                    value={form.customer_product_id || ""} onChange={(v) => {
                      const cp = form.customer_products?.find(c => c.id == v);
                      if (cp) {
                        setForm({ ...form, customer_product_id: v, product_id: cp.product_id?.toString(), license_number: cp.license_number || "", subscription_expiry: cp.subscription_expiry || "" });
                      } else {
                        setForm({ ...form, customer_product_id: "", license_number: "" });
                      }
                    }} placeholder="Select license..." />
                ) : (
                  <><FastText style={styles.input} value={form.license_number || ""} onCommit={(t) => setForm(f => ({ ...f, license_number: t }))} placeholder="No licenses linked — type manually" />
                  {form.client_id && (
                    <span style={{ fontSize: 11, color: colors.primary, cursor: "pointer", textDecoration: "underline", display: "inline-block", marginTop: 4 }}
                      onClick={() => { const c = masters.clients?.find(x => String(x.id) === String(form.client_id)); if (c) setNewClientForLicences(c); }}>+ Add licence for this customer</span>
                  )}</>
                )}
              </div>
              <div><label style={styles.label}>Product / Service For</label>
                <SearchableSelect options={masters.products?.map(p => ({ value: p.id.toString(), label: p.name })) || []}
                  value={form.product_id || ""} onChange={(v) => {
                    setForm({ ...form, product_id: v });
                  }} placeholder="Search product..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.products)} createLabel="Create Product" />
              </div>
              <div><label style={styles.label}>TDL Type</label>
                {/* From Master Data > TDL Types (the original three until migration v51 has run) */}
                <SearchableSelect
                  options={(() => {
                    const names = (masters.tdlTypes || []).length ? masters.tdlTypes.map(t => t.name) : ["Bizom to Tally", "Tally to Bizom", "Both-way Sync"];
                    if (form.tdl_type && !names.includes(form.tdl_type)) names.push(form.tdl_type);   // an older value still shows
                    return names.map(n => ({ value: n, label: n }));
                  })()}
                  value={form.tdl_type || ""} onChange={(v) => setForm({ ...form, tdl_type: v })} placeholder="Select TDL type..."
                  onCreate={() => setQuickCreate({ title: "TDL Type", endpoint: "tdl-types", targetField: "tdl_type", fields: [{ key: "name", label: "TDL Type", required: true }] })}
                  createLabel="Create TDL Type" />
              </div>
              <div><label style={styles.label}>Start Time *</label><TimeInput value={form.call_start_time || ""} onChange={(v) => setForm({ ...form, call_start_time: v })} /></div>
              <div><label style={styles.label}>End Time *</label><TimeInput value={form.call_end_time || ""} onChange={(v) => setForm({ ...form, call_end_time: v })} /></div>
              <div><label style={styles.label}>Hours Spent</label>
                <div style={{ ...styles.input, background: hoursSpent ? "#ecfdf5" : "#f8fafc", fontWeight: 700, fontSize: 15, color: hoursSpent ? "#059669" : "#94a3b8", display: "flex", alignItems: "center" }}>
                  {hoursSpent ? `${hoursSpent} hours` : "Enter start & end time"}
                </div>
              </div>
              <div><label style={styles.label}>Call Type *</label>
                <SearchableSelect options={masters.callTypes?.map(c => ({ value: c.id.toString(), label: c.name })) || []}
                  value={form.call_type_id || ""} onChange={(v) => setForm({ ...form, call_type_id: v })} placeholder="Search call type..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.callTypes)} createLabel="Create Call Type" />
              </div>
              {(() => {
                const amcVisitTypeId = masters.callTypes?.find(c => c.name && c.name.toLowerCase().trim() === "amc visit")?.id;
                const isAmcVisit = amcVisitTypeId != null && String(form.call_type_id) === String(amcVisitTypeId);
                if (!isAmcVisit) return null;
                return (
                  <div style={{ gridColumn: "1 / -1", border: `1px solid ${colors.primary}`, borderRadius: 8, padding: 12, background: "#f5f8ff" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: colors.primary, marginBottom: 8 }}>🛡️ AMC Visit</div>
                    <label style={styles.label}>AMC Contract *</label>
                    <SearchableSelect options={amcOptions} value={form.amc_contract_id ? String(form.amc_contract_id) : ""}
                      onChange={(v) => {
                        if (v && amcMap[v]) { const a = amcMap[v]; setForm(f => ({ ...f, amc_contract_id: v, client_id: a.client_id ? String(a.client_id) : f.client_id, product_id: a.product_id ? String(a.product_id) : f.product_id })); }
                        else setForm(f => ({ ...f, amc_contract_id: v }));
                      }} placeholder="Search AMC contract / customer..." />
                    <div style={{ fontSize: 11, color: colors.textLight, marginTop: 6, marginBottom: 8 }}>Set the visit appointment in <b>Next Action Date / Time</b> above. Mark status <b>Completed</b> when done (counts against the contract), or <b>Cancelled</b> to drop it. Start/End time are optional until the visit happens.</div>
                    {editItem && (form.original_visit_date || form.reschedule_count > 0) && (
                      <div style={{ fontSize: 12, background: "#fff", border: `1px solid ${colors.border}`, borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
                        {form.original_visit_date && <span>Originally scheduled: <b>{formatDateDMY(form.original_visit_date)}{form.original_visit_time ? " " + formatTime12hr(String(form.original_visit_time).slice(0,5)) : ""}</b></span>}
                        {form.reschedule_count > 0 && <span style={{ color: "#d97706", marginLeft: 10 }}>Rescheduled ×{form.reschedule_count}</span>}
                        {editItem && <span style={{ color: colors.primary, cursor: "pointer", textDecoration: "underline", marginLeft: 10 }} onClick={() => openRescheduleHistory(editItem.id)}>View history</span>}
                      </div>
                    )}
                    <label style={styles.label}>Reschedule / Cancel reason <span style={{ color: colors.textLight, fontWeight: 400 }}>(optional)</span></label>
                    <FastText style={styles.input} value={form.reschedule_reason || ""} onCommit={(t) => setForm(f => ({ ...f, reschedule_reason: t }))} placeholder="e.g. customer busy, moved to next week" />
                  </div>
                );
              })()}
              <div><label style={styles.label}>Status</label>
                <select style={styles.select} value={form.call_status || "Open"} onChange={(e) => setForm({ ...form, call_status: e.target.value })}>
                  {["Open", "In Progress", "Completed", "Escalated", "On Hold", "Cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label style={styles.label}>Location</label>
                <SearchableSelect options={masters.locations?.map(l => ({ value: l.id.toString(), label: l.name })) || []}
                  value={form.location_id || ""} onChange={(v) => setForm({ ...form, location_id: v })} placeholder="Search location..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.locations)} createLabel="Create Location" />
              </div>
              <div><label style={styles.label}>Physical Days</label><FastText style={styles.input} type="number" value={form.physical_days || ""} onCommit={(t) => setForm(f => ({ ...f, physical_days: t }))} /></div>
              <div><label style={styles.label}>Subscription Expiry</label><DateInput value={form.subscription_expiry} onChange={(v) => setForm({ ...form, subscription_expiry: v })} /></div>
              <div><label style={styles.label}>Billed Through / Partner</label>
                <SearchableSelect options={masters.partners?.map(p => ({ value: p.id.toString(), label: p.name })) || []}
                  value={form.billed_through || ""} onChange={(v) => setForm({ ...form, billed_through: v })} placeholder="Search partner..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.partners)} createLabel="Create Partner" />
              </div>
              <div><label style={styles.label}>Assigned To</label>
                <SearchableSelect options={masters.executives?.map(e => ({ value: e.id.toString(), label: e.name })) || []}
                  value={form.assigned_to_id || ""} onChange={(v) => setForm({ ...form, assigned_to_id: v })} placeholder="Assign to executive..."
                  onCreate={() => setQuickCreate(masterFieldConfigs.executives)} createLabel="Create Executive" />
              </div>
              <div><label style={styles.label}>Next Action Date</label><DateInput value={form.next_action_date || ""} onChange={(v) => setForm({ ...form, next_action_date: v })} /></div>
              <div><label style={styles.label}>Next Action Time</label><TimeInput value={form.next_action_time || ""} onChange={(v) => setForm({ ...form, next_action_time: v })} /></div>
              {/* Automatic e-mails (Settings > Email Settings): visit assigned / rescheduled, and completed */}
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", paddingBottom: 10 }}
                  title="When the call is assigned with a date, and when it is completed, an e-mail goes to the customer as well as to our team">
                  <input type="checkbox" checked={form.email_customer !== false} onChange={(e) => setForm({ ...form, email_customer: e.target.checked })} />
                  ✉ Email the customer
                </label>
              </div>
              <div><label style={styles.label}>Continuation of Ticket <span style={{ color: colors.textLight, fontWeight: 400 }}>(optional)</span></label>
                <SearchableSelect options={parentOptions.filter(o => !editItem || o.value !== String(editItem.id))}
                  value={form.parent_call_id ? String(form.parent_call_id) : ""}
                  onChange={(v) => {
                    if (v && parentCalls[v]) {
                      const p = parentCalls[v];
                      setForm(f => ({ ...f, parent_call_id: v,
                        client_id: p.client_id ? String(p.client_id) : f.client_id,
                        principal_id: p.principal_id ? String(p.principal_id) : f.principal_id,
                        product_id: p.product_id ? String(p.product_id) : f.product_id,
                        call_type_id: p.call_type_id ? String(p.call_type_id) : f.call_type_id,
                        regarding: f.regarding || p.regarding || "" }));
                    } else { setForm(f => ({ ...f, parent_call_id: v })); }
                  }}
                  placeholder="Search original ticket # / client..." />
                {form.parent_call_id && (
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    <span style={{ color: colors.primary, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => openThread(editItem?.id || form.parent_call_id)}>📑 View full thread</span>
                  </div>
                )}
              </div>
              <div><label style={styles.label}>Contact Person Spoke To</label><FastText style={styles.input} value={form.contact_person_called || ""} onCommit={(t) => setForm(f => ({ ...f, contact_person_called: t }))} placeholder="Name of person contacted" /></div>
              <div><label style={styles.label}>Release / Version</label><FastText style={styles.input} value={form.software_version || ""} onCommit={(t) => setForm(f => ({ ...f, software_version: t }))} placeholder="e.g., Tally Prime 4.1" /></div>
              <div><label style={styles.label}>Problem Faced</label>
                <SearchableSelect options={masters.problems?.map(p => ({ value: p.id.toString(), label: p.category ? `${p.name} (${p.category})` : p.name })) || []}
                  value={form.problem_id?.toString() || ""} onChange={(v) => setForm({ ...form, problem_id: v })}
                  placeholder="Search problem..." onCreate={() => setQuickCreate(masterFieldConfigs.problems)} createLabel="Add Problem" />
              </div>
              {form.client_id && (() => { const cl = masters.clients?.find(c => c.id == form.client_id); return cl?.email ? (
                <div><label style={styles.label}>Customer Email</label>
                  <div style={{ ...styles.input, background: "#f0f4ff", fontWeight: 600, color: colors.primary }}>{cl.email}</div>
                </div>
              ) : null; })()}
            </div>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <VoiceMinutes kind="call"
                context={`Customer: ${(masters.clients || []).find(c => String(c.id) === String(form.client_id))?.company_name || ""}. Subject: ${form.regarding || ""}`}
                onUse={(v) => setForm(f => ({
                  ...f,
                  problem_observed: [f.problem_observed, v.problem].filter(Boolean).join("\n"),
                  solution_provided: [f.solution_provided, v.solution].filter(Boolean).join("\n"),
                  call_details: [f.call_details, v.minutesText].filter(Boolean).join("\n\n"),
                  next_action_date: v.next_action_date || f.next_action_date,
                  next_action_time: v.next_action_time || f.next_action_time,
                  _voice: { transcript: v.transcript, english: v.english, minutes: v.minutesText, language: v.language, seconds: v.seconds },
                }))} />
              {form._voice && <span style={{ fontSize: 11, color: colors.success }}>✓ Voice minutes added — check the fields below, then save</span>}
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Problem Observed</label>
              <FastText multiline style={{ ...styles.input, minHeight: 60 }} value={form.problem_observed || ""} onCommit={(t) => setForm(f => ({ ...f, problem_observed: t }))} placeholder="Describe the problem observed..." />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Solution Provided</label>
              <FastText multiline style={{ ...styles.input, minHeight: 60 }} value={form.solution_provided || ""} onCommit={(t) => setForm(f => ({ ...f, solution_provided: t }))} placeholder="Describe the solution provided..." />
            </div>
            <div style={{ ...styles.grid(1), marginTop: 12 }}>
              <div><label style={styles.label}>Subject</label><FastText style={styles.input} value={form.regarding || ""} onCommit={(t) => setForm(f => ({ ...f, regarding: t }))} /></div>
              <div><label style={styles.label}>Call Notes</label><FastText multiline style={{ ...styles.input, minHeight: 80 }} value={form.call_details || ""} onCommit={(t) => setForm(f => ({ ...f, call_details: t }))} /></div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
              <button style={styles.btn("ghost")} onClick={() => { if (confirmCancel(form)) setShowForm(false); }}>Cancel <span style={{ fontSize: 9, color: colors.textLight, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
              <button style={styles.btn("primary")} onClick={handleSave}>{editItem ? "Update" : "Log Call"} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span></button>
            </div>
          </div>
        </div>
      )}

      {/* Full Create Modal — opens on top of everything */}
      {quickCreate && (
        <FullCreate title={quickCreate.title} endpoint={quickCreate.endpoint} fields={quickCreate.fields}
          onCreated={(newItem) => {
            loadMasters();
            setForm(f => ({ ...f, [quickCreate.targetField]: quickCreate.targetField === "tdl_type" ? (newItem.name || "") : newItem.id?.toString() }));
            // For a brand-new customer, go straight on to its products/licences so the
            // licence dropdown on this call is populated before we return.
            if (quickCreate.endpoint === "clients" && newItem?.id) setNewClientForLicences(newItem);
          }}
          onClose={() => setQuickCreate(null)} />
      )}

      {newClientForLicences && (
        <QuickLicences client={newClientForLicences} masters={masters} loadMasters={loadMasters}
          onDone={async () => {
            const c = newClientForLicences;
            setNewClientForLicences(null);
            // Reload this customer's licences into the open call form
            try {
              const r = await api(`/customer-products/customer/${c.id}`);
              const cps = r.data || [];
              const only = cps.length === 1 ? cps[0] : null;
              setForm(f => ({ ...f, client_id: c.id?.toString(), customer_products: cps,
                customer_product_id: only ? only.id?.toString() : (f.customer_product_id || ""),
                product_id: only ? only.product_id?.toString() : f.product_id,
                license_number: only ? (only.license_number || "") : f.license_number,
                subscription_expiry: only ? (only.subscription_expiry || f.subscription_expiry) : f.subscription_expiry,
              }));
            } catch (e) {}
          }} />
      )}

      {/* Call thread (original + continuations) */}
      {threadModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 11000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }} onClick={() => setThreadModal(null)}>
          <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 720, boxShadow: "0 12px 40px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: colors.text }}>📑 Ticket Thread</span>
              <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: colors.textLight }} onClick={() => setThreadModal(null)}>✕</button>
            </div>
            {threadModal.loading ? (
              <div style={{ padding: 30, textAlign: "center", color: colors.textLight }}>Loading thread…</div>
            ) : threadModal.error ? (
              <div style={{ padding: 20, color: colors.danger }}>Could not load thread: {threadModal.error}</div>
            ) : (
              <div style={{ padding: 20 }}>
                {threadModal.summary && (
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13 }}>
                    <span><b>{threadModal.summary.session_count}</b> session(s)</span>
                    <span>Total hours: <b>{Number(threadModal.summary.total_hours || 0).toFixed(2)}</b></span>
                    <span>Ticket status: <b style={{ color: threadModal.summary.ticket_status === "Completed" ? colors.success : colors.primary }}>{threadModal.summary.ticket_status || "—"}</b></span>
                  </div>
                )}
                {threadModal.rows.map((s, i) => {
                  const d = s.call_date ? toISODate(s.call_date).split("-").reverse().join("/") : "";
                  const isOriginal = !s.parent_call_id;
                  return (
                    <div key={s.id} style={{ borderLeft: `3px solid ${isOriginal ? colors.primary : colors.border}`, paddingLeft: 14, marginBottom: 14, position: "relative" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: colors.text }}>
                          {isOriginal ? "🟢 Original" : `↳ Session ${i + 1}`} · {s.ticket_number}
                        </span>
                        <span style={{ fontSize: 12, color: colors.textLight }}>{d} · {s.executive_name || "—"} · {Number(s.hours_spent || 0)}h · {s.call_status}</span>
                      </div>
                      {s.regarding && <div style={{ fontSize: 12.5, marginTop: 3 }}><b>Subject:</b> {s.regarding}</div>}
                      {s.call_details && <div style={{ fontSize: 12.5, marginTop: 2, color: "#475569" }}>{s.call_details}</div>}
                      {s.next_action_date && <div style={{ fontSize: 11.5, marginTop: 2, color: colors.primary }}>Next action: {toISODate(s.next_action_date).split("-").reverse().join("/")}{s.next_action_time ? " " + formatTime12hr(String(s.next_action_time).slice(0,5)) : ""}</div>}
                    </div>
                  );
                })}
                {threadModal.rows.length === 0 && <div style={{ color: colors.textLight }}>No linked calls.</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AMC visit reschedule / cancellation history */}
      {rescheduleHistory && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 11000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }} onClick={() => setRescheduleHistory(null)}>
          <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 560, boxShadow: "0 12px 40px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>🗓 Reschedule History</span>
              <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: colors.textLight }} onClick={() => setRescheduleHistory(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              {rescheduleHistory.loading ? <div style={{ color: colors.textLight }}>Loading…</div> : (
                <>
                  {rescheduleHistory.rows.map((h, i) => (
                    <div key={h.id} style={{ borderLeft: `3px solid ${h.action === "Cancelled" ? colors.danger : "#d97706"}`, paddingLeft: 12, marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: h.action === "Cancelled" ? colors.danger : "#d97706" }}>
                        {h.action === "Cancelled" ? "❌ Cancelled" : `🗓 Rescheduled`}
                        <span style={{ fontWeight: 400, color: colors.textLight, marginLeft: 8 }}>{h.changed_at ? new Date(h.changed_at).toLocaleString() : ""}</span>
                      </div>
                      {h.action !== "Cancelled" && (
                        <div style={{ fontSize: 12.5, marginTop: 2 }}>
                          From <b>{h.old_date ? formatDateDMY(h.old_date) : "—"}{h.old_time ? " " + formatTime12hr(String(h.old_time).slice(0,5)) : ""}</b> → <b>{h.new_date ? formatDateDMY(h.new_date) : "—"}{h.new_time ? " " + formatTime12hr(String(h.new_time).slice(0,5)) : ""}</b>
                        </div>
                      )}
                      {h.reason && <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Reason: {h.reason}</div>}
                    </div>
                  ))}
                  {rescheduleHistory.rows.length === 0 && <div style={{ color: colors.textLight }}>No reschedules or cancellations recorded.</div>}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LEADS SCREEN
// ============================================================================
let __leadAdvRows = [];     // Leads: advanced filters kept across screen navigation
let __leadAdvApplied = {};  // ...and the parameters they resolve to
// ---- Lead visits: check-in / check-out helpers (same behaviour as the Call Tracker) ----
const geoMapLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
const geoGetLoc = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve({ lat: null, lng: null }); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    () => resolve({ lat: null, lng: null }),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
});
const geoConsent = () => {
  if (localStorage.getItem("geo_consent") === "yes") return true;
  const ok = window.confirm("This records your current location for this visit (at check-in and check-out). Do you consent?");
  if (ok) localStorage.setItem("geo_consent", "yes");
  return ok;
};
// Hours between two "HH:MM" times (past midnight counts as the next day) — matches the database
const fuHours = (start, end) => {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number), [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 1440;
  return (mins / 60).toFixed(2);
};
const VISIT_TYPES = ["Meeting", "Site Visit", "Demo"];
// Dates arrive from the server as UTC timestamps (19 Oct in India = "2026-10-18T18:30Z").
// Before editing, turn them into plain "YYYY-MM-DD" local dates so saving
// without touching a date can never move it back a day.
const withLocalDates = (rec, keys) => {
  const o = { ...rec };
  keys.forEach(k => { if (o[k]) o[k] = toISODate(String(o[k])); });
  return o;
};
const LEAD_DATE_KEYS = ["enquiry_date", "follow_up_date", "demo_date", "conversion_date"];
const FU_DATE_KEYS = ["followup_date", "next_followup_date"];
let __leadView = "all";     // Leads tab: "all" | "assigned_to_me" | "assigned_by_me" (the Call Tracker reminder sets it)
const LeadsScreen = ({ masters, loadMasters, pendingLead, setPendingLead, setActiveScreen, pendingReturn, user }) => {
  const [leadView, setLeadViewState] = useState(__leadView);
  const setLeadView = (v) => { __leadView = v; setLeadViewState(v); setSelectedIds(new Set()); setSelectAllPages(false); };
  const myExecId = user?.executive_id?.toString() || "";
  // A new lead starts with the logged-in executive as Attended By (blank for logins
  // with no linked executive, e.g. admin)
  const newLeadForm = () => ({ status: "New", priority: "Warm", ...(myExecId ? { attended_by_id: myExecId } : {}) });
  // Parameters for the current tab. Assigned tabs show open leads only, unless a
  // Status is picked in the filters (so you can still look up converted ones).
  const leadViewParams = (p, f) => {
    if (leadView === "all") return;
    if (leadView === "assigned_to_me") {
      if (user?.role === "admin") p.append("assigned_only", "true");
      else p.append("assigned_to_id", myExecId || "0");   // no executive linked to this login = nothing to show
    }
    if (leadView === "assigned_by_me") p.append("assigned_by_me", "true");
    if (!f.status) p.append("active_only", "true");
  };
  const [advRows, setAdvRowsState] = useState(__leadAdvRows);
  const setAdvRows = (v) => { __leadAdvRows = v; setAdvRowsState(v); };
  const [advApplied, setAdvAppliedState] = useState(__leadAdvApplied);   // params currently in force
  const setAdvApplied = (v) => { __leadAdvApplied = v; setAdvAppliedState(v); };
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState("enquiry_date");
  const [sortDir, setSortDir] = useState("desc");
  const [showForm, setShowForm] = useState(false);
  // ---- First contact on the New Lead form (the creator's effort) ----
  const FF_BLANK = { enabled: true, followup_type: "Phone Call", start_time: "", end_time: "", outcome: "", summary: "" };
  const [firstFu, setFirstFu] = useState(FF_BLANK);
  const [ffBusy, setFfBusy] = useState(false);
  const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
  const ffStamp = async (which) => {              // which: "checkin" | "checkout"
    if (which === "checkin" && !geoConsent()) return;
    setFfBusy(true);
    const loc = await geoGetLoc();
    const at = new Date().toISOString(), t = nowHHMM();
    setFirstFu(f => which === "checkin"
      ? { ...f, enabled: true, checkin_at: at, checkin_lat: loc.lat, checkin_lng: loc.lng, start_time: t }
      : { ...f, enabled: true, checkout_at: at, checkout_lat: loc.lat, checkout_lng: loc.lng, end_time: t });
    setToast({ message: `${which === "checkin" ? "Checked in" : "Checked out"} at ${formatTime12hr(t)}${loc.lat ? " with location" : " (location unavailable)"}.`, type: "success" });
    setFfBusy(false);
  };
  // New lead with nobody in Assigned To → it belongs to the Attended By executive
  // (normally the creator), so it always shows in someone's "Assigned to Me".
  const ownerDefault = () => (!editItem && !form.assigned_to_id && (form.attended_by_id || myExecId))
    ? { assigned_to_id: String(form.attended_by_id || myExecId) } : {};
  // The first contact to send with a new lead (null = nothing to record)
  const firstFuPayload = () => {
    const f = firstFu;
    if (editItem || !f.enabled) return null;
    if (!(f.start_time || f.end_time || f.checkin_at || f.summary || f.outcome)) return null;
    const rest = { ...f }; delete rest.enabled;
    return rest;
  };
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ status: "New", priority: "Warm" });
  const [toast, setToast] = useState(null);
  useEffect(() => { if (showForm && !editItem) setFirstFu(FF_BLANK); }, [showForm]);
  // ---- Unsaved-changes guard: Esc, clicking outside or Cancel asks before throwing away typing ----
  const leadSnap = (o) => JSON.stringify(Object.keys(o || {}).filter(k => !k.startsWith("_") && o[k] !== "" && o[k] != null).sort().map(k => [k, String(o[k])]));
  const formOpenedAs = useRef("");
  useEffect(() => { if (showForm) formOpenedAs.current = leadSnap(form); }, [showForm, editItem]);   // eslint-disable-line react-hooks/exhaustive-deps
  const leadFormDirty = () => leadSnap(form) !== formOpenedAs.current
    || (!editItem && !!(firstFu.start_time || firstFu.end_time || firstFu.checkin_at || firstFu.summary || firstFu.outcome));
  const closeLeadForm = () => {
    if (leadFormDirty() && !window.confirm("You have unsaved changes in this lead. Discard them?")) return;
    setShowForm(false); setKnown(null);
  };
  const latestLead = useRef({});   // filled in below, after handleSave exists
  // While a NEW lead is typed, look up leads / customers with a similar company
  // name, mobile or e-mail (partial match), so the executive sees it already exists.
  const [known, setKnown] = useState(null);
  useEffect(() => {
    if (!showForm || editItem) { setKnown(null); return; }
    const name = String(form.prospect_company || "").trim(), ph = String(form.phone || "").replace(/\D/g, ""), em = String(form.email || "").trim();
    if (name.replace(/[^a-z0-9]/gi, "").length < 3 && ph.length < 5 && em.length < 4) { setKnown(null); return; }
    const t = setTimeout(async () => {
      try {
        const q = new URLSearchParams({ mode: "suggest", prospect_company: name, phone: ph, email: em });
        const r = await api(`/leads/duplicate-check?${q}`);
        const d = r.data || {};
        setKnown((d.open?.length || d.closed?.length || d.customers?.length) ? d : null);
      } catch (e) { setKnown(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [showForm, editItem, form.prospect_company, form.phone, form.email]);
  // Open an existing lead's Follow-up window instead of creating a duplicate
  const goToLead = async (id) => {
    try {
      const r = await api(`/leads/${id}`);
      setShowForm(false); setEditItem(null); setKnown(null);
      openFollowup(r.data);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };   // fresh "First contact" for every new lead
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [showFollowup, setShowFollowup] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [followupForm, setFollowupForm] = useState({ followup_type: "Phone Call" });
  const [quickCreate, setQuickCreate] = useState(null);
  const [editingFollowup, setEditingFollowup] = useState(null);

  // If we arrived here from a Dashboard/Reports drill-down, open that lead's edit
  // form, and return to the origin screen when the form is closed (save or cancel).
  const [returnTo, setReturnTo] = useState(null);
  useEffect(() => {
    if (pendingLead) { setEditItem(pendingLead); setForm(withLocalDates(pendingLead, LEAD_DATE_KEYS)); setShowForm(true); setReturnTo(pendingReturn || "dashboard"); if (setPendingLead) setPendingLead(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLead]);
  // Before anything is drawn: a form opened from another screen (e.g. a Dashboard list)
  // goes straight back there when it closes — no frame of this screen's own list in between
  useLayoutEffect(() => {
    if (!showForm && returnTo) { const t = returnTo; setReturnTo(null); if (setActiveScreen) setActiveScreen(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  // Keyboard shortcuts for Leads
  useEffect(() => {
    // Use the LATEST versions (refs) — this handler is only set up when the window
    // opens, so calling closeLeadForm / handleSave directly saw the form as it was
    // then: Esc thought nothing was typed, and Alt+S saved the empty form.
    const closeModal = () => { if (quickCreate) return; if (showFollowup) setShowFollowup(null); else if (showForm) latestLead.current.close(); };
    const addNew = () => { setEditItem(null); setForm(newLeadForm()); setShowForm(true); };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (quickCreate) return; // Let FullCreate handle it
        if (showFollowup) { setShowFollowup(null); return; }
        // The lead form is closed by the app-wide Esc → "close-modal" → closeLeadForm(),
        // which asks first if anything was typed. (This line used to close it directly.)
        if (showForm) return;
      }
      if (e.altKey && e.key.toLowerCase() === "s" && showForm && !quickCreate) { e.preventDefault(); latestLead.current.save(); }
    };
    document.addEventListener("close-modal", closeModal);
    document.addEventListener("shortcut-add-new", addNew);
    window.addEventListener("keydown", handleKey);
    return () => { document.removeEventListener("close-modal", closeModal); document.removeEventListener("shortcut-add-new", addNew); window.removeEventListener("keydown", handleKey); };
  }, [showForm, showFollowup, quickCreate]);

  const toggleSelect = (id) => { setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const toggleSelectAll = () => { if (selectedIds.size === data.length) setSelectedIds(new Set()); else setSelectedIds(new Set(data.map(d => d.id))); };

  const blankExportLeads = () => {
    const csv = "Enquiry Date (DD/MM/YYYY),Lead Source,Priority,Prospect Company,Contact Person,Designation,Phone,Email,City,Product,No. of Users,Deal Value,Status,Follow-up Date,Remarks\n";
    downloadCSV(csv, "leads_blank_template.csv");
    setToast({ message: "Blank template downloaded!", type: "success" });
  };

  const allLeadHeaders = ["Enquiry #", "Enquiry Date", "Prospect Company", "Contact Person", "Designation", "Phone", "Email", "City", "Location", "Lead Source", "Product", "No. of Users", "Estimated Deal Value", "Attended By", "Assigned To", "Status", "Priority", "Follow-up Date", "Next Action", "Remarks", "Total Follow-ups", "Conversion Date", "Actual Deal Value", "Lost Reason", "Latest Follow-up Date", "Latest Outcome", "Latest Summary", "Latest Follow-up By"];
  const leadToRow = (r) => csvRow([
    r.enquiry_number, formatDateDMY(r.enquiry_date), r.prospect_company || "", r.contact_person || "",
    r.designation || "", r.phone || "", r.email || "", r.city || "", r.location_name || "",
    r.lead_source || "", r.product_name || "", r.number_of_users || "", r.estimated_deal_value || "",
    r.executive_name || r.attended_by_name || "", r.assigned_to_name || "", r.status || "", r.priority || "",
    r.follow_up_date ? formatDateDMY(r.follow_up_date) : "", r.next_action || "", r.remarks || "",
    r.total_followups || 0, r.conversion_date ? formatDateDMY(r.conversion_date) : "",
    r.actual_deal_value || "", r.lost_reason || "",
    r.latest_fu_date ? formatDateDMY(r.latest_fu_date) : "", r.latest_outcome || "", r.latest_summary || "", r.latest_fu_by || ""
  ]);

  const exportLeadsData = (rows) => {
    const headers = csvRow(allLeadHeaders);
    const csvRows = rows.map(r => leadToRow(r)).join("\n");
    downloadCSV(headers + "\n" + csvRows + "\n", `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
    setToast({ message: `Exported ${rows.length} records!`, type: "success" });
  };

  const exportSelectedLeads = () => {
    if (selectedIds.size === 0) { setToast({ message: "No records selected.", type: "warning" }); return; }
    const rows = data.filter(d => selectedIds.has(d.id));
    exportLeadsData(rows);
  };

  const fetchData = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: filters.search ? 10000 : 50 });
      Object.entries(filters).forEach(([k, v]) => { if (v && k !== "search") params.append(k, v); });
      Object.entries(advApplied).forEach(([k, v]) => { if (v) params.append(k, v); });
      leadViewParams(params, { ...filters, ...advApplied });
      const r = await api(`/leads?${params}`);
      let sorted = r.data;
      // Client-side search across all fields
      if (filters.search) {
        const s = filters.search.toLowerCase();
        const sDigits = s.replace(/[^0-9]/g, "");
        sorted = sorted.filter(row =>
          (row.enquiry_number?.toString() || "").toLowerCase().includes(s) ||
          (row.prospect_company || "").toLowerCase().includes(s) ||
          (row.contact_person || "").toLowerCase().includes(s) ||
          (row.phone || "").toLowerCase().includes(s) ||
          (sDigits && (row.phone || "").replace(/[^0-9]/g, "").includes(sDigits)) ||
          (row.email || "").toLowerCase().includes(s) ||
          (row.city || "").toLowerCase().includes(s) ||
          (row.lead_source || "").toLowerCase().includes(s) ||
          (row.status || "").toLowerCase().includes(s) ||
          (row.priority || "").toLowerCase().includes(s) ||
          (row.remarks || "").toLowerCase().includes(s) ||
          (row.designation || "").toLowerCase().includes(s) ||
          (row.product_name || "").toLowerCase().includes(s) ||
          (row.executive_name || "").toLowerCase().includes(s) ||
          (row.next_action || "").toLowerCase().includes(s) ||
          (row.estimated_deal_value?.toString() || "").includes(s)
        );
      }
      if (sortBy) {
        sorted = [...sorted].sort((a, b) => {
          let va = a[sortBy], vb = b[sortBy];
          if (sortBy.includes("date")) { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; return sortDir === "asc" ? va - vb : vb - va; }
          if (sortBy === "estimated_deal_value") { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; return sortDir === "asc" ? va - vb : vb - va; }
          va = (va || "").toString().toLowerCase(); vb = (vb || "").toString().toLowerCase();
          return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        });
      }
      setData(sorted); setPagination(r.pagination);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  }, [JSON.stringify(filters), JSON.stringify(advApplied), sortBy, sortDir, leadView]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Debounce search for leads
  useEffect(() => {
    const timer = setTimeout(() => { fetchData(); }, 400);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const savingRef = useRef(false);
  const handleSave = async () => {
    if (savingRef.current) return;      // ignore a second save while one is in flight
    savingRef.current = true;
    try {
      // ---- Duplicate check (new lead, or company / mobile / e-mail changed) ----
      const nm = (v) => String(v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const changed = !editItem || nm(form.prospect_company) !== nm(editItem.prospect_company)
        || normPhone(form.phone) !== normPhone(editItem.phone)
        || String(form.email || "").trim().toLowerCase() !== String(editItem.email || "").trim().toLowerCase();
      if (changed) {
        const q = new URLSearchParams({ prospect_company: form.prospect_company || "", phone: form.phone || "", email: form.email || "", exclude_id: editItem?.id || 0 });
        let d = null;
        try { d = (await api(`/leads/duplicate-check?${q}`)).data; } catch (e) {}
        if (d?.open?.length) {
          const x = d.open[0];
          setToast({ message: `Duplicate — open lead #${x.enquiry_number} "${x.prospect_company}" has the same ${String(x.matched_on).toLowerCase()} (${x.status}${x.assigned_to_name ? `, assigned to ${x.assigned_to_name}` : ""}). Add a follow-up to that lead instead.`, type: "error" });
          return;
        }
        const warn = [
          ...(d?.customers || []).map(c => `• Existing customer: ${c.company_name} (same ${String(c.matched_on).toLowerCase()})`),
          ...(d?.closed || []).map(l => `• ${l.status} lead #${l.enquiry_number}: ${l.prospect_company} (same ${String(l.matched_on).toLowerCase()})`),
        ];
        if (warn.length && !window.confirm(`This prospect is already known:\n\n${warn.slice(0, 5).join("\n")}\n\nSave this lead anyway?`)) return;
      }

      // Status changed to Converted: ask the closing value, save everything else,
      // then open the customer step. The lead only becomes "Converted" when that
      // step finishes, so cancelling never leaves a converted lead with no customer.
      if (form.status === "Converted" && editItem?.status !== "Converted") {
        const ans = window.prompt("Deal closed at what value? (₹)", form.actual_deal_value || form.estimated_deal_value || "");
        if (ans === null) return;                                   // back to the form
        const dealValue = String(ans).replace(/[₹,\s]/g, "");
        if (dealValue === "" || isNaN(Number(dealValue))) {
          setToast({ message: "Please enter the deal value as a number, e.g. 22500.", type: "error" }); return;
        }
        const body = { ...form, status: editItem ? editItem.status : "New", actual_deal_value: dealValue };
        const r = editItem
          ? await api(`/leads/${editItem.id}`, { method: "PUT", body })
          : await api("/leads", { method: "POST", body: { ...body, ...ownerDefault(), first_followup: firstFuPayload() } });
        setShowForm(false); setEditItem(null); setForm(newLeadForm()); fetchData();
        setConvertLead({ ...r.data, status: "Converted", actual_deal_value: dealValue });
        setToast({ message: "Details saved — now choose the customer for this sale.", type: "success" });
        return;
      }
      if (editItem) {
        try {
          await api(`/leads/${editItem.id}`, { method: "PUT", body: { ...form, expected_updated_at: editItem.updated_at } });
        } catch (e) {
          if (e.status !== 409) throw e;
          // Someone else saved this lead after you opened it
          if (window.confirm(`${e.message}\n\nOK = save YOUR changes anyway (theirs will be overwritten)\nCancel = load THEIR latest version instead`)) {
            await api(`/leads/${editItem.id}`, { method: "PUT", body: { ...form, force_overwrite: true } });
          } else {
            const fresh = await api(`/leads/${editItem.id}`);
            setEditItem(fresh.data); setForm(withLocalDates(fresh.data));
            setToast({ message: "Loaded the latest version — check it, then save again if needed.", type: "info" });
            return;
          }
        }
      }
      else { await api("/leads", { method: "POST", body: { ...form, ...ownerDefault(), first_followup: firstFuPayload() } }); }
      setToast({ message: editItem ? "Updated!" : "Lead created!", type: "success" });
      setShowForm(false); setEditItem(null); setForm(newLeadForm()); fetchData();
      window.dispatchEvent(new Event("reminders-refresh"));
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    finally { savingRef.current = false; }
  };
  // Always point the keyboard handler at the current form (see closeModal below)
  latestLead.current = { close: closeLeadForm, save: handleSave };

  // Alt+S is already handled by the main shortcut effect above; a second keydown
  // listener here meant one Alt+S fired handleSave twice and created two leads.
  // Only the "shortcut-save" event is kept.
  useEffect(() => {
    const doSave = () => { if (showForm) handleSave(); };
    document.addEventListener("shortcut-save", doSave);
    return () => { document.removeEventListener("shortcut-save", doSave); };
  });

  const exportLeads = async () => {
    if (data.length === 0) { setToast({ message: "No leads to export.", type: "error" }); return; }
    setToast({ message: "Fetching follow-up data...", type: "success" });

    // Fetch follow-ups for all leads
    const leadFollowups = {};
    let maxFollowups = 0;
    for (const lead of data) {
      try {
        const r = await api(`/lead-followups/lead/${lead.id}`);
        leadFollowups[lead.id] = r.data || [];
        if (r.data.length > maxFollowups) maxFollowups = r.data.length;
      } catch (e) { leadFollowups[lead.id] = []; }
    }

    // Build headers with dynamic follow-up columns
    const baseHeaders = ["Enquiry #", "Date", "Company", "Contact Person", "Phone", "Email", "Source", "Product", "Location", "Status", "Priority", "Estimated Value", "Attended By", "Total Follow-ups"];
    const fuHeaders = [];
    for (let i = 1; i <= maxFollowups; i++) {
      fuHeaders.push(`FU${i} Date`, `FU${i} Type`, `FU${i} Outcome`, `FU${i} Notes`, `FU${i} Next Date`, `FU${i} Next Action`);
    }
    const headers = csvRow([...baseHeaders, ...fuHeaders]);

    const rows = data.map(r => {
      const base = [
        r.enquiry_number, formatDateDMY(r.enquiry_date), r.prospect_company || "",
        r.contact_person || "", r.contact_phone || "", r.contact_email || "",
        r.lead_source || "", r.product_name || "", r.location_name || "", r.status || "",
        r.priority || "", r.estimated_deal_value || "", r.executive_name || "",
        r.total_followups || 0
      ];
      const fus = leadFollowups[r.id] || [];
      const fuCols = [];
      for (let i = 0; i < maxFollowups; i++) {
        const fu = fus[i];
        if (fu) {
          fuCols.push(formatDateDMY(fu.followup_date), fu.followup_type || "", fu.outcome || "", fu.summary || "", fu.next_followup_date ? formatDateDMY(fu.next_followup_date) : "", fu.next_action || "");
        } else {
          fuCols.push("", "", "", "", "", "");
        }
      }
      return csvRow([...base, ...fuCols]);
    }).join("\n");

    downloadCSV(headers + "\n" + rows + "\n", "leads_with_followups_export.csv");
    setToast({ message: `Exported ${data.length} leads with follow-up details!`, type: "success" });
  };

  const openFollowup = async (lead) => {
    setShowFollowup(lead);
    setFollowupForm({ followup_type: "Phone Call", followup_date: new Date().toISOString().slice(0, 10) });
    try {
      const r = await api(`/lead-followups/lead/${lead.id}`);
      setFollowups(r.data);
    } catch (e) { setFollowups([]); }
  };

  // ---- Visits: check-in / check-out on follow-ups ----
  const [fuBusy, setFuBusy] = useState(false);
  const reloadFollowups = async () => {
    if (!showFollowup) return;
    try { const r = await api(`/lead-followups/lead/${showFollowup.id}`); setFollowups(r.data || []); } catch (e) {}
  };
  // Arrived at the customer: create today's visit, already checked in
  const startVisit = async () => {
    if (!showFollowup || !geoConsent()) return;
    const type = VISIT_TYPES.includes(followupForm.followup_type) ? followupForm.followup_type : "Site Visit";
    setFuBusy(true);
    const loc = await geoGetLoc();
    try {
      await api("/lead-followups/checkin", { method: "POST", body: { lead_id: showFollowup.id, followup_type: type, ...loc } });
      setToast({ message: loc.lat ? `Checked in (${type}) with location!` : `Checked in (${type}) — location unavailable, time recorded.`, type: "success" });
      await reloadFollowups(); fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setFuBusy(false);
  };
  // action: "checkin" | "checkout" | "undo-checkin" | "undo-checkout"
  const visitAction = async (f, action) => {
    if (action === "checkin" && !geoConsent()) return;
    if (action === "undo-checkin" && !window.confirm("Undo check-in? This clears the check-in time, location and the Start time it set.")) return;
    if (action === "undo-checkout" && !window.confirm("Undo check-out? This clears the check-out time, location, End time and Hours.")) return;
    setFuBusy(true);
    const loc = action.startsWith("undo") ? {} : await geoGetLoc();
    try {
      const r = await api(`/lead-followups/${f.id}/${action}`, { method: "POST", body: loc });
      const msgs = { checkin: "Checked in", checkout: "Checked out", "undo-checkin": "Check-in undone", "undo-checkout": "Check-out undone" };
      setToast({ message: msgs[action] + (action.startsWith("undo") ? "." : (loc.lat ? " with location!" : " — location unavailable, time recorded.")), type: "success" });
      await reloadFollowups(); fetchData();
      // After check-out, open the entry so the executive writes what happened
      if (action === "checkout") {
        // Anything already typed in the form (summary, outcome, next step) goes into this visit
        const ff = followupForm, keep = {};
        ["summary", "outcome", "next_followup_date", "next_action", "_voice"].forEach(k => { if (ff[k] && !r.data[k]) keep[k] = ff[k]; });
        setEditingFollowup({ ...withLocalDates(r.data, FU_DATE_KEYS), ...keep });
        if (Object.keys(keep).length) setFollowupForm(f => ({ ...f, summary: "", outcome: "", next_followup_date: "", next_action: "", _voice: undefined }));
      }
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setFuBusy(false);
  };

  // ---- Follow-up window actions (used by the buttons and the keyboard shortcuts) ----
  const markConverted = async () => {
    const val = prompt("Deal closed at what value? (₹)", showFollowup.estimated_deal_value || "");
    if (val === null) return;
    const dealValue = String(val).replace(/[₹,\s]/g, "");
    if (dealValue === "" || isNaN(Number(dealValue))) { setToast({ message: "Please enter the deal value as a number, e.g. 22500.", type: "error" }); return; }
    // The lead becomes Converted when the customer step finishes (it saves the status)
    setConvertLead({ ...showFollowup, status: "Converted", actual_deal_value: dealValue });
    setShowFollowup(null);
  };
  // "Mark as Lost" opens a small window: pick a reason (from Master Data > Lost
  // Reasons), optional competitor and details. (It used to ask you to TYPE a
  // reason, which the database refused unless it matched one of six exactly.)
  const [lostDlg, setLostDlg] = useState(null);   // { reason, competitor, details }
  const [qc, setQc] = useState(null);             // quick-create inside the Lost window
  const markLost = () => setLostDlg({ reason: "", competitor: "", details: "" });
  const saveLost = async () => {
    if (!lostDlg?.reason) { setToast({ message: "Pick a reason.", type: "error" }); return; }
    try {
      await api(`/leads/${showFollowup.id}`, { method: "PUT", body: { ...withLocalDates(showFollowup, LEAD_DATE_KEYS), status: "Lost", lost_reason: lostDlg.reason, lost_to_competitor: lostDlg.competitor || "", lost_details: lostDlg.details || "" } });
      setToast({ message: "Lead marked as Lost.", type: "success" }); setLostDlg(null); setShowFollowup(null); fetchData();
    } catch(e) { setToast({ message: e.message, type: "error" }); }
  };
  const saveEditFollowup = async () => {
    try {
      const { _voice, ...efBody } = editingFollowup;
      await api(`/lead-followups/${editingFollowup.id}`, { method: "PUT", body: efBody });
      if (_voice) api("/voice/notes", { method: "POST", body: { ..._voice, followup_id: editingFollowup.id, lead_id: showFollowup?.id } }).catch(() => {});
      setToast({ message: "Follow-up updated!", type: "success" });
      setEditingFollowup(null);
      await reloadFollowups(); fetchData();
    } catch(e) { setToast({ message: e.message, type: "error" }); }
  };
  // Shortcuts while the Follow-up window is open. Caught before the screen-wide
  // shortcuts (Alt+O, Alt+I, Alt+L … normally switch screens).
  useEffect(() => {
    if (!showFollowup) return;
    const onKey = (e) => {
      if (convertLead) return;
      if (lostDlg) {
        if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); setLostDlg(null); }
        else if (e.altKey && (e.key || "").toLowerCase() === "s") { e.preventDefault(); e.stopImmediatePropagation(); saveLost(); }
        return;
      }
      const k = (e.key || "").toLowerCase();
      if (e.key === "Escape") {
        e.preventDefault(); e.stopImmediatePropagation();
        if (editingFollowup) setEditingFollowup(null); else setShowFollowup(null);
        return;
      }
      if (!e.altKey || e.shiftKey) return;
      const act = {
        s: () => (editingFollowup ? saveEditFollowup() : saveFollowup()),
        e: () => { if (followups[0]) setEditingFollowup(withLocalDates(followups[0], FU_DATE_KEYS)); },
        i: () => startVisit(),
        o: () => { const f = followups.find(x => x.checkin_time && !x.checkout_time); if (f) visitAction(f, "checkout"); else setToast({ message: "No visit is checked in right now.", type: "error" }); },
        v: () => { if (!["Converted", "Lost"].includes(showFollowup.status)) markConverted(); },
        l: () => { if (!["Converted", "Lost"].includes(showFollowup.status)) markLost(); },
      }[k];
      if (act) { e.preventDefault(); e.stopImmediatePropagation(); act(); }
    };
    window.addEventListener("keydown", onKey, true);   // capture: runs before the screen shortcuts
    return () => window.removeEventListener("keydown", onKey, true);
  });

  // ---- One follow-up form: logs a NEW follow-up, or — after check-out / Edit / Alt+E —
  // completes or corrects an EXISTING one (loaded into the same form).
  const fuEditing = !!editingFollowup;
  // A visit checked in but not yet out — the form is meant for completing THAT visit
  const fuOpenVisit = !editingFollowup ? (followups || []).find(x => x.checkin_time && !x.checkout_time) : null;
  const fuVal = editingFollowup || followupForm;
  const setFuVal = (patch) => {
    const apply = (f) => ({ ...f, ...(typeof patch === "function" ? patch(f) : patch) });
    if (editingFollowup) setEditingFollowup(apply); else setFollowupForm(apply);
  };
  const fuFormRef = useRef(null);
  useEffect(() => {   // bring the form into view when an entry is loaded into it
    if (editingFollowup && fuFormRef.current) fuFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editingFollowup?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Voice notes of this lead's follow-ups ("🎤 what was said" on each entry)
  const [fuVoice, setFuVoice] = useState({});
  const [fuVoiceOpen, setFuVoiceOpen] = useState(null);
  useEffect(() => {
    if (!showFollowup?.id) { setFuVoice({}); return; }
    api(`/voice/notes?lead_id=${showFollowup.id}`).then(r => {
      const m = {}; (r.data || []).forEach(n => { if (n.followup_id && !m[n.followup_id]) m[n.followup_id] = n; }); setFuVoice(m);
    }).catch(() => setFuVoice({}));
  }, [showFollowup?.id, followups.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  const saveFollowup = async () => {
    if (fuOpenVisit) { setToast({ message: "A visit is in progress — check out first, then complete it here.", type: "error" }); return; }
    // Date and Type are always pre-filled, so a click on "Log Follow-up" with
    // nothing else typed used to save an empty entry. Require one real detail.
    const f = followupForm;
    if (!(f.outcome || (f.summary || "").trim() || f.start_time || f.end_time || f.next_followup_date || f.next_action)) {
      setToast({ message: "Nothing to log — fill in at least the Outcome or Summary (or the times / next date).", type: "error" });
      return;
    }
    try {
      const { _voice, ...fuBody } = followupForm;
      const saved = await api("/lead-followups", { method: "POST", body: { ...fuBody, lead_id: showFollowup.id } });
      if (_voice && saved.data?.id) api("/voice/notes", { method: "POST", body: { ..._voice, followup_id: saved.data.id, lead_id: showFollowup.id } }).catch(() => {});
      setToast({ message: "Follow-up logged!", type: "success" });
      openFollowup(showFollowup);
      fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const followupTypes = ["Phone Call", "Email", "Meeting", "Site Visit", "Demo", "Video Call", "WhatsApp", "SMS"];
  const [convertLead, setConvertLead] = useState(null);   // lead awaiting the conversion flow
  const auto = useAutoRefresh(() => fetchData(pagination?.page || 1), showForm || !!showFollowup || !!convertLead);
  const outcomeOptions = ["Interested", "Not Ready", "Callback Requested", "Proposal Sent", "Negotiating", "No Response", "Converted", "Lost"];

  const leadSources = ["Inbound Call", "Customer Referral", "Field Visit", "Telemarketing", "Email Enquiry", "Website", "Walk-in", "Trade Show/Exhibition", "Social Media", "Others"];
  const statuses = ["New", "Contacted", "Demo Scheduled", "Demo Completed", "Proposal Sent", "Negotiation", "Converted", "Lost", "On Hold"];

  return (
    <div>
      {convertLead && (
        <LeadConversion lead={convertLead} masters={masters} loadMasters={loadMasters}
          onDone={(msg) => { setConvertLead(null); if (msg) setToast({ message: msg, type: "success" }); fetchData(); }} />
      )}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Lead / Enquiry Management</h1>
        <div style={styles.flexRow}>
          <button style={styles.btn("primary")} onClick={() => { setEditItem(null); setForm(newLeadForm()); setShowForm(true); }}>+ New Lead <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+N)</span></button>
          <button style={styles.btn("success", "sm")} onClick={exportLeads}>📊 Export <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+X)</span></button>
          <button style={styles.btn("ghost", "sm")} onClick={blankExportLeads}>📄 Blank Export</button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 16px", marginBottom: 8, background: "#e8eefb", borderRadius: 8, border: `1px solid ${colors.primary}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: colors.primary }}>{selectAllPages ? `All ${pagination?.total || "all"} records selected` : `${selectedIds.size} record(s) selected on this page`}</span>
            <button style={styles.btn("success", "sm")} onClick={async () => {
              if (selectAllPages) {
                setToast({ message: "Fetching all records for export...", type: "success" });
                const params = new URLSearchParams({ limit: 100000 });
                Object.entries(filters).forEach(([k, v]) => { if (v && k !== "search") params.append(k, v); });
                Object.entries(advApplied).forEach(([k, v]) => { if (v) params.append(k, v); });
                leadViewParams(params, { ...filters, ...advApplied });
                const r = await api(`/leads?${params}`);
                exportLeadsData(r.data);
              } else { exportSelectedLeads(); }
            }}>📊 Export {selectAllPages ? "All" : "Selected"}</button>
            <button style={styles.btn("ghost", "sm")} onClick={() => { setSelectedIds(new Set()); setSelectAllPages(false); }}>✕ Clear</button>
          </div>
          {!selectAllPages && selectedIds.size === data.length && pagination?.total > data.length && (
            <div style={{ fontSize: 12, color: colors.primary, cursor: "pointer", textDecoration: "underline" }}
              onClick={() => setSelectAllPages(true)}>
              Select all {pagination.total} records across all pages?
            </div>
          )}
        </div>
      )}

      {/* Tabs — same idea as the Call Tracker */}
      <div style={{ display: "flex", gap: 0, marginBottom: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: leadView === "all" ? `3px solid #1a56db` : "3px solid transparent", background: leadView === "all" ? "#e8eefb" : "#f8fafc", color: leadView === "all" ? "#1a56db" : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setLeadView("all")}>📋 All Leads</button>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: leadView === "assigned_to_me" ? `3px solid #7c3aed` : "3px solid transparent", background: leadView === "assigned_to_me" ? "#f5f3ff" : "#f8fafc", color: leadView === "assigned_to_me" ? "#7c3aed" : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setLeadView("assigned_to_me")}>👤 {user?.role === "admin" ? "Assigned Leads" : "Assigned to Me"}</button>
        <button style={{ padding: isMobileNow() ? "10px 14px" : "10px 20px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, borderBottom: leadView === "assigned_by_me" ? `3px solid #0891b2` : "3px solid transparent", background: leadView === "assigned_by_me" ? "#ecfeff" : "#f8fafc", color: leadView === "assigned_by_me" ? "#0891b2" : colors.textLight, borderRadius: "8px 8px 0 0" }} onClick={() => setLeadView("assigned_by_me")}>📤 Assigned by Me</button>
      </div>
      <div style={styles.card}>
        {leadView === "assigned_to_me" && user?.role !== "admin" && !myExecId && (
          <div style={{ padding: "10px 14px", marginBottom: 10, borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: 13, fontWeight: 600 }}>
            ⚠ Your login is not linked to an executive, so leads assigned to you can't be shown. Ask the admin to link it in User Management, then sign out and sign in again.
          </div>
        )}
        {leadView !== "all" && (
          <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 8 }}>
            {leadView === "assigned_to_me"
              ? (user?.role === "admin" ? "Every lead assigned to an executive." : "Leads assigned to you.")
              : "Leads you created and assigned to someone."} Showing open leads — pick a Status in the filters to see converted or lost ones.
          </div>
        )}
        <RefreshStamp updatedAt={auto.updatedAt} onRefresh={auto.refreshNow} />
        <AdvancedFilters masters={masters} screen="leads" fieldsFn={leadFilterFields} rows={advRows} setRows={setAdvRows}
          onApply={(r) => { setAdvRows(r); setAdvApplied(advParams(r, leadFilterFields(masters))); }} />
        <div style={styles.filterBar}>
          <div><label style={styles.label}>Search</label><SearchInput style={{ ...styles.input, minWidth: 180 }} placeholder="Enquiry#, Company, Contact, Phone..." value={filters.search || ""} onSearch={(t) => setFilters(f => ({ ...f, search: t }))} /></div>
          <div><label style={styles.label}>From</label><DateInput value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} /></div>
          <div><label style={styles.label}>To</label><DateInput value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} /></div>
          <div><label style={styles.label}>Source</label>
            <select style={styles.select} value={filters.lead_source || ""} onChange={(e) => setFilters({ ...filters, lead_source: e.target.value })}>
              <option value="">All</option>{(masters.leadSources || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Status</label>
            <select style={styles.select} value={filters.status || ""} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All</option>{(masters.leadStatuses || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Priority</label>
            <select style={styles.select} value={filters.priority || ""} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
              <option value="">All</option>{["Hot", "Warm", "Cold"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Executive</label>
            <select style={styles.select} value={filters.attended_by_id || ""} onChange={(e) => setFilters({ ...filters, attended_by_id: e.target.value })}>
              <option value="">All</option>{masters.executives?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <button style={styles.btn("ghost", "sm")} onClick={() => setFilters({})}>Clear</button>
          <div style={{ marginLeft: "auto" }}>
            <label style={styles.label}>Sort</label>
            <select style={{ ...styles.select, width: 130 }} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="enquiry_date">Date</option><option value="prospect_company">Company</option>
              <option value="estimated_deal_value">Deal Value</option><option value="priority">Priority</option>
              <option value="status">Status</option><option value="follow_up_date">Follow-up</option>
            </select>
          </div>
          <button style={styles.btn("ghost", "sm")} onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}>
            {sortDir === "asc" ? "↑" : "↓"}
          </button>
        </div>

        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%", minWidth: 0 }}>
        <table className="freeze-cols" style={{ ...styles.table, minWidth: 1200 }}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 36, textAlign: "center" }}>
                <input type="checkbox" checked={data.length > 0 && selectedIds.size === data.length} onChange={toggleSelectAll} style={{ cursor: "pointer" }} />
              </th>
              <th style={styles.th}>Enq #</th><th style={styles.th}>Date</th><th style={styles.th}>Prospect</th>
              <th style={styles.th}>Contact</th><th style={styles.th}>Source</th><th style={styles.th}>Product</th><th style={styles.th}>Value</th>
              <th style={styles.th}>Attended By</th><th style={styles.th}>Assigned To</th><th style={styles.th}>Priority</th><th style={styles.th}>Status</th><th style={{ ...styles.th, minWidth: 220 }}>Latest Follow-up</th><th style={styles.th}>Follow-up</th><th style={{ ...styles.th, minWidth: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.id} style={{ background: selectedIds.has(row.id) ? "#f0f4ff" : (row.follow_up_date && new Date(row.follow_up_date) < new Date() && !["Converted", "Lost"].includes(row.status) ? "#fef2f2" : "#fff"), cursor: "pointer" }}
                onClick={(e) => {
                  if (e.target.type === "checkbox" || e.target.tagName === "BUTTON" || e.target.closest("button")) return;
                  setEditItem(row); setForm(withLocalDates(row, LEAD_DATE_KEYS)); setShowForm(true);
                }}>
                <td style={{ ...styles.td, textAlign: "center" }}>
                  <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)} style={{ cursor: "pointer" }} />
                </td>
                <td style={{ ...styles.td, fontWeight: 700 }}>{row.enquiry_number}</td>
                <td style={styles.td}>{formatDateDMY(row.enquiry_date)}</td>
                <td style={styles.td}>{row.prospect_company}</td>
                <td style={styles.td}>{row.contact_person || "—"}</td>
                <td style={styles.td}>{row.lead_source}</td>
                <td style={styles.td}>{row.product_name || "—"}</td>
                <td style={styles.td}>{row.estimated_deal_value ? formatCurrencyINR(row.estimated_deal_value) : "—"}</td>
                <td style={styles.td}>{row.attended_by_name || "—"}</td>
                <td style={styles.td}>
                  {row.assigned_to_name || "—"}
                  {row.assigned_to_name && row.assigned_by_name && <div style={{ fontSize: 10, color: colors.textLight }}>by {row.assigned_by_name}</div>}
                </td>
                <td style={styles.td}><StatusBadge status={row.priority} /></td>
                <td style={styles.td}><StatusBadge status={row.status} /></td>
                {/* Latest follow-up: outcome, first lines of the summary, date · who — click to open the follow-ups */}
                <td style={{ ...styles.td, maxWidth: 280, cursor: row.latest_fu_date ? "pointer" : "default" }}
                  title={row.latest_summary || ""} onClick={(e) => { if (row.latest_fu_date) { e.stopPropagation(); openFollowup(row); } }}>
                  {row.latest_fu_date ? (<>
                    {row.latest_outcome && <span style={{ ...styles.badge(["Interested", "Converted", "Proposal Sent", "Negotiating"].includes(row.latest_outcome) ? colors.success : ["No Response", "Lost", "Not Ready"].includes(row.latest_outcome) ? colors.danger : colors.warning), fontSize: 10 }}>{row.latest_outcome}</span>}
                    {row.latest_summary && <div style={{ fontSize: 12, marginTop: 3, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", whiteSpace: "normal" }}>{row.latest_summary}</div>}
                    <div style={{ fontSize: 10, color: colors.textLight, marginTop: 2 }}>{formatDateDMY(row.latest_fu_date)}{row.latest_fu_by ? ` · ${row.latest_fu_by}` : ""}</div>
                  </>) : <span style={{ color: colors.textLight }}>—</span>}
                </td>
                <td style={styles.td}>{row.follow_up_date ? formatDateDMY(row.follow_up_date) : "—"}</td>
                <td style={styles.td}>
                  <div style={styles.flexRow}>
                    <button style={styles.btn("primary", "sm")} onClick={() => { setEditItem(row); setForm(withLocalDates(row, LEAD_DATE_KEYS)); setShowForm(true); }}>Edit</button>
                    <button style={styles.btn("success", "sm")} onClick={() => openFollowup(row)}>
                      Follow-up {row.total_followups > 0 && <span style={{ background: "#fff", color: colors.success, borderRadius: 10, padding: "0 5px", fontSize: 10, marginLeft: 4 }}>{row.total_followups}{parseFloat(row.total_hours) > 0 ? ` · ${parseFloat(row.total_hours).toFixed(1)}h` : ""}</span>}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={15} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>{leadView === "all" ? "No leads found." : "No open leads in this tab."}</td></tr>}
          </tbody>
        </table>
        </div>
        <Pagination pagination={pagination} onPageChange={fetchData} />
      </div>

      {showForm && (
        <div style={styles.modal} onClick={closeLeadForm}>
          <div style={{ ...styles.modalContent, maxWidth: 850 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>{editItem ? `Edit Lead #${editItem.enquiry_number}` : "New Lead"}</h2>
            <div style={styles.grid(3)} ref={el => { if (el && !el.dataset.focused) { el.dataset.focused = "1"; const inp = el.querySelector("input"); if (inp) setTimeout(() => inp.focus(), 100); } }}>
              <div><label style={styles.label}>Enquiry Date *</label><DateInput value={form.enquiry_date} onChange={(v) => setForm({ ...form, enquiry_date: v })} /></div>
              <div><label style={styles.label}>Lead Source *</label>
                <SearchableSelect options={masters.leadSources?.map(s => ({ value: s.name, label: s.name })) || []}
                  value={form.lead_source || ""} onChange={(v) => setForm({ ...form, lead_source: v })} placeholder="Search source..."
                  onCreate={() => setQuickCreate({ title: "Lead Source", endpoint: "lead-sources", targetField: "lead_source", fields: [{ key: "name", label: "Source Name", required: true }] })} createLabel="Create Source" />
              </div>
              <div><label style={styles.label}>Priority</label>
                <select style={styles.select} value={form.priority || "Warm"} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {["Hot", "Warm", "Cold"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div><label style={styles.label}>Prospect Company *</label><FastText style={styles.input} value={form.prospect_company || ""} onCommit={(t) => setForm(f => ({ ...f, prospect_company: t }))} /></div>
              <div><label style={styles.label}>Contact Person</label><FastText style={styles.input} value={form.contact_person || ""} onCommit={(t) => setForm(f => ({ ...f, contact_person: t }))} /></div>
              <div><label style={styles.label}>Designation</label><FastText style={styles.input} value={form.designation || ""} onCommit={(t) => setForm(f => ({ ...f, designation: t }))} /></div>
              <div><label style={styles.label}>Phone</label><FastText style={styles.input} value={form.phone || ""} onCommit={(t) => setForm(f => ({ ...f, phone: t }))} /></div>
              <div><label style={styles.label}>Email</label><FastText style={styles.input} type="email" value={form.email || ""} onCommit={(t) => setForm(f => ({ ...f, email: t }))} /></div>
              {known && !editItem && (
                <div style={{ gridColumn: "1 / -1", padding: "10px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fcd34d", fontSize: 12.5 }}>
                  <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 6 }}>⚠ Already known ({(known.open?.length || 0) + (known.closed?.length || 0) + (known.customers?.length || 0)}) — check before creating a new lead</div>
                  {(known.open || []).map(l => (
                    <div key={"o" + l.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "3px 0" }}>
                      <b style={{ color: colors.danger }}>Open lead #{l.enquiry_number}</b> {l.prospect_company}
                      <span style={{ color: colors.textLight }}>· {l.status}{l.assigned_to_name ? ` · assigned to ${l.assigned_to_name}` : l.attended_by_name ? ` · with ${l.attended_by_name}` : ""} · same {String(l.matched_on).toLowerCase()}</span>
                      <button type="button" style={{ ...styles.btn("success", "sm"), padding: "2px 8px", fontSize: 11 }} onClick={() => goToLead(l.id)}>Add follow-up</button>
                    </div>
                  ))}
                  {(known.closed || []).map(l => (
                    <div key={"c" + l.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "3px 0" }}>
                      <b>{l.status} lead #{l.enquiry_number}</b> {l.prospect_company}
                      <span style={{ color: colors.textLight }}>· same {String(l.matched_on).toLowerCase()}</span>
                      <button type="button" style={{ ...styles.btn("ghost", "sm"), padding: "2px 8px", fontSize: 11 }} onClick={() => goToLead(l.id)}>Open</button>
                    </div>
                  ))}
                  {(known.customers || []).map(c => (
                    <div key={"k" + c.id} style={{ padding: "3px 0" }}>
                      <b style={{ color: colors.primary }}>Customer:</b> {c.company_name} <span style={{ color: colors.textLight }}>· existing customer · same {String(c.matched_on).toLowerCase()}</span>
                    </div>
                  ))}
                </div>
              )}
              <div><label style={styles.label}>Company Address</label><FastText style={styles.input} value={form.city || ""} onCommit={(t) => setForm(f => ({ ...f, city: t }))} placeholder="City / Address" /></div>
              <div><label style={styles.label}>Location</label>
                <SearchableSelect options={masters.locations?.map(l => ({ value: l.id.toString(), label: l.name })) || []}
                  value={form.location_id || ""} onChange={(v) => setForm({ ...form, location_id: v })} placeholder="Search location..."
                  onCreate={() => setQuickCreate({ title: "Location", endpoint: "locations", targetField: "location_id", fields: [{ key: "name", label: "Location Name", required: true }] })} createLabel="Create Location" />
              </div>
              <div><label style={styles.label}>Product</label>
                <SearchableSelect options={masters.products?.map(p => ({ value: p.id.toString(), label: p.name })) || []}
                  value={form.product_id || ""} onChange={(v) => setForm({ ...form, product_id: v })} placeholder="Search product..."
                  onCreate={() => setQuickCreate({ title: "Product", endpoint: "products", targetField: "product_id", fields: [{ key: "name", label: "Product Name", required: true }] })} createLabel="Create Product" />
              </div>
              <div><label style={styles.label}>No. of Users / Licenses</label><FastText style={styles.input} type="number" value={form.number_of_users || ""} onCommit={(t) => setForm(f => ({ ...f, number_of_users: t }))} /></div>
              <div><label style={styles.label}>Estimated Deal Value (₹)</label><FastText style={styles.input} type="number" value={form.estimated_deal_value || ""} onCommit={(t) => setForm(f => ({ ...f, estimated_deal_value: t }))} /></div>
              <div><label style={styles.label}>Attended By</label>
                <SearchableSelect options={masters.executives?.map(e => ({ value: e.id.toString(), label: e.name })) || []}
                  value={form.attended_by_id || ""} onChange={(v) => setForm({ ...form, attended_by_id: v })} placeholder="Search executive..."
                  onCreate={() => setQuickCreate({ title: "Executive", endpoint: "executives", targetField: "attended_by_id", fields: [{ key: "name", label: "Full Name", required: true }, { key: "phone", label: "Phone" }, { key: "email", label: "Email", type: "email" }] })} createLabel="Create Executive" />
              </div>
              <div><label style={styles.label}>Assigned To</label>
                <SearchableSelect options={masters.executives?.map(e => ({ value: e.id.toString(), label: e.name })) || []}
                  value={form.assigned_to_id || ""} onChange={(v) => setForm({ ...form, assigned_to_id: v })} placeholder="Assign to executive..."
                  onCreate={() => setQuickCreate({ title: "Executive", endpoint: "executives", targetField: "assigned_to_id", fields: [{ key: "name", label: "Full Name", required: true }, { key: "phone", label: "Phone" }, { key: "email", label: "Email", type: "email" }] })} createLabel="Create Executive" />
              </div>
              <div><label style={styles.label}>Follow-up Date</label><DateInput value={form.follow_up_date} onChange={(v) => setForm({ ...form, follow_up_date: v })} /></div>
              <div><label style={styles.label}>Follow-up Time</label><TimeInput value={form.follow_up_time || ""} onChange={(v) => setForm({ ...form, follow_up_time: v })} /></div>
              <div><label style={styles.label}>Next Action</label>
                <select style={styles.select} value={form.next_action || ""} onChange={(e) => setForm({ ...form, next_action: e.target.value })}>
                  <option value="">Select...</option>
                  {["Call Back", "Send Brochure", "Schedule Demo", "Send Proposal", "Site Visit", "Follow Up Email", "Negotiate", "Close Deal", "No Action"].map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div><label style={styles.label}>Demo Date</label><DateInput value={form.demo_date} onChange={(v) => setForm({ ...form, demo_date: v })} /></div>
              <div><label style={styles.label}>Status</label>
                <SearchableSelect options={(masters.leadStatuses || []).map(s => ({ value: s.name, label: s.name }))}
                  value={form.status || "New"} onChange={(v) => setForm({ ...form, status: v })} placeholder="Select status..."
                  onCreate={() => setQuickCreate({ title: "Lead Status", endpoint: "lead-statuses", targetField: "status", fields: [{ key: "name", label: "Status Name", required: true }] })} createLabel="Create Status" />
                {form.status === "Converted" && editItem?.status !== "Converted" && (
                  <div style={{ fontSize: 11, color: colors.success, marginTop: 4 }}>On {editItem ? "Update" : "Save"} you'll be asked the deal value and the customer.</div>
                )}
              </div>
              {form.lead_source === "Customer Referral" && (
                <div><label style={styles.label}>Referred By (Client)</label>
                  <SearchableSelect options={masters.clients?.map(c => ({ value: c.id.toString(), label: c.company_name })) || []}
                    value={form.referral_client_id ? String(form.referral_client_id) : ""} onChange={(v) => setForm({ ...form, referral_client_id: v })} placeholder="Search customer..."
                    onCreate={() => setQuickCreate({ title: "Customer", endpoint: "clients", targetField: "referral_client_id", fields: clientsCreateFields(masters) })} createLabel="Create Customer" />
                </div>
              )}
              {form.status === "Lost" && (
                <div><label style={styles.label}>Lost Reason</label>
                  <SearchableSelect options={lostReasonOptions(masters)} value={form.lost_reason || ""} onChange={(v) => setForm({ ...form, lost_reason: v })} placeholder="Pick a reason..."
                    onCreate={() => setQuickCreate({ title: "Lost Reason", endpoint: "lost-reasons", targetField: "lost_reason", fields: [{ key: "name", label: "Reason", required: true }] })} createLabel="Create Reason" />
                  <FastText style={{ ...styles.input, marginTop: 6 }} value={form.lost_details || ""} onCommit={(t) => setForm(f => ({ ...f, lost_details: t }))} placeholder="Details (optional), e.g. went with Busy, Rs 8,000 cheaper" />
                </div>
              )}
            </div>
            <div style={{ ...styles.grid(2), marginTop: 12 }}>
              <div><label style={styles.label}>Remarks / Notes</label>
                <FastText multiline style={{ ...styles.input, minHeight: 70 }} value={form.remarks || ""} onCommit={(t) => setForm(f => ({ ...f, remarks: t }))} placeholder="Any additional notes..." />
              </div>
              <div><label style={styles.label}>Competitor Info</label>
                <FastText multiline style={{ ...styles.input, minHeight: 70 }} value={form.competitor_info || ""} onCommit={(t) => setForm(f => ({ ...f, competitor_info: t }))} placeholder="Competing products or vendors..." />
              </div>
            </div>

            {/* First contact — the creator's effort, saved as the lead's first follow-up */}
            {!editItem && (
              <div style={{ marginTop: 16, padding: 14, background: "#f0f4ff", borderRadius: 8, border: `1px solid ${colors.primary}30` }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: colors.primary, cursor: "pointer" }}>
                  <input type="checkbox" checked={firstFu.enabled} onChange={e => setFirstFu({ ...firstFu, enabled: e.target.checked })} />
                  📞 First contact — record your effort
                </label>
                {firstFu.enabled && (<>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "10px 0" }}>
                    <button type="button" style={styles.btn("success", "sm")} disabled={ffBusy} onClick={() => ffStamp("checkin")}>📍 Check-in</button>
                    <button type="button" style={styles.btn("warning", "sm")} disabled={ffBusy || !firstFu.start_time} onClick={() => ffStamp("checkout")}>📍 Check-out</button>
                    {firstFu.checkin_lat && <a href={geoMapLink(firstFu.checkin_lat, firstFu.checkin_lng)} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: colors.primary }}>Check-in location</a>}
                    {firstFu.checkout_lat && <a href={geoMapLink(firstFu.checkout_lat, firstFu.checkout_lng)} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: colors.primary }}>Check-out location</a>}
                    <span style={{ fontSize: 11, color: colors.textLight }}>or type the times below</span>
                  </div>
                  <div style={styles.grid(isMobileNow() ? 2 : 4)}>
                    <div><label style={styles.label}>Type</label>
                      <select style={styles.select} value={firstFu.followup_type} onChange={e => setFirstFu({ ...firstFu, followup_type: e.target.value })}>
                        {["Phone Call", "Email", "Meeting", "Site Visit", "Demo", "Video Call", "WhatsApp", "SMS"].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div><label style={styles.label}>Start Time</label><TimeInput value={firstFu.start_time} onChange={v => setFirstFu({ ...firstFu, start_time: v })} /></div>
                    <div><label style={styles.label}>End Time</label><TimeInput value={firstFu.end_time} onChange={v => setFirstFu({ ...firstFu, end_time: v })} /></div>
                    <div><label style={styles.label}>Hours (auto)</label>
                      <input style={{ ...styles.input, background: "#f1f5f9" }} readOnly value={fuHours(firstFu.start_time, firstFu.end_time)} placeholder={firstFu.start_time ? "in progress" : "—"} />
                    </div>
                    <div><label style={styles.label}>Outcome</label>
                      <select style={styles.select} value={firstFu.outcome} onChange={e => setFirstFu({ ...firstFu, outcome: e.target.value })}>
                        <option value="">Select...</option>
                        {["Interested", "Not Ready", "Callback Requested", "Proposal Sent", "Negotiating", "No Response"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div style={{ gridColumn: isMobileNow() ? "1 / -1" : "span 3" }}><label style={styles.label}>What was discussed</label>
                      <input style={styles.input} value={firstFu.summary} onChange={e => setFirstFu({ ...firstFu, summary: e.target.value })} placeholder="e.g. Explained GST reports; agreed to a demo" />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: colors.textLight, marginTop: 6 }}>
                    Saved as this lead's first follow-up, under your name. Its next date and action are taken from the lead's Follow-up Date and Next Action above.
                    {firstFu.start_time && !firstFu.end_time ? " Not checked out yet — you can check out later from the Follow-up window." : ""}
                  </div>
                </>)}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
              <button style={styles.btn("ghost")} onClick={closeLeadForm}>Cancel <span style={{ fontSize: 9, color: colors.textLight, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
              <button style={styles.btn("primary")} onClick={handleSave}>{editItem ? "Update" : "Create Lead"} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span></button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowup && (
        <div style={styles.modal} onClick={() => setShowFollowup(null)}>
          <div style={{ ...styles.modalContent, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Follow-ups — {showFollowup.prospect_company}</h2>
                <div style={{ fontSize: 12, color: colors.textLight, marginTop: 2 }}>Enquiry #{showFollowup.enquiry_number} · Status: {showFollowup.status} · {followups.length} follow-up(s) · ⏱ {followups.reduce((t, f) => t + (parseFloat(f.hours_spent) || 0), 0).toFixed(2)} h total</div>
                {/* Prospect's contact details — tap a number to call it (on a phone), or WhatsApp it */}
                {(showFollowup.contact_person || showFollowup.phone || showFollowup.email) && (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 14px", marginTop: 6, fontSize: 13 }}>
                    {showFollowup.contact_person && <span>👤 <b>{showFollowup.contact_person}</b>{showFollowup.designation ? <span style={{ color: colors.textLight }}> · {showFollowup.designation}</span> : null}</span>}
                    {String(showFollowup.phone || "").split(/[,;/]+/).map(x => x.trim()).filter(Boolean).map((ph, i) => {
                      const digits = ph.replace(/\D/g, "");
                      const wa = digits.length === 10 ? "91" + digits : digits.replace(/^0+/, "");
                      return (
                        <span key={i} style={{ whiteSpace: "nowrap" }}>
                          📞 <a href={`tel:${ph.replace(/[^\d+]/g, "")}`} style={{ color: colors.primary, fontWeight: 600, textDecoration: "none" }}>{ph}</a>
                          {digits.length >= 10 && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
                            style={{ marginLeft: 6, fontSize: 11, color: "#15803d", textDecoration: "none", border: "1px solid #bbf7d0", borderRadius: 10, padding: "1px 7px" }}>WhatsApp</a>}
                        </span>
                      );
                    })}
                    {String(showFollowup.email || "").split(/[,;\s]+/).filter(e => e.includes("@")).map((em, i) => (
                      <span key={"e" + i}>✉ <a href={`mailto:${em}`} style={{ color: colors.primary, textDecoration: "none" }}>{em}</a></span>
                    ))}
                  </div>
                )}
              </div>
              <button style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer" }} onClick={() => setShowFollowup(null)}>✕</button>
            </div>

            {/* ONE follow-up form: "Log New Follow-up", or — after check-out / Edit / Alt+E —
                "Completing visit" / "Editing follow-up" with that entry loaded into it */}
            <div ref={fuFormRef} style={{ padding: "16px", background: fuEditing ? "#fffbeb" : "#f0f4ff", borderRadius: 8, border: `1px solid ${fuEditing ? "#fcd34d" : colors.primary + "30"}`, marginBottom: 16, scrollMarginTop: 10 }}>
              {fuOpenVisit && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 12px", marginBottom: 10, borderRadius: 6, background: "#ecfdf5", border: "1px solid #a7f3d0", fontSize: 12.5, color: "#065f46" }}>
                  <span>📍 <b>Visit in progress</b>: {fuOpenVisit.followup_type}, checked in {fuOpenVisit.start_time ? formatTime12hr(String(fuOpenVisit.start_time).slice(0, 5)) : ""} · press Check-out when leaving, then complete the summary here</span>
                  <button style={{ ...styles.btn("warning", "sm"), marginLeft: "auto" }} disabled={fuBusy} onClick={() => visitAction(fuOpenVisit, "checkout")}>📍 Check-out <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+O)</span></button>
                </div>
              )}
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: fuEditing ? "#92400e" : colors.primary }}>
                {!fuEditing ? "Log New Follow-up" : (
                  <>✏ {editingFollowup.checkout_time && !editingFollowup.outcome && !editingFollowup.summary ? "Completing visit" : "Editing follow-up"}:{" "}
                    {editingFollowup.followup_type} · {formatDateDMY(editingFollowup.followup_date)}
                    {editingFollowup.start_time ? ` · ${formatTime12hr(String(editingFollowup.start_time).slice(0, 5))}${editingFollowup.end_time ? "–" + formatTime12hr(String(editingFollowup.end_time).slice(0, 5)) : ""}` : ""}
                    {editingFollowup.created_by_name ? ` · by ${editingFollowup.created_by_name}` : ""}</>
                )}
              </div>
              <div style={styles.grid(3)}>
                <div><label style={styles.label}>Date *</label><DateInput value={fuVal.followup_date} onChange={v => setFuVal({ followup_date: v })} /></div>
                <div><label style={styles.label}>Type *</label>
                  <select style={styles.select} value={fuVal.followup_type || ""} onChange={e => setFuVal({ followup_type: e.target.value })}>
                    {followupTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label style={styles.label}>Outcome</label>
                  <select style={styles.select} value={fuVal.outcome || ""} onChange={e => setFuVal({ outcome: e.target.value })}>
                    <option value="">Select...</option>
                    {outcomeOptions.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: "1 / -1" }}><div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}><label style={{ ...styles.label, margin: 0 }}>Summary / Notes</label>
                  <VoiceMinutes kind="lead" context={`Prospect: ${showFollowup?.prospect_company || ""}`}
                    onUse={(v) => setFuVal(f => ({
                      summary: [f.summary, v.minutesText].filter(Boolean).join("\n"),
                      outcome: outcomeOptions.includes(v.outcome) ? v.outcome : f.outcome,
                      next_followup_date: v.next_action_date || f.next_followup_date,
                      next_action: v.next_action || f.next_action,
                      _voice: { transcript: v.transcript, english: v.english, minutes: v.minutesText, language: v.language, seconds: v.seconds },
                    }))} /></div>
                  <FastText key={fuEditing ? "e" + editingFollowup.id : "new"} multiline style={{ ...styles.input, minHeight: 50 }} value={fuVal.summary || ""} onCommit={(t) => setFuVal({ summary: t })} placeholder={fuOpenVisit ? "Type the summary after check-out… (anything typed now is kept)" : "What was discussed..."} />
                </div>
                <div><label style={styles.label}>Start Time</label><TimeInput value={String(fuVal.start_time || "").slice(0, 5)} onChange={v => setFuVal({ start_time: v })} /></div>
                <div><label style={styles.label}>End Time</label><TimeInput value={String(fuVal.end_time || "").slice(0, 5)} onChange={v => setFuVal({ end_time: v })} /></div>
                <div><label style={styles.label}>Hours (auto)</label>
                  <input style={{ ...styles.input, background: "#f1f5f9" }} readOnly value={fuHours(String(fuVal.start_time || "").slice(0, 5), String(fuVal.end_time || "").slice(0, 5))} placeholder="—" />
                </div>
                {!fuEditing && (
                  <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: -4 }}>
                    <span style={{ fontSize: 11, color: colors.textLight }}>Phone call?</span>
                    <button type="button" style={{ ...styles.btn("ghost", "sm"), border: `1px solid ${colors.border}` }}
                      onClick={() => setFuVal({ start_time: nowHHMM(), end_time: "" })}>⏱ Start call</button>
                    <button type="button" style={{ ...styles.btn("ghost", "sm"), border: `1px solid ${colors.border}` }} disabled={!followupForm.start_time}
                      onClick={() => setFuVal({ end_time: nowHHMM() })}>⏹ End call</button>
                    {followupForm.start_time && !followupForm.end_time && <span style={{ fontSize: 11, color: colors.success, fontWeight: 600 }}>● call running since {formatTime12hr(followupForm.start_time)}</span>}
                  </div>
                )}
                <div><label style={styles.label}>Next Follow-up Date</label><DateInput value={fuVal.next_followup_date} onChange={v => setFuVal({ next_followup_date: v })} /></div>
                <div><label style={styles.label}>Next Action</label>
                  <select style={styles.select} value={fuVal.next_action || ""} onChange={e => setFuVal({ next_action: e.target.value })}>
                    <option value="">Select...</option>
                    {["Call Back", "Send Brochure", "Schedule Demo", "Send Proposal", "Site Visit", "Follow Up Email", "Negotiate", "Close Deal", "No Action"].map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
                  {!fuEditing ? (<>
                    <button style={styles.btn("primary")} onClick={saveFollowup}>Log Follow-up <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+S)</span></button>
                    <button style={styles.btn("success")} disabled={fuBusy} onClick={startVisit}
                      title="At the customer now? Creates today's visit and records the time and your location.">📍 Start visit (check-in) <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+I)</span></button>
                  </>) : (<>
                    <button style={styles.btn("primary")} onClick={saveEditFollowup}>Save changes <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+S)</span></button>
                    <button style={styles.btn("ghost")} onClick={() => setEditingFollowup(null)}>Cancel <span style={{ fontSize: 9, opacity: 0.75 }}>(Esc)</span></button>
                  </>)}
                </div>
              </div>
            </div>

            {/* Mark as Lost/Converted */}
            {!["Converted", "Lost"].includes(showFollowup.status) && (
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <button style={styles.btn("success", "sm")} onClick={markConverted}>✓ Mark as Converted <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+V)</span></button>
                <button style={styles.btn("danger", "sm")} onClick={markLost}>✗ Mark as Lost <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+L)</span></button>
              </div>
            )}

            {/* Follow-up History */}
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Follow-up History ({followups.length})
              {followups.length > 0 && <span style={{ fontSize: 11, fontWeight: 400, color: colors.textLight, marginLeft: 8, display: isMobileNow() ? "none" : "inline" }}>Alt+E edits the latest · Esc closes</span>}</div>
            {followups.length > 0 && (() => {
              const by = {}; followups.forEach(f => { const n = f.created_by_name || "—"; by[n] = (by[n] || 0) + 1; });
              const hrs = followups.reduce((t, f) => t + (parseFloat(f.hours_spent) || 0), 0);
              return <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 8 }}>
                {followups.length} follow-up{followups.length === 1 ? "" : "s"} · {hrs.toFixed(2)} h total · {Object.entries(by).map(([n, c]) => `${n} (${c})`).join(", ")}
              </div>;
            })()}
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              {followups.length === 0 && <p style={{ fontSize: 12, color: colors.textLight, textAlign: "center", padding: 20 }}>No follow-ups logged yet. Use the form above to log the first one.</p>}
              {followups.map((f, i) => (
                <div key={f.id} style={{ padding: "12px 16px", background: editingFollowup?.id === f.id ? "#fffbeb" : (i % 2 === 0 ? "#f8fafc" : "#fff"), borderRadius: 8, marginBottom: 4, border: `1px solid ${editingFollowup?.id === f.id ? colors.warning : colors.border}` }}>
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ ...styles.badge(colors.primary), fontSize: 10 }}>{f.followup_type}</span>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{formatDateDMY(f.followup_date)}</span>
                          {editingFollowup?.id === f.id && <span style={{ fontSize: 10, fontWeight: 700, color: "#92400e" }}>✏ being edited above</span>}
                          {f.outcome && <span style={{ ...styles.badge(f.outcome === "Interested" || f.outcome === "Converted" ? colors.success : f.outcome === "No Response" || f.outcome === "Lost" ? colors.danger : colors.warning), fontSize: 10 }}>{f.outcome}</span>}
                        </div>
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <span style={{ fontSize: 10, color: colors.textLight, marginRight: 8 }}>{f.created_by_name}</span>
                          <button style={{ ...styles.btn("ghost", "sm"), padding: "2px 8px", fontSize: 11 }} onClick={() => setEditingFollowup(withLocalDates(f, FU_DATE_KEYS))}>Edit</button>
                          <button style={{ ...styles.btn("danger", "sm"), padding: "2px 8px", fontSize: 11 }} onClick={async () => {
                            if (!window.confirm("Delete this follow-up?")) return;
                            try {
                              await api(`/lead-followups/${f.id}`, { method: "DELETE" });
                              setToast({ message: "Follow-up deleted!", type: "success" });
                              await reloadFollowups(); fetchData();
                            } catch(e) { setToast({ message: e.message, type: "error" }); }
                          }}>Delete</button>
                        </div>
                      </div>
                      {/* Visit time and check-in / check-out */}
                      {(f.start_time || f.checkin_time || VISIT_TYPES.includes(f.followup_type)) && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", fontSize: 11, marginTop: 6, color: colors.textLight }}>
                          {f.start_time && <span>🕐 {formatTime12hr(f.start_time)}{f.end_time ? ` – ${formatTime12hr(f.end_time)}` : " – in progress"}{f.hours_spent != null ? ` · ${parseFloat(f.hours_spent).toFixed(2)} h` : ""}</span>}
                          {f.checkin_time && (f.checkin_lat
                            ? <a href={geoMapLink(f.checkin_lat, f.checkin_lng)} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>📍 Check-in location</a>
                            : <span>📍 Checked in (no location)</span>)}
                          {f.checkin_time && <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => visitAction(f, "undo-checkin")}>↺ undo</span>}
                          {f.checkout_time && (f.checkout_lat
                            ? <a href={geoMapLink(f.checkout_lat, f.checkout_lng)} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>📍 Check-out location</a>
                            : <span>📍 Checked out (no location)</span>)}
                          {f.checkout_time && <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => visitAction(f, "undo-checkout")}>↺ undo</span>}
                          {!f.checkin_time && !f.end_time && (
                            <button style={{ ...styles.btn("success", "sm"), padding: "2px 8px", fontSize: 11 }} disabled={fuBusy} onClick={() => visitAction(f, "checkin")}>📍 Check-in</button>
                          )}
                          {f.checkin_time && !f.checkout_time && (
                            <button style={{ ...styles.btn("warning", "sm"), padding: "2px 8px", fontSize: 11 }} disabled={fuBusy} onClick={() => visitAction(f, "checkout")}>📍 Check-out <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+O)</span></button>
                          )}
                        </div>
                      )}
                      {f.summary && <div style={{ fontSize: 12, marginTop: 6, color: colors.text, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{f.summary}</div>}
                      {fuVoice[f.id] && (
                        <div style={{ fontSize: 11, marginTop: 4 }}>
                          <span style={{ color: colors.primary, cursor: "pointer", textDecoration: "underline" }} onClick={() => setFuVoiceOpen(fuVoiceOpen === f.id ? null : f.id)}>🎤 what was said</span>
                          {fuVoiceOpen === f.id && (
                            <div style={{ marginTop: 6, padding: 8, background: "#f8fafc", border: `1px solid ${colors.border}`, borderRadius: 6, lineHeight: 1.5 }}>
                              <div style={{ color: colors.textLight }}>As spoken{fuVoice[f.id].language ? ` (${fuVoice[f.id].language})` : ""}:</div>
                              <div style={{ whiteSpace: "pre-wrap", marginBottom: 6 }}>{fuVoice[f.id].transcript}</div>
                              {fuVoice[f.id].english && <><div style={{ color: colors.textLight }}>English:</div><div style={{ whiteSpace: "pre-wrap" }}>{fuVoice[f.id].english}</div></>}
                            </div>
                          )}
                        </div>
                      )}
                      {f.next_followup_date && <div style={{ fontSize: 11, marginTop: 4, color: colors.textLight }}>Next: {formatDateDMY(f.next_followup_date)} {f.next_action ? `· ${f.next_action}` : ""}</div>}
                    </>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {lostDlg && showFollowup && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setLostDlg(null)}>
          <div style={{ ...styles.card, width: "100%", maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px", fontSize: 17 }}>✗ Mark as Lost</h3>
            <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 12 }}>#{showFollowup.enquiry_number} {showFollowup.prospect_company}</div>
            <div style={{ marginBottom: 10 }}><label style={styles.label}>Reason *</label>
              <SearchableSelect options={lostReasonOptions(masters)} value={lostDlg.reason} onChange={v => setLostDlg(d => ({ ...d, reason: v }))} placeholder="Pick a reason..."
                onCreate={() => setQc({ kind: "lostReason", apply: (id, item) => setLostDlg(d => d && ({ ...d, reason: item?.name || d.reason })) })} createLabel="Create Reason" /></div>
            <div style={{ marginBottom: 10 }}><label style={styles.label}>Lost to which competitor? (optional)</label>
              <input style={styles.input} value={lostDlg.competitor} onChange={e => setLostDlg(d => ({ ...d, competitor: e.target.value }))} placeholder="e.g. Busy, Marg" /></div>
            <div style={{ marginBottom: 14 }}><label style={styles.label}>Details (optional)</label>
              <input style={styles.input} value={lostDlg.details} onChange={e => setLostDlg(d => ({ ...d, details: e.target.value }))} placeholder="e.g. Rs 8,000 cheaper, decided after demo" /></div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button style={styles.btn("ghost")} onClick={() => setLostDlg(null)}>Cancel <span style={{ fontSize: 9, opacity: 0.75 }}>(Esc)</span></button>
              <button style={styles.btn("danger")} onClick={saveLost}>Mark as Lost <span style={{ fontSize: 9, opacity: 0.75 }}>(Alt+S)</span></button>
            </div>
          </div>
          <QuickCreateModal qc={qc} masters={masters} onClose={() => setQc(null)} />
        </div>
      )}
      {quickCreate && (
        <FullCreate title={quickCreate.title} endpoint={quickCreate.endpoint} fields={quickCreate.fields}
          onCreated={(newItem) => {
            const val = ["lead_source", "status", "lost_reason"].includes(quickCreate.targetField) ? (newItem.name || newItem.id?.toString()) : newItem.id?.toString();
            setForm(f => ({ ...f, [quickCreate.targetField]: val }));
            if (loadMasters) loadMasters();
          }}
          onClose={() => setQuickCreate(null)} />
      )}
    </div>
  );
};
// ============================================================================
// ============================================================================
// REPORTS SCREEN
// ============================================================================
// ---- Report Writer: build your own report from Call or Lead fields ----
const RW_CALL_FIELDS = [
  ["ticket_number", "Ticket #"], ["log_date", "Log Date", "date"], ["call_date", "Call Date", "date"],
  ["executive_name", "Executive"], ["principal_name", "Principal Company"], ["client_name", "Customer"],
  ["customer_phone", "Phone"], ["contact_person", "Contact Person"], ["contact_phone", "Contact Number"],
  ["customer_email", "Email"], ["contact_email", "Contact Email"], ["license_number", "License Number"],
  ["product_name", "Product / Service For"], ["tdl_type", "TDL Type"], ["call_start_time", "Start Time", "time"],
  ["call_end_time", "End Time", "time"], ["hours_spent", "Hours Spent"], ["call_type_name", "Call Type"],
  ["call_status", "Status"], ["location_name", "Location"], ["physical_days", "Physical Days"],
  ["subscription_expiry", "Subscription Expiry", "date"], ["billed_through", "Billed Through / Partner"],
  ["assigned_to_name", "Assigned To"], ["next_action_date", "Next Action Date", "date"],
  ["next_action_time", "Next Action Time", "time"], ["parent_ticket_number", "Continuation of Ticket"],
  ["contact_person_called", "Contact Person Spoke To"], ["software_version", "Release / Version"],
  ["problem_observed", "Problem Observed"], ["solution_provided", "Solution Provided"],
  ["regarding", "Subject"], ["call_details", "Call Notes"],
];
const RW_LEAD_FIELDS = [
  ["enquiry_number", "Enquiry #"], ["enquiry_date", "Enquiry Date", "date"], ["prospect_company", "Prospect Company"],
  ["contact_person", "Contact Person"], ["designation", "Designation"], ["phone", "Phone"], ["email", "Email"],
  ["city", "City"], ["location_name", "Location"], ["lead_source", "Lead Source"], ["referral_client_name", "Referred By"],
  ["product_name", "Product"], ["number_of_users", "No. of Users"], ["estimated_deal_value", "Estimated Deal Value"],
  ["attended_by_name", "Attended By"], ["status", "Status"], ["priority", "Priority"],
  ["follow_up_date", "Follow-up Date", "date"], ["next_action", "Next Action"], ["remarks", "Remarks"],
  ["assigned_to_name", "Assigned To"], ["assigned_by_name", "Assigned By"], ["demo_date", "Demo Date", "date"],
  ["conversion_date", "Conversion Date", "date"], ["actual_deal_value", "Deal Value (closed)"], ["sale_type_name", "Sale Type"],
  ["lost_reason", "Lost Reason"], ["lost_details", "Lost Details"], ["lost_to_competitor", "Lost To (competitor)"], ["competitor_info", "Competitor Info"], ["total_followups", "Follow-ups"],
  ["total_hours", "Total Hours"], ["latest_fu_date", "Latest Follow-up Date", "date"], ["latest_outcome", "Latest Outcome"], ["latest_summary", "Latest Summary"], ["latest_fu_by", "Latest Follow-up By"],
  ["updated_at", "Last Updated", "date"], ["created_at", "Created On", "date"],
];
const RW_CALL_DATE_FIELDS = [["call_date", "Call Date"], ["log_date", "Log Date"], ["next_action_date", "Next Action Date"], ["subscription_expiry", "Subscription Expiry"]];
const RW_LEAD_DATE_FIELDS = [["enquiry_date", "Enquiry Date"], ["follow_up_date", "Follow-up Date"], ["demo_date", "Demo Date"],
  ["conversion_date", "Conversion Date"], ["updated_at", "Last Updated"], ["created_at", "Created On"]];

let __rwAdvRows = [];   // Report Writer: advanced filters kept across screen navigation
const ReportWriter = ({ masters, companyLogo, companyName }) => {
  const [source, setSource] = useState("calls");
  const fields = source === "calls" ? RW_CALL_FIELDS : RW_LEAD_FIELDS;
  const dateFields = source === "calls" ? RW_CALL_DATE_FIELDS : RW_LEAD_DATE_FIELDS;
  const defaultCols = source === "calls"
    ? ["ticket_number", "call_date", "executive_name", "client_name", "call_type_name", "call_status", "hours_spent"]
    : ["enquiry_number", "enquiry_date", "prospect_company", "contact_person", "phone", "status", "priority"];

  const [cols, setCols] = useState(defaultCols);
  const [dateField, setDateField] = useState(source === "calls" ? "call_date" : "enquiry_date");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [advRows, setAdvRowsState] = useState(__rwAdvRows);
  const setAdvRows = (v) => { __rwAdvRows = v; setAdvRowsState(v); };
  const [ran, setRan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const { sorts, toggleSort, clearSort } = useMultiSort();

  // ---- Saved reports ----
  const [saved, setSaved] = useState([]);
  const [savedId, setSavedId] = useState("");
  const loadSaved = async () => { try { const r = await api(`/reports/saved-reports`); setSaved(r.data || []); } catch (e) {} };
  useEffect(() => { loadSaved(); }, []);

  const applySaved = (id) => {
    setSavedId(id);
    if (!id) return;
    const rep = saved.find(s => String(s.id) === String(id));
    if (!rep) return;
    const d = typeof rep.definition === "string" ? JSON.parse(rep.definition) : rep.definition;
    setSource(rep.source);
    // let the source-change effect run first, then restore the saved selections
    setTimeout(() => {
      setCols(d.cols || []);
      setDateField(d.dateField || (rep.source === "calls" ? "call_date" : "enquiry_date"));
      setFrom(d.from || ""); setTo(d.to || ""); setFilters(d.filters || {}); setSearch(d.search || "");
      setRows([]); setRan(false);
      setToast({ message: `Loaded "${rep.name}" — click Run Report.`, type: "success" });
    }, 0);
  };

  const saveReport = async () => {
    if (cols.length === 0) { setToast({ message: "Pick at least one column before saving.", type: "warning" }); return; }
    const current = saved.find(s => String(s.id) === String(savedId));
    const name = window.prompt("Save this report as:", current?.name || "");
    if (!name || !name.trim()) return;
    try {
      const r = await api(`/reports/saved-reports`, { method: "POST", body: { name: name.trim(), source, definition: { cols, dateField, from, to, filters, search } } });
      await loadSaved();
      setSavedId(String(r.data.id));
      setToast({ message: r.updated ? `Updated "${name.trim()}".` : `Saved "${name.trim()}".`, type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const deleteReport = async () => {
    const rep = saved.find(s => String(s.id) === String(savedId));
    if (!rep) { setToast({ message: "Pick a saved report first.", type: "warning" }); return; }
    if (!window.confirm(`Delete saved report "${rep.name}"?`)) return;
    try {
      await api(`/reports/saved-reports/${rep.id}`, { method: "DELETE" });
      setSavedId(""); await loadSaved();
      setToast({ message: "Deleted.", type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // Switching source resets the column choice and filters to that source's defaults
  useEffect(() => {
    setCols(source === "calls"
      ? ["ticket_number", "call_date", "executive_name", "client_name", "call_type_name", "call_status", "hours_spent"]
      : ["enquiry_number", "enquiry_date", "prospect_company", "contact_person", "phone", "status", "priority"]);
    setDateField(source === "calls" ? "call_date" : "enquiry_date");
    setFilters({}); setRows([]); setAdvRows([]); setRan(false); clearSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const toggleCol = (k) => setCols(prev => prev.includes(k) ? prev.filter(c => c !== k) : [...prev, k]);
  const label = (k) => (fields.find(f => f[0] === k) || [k, k])[1];

  const run = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "100000" });   // every matching record (was capped at 5,000)
      Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
      const advFields = source === "calls" ? callFilterFields(masters, advRows) : leadFilterFields(masters);
      Object.entries(advParams(advRows, advFields)).forEach(([k, v]) => { if (v) p.append(k, v); });
      if (search) p.append("search", search);
      const r = await api(`/${source === "calls" ? "call-logs" : "leads"}?${p}`);
      let data = r.data || [];
      // Date range is applied on the chosen date field
      if (from || to) {
        data = data.filter(x => {
          const v = x[dateField]; if (!v) return false;
          const d = toISODate(String(v));   // local date (slice(0,10) of a UTC timestamp was a day early)
          if (from && d < from) return false;
          if (to && d > to) return false;
          return true;
        });
      }
      setRows(data); setRan(true);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  };

  const cell = (r, k) => {
    const def = fields.find(f => f[0] === k);
    const type = def && def[2];
    const v = r[k];
    if (v === null || v === undefined || v === "") return "—";
    if (type === "date") return formatDateDMY(v);
    if (type === "time") return formatTime12hr(String(v).slice(0, 5));
    if (k === "estimated_deal_value") return formatCurrencyINR(v);
    return String(v);
  };
  const sorted = applyMultiSort(rows, sorts);

  const exportExcel = () => {
    if (sorted.length === 0) { setToast({ message: "Nothing to export — run the report first.", type: "warning" }); return; }
    const content = csvRow(cols.map(label)) + "\n" + sorted.map(r => csvRow(cols.map(k => { const t = cell(r, k); return t === "—" ? "" : t; }))).join("\n") + "\n";
    downloadCSV(content, `custom_report_${source}_${new Date().toISOString().slice(0,10)}.csv`);
    setToast({ message: `Exported ${sorted.length} rows!`, type: "success" });
  };
  const exportPDF = () => {
    if (sorted.length === 0) { setToast({ message: "Nothing to export — run the report first.", type: "warning" }); return; }
    const logoHtml = companyLogo ? `<img src="${companyLogo}" style="height:46px;margin-right:12px" />` : "";
    const head = cols.map(k => `<th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">${label(k)}</th>`).join("");
    const body = sorted.map(r => `<tr>${cols.map(k => `<td style="padding:5px;border:1px solid #ddd">${(cell(r, k) === "—" ? "" : cell(r, k)).toString().replace(/</g, "&lt;")}</td>`).join("")}</tr>`).join("");
    const period = (from || to) ? `${label(dateField)}: ${from ? formatDateDMY(from) : "start"} to ${to ? formatDateDMY(to) : "today"}` : "All dates";
    const html = `<html><head><title>Custom Report</title><style>@page{size:landscape;margin:10mm}</style></head><body style="font-family:Arial,sans-serif;padding:18px">
      <div style="display:flex;align-items:center;margin-bottom:8px">${logoHtml}<h1 style="color:#1a56db;margin:0;font-size:20px">${companyName || "CLAS CRM Suite"}</h1></div>
      <h2 style="color:#333;margin:8px 0 4px">Custom Report — ${source === "calls" ? "Calls" : "Leads"}</h2>
      <p style="color:#666;font-size:11px">${period} | ${sorted.length} record(s)</p><hr style="border:1px solid #ddd;margin:8px 0">
      <table style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); w.print();
    setToast({ message: "PDF ready!", type: "success" });
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div style={styles.card}>
      {/* Saved reports */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 10px", marginBottom: 10, background: "#f0f9ff", border: `1px solid ${colors.border}`, borderRadius: 8 }}>
        <b style={{ fontSize: 12.5, color: "#0891b2" }}>📁 Saved Reports</b>
        <select style={{ ...styles.select, minWidth: 240 }} value={savedId} onChange={e => applySaved(e.target.value)}>
          <option value="">— Load a saved report —</option>
          {saved.map(s => <option key={s.id} value={s.id}>{s.name} ({s.source === "calls" ? "Calls" : "Leads"}{s.created_by_name ? " · " + s.created_by_name : ""})</option>)}
        </select>
        <button style={styles.btn("primary", "sm")} onClick={saveReport}>💾 Save current</button>
        {savedId && <button style={{ ...styles.btn("ghost", "sm"), color: colors.danger }} onClick={deleteReport}>🗑 Delete</button>}
        <span style={{ fontSize: 11, color: colors.textLight }}>Saved reports are shared with your team. Saving with an existing name updates it.</span>
      </div>

      {/* Source + date range */}
      <div style={styles.filterBar}>
        <div><label style={styles.label}>Report On</label>
          <select style={styles.select} value={source} onChange={e => { setSavedId(""); setSource(e.target.value); }}>
            <option value="calls">Calls (Call Tracker)</option>
            <option value="leads">Leads / Enquiries</option>
          </select>
        </div>
        <div><label style={styles.label}>Date Field</label>
          <select style={styles.select} value={dateField} onChange={e => setDateField(e.target.value)}>
            {dateFields.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>From</label><DateInput value={from} onChange={setFrom} /></div>
        <div><label style={styles.label}>To</label><DateInput value={to} onChange={setTo} /></div>
        <div><label style={styles.label}>Search</label><SearchInput style={{ ...styles.input, minWidth: 160 }} placeholder="Any text…" value={search} onSearch={setSearch} /></div>
      </div>

      {/* Source-specific filters */}
      <div style={styles.filterBar}>
        {source === "calls" ? (<>
          <div><label style={styles.label}>Executive</label>
            <select style={styles.select} value={filters.executive_id || ""} onChange={e => setF("executive_id", e.target.value)}>
              <option value="">All</option>{masters.executives?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select></div>
          <div><label style={styles.label}>Customer</label>
            <select style={styles.select} value={filters.client_id || ""} onChange={e => setF("client_id", e.target.value)}>
              <option value="">All</option>{masters.clients?.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select></div>
          <div><label style={styles.label}>Call Type</label>
            <select style={styles.select} value={filters.call_type_id || ""} onChange={e => setF("call_type_id", e.target.value)}>
              <option value="">All</option>{masters.callTypes?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div><label style={styles.label}>Status</label>
            <select style={styles.select} value={filters.call_status || ""} onChange={e => setF("call_status", e.target.value)}>
              <option value="">All</option>{["Open","In Progress","Completed","Escalated","On Hold","Cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
        </>) : (<>
          <div><label style={styles.label}>Attended By</label>
            <select style={styles.select} value={filters.attended_by_id || ""} onChange={e => setF("attended_by_id", e.target.value)}>
              <option value="">All</option>{masters.executives?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select></div>
          <div><label style={styles.label}>Status</label>
            <select style={styles.select} value={filters.status || ""} onChange={e => setF("status", e.target.value)}>
              <option value="">All</option>{["New","Contacted","Demo","Proposal","Negotiation","Converted","Lost"].map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label style={styles.label}>Priority</label>
            <select style={styles.select} value={filters.priority || ""} onChange={e => setF("priority", e.target.value)}>
              <option value="">All</option>{["Hot","Warm","Cold"].map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label style={styles.label}>Source</label>
            <select style={styles.select} value={filters.lead_source || ""} onChange={e => setF("lead_source", e.target.value)}>
              <option value="">All</option>{masters.leadSources?.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select></div>
        </>)}
        <div style={{ flex: 1 }} />
        <button style={styles.btn("ghost", "sm")} onClick={() => { setFilters({}); setSearch(""); setFrom(""); setTo(""); setRows([]); setRan(false); }}>Clear</button>
        <button style={styles.btn("primary", "sm")} onClick={run} disabled={loading || cols.length === 0}>{loading ? "Running…" : "▶ Run Report"}</button>
        <button style={styles.btn("success", "sm")} onClick={exportExcel}>📊 Excel</button>
        <button style={{ ...styles.btn("ghost", "sm"), background: "#dc2626", color: "#fff" }} onClick={exportPDF}>📄 PDF</button>
      </div>

      {/* Column picker */}
      {/* Advanced filters — any field, on top of the simple filters above */}
      <AdvancedFilters masters={masters} screen={source === "calls" ? "calls" : "leads"}
        fieldsFn={source === "calls" ? callFilterFields : leadFilterFields}
        rows={advRows} setRows={setAdvRows} onApply={(r) => { setAdvRows(r); setRan(false); }} />
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 12, background: "#f8fafc" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <b style={{ fontSize: 12.5 }}>Columns ({cols.length} selected)</b>
          <button style={styles.btn("ghost", "sm")} onClick={() => setCols(fields.map(f => f[0]))}>Select all</button>
          <button style={styles.btn("ghost", "sm")} onClick={() => setCols([])}>Clear all</button>
          <span style={{ fontSize: 11, color: colors.textLight }}>Tick the fields you want; they appear in the order you tick them.</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", maxHeight: 140, overflowY: "auto" }}>
          {fields.map(([k, l]) => (
            <label key={k} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", minWidth: 170 }}>
              <input type="checkbox" checked={cols.includes(k)} onChange={() => toggleCol(k)} /> {l}
            </label>
          ))}
        </div>
      </div>

      {!ran ? (
        <div style={{ padding: 30, textAlign: "center", color: colors.textLight, fontSize: 14 }}>Pick your columns and filters, then click <b>▶ Run Report</b>.</div>
      ) : (<>
        <div style={{ fontSize: 13, color: colors.textLight, marginBottom: 6 }}><b style={{ color: colors.text }}>{sorted.length}</b> record(s){(from || to) ? ` · ${label(dateField)} ${from ? formatDateDMY(from) : "start"} → ${to ? formatDateDMY(to) : "today"}` : ""}. Click headers to sort (multi-level).</div>
        <SortBar sorts={sorts} clearSort={clearSort} labels={Object.fromEntries(fields.map(f => [f[0], f[1]]))} />
        <ScrollBox>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: Math.max(700, cols.length * 130) }}>
            <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
              {cols.map((k, i) => <SortTh key={k} label={label(k)} sortKey={k} sorts={sorts} onToggle={toggleSort}
                style={{ ...styles.td, fontWeight: 700, color: colors.textLight, position: "sticky", top: 0, background: "#f8fafc", zIndex: 3, ...(i === 0 ? { left: 0, zIndex: 4, minWidth: 140 } : {}) }} />)}
            </tr></thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${colors.border}` }}>
                  {cols.map((k, ci) => <td key={k} style={{ ...styles.td, ...(ci === 0 ? { position: "sticky", left: 0, background: "#fff", zIndex: 1, fontWeight: 600, minWidth: 140 } : {}) }}>{cell(r, k)}</td>)}
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={cols.length} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>No records match these filters.</td></tr>}
            </tbody>
          </table>
        </ScrollBox>
      </>)}
    </div>
  );
};

let __licAdvRows = [];   // Licences & Renewals: advanced filters kept across screen navigation
const LicensesReport = ({ masters, companyLogo, companyName, setPendingClient, setPendingReturn, setActiveScreen }) => {
  const monthStart = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-01"; };
  const todayStr = () => new Date().toISOString().slice(0,10);
  const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate()+n); return d.toISOString().slice(0,10); };
  const [view, setView] = useState("expiring");
  const [days, setDays] = useState("30");
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(plusDays(30));
  const [clientId, setClientId] = useState("");
  const [productId, setProductId] = useState("");
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [advRows, setAdvRowsState] = useState(__licAdvRows);
  const setAdvRows = (v) => { __licAdvRows = v; setAdvRowsState(v); };
  const { sorts, toggleSort, clearSort } = useMultiSort();

  const matchRow = (r) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return [r.client_name, r.contact_person, r.phone, r.email, r.product_name, r.license_number, r.user_type, r.term, r.note, r.renewed_by].some(v => (v || "").toString().toLowerCase().includes(q));
  };
  const filteredData = applyMultiSort(data.filter(matchRow), sorts, {
    contacts: (r) => contactNums(r), emails: (r) => contactMails(r),
    status: (r) => r.days_left,
  });
  const filteredRenewedTotal = filteredData.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ view });
      if (view === "expiring") p.append("days", days);
      if (view === "due" || view === "renewed") { if (from) p.append("date_from", from); if (to) p.append("date_to", to); }
      if (clientId) p.append("client_id", clientId);
      if (productId) p.append("product_id", productId);
      Object.entries(advParams(advRows, licenceFilterFields(masters))).forEach(([k, v]) => { if (v) p.append(k, v); });
      const r = await api(`/reports/licenses?${p}`);
      setData(r.data || []); setTotals(r.totals || {});
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  }, [view, days, from, to, clientId, productId, advRows, masters]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // sensible default date range per view
    if (view === "due") { setFrom(todayStr()); setTo(plusDays(30)); }
    else if (view === "renewed") { setFrom(monthStart()); setTo(todayStr()); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openHistory = async (row) => {
    setHistoryRow(row); setHistoryData([]);
    try { const r = await api(`/customer-products/${row.customer_product_id}/renewals`); setHistoryData(r.data || []); } catch (e) {}
  };

  const generateReminders = async () => {
    if (!window.confirm("Create follow-up calls for licenses expiring within 30 days (skips any already reminded)?")) return;
    try {
      const r = await api(`/call-logs/renewal-reminders/generate`, { method: "POST", body: { days: view === "expiring" ? parseInt(days) : 30 } });
      setToast({ message: `Created ${r.created} follow-up(s) from ${r.checked} due license(s).`, type: "success" });
      load();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };
  const createOneFollowup = async (row, e) => {
    e.stopPropagation();
    try { await api(`/call-logs/renewal-reminders/${row.customer_product_id}`, { method: "POST", body: {} }); setToast({ message: "Follow-up created & assigned.", type: "success" }); }
    catch (err) { setToast({ message: err.message, type: "error" }); }
  };
  const openCustomer = (r) => { if (setPendingClient) { setPendingReturn("reports"); setPendingClient(r.client_id); setActiveScreen("clients"); } };

  const statusOf = (d) => {
    if (!d) return { label: "—", color: colors.textLight };
    const days = Math.ceil((new Date(String(d).slice(0,10)) - new Date(new Date().toDateString())) / 86400000);
    if (days < 0) return { label: "Expired", color: colors.danger };
    if (days <= 30) return { label: "Expiring", color: "#d97706" };
    return { label: "Active", color: colors.success };
  };

  const isRenewed = view === "renewed";

  const exportExcel = () => {
    if (filteredData.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    let headers, rows;
    if (isRenewed) {
      headers = ["Customer", "Contact", "Phone", "Contact Email", "Product", "License #", "Old Expiry", "New Expiry", "Renewed On", "Term", "Amount", "Note", "Renewed By"];
      rows = filteredData.map(r => csvRow([r.client_name||"", r.contact_person||"", r.phone||"", r.contact_email||"", r.product_name||"", r.license_number||"", r.old_expiry?formatDateDMY(r.old_expiry):"", r.new_expiry?formatDateDMY(r.new_expiry):"", r.renewed_date?formatDateDMY(r.renewed_date):"", r.term||"", r.amount||0, r.note||"", r.renewed_by||""]));
    } else {
      headers = ["Customer", "Contact Person", "Phone", "Contact Number", "Email", "Contact Email", "Product", "License #", "User Type", "Qty", "Expiry", "Days Left", "Status"];
      rows = filteredData.map(r => csvRow([r.client_name||"", r.contact_person||"", r.phone||"", r.contact_phone||"", r.email||"", r.contact_email||"", r.product_name||"", r.license_number||"", r.user_type||"", r.quantity||"", r.subscription_expiry?formatDateDMY(r.subscription_expiry):"", r.days_left, statusOf(r.subscription_expiry).label]));
    }
    downloadCSV(csvRow(headers) + "\n" + rows.join("\n") + "\n", `licenses_${view}_${todayStr()}.csv`);
    setToast({ message: `Exported ${filteredData.length} rows!`, type: "success" });
  };

  const exportPDF = () => {
    if (filteredData.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const logoHtml = companyLogo ? `<img src="${companyLogo}" style="height:46px;margin-right:12px" />` : "";
    let cols, body;
    if (isRenewed) {
      cols = ["Customer", "Contact Email", "Product", "License", "New Expiry", "Renewed On", "Term", "Amount"];
      body = filteredData.map(r => `<tr><td style="padding:5px;border:1px solid #ddd">${r.client_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.contact_email||""}</td><td style="padding:5px;border:1px solid #ddd">${r.product_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.license_number||""}</td><td style="padding:5px;border:1px solid #ddd">${r.new_expiry?formatDateDMY(r.new_expiry):""}</td><td style="padding:5px;border:1px solid #ddd">${r.renewed_date?formatDateDMY(r.renewed_date):""}</td><td style="padding:5px;border:1px solid #ddd">${r.term||""}</td><td style="padding:5px;border:1px solid #ddd;text-align:right">${r.amount?formatCurrencyINR(r.amount):""}</td></tr>`).join("");
    } else {
      cols = ["Customer", "Contact Email", "Product", "License", "User Type", "Qty", "Expiry", "Days Left"];
      body = filteredData.map(r => `<tr><td style="padding:5px;border:1px solid #ddd">${r.client_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.contact_email||r.email||""}</td><td style="padding:5px;border:1px solid #ddd">${r.product_name||""}</td><td style="padding:5px;border:1px solid #ddd">${r.license_number||""}</td><td style="padding:5px;border:1px solid #ddd">${r.user_type||""}</td><td style="padding:5px;border:1px solid #ddd">${r.quantity||""}</td><td style="padding:5px;border:1px solid #ddd">${r.subscription_expiry?formatDateDMY(r.subscription_expiry):""}</td><td style="padding:5px;border:1px solid #ddd;text-align:right">${r.days_left}</td></tr>`).join("");
    }
    const head = cols.map(h => `<th style="padding:6px;border:1px solid #ddd;background:#0891b2;color:#fff;text-align:left">${h}</th>`).join("");
    const totalLine = isRenewed ? `<p style="font-size:12px"><b>${isRenewed ? filteredData.length : filteredData.length}</b> renewal(s) &middot; Total amount: <b>${formatCurrencyINR(searchText ? filteredRenewedTotal : (totals.total_amount||0))}</b></p>` : `<p style="font-size:12px"><b>${filteredData.length}</b> license(s)</p>`;
    const html = `<html><head><title>Licenses & Renewals</title><style>@page{size:landscape;margin:10mm}</style></head><body style="font-family:Arial,sans-serif;padding:18px">
      <div style="display:flex;align-items:center;margin-bottom:8px">${logoHtml}<h1 style="color:#0891b2;margin:0;font-size:20px">${companyName || "CLAS CRM Suite"}</h1></div>
      <h2 style="color:#333;margin:8px 0 4px">Licenses & Renewals — ${view.charAt(0).toUpperCase()+view.slice(1)}</h2>
      ${totalLine}<hr style="border:1px solid #ddd;margin:8px 0">
      <table style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); w.print();
    setToast({ message: "PDF ready!", type: "success" });
  };

  return (
    <div style={styles.card}>
      <div style={styles.filterBar}>
        <div><label style={styles.label}>Search</label><input style={{ ...styles.input, minWidth: 180 }} placeholder="Customer, license, product..." value={searchText} onChange={e => setSearchText(e.target.value)} onKeyDown={e => { if (e.key === "Escape" && searchText) { e.stopPropagation(); setSearchText(""); } }} /></div>
        <div><label style={styles.label}>View</label>
          <select style={styles.select} value={view} onChange={e => setView(e.target.value)}>
            <option value="expiring">Expiring soon</option>
            <option value="due">Renewals due (date range)</option>
            <option value="expired">Expired</option>
            <option value="renewed">Renewed (history)</option>
          </select>
        </div>
        {view === "expiring" && <div><label style={styles.label}>Within</label>
          <select style={styles.select} value={days} onChange={e => setDays(e.target.value)}>
            <option value="30">30 days</option><option value="60">60 days</option><option value="90">90 days</option>
          </select></div>}
        {(view === "due" || view === "renewed") && <>
          <div><label style={styles.label}>{view === "renewed" ? "Renewed From" : "Expiry From"}</label><DateInput value={from} onChange={setFrom} /></div>
          <div><label style={styles.label}>To</label><DateInput value={to} onChange={setTo} /></div>
        </>}
        <div><label style={styles.label}>Customer</label>
          <select style={styles.select} value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">All</option>{masters.clients?.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Product</label>
          <select style={styles.select} value={productId} onChange={e => setProductId(e.target.value)}>
            <option value="">All</option>{masters.products?.map(pp => <option key={pp.id} value={pp.id}>{pp.name}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        {!isRenewed && <button style={styles.btn("primary", "sm")} onClick={generateReminders} title="Create follow-up calls for licenses expiring within 30 days (one per license, no duplicates)">🔔 Generate follow-ups</button>}
        <button style={styles.btn("success", "sm")} onClick={exportExcel}>📊 Excel</button>
        <button style={{ ...styles.btn("ghost", "sm"), background: "#dc2626", color: "#fff" }} onClick={exportPDF}>📄 PDF</button>
      </div>

      <div style={{ padding: "2px 8px 10px", fontSize: 13, color: colors.textLight }}>
        {loading ? "Loading…" : isRenewed
          ? <span><b style={{ color: colors.text }}>{filteredData.length}</b> renewal(s) · Total amount: <b style={{ color: colors.success }}>{formatCurrencyINR(searchText ? filteredRenewedTotal : (totals.total_amount || 0))}</b></span>
          : <span><b style={{ color: colors.text }}>{filteredData.length}</b> license(s). Click a row to open the customer, or 🕘 History for its renewal log.</span>}
      </div>

      <AdvancedFilters masters={masters} screen="licences" fieldsFn={licenceFilterFields}
        rows={advRows} setRows={setAdvRows} onApply={(r) => setAdvRows(r)} />
      <SortBar sorts={sorts} clearSort={clearSort} labels={{ client_name: "Customer", contact_person: "Contact Person", contacts: "Contacts", emails: "Email", product_name: "Product", license_number: "License #", user_type: "User Type", quantity: "Qty", subscription_expiry: "Expiry", days_left: "Days Left", status: "Status", old_expiry: "Old Expiry", new_expiry: "New Expiry", renewed_date: "Renewed On", term: "Term", amount: "Amount", note: "Note", contact_email: "Contact Email" }} />

      <ScrollBox>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 900 }}>
          <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
            {(isRenewed
              ? [["Customer","client_name"],["Product","product_name"],["License #","license_number"],["Contact Email","contact_email"],["Old Expiry","old_expiry"],["New Expiry","new_expiry"],["Renewed On","renewed_date"],["Term","term"],["Amount","amount"],["Note","note"],["Action",null]]
              : [["Customer","client_name"],["Contact Person","contact_person"],["Contacts","contacts"],["Email","emails"],["Product","product_name"],["License #","license_number"],["User Type","user_type"],["Qty","quantity"],["Expiry","subscription_expiry"],["Days Left","days_left"],["Status","status"],["Action",null]]
            ).map(([h, k], hi) => k
              ? <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={{ ...styles.td, fontWeight: 700, color: colors.textLight, position: "sticky", top: 0, background: "#f8fafc", zIndex: 3, ...(hi === 0 ? { left: 0, zIndex: 4, minWidth: 160 } : {}) }} />
              : <th key={h} style={{ ...styles.td, fontWeight: 700, color: colors.textLight, position: "sticky", top: 0, background: "#f8fafc", zIndex: 3 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filteredData.map((r, i) => (
              <tr key={i} onClick={() => openCustomer(r)} style={{ borderTop: `1px solid ${colors.border}`, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {isRenewed ? (<>
                  <td style={{ ...styles.td, fontWeight: 600, position: "sticky", left: 0, background: "#fff", zIndex: 1, minWidth: 160 }}>{r.client_name}</td>
                  <td style={styles.td}>{r.product_name || "—"}</td>
                  <td style={styles.td}>{r.license_number || "—"}</td>
                  <td style={styles.td}>{r.contact_email || "—"}</td>
                  <td style={styles.td}>{r.old_expiry ? formatDateDMY(r.old_expiry) : "—"}</td>
                  <td style={styles.td}>{r.new_expiry ? formatDateDMY(r.new_expiry) : "—"}</td>
                  <td style={styles.td}>{r.renewed_date ? formatDateDMY(r.renewed_date) : "—"}</td>
                  <td style={styles.td}>{r.term || "—"}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{r.amount ? formatCurrencyINR(r.amount) : "—"}</td>
                  <td style={styles.td}>{r.note || "—"}</td>
                  <td style={styles.td}><button style={styles.btn("ghost", "sm")} onClick={(e) => { e.stopPropagation(); openHistory(r); }} title="Full renewal history for this license">🕘 History</button></td>
                </>) : (<>
                  <td style={{ ...styles.td, fontWeight: 600, position: "sticky", left: 0, background: "#fff", zIndex: 1, minWidth: 160 }}>{r.client_name}</td>
                  <td style={styles.td}>{r.contact_person || "—"}</td>
                  <td style={styles.td}>{contactNums(r)}</td>
                  <td style={styles.td}>{contactMails(r)}</td>
                  <td style={styles.td}>{r.product_name || "—"}</td>
                  <td style={styles.td}>{r.license_number || "—"}</td>
                  <td style={styles.td}>{r.user_type || "—"}</td>
                  <td style={styles.td}>{r.quantity || "—"}</td>
                  <td style={styles.td}>{r.subscription_expiry ? formatDateDMY(r.subscription_expiry) : "—"}</td>
                  <td style={{ ...styles.td, fontWeight: 600, color: r.days_left < 0 ? colors.danger : r.days_left <= 30 ? "#d97706" : colors.text }}>{r.days_left}</td>
                  <td style={styles.td}>{(() => { const s = statusOf(r.subscription_expiry); return <span style={{ background: s.color + "22", color: s.color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{s.label}</span>; })()}</td>
                  <td style={styles.td}>
                    <button style={styles.btn("primary", "sm")} onClick={(e) => createOneFollowup(r, e)} title="Create a renewal follow-up assigned to the most-recent handler">🔔 Follow-up</button>
                    <button style={{ ...styles.btn("ghost", "sm"), marginLeft: 6 }} onClick={(e) => { e.stopPropagation(); openHistory(r); }} title="Full renewal history for this license">🕘 History</button>
                    {setPendingClient && <button style={{ ...styles.btn("ghost", "sm"), marginLeft: 6 }} onClick={(e) => { e.stopPropagation(); openCustomer(r); }} title="Open this customer to renew the license">Open ↗</button>}
                  </td>
                </>)}
              </tr>
            ))}
            {!loading && filteredData.length === 0 && <tr><td colSpan={isRenewed ? 11 : 12} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>{searchText ? "No matches for your search." : "No records for this view."}</td></tr>}
          </tbody>
        </table>
      </ScrollBox>

      {historyRow && (
        <div style={styles.modal} onClick={() => setHistoryRow(null)}>
          <div style={{ ...styles.modalContent, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: "#0891b2" }}>Renewal History</h3>
            <div style={{ fontSize: 13, color: colors.textLight, marginBottom: 10 }}><b>{historyRow.client_name}</b> · {historyRow.product_name || "—"}{historyRow.license_number ? ` · ${historyRow.license_number}` : ""}</div>
            {historyData.length === 0 ? <p style={{ color: colors.textLight, fontSize: 13 }}>No renewals recorded for this license yet.</p> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>{["Renewed On", "Old → New Expiry", "Term", "Amount", "Note", "By"].map(h => <th key={h} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }}>{h}</th>)}</tr></thead>
                <tbody>{historyData.map(h => (
                  <tr key={h.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={styles.td}>{formatDateDMY(h.renewed_date)}</td>
                    <td style={styles.td}>{h.old_expiry ? formatDateDMY(h.old_expiry) : "—"} → {h.new_expiry ? formatDateDMY(h.new_expiry) : "—"}</td>
                    <td style={styles.td}>{h.term || "—"}</td>
                    <td style={styles.td}>{h.amount ? formatCurrencyINR(h.amount) : "—"}</td>
                    <td style={styles.td}>{h.note || "—"}</td>
                    <td style={styles.td}>{h.renewed_by || "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            <div style={{ textAlign: "right", marginTop: 12 }}><button style={styles.btn("ghost", "sm")} onClick={() => setHistoryRow(null)}>Close</button></div>
          </div>
        </div>
      )}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

let __svAdvRows = [];   // Scheduled Visits: advanced filters kept across screen navigation
// ============================================================================
// Lead Follow-ups report: every follow-up as its own row (Detailed) and a
// combined view grouped by lead / done by / type / outcome / month (Summary),
// with totals. Click a summary row to see its follow-ups.
// ============================================================================
// ============================================================================
// Settings > Email Settings — the Google Workspace account that sends the
// automatic call e-mails, with step-by-step help for the app password.
// ============================================================================
const EmailSettingsScreen = () => {
  const [s, setS] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const load = async () => {
    try { const r = await api("/email/settings"); setS({ ...r.data, password: "" }); }
    catch (e) { setToast({ message: e.message, type: "error" }); setS({}); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (s.enabled && !s.username) { setToast({ message: "Enter the Gmail / Workspace address that sends the e-mails.", type: "error" }); return; }
    if (s.enabled && !s.password && !s.has_password) { setToast({ message: "Enter the 16-letter app password.", type: "error" }); return; }
    setBusy(true);
    try { await api("/email/settings", { method: "PUT", body: s }); setToast({ message: "E-mail settings saved.", type: "success" }); await load(); }
    catch (e) { setToast({ message: e.message, type: "error" }); }
    setBusy(false);
  };
  const test = async () => {
    setBusy(true);
    try { const r = await api("/email/test", { method: "POST", body: { to: testTo || s.username } }); setToast({ message: r.message || "Test e-mail sent.", type: "success" }); }
    catch (e) { setToast({ message: "Test failed: " + e.message, type: "error" }); }
    setBusy(false);
  };
  if (!s) return <div style={styles.card}>Loading…</div>;
  const tick = (key, label, hint) => (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 8 }}>
      <input type="checkbox" checked={key === "enabled" ? !!s.enabled : s[key] !== false} onChange={e => setS({ ...s, [key]: e.target.checked })} style={{ marginTop: 3 }} />
      <span><b>{label}</b>{hint && <span style={{ color: colors.textLight }}> — {hint}</span>}</span>
    </label>
  );
  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <h2 style={{ margin: "0 0 14px" }}>✉ Email Settings</h2>
      <div style={{ display: "grid", gridTemplateColumns: isMobileNow() ? "1fr" : "1.2fr 1fr", gap: 16 }}>
        <div style={styles.card}>
          {tick("enabled", "Send automatic e-mails", "switch everything on / off")}
          {tick("send_visit", "Visit assigned / rescheduled", "to the assigned executive and the customer")}
          {tick("send_done", "Call completed", "to the person who assigned the call and the customer")}
          <div style={{ height: 8 }} />
          <div style={{ marginBottom: 10 }}><label style={styles.label}>Sending address (Google Workspace / Gmail)</label>
            <input style={styles.input} value={s.username || ""} onChange={e => setS({ ...s, username: e.target.value })} placeholder="e.g. support@yourcompany.com" /></div>
          <div style={{ marginBottom: 10 }}><label style={styles.label}>App password (16 letters)</label>
            <input type="password" style={styles.input} value={s.password || ""} onChange={e => setS({ ...s, password: e.target.value })}
              placeholder={s.has_password ? "•••••••••••••••• saved — leave blank to keep it" : "paste the app password here"} autoComplete="new-password" /></div>
          <div style={{ marginBottom: 10 }}><label style={styles.label}>Sender name (what customers see)</label>
            <input style={styles.input} value={s.from_name || ""} onChange={e => setS({ ...s, from_name: e.target.value })} placeholder="e.g. A R Infomatrix Support" /></div>
          <div style={{ marginBottom: 10 }}><label style={styles.label}>Office copy (optional) — a hidden copy of every e-mail goes here</label>
            <input style={styles.input} value={s.office_copy || ""} onChange={e => setS({ ...s, office_copy: e.target.value })} placeholder="e.g. office@yourcompany.com" /></div>
          <details style={{ marginBottom: 12, fontSize: 12, color: colors.textLight }}>
            <summary style={{ cursor: "pointer" }}>Advanced (normally leave as is)</summary>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginTop: 8 }}>
              <div><label style={styles.label}>Mail server</label><input style={styles.input} value={s.smtp_host || ""} onChange={e => setS({ ...s, smtp_host: e.target.value })} /></div>
              <div><label style={styles.label}>Port</label><input style={styles.input} value={s.smtp_port || ""} onChange={e => setS({ ...s, smtp_port: e.target.value })} /></div>
            </div>
          </details>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={styles.btn("primary")} disabled={busy} onClick={save}>{busy ? "Please wait…" : "Save settings"}</button>
          </div>
          <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: 16, paddingTop: 14 }}>
            <label style={styles.label}>Send a test e-mail to</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input style={{ ...styles.input, flex: 1, minWidth: 200 }} value={testTo} onChange={e => setTestTo(e.target.value)} placeholder={s.username || "your e-mail address"} />
              <button style={styles.btn("success")} disabled={busy} onClick={test}>Send test e-mail</button>
            </div>
            <div style={{ fontSize: 11, color: colors.textLight, marginTop: 6 }}>Save the settings first. The test uses the saved account.</div>
          </div>
        </div>
        <div style={{ ...styles.card, fontSize: 13, lineHeight: 1.6 }}>
          <b>How to get the app password (one time)</b>
          <ol style={{ paddingLeft: 18, margin: "8px 0" }}>
            <li>Sign in to the sending Google account in your browser.</li>
            <li>Open <b>myaccount.google.com</b> → <b>Security</b>.</li>
            <li>Make sure <b>2-Step Verification</b> is <b>On</b> (turn it on if needed).</li>
            <li>In the search box at the top, type <b>App passwords</b> and open it.</li>
            <li>App name: <b>CLAS CRM</b> → <b>Create</b>.</li>
            <li>Google shows a <b>16-letter password</b> (in 4 groups). Copy it and paste it on the left. Spaces don't matter.</li>
            <li>Click <b>Save settings</b>, then <b>Send test e-mail</b>.</li>
          </ol>
          <div style={{ color: colors.textLight }}>
            If “App passwords” doesn’t appear, your Google Workspace administrator may need to allow it
            (Admin console → Security → 2-Step Verification). Use a normal mailbox such as support@…, not a group.
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Settings > Email Log — every automatic e-mail: sent / failed / skipped
// ============================================================================
const EmailLogScreen = () => {
  const [rows, setRows] = useState([]);
  const [toast, setToast] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(); if (status) p.append("status", status); if (search) p.append("search", search);
      const r = await api(`/email/log?${p}`); setRows(r.data || []);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [status]);   // eslint-disable-line react-hooks/exhaustive-deps
  const loadRef = useRef(null); loadRef.current = load;   // Enter in the search box runs the newest load
  const resendOne = async (row) => {
    try { const r = await api(`/email/log/${row.id}/resend`, { method: "POST" }); setToast({ message: r.message, type: "success" }); load(); }
    catch (e) { setToast({ message: e.message, type: "error" }); load(); }
  };
  const KIND = { scheduled: "Visit scheduled", rescheduled: "Visit rescheduled", completed: "Call completed", test: "Test" };
  const WHO = { customer: "Customer", executive: "Executive", assigner: "Assigned by", test: "Test" };
  const badge = (st) => {
    const c = { sent: ["#dcfce7", "#166534"], failed: ["#fee2e2", "#b91c1c"], skipped: ["#f1f5f9", "#475569"] }[st] || ["#f1f5f9", "#475569"];
    return <span style={{ background: c[0], color: c[1], padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{st}</span>;
  };
  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <h2 style={{ margin: "0 0 14px" }}>📨 Email Log</h2>
      <div style={styles.card}>
        <div style={styles.filterBar}>
          <div><label style={styles.label}>Status</label>
            <select style={styles.select} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">All</option><option value="sent">Sent</option><option value="failed">Failed</option><option value="skipped">Skipped</option>
            </select></div>
          <div><label style={styles.label}>Search</label>
            <SearchInput style={styles.input} value={search} onSearch={setSearch} onEnter={() => loadRef.current && loadRef.current()} placeholder="Ticket #, e-mail address, subject…" /></div>
          <div style={{ display: "flex", alignItems: "flex-end" }}><button style={styles.btn("primary")} onClick={load}>{loading ? "Loading…" : "Refresh"}</button></div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>When</th><th style={styles.th}>Ticket #</th><th style={styles.th}>E-mail</th><th style={styles.th}>To</th>
              <th style={styles.th}>Address</th><th style={styles.th}>Status</th><th style={styles.th}>Reason</th><th style={styles.th}></th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td style={styles.td}>{r.ticket_number || "—"}</td>
                  <td style={styles.td}>{KIND[r.kind] || r.kind}</td>
                  <td style={styles.td}>{WHO[r.audience] || r.audience}</td>
                  <td style={{ ...styles.td, maxWidth: 240, wordBreak: "break-all" }}>{r.to_address || "—"}</td>
                  <td style={styles.td}>{badge(r.status)}</td>
                  <td style={{ ...styles.td, maxWidth: 300, color: colors.textLight }}>{r.error || ""}</td>
                  <td style={styles.td}>{r.call_id && r.status !== "sent" && <button style={styles.btn("ghost", "sm")} onClick={() => resendOne(r)}>Resend</button>}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>{loading ? "Loading…" : "No e-mails yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Voice minutes: record a voice note in any language → text → English minutes.
// Records in 25-second pieces (the speech service takes up to 30 s at a time),
// sends each piece as soon as it is ready, and joins the text in order.
// No audio is kept — only the text.
// ============================================================================
let __voiceEnabled = null;   // cached: is Voice Minutes switched on?
const voiceMinutesText = (m) => [
  m.summary && `Summary: ${m.summary}`,
  m.problem && `Problem: ${m.problem}`,
  m.solution && `Solution: ${m.solution}`,
  (m.action_items || []).filter(Boolean).length && `Action items:\n${m.action_items.filter(Boolean).map(a => `- ${a}`).join("\n")}`,
  (m.next_action || m.next_action_date) && `Next: ${[m.next_action_date ? formatDateDMY(m.next_action_date) : "", m.next_action_time || "", m.next_action || ""].filter(Boolean).join(" · ")}`,
].filter(Boolean).join("\n");
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result).split(",")[1] || "");
  fr.onerror = reject;
  fr.readAsDataURL(blob);
});

const VoiceMinutes = ({ kind = "call", context = "", onUse, label = "🎤 Voice minutes" }) => {
  const [enabled, setEnabled] = useState(__voiceEnabled);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (__voiceEnabled !== null) return;
    api("/voice/status").then(r => { __voiceEnabled = !!r.data?.enabled; setEnabled(__voiceEnabled); }).catch(() => { __voiceEnabled = false; setEnabled(false); });
  }, []);
  if (!enabled) return null;
  return (<>
    <button type="button" style={{ ...styles.btn("ghost", "sm"), border: `1px solid ${colors.primary}`, color: colors.primary }}
      onClick={() => setOpen(true)} title="Speak the summary in any language — it becomes text and minutes">{label}</button>
    {open && <VoiceMinutesWindow kind={kind} context={context} onClose={() => setOpen(false)} onUse={(v) => { onUse(v); setOpen(false); }} />}
  </>);
};

const VoiceMinutesWindow = ({ kind, context, onClose, onUse }) => {
  const [stage, setStage] = useState("idle");        // idle | recording | working | review
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState("");
  const [pieces, setPieces] = useState({ sent: 0, done: 0 });
  const [res, setRes] = useState(null);               // { transcript, language, m }
  const live = useRef({ stream: null, rec: null, timer: null, tick: null, texts: [], langs: [], jobs: [], stopping: false, idx: 0, mime: "" });

  const cleanup = () => {
    const L = live.current;
    clearTimeout(L.timer); clearInterval(L.tick);
    try { if (L.rec && L.rec.state !== "inactive") L.rec.stop(); } catch (e) {}
    if (L.stream) L.stream.getTracks().forEach(t => t.stop());
    L.stream = null;
  };
  useEffect(() => () => cleanup(), []);   // stop the microphone when the window closes
  // Esc closes only this window (not the form underneath)
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); cleanup(); onClose(); } };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  });

  const sendPiece = (blob, idx) => {
    const L = live.current;
    setPieces(p => ({ ...p, sent: p.sent + 1 }));
    const job = (async () => {
      const audio = await blobToBase64(blob);
      const r = await api("/voice/transcribe", { method: "POST", body: { audio, mime: blob.type || L.mime } });
      L.texts[idx] = r.data?.text || ""; if (r.data?.language) L.langs.push(r.data.language);
      setPieces(p => ({ ...p, done: p.done + 1 }));
    })();
    L.jobs.push(job);
  };
  const startPiece = () => {
    const L = live.current;
    const rec = new MediaRecorder(L.stream, L.mime ? { mimeType: L.mime } : undefined);
    const chunks = []; const idx = L.idx++;
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      if (chunks.length) sendPiece(new Blob(chunks, { type: rec.mimeType || L.mime || "audio/webm" }), idx);
      if (!L.stopping) startPiece();          // carry on with the next 25-second piece
    };
    rec.start(); L.rec = rec;
    L.timer = setTimeout(() => { try { rec.stop(); } catch (e) {} }, 25000);
  };
  const start = async () => {
    setErr("");
    try {
      const L = live.current;
      L.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      L.mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || "";
      L.texts = []; L.langs = []; L.jobs = []; L.stopping = false; L.idx = 0;
      setSecs(0); setPieces({ sent: 0, done: 0 }); setStage("recording");
      L.tick = setInterval(() => setSecs(s => { if (s + 1 >= 300) stop(); return s + 1; }), 1000);   // 5-minute limit
      startPiece();
    } catch (e) {
      setErr("Microphone not available. Allow the microphone for this site (the icon in the address bar) and try again.");
      cleanup(); setStage("idle");
    }
  };
  const stop = async () => {
    const L = live.current;
    if (L.stopping) return;
    L.stopping = true; clearTimeout(L.timer); clearInterval(L.tick);
    const lastStopped = new Promise(r => { if (L.rec && L.rec.state !== "inactive") { const prev = L.rec.onstop; L.rec.onstop = () => { prev && prev(); r(); }; L.rec.stop(); } else r(); });
    setStage("working");
    try {
      await lastStopped;
      await new Promise(r => setTimeout(r, 400));   // let a piece that ended at the same moment be queued too
      if (L.stream) L.stream.getTracks().forEach(t => t.stop());
      await Promise.all(L.jobs);
      const transcript = L.texts.filter(Boolean).join(" ").trim();
      if (!transcript) throw new Error("Nothing was heard. Please record again, a little closer to the phone.");
      const r = await api("/voice/minutes", { method: "POST", body: { transcript, kind, context } });
      const counts = {}; L.langs.forEach(l => { counts[l] = (counts[l] || 0) + 1; });
      const language = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || "";
      setRes({ transcript, language, m: { action_items: [], ...r.data } });
      setStage("review");
    } catch (e) { setErr(e.message); setStage("idle"); }
  };
  const use = () => {
    const m = res.m;
    onUse({ ...m, transcript: res.transcript, language: res.language, seconds: secs, minutesText: voiceMinutesText(m) });
  };
  const setM = (k, v) => setRes(r => ({ ...r, m: { ...r.m, [k]: v } }));
  const box = { ...styles.input, width: "100%", boxSizing: "border-box" };
  const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  const LANG = { "kn-IN": "Kannada", "hi-IN": "Hindi", "ta-IN": "Tamil", "ml-IN": "Malayalam", "te-IN": "Telugu", "en-IN": "English", "mr-IN": "Marathi", "bn-IN": "Bengali", "gu-IN": "Gujarati" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 3000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 16, overflowY: "auto" }}>
      <div style={{ ...styles.card, width: "100%", maxWidth: 680, marginTop: 30 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>🎤 Voice minutes</h3>
          <button style={styles.btn("ghost", "sm")} onClick={() => { cleanup(); onClose(); }}>✕ <span style={{ fontSize: 9, opacity: 0.7 }}>(Esc)</span></button>
        </div>

        {stage === "idle" && (<>
          <div style={{ fontSize: 13, color: colors.textLight, lineHeight: 1.6, marginBottom: 12 }}>
            Speak in any language — Kannada, Hindi, Tamil, Malayalam, English or a mix. Say what the customer needed, what you did,
            and what happens next (with the date). Speak close to the phone, in a quiet place. Up to 5 minutes.
          </div>
          {err && <div style={{ color: colors.danger, fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button style={{ ...styles.btn("danger"), fontSize: 15, padding: "12px 22px" }} onClick={start}>● Start recording</button>
        </>)}

        {stage === "recording" && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div style={{ fontSize: 34, fontWeight: 800, color: colors.danger }}>● {mmss}</div>
            <div style={{ fontSize: 12, color: colors.textLight, margin: "6px 0 14px" }}>Recording… {pieces.done > 0 ? `${pieces.done} part(s) already converted` : ""}</div>
            <button style={{ ...styles.btn("primary"), fontSize: 15, padding: "12px 26px" }} onClick={stop}>■ Stop</button>
          </div>
        )}

        {stage === "working" && (
          <div style={{ textAlign: "center", padding: "24px 0", color: colors.textLight }}>
            Converting your voice to text and writing the minutes… <br />
            <span style={{ fontSize: 12 }}>{pieces.done} of {pieces.sent} part(s) converted</span>
          </div>
        )}

        {stage === "review" && res && (<>
          <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 8 }}>
            {LANG[res.language] ? `Language heard: ${LANG[res.language]} · ` : ""}{mmss} recorded · check and correct anything below, then click Use these minutes.
          </div>
          <details style={{ marginBottom: 10 }} open={false}>
            <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>What was said (as spoken) and English version</summary>
            <label style={{ ...styles.label, marginTop: 8 }}>As spoken</label>
            <textarea style={{ ...box, minHeight: 70 }} value={res.transcript} onChange={e => setRes(r => ({ ...r, transcript: e.target.value }))} />
            <label style={{ ...styles.label, marginTop: 8 }}>English version</label>
            <textarea style={{ ...box, minHeight: 70 }} value={res.m.english || ""} onChange={e => setM("english", e.target.value)} />
          </details>
          <div style={{ display: "grid", gap: 8 }}>
            <div><label style={styles.label}>Summary</label><textarea style={{ ...box, minHeight: 55 }} value={res.m.summary || ""} onChange={e => setM("summary", e.target.value)} /></div>
            <div style={{ display: "grid", gridTemplateColumns: isMobileNow() ? "1fr" : "1fr 1fr", gap: 8 }}>
              <div><label style={styles.label}>Problem / need</label><textarea style={{ ...box, minHeight: 50 }} value={res.m.problem || ""} onChange={e => setM("problem", e.target.value)} /></div>
              <div><label style={styles.label}>Solution / what was done</label><textarea style={{ ...box, minHeight: 50 }} value={res.m.solution || ""} onChange={e => setM("solution", e.target.value)} /></div>
            </div>
            <div><label style={styles.label}>Action items (one per line)</label>
              <textarea style={{ ...box, minHeight: 50 }} value={(res.m.action_items || []).join("\n")} onChange={e => setM("action_items", e.target.value.split("\n"))} /></div>
            <div style={{ display: "grid", gridTemplateColumns: isMobileNow() ? "1fr 1fr" : "1fr 1fr 2fr", gap: 8 }}>
              <div><label style={styles.label}>Next date</label><DateInput value={res.m.next_action_date || ""} onChange={v => setM("next_action_date", v)} /></div>
              <div><label style={styles.label}>Next time</label><TimeInput value={res.m.next_action_time || ""} onChange={v => setM("next_action_time", v)} /></div>
              <div style={{ gridColumn: isMobileNow() ? "1 / -1" : undefined }}><label style={styles.label}>Next action</label><input style={box} value={res.m.next_action || ""} onChange={e => setM("next_action", e.target.value)} /></div>
            </div>
            {kind === "lead" && <div><label style={styles.label}>Outcome</label><input style={box} value={res.m.outcome || ""} onChange={e => setM("outcome", e.target.value)} /></div>}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14, flexWrap: "wrap" }}>
            <button style={styles.btn("ghost")} onClick={() => { setRes(null); setStage("idle"); }}>↺ Record again</button>
            <button style={styles.btn("success")} onClick={use}>✓ Use these minutes</button>
          </div>
        </>)}
      </div>
    </div>
  );
};

// Settings > Voice Minutes (admin): the two service keys
const VoiceSettingsScreen = () => {
  const [s, setS] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api("/voice/settings"); setS({ ...r.data, sarvam_key: "", anthropic_key: "" }); }
    catch (e) { setToast({ message: e.message, type: "error" }); setS({}); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try { await api("/voice/settings", { method: "PUT", body: s }); __voiceEnabled = null; setToast({ message: "Voice minutes settings saved.", type: "success" }); await load(); }
    catch (e) { setToast({ message: e.message, type: "error" }); }
    setBusy(false);
  };
  const test = async () => {
    setBusy(true);
    try { const r = await api("/voice/test", { method: "POST" }); setToast({ message: r.message, type: "success" }); }
    catch (e) { setToast({ message: "Test failed: " + e.message, type: "error" }); }
    setBusy(false);
  };
  if (!s) return <div style={styles.card}>Loading…</div>;
  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <h2 style={{ margin: "0 0 14px" }}>🎤 Voice Minutes</h2>
      <div style={{ display: "grid", gridTemplateColumns: isMobileNow() ? "1fr" : "1.2fr 1fr", gap: 16 }}>
        <div style={styles.card}>
          <label style={{ display: "flex", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 12 }}>
            <input type="checkbox" checked={!!s.enabled} onChange={e => setS({ ...s, enabled: e.target.checked })} />
            <b>Switch on Voice minutes</b> <span style={{ color: colors.textLight }}>— shows the 🎤 button on calls and lead follow-ups</span>
          </label>
          <div style={{ marginBottom: 10 }}><label style={styles.label}>Speech key (Sarvam AI)</label>
            <input type="password" style={styles.input} value={s.sarvam_key} onChange={e => setS({ ...s, sarvam_key: e.target.value })}
              placeholder={s.has_sarvam_key ? "•••••••• saved — leave blank to keep it" : "paste the Sarvam API key"} autoComplete="new-password" /></div>
          <div style={{ marginBottom: 10 }}><label style={styles.label}>AI key (Anthropic Claude API)</label>
            <input type="password" style={styles.input} value={s.anthropic_key} onChange={e => setS({ ...s, anthropic_key: e.target.value })}
              placeholder={s.has_anthropic_key ? "•••••••• saved — leave blank to keep it" : "paste the Claude API key (starts with sk-ant-)"} autoComplete="new-password" /></div>
          <details style={{ marginBottom: 12, fontSize: 12, color: colors.textLight }}>
            <summary style={{ cursor: "pointer" }}>Advanced (normally leave as is)</summary>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <div><label style={styles.label}>Speech model</label><input style={styles.input} value={s.stt_model || ""} onChange={e => setS({ ...s, stt_model: e.target.value })} /></div>
              <div><label style={styles.label}>AI model</label><input style={styles.input} value={s.ai_model || ""} onChange={e => setS({ ...s, ai_model: e.target.value })} /></div>
            </div>
          </details>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={styles.btn("primary")} disabled={busy} onClick={save}>Save settings</button>
            <button style={styles.btn("success")} disabled={busy} onClick={test}>Test AI key</button>
          </div>
        </div>
        <div style={{ ...styles.card, fontSize: 13, lineHeight: 1.6 }}>
          <b>Getting the two keys (one time)</b>
          <ol style={{ paddingLeft: 18, margin: "8px 0" }}>
            <li><b>Speech key:</b> sign up at <b>sarvam.ai</b> → open the dashboard → <b>API keys</b> → create a key → copy it.</li>
            <li><b>AI key:</b> sign up at <b>console.anthropic.com</b> → add a payment method / credits under Billing → <b>API Keys</b> → <b>Create Key</b> → copy it.</li>
            <li>Paste both keys on the left, tick <b>Switch on</b>, click <b>Save settings</b>, then <b>Test AI key</b>.</li>
            <li>Open any call → <b>🎤 Voice minutes</b> → record a short note to check the speech key.</li>
          </ol>
          <div style={{ color: colors.textLight }}>Both services charge per use (per minute of speech, and per summary). Keys are stored on your server and never shown again.</div>
        </div>
      </div>
    </div>
  );
};

// Lost reasons: the Master Data list, or the original six until migration v48 has run
const lostReasonOptions = (masters) => {
  const list = (masters.lostReasons || []).map(r => r.name);
  return (list.length ? list : ["Too Expensive", "Chose Competitor", "Not Ready", "Budget Constraints", "No Response", "Others"]).map(v => ({ value: v, label: v }));
};
const FU_TYPES = ["Phone Call", "Email", "Meeting", "Site Visit", "Demo", "Video Call", "WhatsApp", "SMS"];
const FU_OUTCOMES = ["Interested", "Not Ready", "Callback Requested", "Proposal Sent", "Negotiating", "No Response", "Converted", "Lost"];
const FollowupReport = ({ masters, companyLogo, companyName }) => {
  const monthStart = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-01"; };
  const todayStr = () => toISODate(new Date().toISOString());
  const [f, setF] = useState({ date_from: monthStart(), date_to: todayStr(), done_by_id: "", followup_type: "", outcome: "", lead_status: "", search: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [view, setView] = useState("summary");          // "summary" | "detailed"
  const [groupBy, setGroupBy] = useState("lead");       // lead | done_by | type | outcome | month
  const [openKey, setOpenKey] = useState(null);         // summary row opened to show its follow-ups
  const [detailFilter, setDetailFilter] = useState(null); // { label, fn } from a card

  const load = useCallback(async () => {
    setLoading(true); setOpenKey(null);
    try {
      const p = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) p.append(k, v); });
      const r = await api(`/reports/followups?${p}`);
      setRows(r.data || []);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  }, [f]);
  useEffect(() => { load(); }, []);   // first load with this month

  const hrs = (r) => parseFloat(r.hours_spent) || 0;
  const t12 = (t) => t ? formatTime12hr(String(t).slice(0, 5)) : "";
  const keyOf = (r) => ({
    lead: `${r.lead_id}`, done_by: r.done_by || "Unknown", type: r.followup_type || "—",
    outcome: r.outcome || "(no outcome)", month: toISODate(String(r.followup_date)).slice(0, 7),
  }[groupBy]);
  const monthLabel = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" }); };

  // ---- totals (cards) ----
  const totals = {
    count: rows.length,
    leads: new Set(rows.map(r => r.lead_id)).size,
    hours: rows.reduce((t, r) => t + hrs(r), 0),
    visits: rows.filter(r => r.checkin_time).length,
    people: new Set(rows.map(r => r.done_by)).size,
  };

  // ---- summary rows ----
  const groups = (() => {
    const m = new Map();
    rows.forEach(r => {
      const k = keyOf(r);
      if (!m.has(k)) m.set(k, { key: k, items: [] });
      m.get(k).items.push(r);
    });
    return [...m.values()].map(g => {
      const a = g.items[0];
      const label = groupBy === "lead" ? `#${a.enquiry_number} ${a.prospect_company}` : groupBy === "month" ? monthLabel(g.key) : g.key;
      const dates = g.items.map(i => toISODate(String(i.followup_date))).sort();
      return {
        ...g, label, count: g.items.length, hours: g.items.reduce((t, i) => t + hrs(i), 0),
        people: [...new Set(g.items.map(i => i.done_by))].join(", "),
        leads: new Set(g.items.map(i => i.lead_id)).size,
        first: dates[0], last: dates[dates.length - 1], status: a.lead_status,
      };
    }).sort((x, y) => groupBy === "month" ? y.key.localeCompare(x.key) : (y.count - x.count) || x.label.localeCompare(y.label));
  })();

  const detailRows = detailFilter ? rows.filter(detailFilter.fn) : rows;
  const cardClick = (which) => {
    if (which === "count") { setDetailFilter(null); setView("detailed"); }
    if (which === "leads") { setGroupBy("lead"); setView("summary"); }
    if (which === "people") { setGroupBy("done_by"); setView("summary"); }
    if (which === "hours") { setDetailFilter({ label: "with hours", fn: r => hrs(r) > 0 }); setView("detailed"); }
    if (which === "visits") { setDetailFilter({ label: "visits (checked in)", fn: r => !!r.checkin_time }); setView("detailed"); }
  };

  // ---- one follow-up as table cells (used in Detailed and in an opened summary row) ----
  const map = (lat, lng, label) => lat ? <a href={geoMapLink(lat, lng)} target="_blank" rel="noreferrer" style={{ color: colors.primary, fontSize: 11 }}>{label}</a> : null;
  const DetailTable = ({ list, showLead = true }) => (
    <div style={{ overflowX: "auto" }}>
      <table style={styles.table}>
        <thead><tr>
          <th style={styles.th}>Date</th>{showLead && <th style={styles.th}>Lead</th>}<th style={styles.th}>Done By</th><th style={styles.th}>Type</th>
          <th style={styles.th}>Time</th><th style={{ ...styles.th, textAlign: "right" }}>Hours</th><th style={styles.th}>Outcome</th>
          <th style={styles.th}>Summary</th><th style={styles.th}>Next</th><th style={styles.th}>Location</th>
        </tr></thead>
        <tbody>
          {list.map(r => (
            <tr key={r.id}>
              <td style={styles.td}>{formatDateDMY(r.followup_date)}</td>
              {showLead && <td style={styles.td}><b>#{r.enquiry_number}</b> {r.prospect_company}<div style={{ fontSize: 10, color: colors.textLight }}>{r.lead_status}</div></td>}
              <td style={{ ...styles.td, fontWeight: 600 }}>{r.done_by}</td>
              <td style={styles.td}>{r.followup_type}</td>
              <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{r.start_time ? `${t12(r.start_time)}${r.end_time ? " – " + t12(r.end_time) : " – in progress"}` : "—"}</td>
              <td style={{ ...styles.td, textAlign: "right" }}>{hrs(r) ? hrs(r).toFixed(2) : "—"}</td>
              <td style={styles.td}>{r.outcome || "—"}</td>
              <td style={{ ...styles.td, maxWidth: 260 }}>{r.summary || "—"}</td>
              <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{r.next_followup_date ? formatDateDMY(r.next_followup_date) : ""}{r.next_action ? ` · ${r.next_action}` : ""}{!r.next_followup_date && !r.next_action ? "—" : ""}</td>
              <td style={styles.td}>{map(r.checkin_lat, r.checkin_lng, "📍 In")} {map(r.checkout_lat, r.checkout_lng, "📍 Out")}{!r.checkin_lat && !r.checkout_lat ? "—" : ""}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={10} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No follow-ups.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  // ---- exports ----
  const groupTitle = { lead: "Lead", done_by: "Done By", type: "Type", outcome: "Outcome", month: "Month" }[groupBy];
  const period = `${f.date_from ? formatDateDMY(f.date_from) : "start"} to ${f.date_to ? formatDateDMY(f.date_to) : "today"}`;
  const detailCells = (r) => [formatDateDMY(r.followup_date), r.enquiry_number, r.prospect_company, r.lead_status, r.done_by, r.followup_type,
    t12(r.start_time), t12(r.end_time), hrs(r) ? hrs(r).toFixed(2) : "", r.outcome || "", r.summary || "",
    r.next_followup_date ? formatDateDMY(r.next_followup_date) : "", r.next_action || "",
    r.checkin_lat ? geoMapLink(r.checkin_lat, r.checkin_lng) : "", r.checkout_lat ? geoMapLink(r.checkout_lat, r.checkout_lng) : ""];
  const DETAIL_HEAD = ["Date", "Enquiry #", "Prospect", "Lead Status", "Done By", "Type", "Start", "End", "Hours", "Outcome", "Summary", "Next Date", "Next Action", "Check-in Location", "Check-out Location"];
  const summaryHead = [groupTitle, ...(groupBy === "lead" ? ["Lead Status"] : ["Leads"]), "Follow-ups", "Hours", "Done By", "First", "Last"];
  const summaryCells = (g) => [g.label, groupBy === "lead" ? g.status : g.leads, g.count, g.hours.toFixed(2), g.people, formatDateDMY(g.first), formatDateDMY(g.last)];
  const exportExcel = (kind) => {
    if (!rows.length) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const csv = kind === "summary"
      ? csvRow(summaryHead) + "\n" + groups.map(g => csvRow(summaryCells(g))).join("\n") + "\n" + csvRow(["TOTAL", "", totals.count, totals.hours.toFixed(2), "", "", ""]) + "\n"
      : csvRow(DETAIL_HEAD) + "\n" + detailRows.map(r => csvRow(detailCells(r))).join("\n") + "\n";
    downloadCSV(csv, `followups_${kind}_${f.date_from || "all"}_to_${f.date_to || "today"}.csv`);
    setToast({ message: "Exported!", type: "success" });
  };
  const exportPDF = (kind) => {
    if (!rows.length) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const th = (h) => `<th style="padding:5px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">${h}</th>`;
    const td = (v) => `<td style="padding:4px;border:1px solid #ddd">${esc(v)}</td>`;
    const head = kind === "summary" ? summaryHead : DETAIL_HEAD.slice(0, 13);
    const body = kind === "summary"
      ? groups.map(g => `<tr>${summaryCells(g).map(td).join("")}</tr>`).join("") + `<tr style="font-weight:700">${td("TOTAL")}${td("")}${td(totals.count)}${td(totals.hours.toFixed(2))}${td("")}${td("")}${td("")}</tr>`
      : detailRows.map(r => `<tr>${detailCells(r).slice(0, 13).map(td).join("")}</tr>`).join("");
    const logoHtml = companyLogo ? `<img src="${companyLogo}" style="height:50px;margin-right:14px" />` : "";
    const html = `<html><head><title>Lead Follow-ups</title><style>@page{size:landscape;margin:10mm}</style></head><body style="font-family:Arial,sans-serif;padding:18px">
      <div style="display:flex;align-items:center;margin-bottom:8px">${logoHtml}<h1 style="color:#1a56db;margin:0;font-size:20px">${esc(companyName || "CLAS CRM Suite")}</h1></div>
      <h2 style="color:#333;margin:8px 0 4px">Lead Follow-ups — ${kind === "summary" ? "Summary by " + groupTitle : "Detailed"}</h2>
      <p style="color:#666;font-size:11px">${period} | ${totals.count} follow-up(s) · ${totals.leads} lead(s) · ${totals.hours.toFixed(2)} h</p>
      <table style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr>${head.map(th).join("")}</tr></thead><tbody>${body}</tbody></table>
    </body></html>`;
    const w = window.open("", "_blank"); w.document.write(html); w.document.close(); w.print();
  };

  const card = (color, label, value, which) => (
    <div style={{ ...styles.statCard(color), cursor: "pointer" }} onClick={() => cardClick(which)}>
      <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>{label} ↗</div>
      <div style={{ fontSize: 22, fontWeight: 800 }}>{value}</div>
    </div>
  );
  const tab = (v, label) => (
    <button style={{ ...styles.btn(view === v ? "primary" : "ghost", "sm") }} onClick={() => { setView(v); if (v === "summary") setDetailFilter(null); }}>{label}</button>
  );

  return (
    <div style={styles.card}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.filterBar}>
        <div><label style={styles.label}>From</label><DateInput value={f.date_from} onChange={v => setF({ ...f, date_from: v })} /></div>
        <div><label style={styles.label}>To</label><DateInput value={f.date_to} onChange={v => setF({ ...f, date_to: v })} /></div>
        <div><label style={styles.label}>Done By</label>
          <select style={styles.select} value={f.done_by_id} onChange={e => setF({ ...f, done_by_id: e.target.value })}>
            <option value="">All</option>{(masters.executives || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select></div>
        <div><label style={styles.label}>Type</label>
          <select style={styles.select} value={f.followup_type} onChange={e => setF({ ...f, followup_type: e.target.value })}>
            <option value="">All</option>{FU_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><label style={styles.label}>Outcome</label>
          <select style={styles.select} value={f.outcome} onChange={e => setF({ ...f, outcome: e.target.value })}>
            <option value="">All</option>{FU_OUTCOMES.map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><label style={styles.label}>Search</label>
          <input style={styles.input} value={f.search} onChange={e => setF({ ...f, search: e.target.value })} placeholder="Company, Enquiry #, summary…" onKeyDown={e => { if (e.key === "Enter") load(); }} /></div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          <button style={styles.btn("primary")} onClick={load} disabled={loading}>{loading ? "Loading…" : "Generate"}</button>
          <button style={styles.btn("ghost")} onClick={() => setF({ date_from: "", date_to: "", done_by_id: "", followup_type: "", outcome: "", lead_status: "", search: "" })}>Clear</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "12px 0" }}>
        <span style={{ fontSize: 12, color: colors.textLight }}>EXPORT:</span>
        <button style={styles.btn("success", "sm")} onClick={() => exportExcel("summary")}>📊 Summary to Excel</button>
        <button style={styles.btn("primary", "sm")} onClick={() => exportExcel("detailed")}>📋 Detailed to Excel</button>
        <button style={styles.btn("danger", "sm")} onClick={() => exportPDF("summary")}>📄 Summary to PDF</button>
        <button style={{ ...styles.btn("primary", "sm"), background: "#7c3aed" }} onClick={() => exportPDF("detailed")}>📄 Detailed to PDF</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
        {card("#1a56db", "TOTAL FOLLOW-UPS", totals.count, "count")}
        {card("#059669", "LEADS COVERED", totals.leads, "leads")}
        {card("#0ea5e9", "TOTAL HOURS", totals.hours.toFixed(1), "hours")}
        {card("#d97706", "VISITS (CHECKED IN)", totals.visits, "visits")}
        {card("#7c3aed", "EXECUTIVES", totals.people, "people")}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        {tab("summary", "Summary (combined)")}{tab("detailed", "Detailed (one row per follow-up)")}
        {view === "summary" && (<>
          <span style={{ fontSize: 12, color: colors.textLight, marginLeft: 8 }}>Group by</span>
          <select style={{ ...styles.select, width: "auto" }} value={groupBy} onChange={e => { setGroupBy(e.target.value); setOpenKey(null); }}>
            <option value="lead">Lead</option><option value="done_by">Done By</option><option value="type">Type</option>
            <option value="outcome">Outcome</option><option value="month">Month</option>
          </select>
        </>)}
        {view === "detailed" && detailFilter && (
          <span style={{ fontSize: 12, color: colors.primary }}>Showing {detailFilter.label} · <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setDetailFilter(null)}>show all</span></span>
        )}
      </div>

      {view === "detailed" ? <DetailTable list={detailRows} /> : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead><tr>{summaryHead.map((h, i) => <th key={h} style={{ ...styles.th, textAlign: i === 2 || i === 3 ? "right" : "left" }}>{h}</th>)}</tr></thead>
            <tbody>
              {groups.map(g => (
                <Fragment key={g.key}>
                  <tr onClick={() => setOpenKey(openKey === g.key ? null : g.key)} title="Click to see these follow-ups"
                    style={{ cursor: "pointer", background: openKey === g.key ? "#eef4ff" : undefined }}>
                    <td style={{ ...styles.td, fontWeight: 600, color: colors.primary }}>{openKey === g.key ? "▼ " : "▶ "}{g.label}</td>
                    <td style={styles.td}>{groupBy === "lead" ? g.status : g.leads}</td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: 700 }}>{g.count}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{g.hours.toFixed(2)}</td>
                    <td style={styles.td}>{g.people}</td>
                    <td style={styles.td}>{formatDateDMY(g.first)}</td>
                    <td style={styles.td}>{formatDateDMY(g.last)}</td>
                  </tr>
                  {openKey === g.key && (
                    <tr><td colSpan={7} style={{ padding: "6px 6px 14px 24px", background: "#f8fafc" }}>
                      <DetailTable list={[...g.items].sort((a, b) => String(a.followup_date).localeCompare(String(b.followup_date)) || String(a.created_at).localeCompare(String(b.created_at)))} showLead={groupBy !== "lead"} />
                    </td></tr>
                  )}
                </Fragment>
              ))}
              {groups.length === 0 && <tr><td colSpan={7} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>{loading ? "Loading…" : "No follow-ups for these filters."}</td></tr>}
              {groups.length > 0 && (
                <tr style={{ background: "#f1f5f9", fontWeight: 800 }}>
                  <td style={styles.td}>TOTAL</td><td style={styles.td}>{groupBy === "lead" ? "" : totals.leads}</td>
                  <td style={{ ...styles.td, textAlign: "right" }}>{totals.count}</td><td style={{ ...styles.td, textAlign: "right" }}>{totals.hours.toFixed(2)}</td>
                  <td style={styles.td} colSpan={3}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ScheduledVisitsReport = ({ masters, companyLogo, companyName }) => {
  const monthStart = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-01"; };
  const todayStr = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0"); };
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [assignedTo, setAssignedTo] = useState("");
  const [assignedBy, setAssignedBy] = useState("");
  const [callTypeId, setCallTypeId] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const { sorts, toggleSort, clearSort } = useMultiSort();
  const [advRows, setAdvRowsState] = useState(__svAdvRows);
  const setAdvRows = (v) => { __svAdvRows = v; setAdvRowsState(v); };
  const sortAcc = { contacts: (r) => contactNums(r), emails: (r) => contactMails(r), spoke_to: (r) => r.contact_person_called || r.contact_person, next_action: (r) => r.next_action_date };
  const sortedData = applyMultiSort(data, sorts, sortAcc);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ assigned_report: "true", limit: 5000 });
      const adv = advParams(advRows, callFilterFields(masters, advRows));
      Object.entries(adv).forEach(([k, v]) => { if (v) p.append(k, v); });
      // This screen already has its own Next Action From/To at the top. If the user
      // also sets a Next Action Date range in the advanced filters, that one wins —
      // otherwise the two ranges were combined and an awkward pair returned nothing.
      const advHasNextDate = adv.next_from || adv.next_to;
      if (!advHasNextDate) {
        if (from) p.append("sched_from", from);
        if (to) p.append("sched_to", to);
      }
      if (assignedTo) p.append("assigned_to_id", assignedTo);
      if (assignedBy) p.append("executive_id", assignedBy);
      if (callTypeId) p.append("call_type_id", callTypeId);
      if (status) p.append("call_status", status);
      const r = await api(`/call-logs?${p}`);
      setData(r.data || []);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  }, [from, to, assignedTo, assignedBy, callTypeId, status, advRows]);
  useEffect(() => { load(); }, [load]);

  const fmtDT = (d, t) => d ? (formatDateDMY(d) + (t ? " " + formatTime12hr(String(t).slice(0,5)) : "")) : "—";
  const statusColor = (s) => s === "Completed" ? colors.success : s === "Cancelled" ? colors.textLight : s === "Escalated" ? colors.danger : "#d97706";

  const exportExcel = () => {
    if (data.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const headers = ["Ticket #", "Customer", "Phone", "Contact Person", "Contact Number", "Email", "Contact Email", "Spoke To", "Call Type", "Next Action Date & Time", "Call Date", "Assigned By", "Assigned To", "Status", "Hours", "Regarding"];
    const rows = sortedData.map(c => csvRow([c.ticket_number, c.client_name || "", c.customer_phone || "", c.contact_person || "", c.contact_phone || "", c.customer_email || "", c.contact_email || "", c.contact_person_called || "", c.call_type_name || "", fmtDT(c.next_action_date, c.next_action_time), c.call_date ? formatDateDMY(c.call_date) : "", c.executive_name || "", c.assigned_to_name || "", c.call_status || "", c.hours_spent || 0, c.regarding || ""]));
    downloadCSV(csvRow(headers) + "\n" + rows.join("\n") + "\n", `scheduled_visits_${from}_to_${to}.csv`);
    setToast({ message: `Exported ${data.length} rows!`, type: "success" });
  };

  const exportPDF = () => {
    if (data.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const logoHtml = companyLogo ? `<img src="${companyLogo}" style="height:50px;margin-right:14px" />` : "";
    const cols = ["Ticket", "Customer", "Contacts", "Email", "Spoke To", "Call Type", "Next Action", "Call Date", "Assigned By", "Assigned To", "Status", "Hrs"];
    const head = cols.map(h => `<th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">${h}</th>`).join("");
    let rows = "";
    sortedData.forEach(c => { rows += `<tr><td style="padding:5px;border:1px solid #ddd;font-weight:600">${c.ticket_number}</td><td style="padding:5px;border:1px solid #ddd">${c.client_name||""}</td><td style="padding:5px;border:1px solid #ddd">${contactNums(c)}</td><td style="padding:5px;border:1px solid #ddd">${contactMails(c)}</td><td style="padding:5px;border:1px solid #ddd">${c.contact_person_called||c.contact_person||""}</td><td style="padding:5px;border:1px solid #ddd">${c.call_type_name||""}</td><td style="padding:5px;border:1px solid #ddd">${fmtDT(c.next_action_date, c.next_action_time)}</td><td style="padding:5px;border:1px solid #ddd">${c.call_date?formatDateDMY(c.call_date):""}</td><td style="padding:5px;border:1px solid #ddd">${c.executive_name||""}</td><td style="padding:5px;border:1px solid #ddd">${c.assigned_to_name||""}</td><td style="padding:5px;border:1px solid #ddd">${c.call_status||""}</td><td style="padding:5px;border:1px solid #ddd;text-align:right">${c.hours_spent||0}</td></tr>`; });
    const html = `<html><head><title>Scheduled Visits Report</title><style>@page{size:landscape;margin:10mm}</style></head><body style="font-family:Arial,sans-serif;padding:18px">
      <div style="display:flex;align-items:center;margin-bottom:8px">${logoHtml}<h1 style="color:#1a56db;margin:0;font-size:20px">${companyName || "CLAS CRM Suite"}</h1></div>
      <h2 style="color:#333;margin:8px 0 4px">Scheduled Visits Report</h2>
      <p style="color:#666;font-size:11px">${formatDateDMY(from)} to ${formatDateDMY(to)} | ${data.length} row(s)</p>
      <hr style="border:1px solid #ddd;margin:10px 0">
      <table style="width:100%;border-collapse:collapse;font-size:10px"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); w.print();
    setToast({ message: "PDF ready!", type: "success" });
  };

  return (
    <div style={styles.card}>
      <div style={styles.filterBar}>
        <div><label style={styles.label}>Next Action From</label><DateInput value={from} onChange={setFrom} /></div>
        <div><label style={styles.label}>To</label><DateInput value={to} onChange={setTo} /></div>
        <div><label style={styles.label}>Assigned To</label>
          <select style={styles.select} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">All</option>{masters.executives?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Assigned By</label>
          <select style={styles.select} value={assignedBy} onChange={(e) => setAssignedBy(e.target.value)}>
            <option value="">All</option>{masters.executives?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Call Type</label>
          <select style={styles.select} value={callTypeId} onChange={(e) => setCallTypeId(e.target.value)}>
            <option value="">All</option>{masters.callTypes?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div><label style={styles.label}>Status</label>
          <select style={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>{["Open", "In Progress", "Completed", "Escalated", "On Hold", "Cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <button style={styles.btn("success", "sm")} onClick={exportExcel}>📊 Excel</button>
        <button style={{ ...styles.btn("ghost", "sm"), background: "#dc2626", color: "#fff" }} onClick={exportPDF}>📄 PDF</button>
      </div>

      <AdvancedFilters masters={masters} screen="calls" rows={advRows} setRows={setAdvRows}
        onApply={(r) => setAdvRows(r)} />
      {advRows.some(r => r.field === "__date_next" && (r.from || r.to)) && (
        <div style={{ fontSize: 11.5, color: "#b45309", marginBottom: 8 }}>
          ⚠️ The Next Action Date range in the advanced filters is being used — the From/To boxes above are ignored.
        </div>
      )}
      <SortBar sorts={sorts} clearSort={clearSort} labels={{ ticket_number: "Ticket #", client_name: "Customer", contacts: "Contacts", emails: "Email", spoke_to: "Spoke To", call_type_name: "Call Type", next_action: "Next Action", call_date: "Call Date", executive_name: "Assigned By", assigned_to_name: "Assigned To", call_status: "Status", hours_spent: "Hrs", regarding: "Regarding" }} />
      <div tabIndex={0} onKeyDown={scrollByKey} style={{ overflowX: "auto", outline: "none" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
            {[["Ticket #","ticket_number"],["Customer","client_name"],["Contacts","contacts"],["Email","emails"],["Spoke To","spoke_to"],["Call Type","call_type_name"],["Next Action Date & Time","next_action"],["Call Date","call_date"],["Assigned By","executive_name"],["Assigned To","assigned_to_name"],["Status","call_status"],["Hrs","hours_spent"],["Regarding","regarding"]]
              .map(([h, k]) => <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={{ ...styles.td, fontWeight: 700, color: colors.textLight }} />)}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={13} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>Loading…</td></tr> :
             sortedData.map(c => (
              <tr key={c.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ ...styles.td, fontWeight: 700 }}>{c.ticket_number}</td>
                <td style={styles.td}>{c.client_name || "—"}</td>
                <td style={styles.td}>{contactNums(c)}</td>
                <td style={styles.td}>{contactMails(c)}</td>
                <td style={styles.td}>{c.contact_person_called || contactPersons(c)}</td>
                <td style={styles.td}>{c.call_type_name || "—"}</td>
                <td style={styles.td}>{fmtDT(c.next_action_date, c.next_action_time)}</td>
                <td style={styles.td}>{c.call_date ? formatDateDMY(c.call_date) : "—"}</td>
                <td style={styles.td}>{c.executive_name || "—"}</td>
                <td style={styles.td}>{c.assigned_to_name || "—"}</td>
                <td style={styles.td}><span style={{ background: statusColor(c.call_status) + "22", color: statusColor(c.call_status), padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{c.call_status}</span></td>
                <td style={styles.td}>{c.hours_spent || 0}</td>
                <td style={styles.td}>{c.regarding || "—"}</td>
              </tr>
            ))}
            {!loading && data.length === 0 && <tr><td colSpan={13} style={{ ...styles.td, textAlign: "center", color: colors.textLight, padding: 24 }}>No scheduled visits for these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

let __lastReportType = "call-summary"; // remembered across screen navigation so returning to Reports keeps the view
let __lastAdvRows = [];                // ditto for the advanced filters, so opening a
                                       // call from a drill-down and coming back keeps them
// The report (and the list open in it) a record was opened from — restored when
// you come back from that record, instead of landing on the bare report cards.
let __repReturn = null;   // { reportType, filters, groupBy, data, totals, drill: { type, filterObj }, drillData }

const ReportsScreen = ({ masters, companyLogo, companyName, setPendingCall, setPendingLead, setPendingReturn, setActiveScreen, setPendingClient }) => {
  const [reportType, setReportTypeState] = useState(__lastReportType);
  const setReportType = (v) => {
    // A different report has different fields, so drop the advanced filters —
    // done here (a real user action) rather than in an effect, which runs twice
    // on mount in development and wiped them when returning from a call.
    __lastAdvRows = []; setAdvRowsState([]); advRef.current = [];
    __lastReportType = v; setReportTypeState(v);
  };
  // Coming back from a record opened from this report → same filters, grouping, figures and open list
  const repBackRef = useRef((__repReturn && __repReturn.reportType === __lastReportType) ? __repReturn : null);
  const repBack = repBackRef.current;
  const [filters, setFilters] = useState(() => (repBack && repBack.filters) || {});
  const [groupBy, setGroupBy] = useState(() => (repBack && repBack.groupBy) || "");
  const [data, setData] = useState(() => (repBack && repBack.data) || []);
  const [totals, setTotals] = useState(() => (repBack && repBack.totals) || null);
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [drillType, setDrillType] = useState(() => (repBack && repBack.drill && repBack.drill.type) || null);
  const lastDrillRef = useRef((repBack && repBack.drill) || null);   // which list is open, and its filter
  // Bring the drill-down list into view when it opens (it sits above the table)
  useEffect(() => {
    if (drillType) setTimeout(() => { const el = document.getElementById("report-drill"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 80);
  }, [drillType]);
  const [drillData, setDrillData] = useState(() => (repBack && repBack.drillData) || []);
  const [drillLoading, setDrillLoading] = useState(false);
  // Opening a record from a list: remember everything needed to come back to it
  const rememberReport = () => {
    __repReturn = { reportType, filters, groupBy, data, totals, drill: drillType ? lastDrillRef.current : null, drillData };
  };
  const { sorts, toggleSort, clearSort } = useMultiSort();
  const [advRows, setAdvRowsState] = useState(__lastAdvRows);
  const setAdvRows = (v) => { __lastAdvRows = v; setAdvRowsState(v); };
  // Keep the live rows in a ref: any refetch triggered later (Escape, closing a
  // drill-down, changing Group By) then always uses the CURRENT filters instead
  // of a stale copy, which previously reset the cards to all calls.
  const advRef = useRef(__lastAdvRows);
  useEffect(() => { advRef.current = advRows; }, [advRows]);
  // Escape closes the drill-down only — it must not refetch the report
  useEffect(() => {
    const onClose = () => { setDrillType(null); setDrillData([]); };
    document.addEventListener("close-modal", onClose);
    return () => document.removeEventListener("close-modal", onClose);
  }, []);
  const drillAcc = { contacts: (r) => contactNums(r), emails: (r) => contactMails(r), spoke_to: (r) => r.contact_person_called || r.contact_person };
  const drillRows = applyMultiSort(drillData, sorts, drillAcc);

  const repFetchRef = useRef(null);   // Enter in the search box runs the newest version of fetchReport
  const fetchReport = async (overrideFilters, advOverride) => {
    if (reportType === "scheduled-visits" || reportType === "licenses" || reportType === "report-writer" || reportType === "followups") return; // self-contained blocks handle their own data
    try {
      const params = new URLSearchParams({ group_by: groupBy });
      const f = overrideFilters !== undefined ? overrideFilters : filters;
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      const advUse = advOverride !== undefined ? advOverride : advRef.current;
      const advFields = reportType === "lead-summary" ? leadFilterFields(masters, advUse) : callFilterFields(masters, advUse);
      const advOut = advParams(advUse, advFields);
      // In this report "executive_id" means the Executive box (whoever is handling
      // the call), so the advanced "Logged By" filter is sent as logged_by_id.
      if (reportType !== "lead-summary" && advOut.executive_id) { advOut.logged_by_id = advOut.executive_id; delete advOut.executive_id; }
      Object.entries(advOut).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api(`/reports/${reportType}?${params}`);
      setData(r.data); setTotals(r.totals);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };
  repFetchRef.current = fetchReport;


  // On mount (including returning from a call opened via a drill-down) refetch
  // with the remembered filters, passed explicitly so nothing can be stale.
  // Back from a record → keep the list that was open (refreshing its rows); otherwise close it.
  useEffect(() => {
    fetchReport(undefined, __lastAdvRows);
    const back = repBackRef.current;
    if (back && back.reportType === reportType && (back.groupBy || "") === groupBy) {
      __repReturn = null;
      if (back.drill) handleCardClick(back.drill.type, back.drill.filterObj, true);
      return;
    }
    repBackRef.current = null;   // a real change of report / grouping
    setDrillType(null); setDrillData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, groupBy]);

  // Drill-down for report cards
  // Clicking a row of the summary table lists that row's leads / calls.
  // Returns the filter for the row, or null when the row can't be listed
  // (e.g. "Unassigned" / "No Product", which have no id to filter on).
  const rowDrill = (row) => {
    const key = row.group_key;
    if (key === null || key === undefined || key === "") return null;
    if (groupBy === "month") {
      const [y, m] = String(key).split("-").map(Number);
      let from = `${key}-01`, to = `${key}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
      if (filters.date_from && filters.date_from > from) from = filters.date_from;   // stay inside the report's period
      if (filters.date_to && filters.date_to < to) to = filters.date_to;
      return { date_from: from, date_to: to };
    }
    const map = reportType === "call-summary"
      ? { client: "client_id", executive: "handler_id", product: "product_id", call_type: "call_type_id", location: "location_id", principal: "principal_id" }
      : { status: "status", source: "lead_source", priority: "priority", executive: "attended_by_id", product: "product_id", lost_reason: "lost_reason" };
    return map[groupBy] ? { [map[groupBy]]: key } : null;
  };
  const rowDrillKey = (row) => `${groupBy}_${row.group_label}`.replace(/[^A-Za-z0-9 _-]/g, "");

  const handleCardClick = async (type, filterObjIn, refresh = false) => {
    if (!refresh && drillType === type) { setDrillType(null); setDrillData([]); return; }
    lastDrillRef.current = { type, filterObj: filterObjIn };
    // "__nonzero": list only rows where that field is above 0 (Pipeline Value / Total Hours cards)
    const { __nonzero: nonzeroField, ...filterObj } = filterObjIn || {};
    const keepNonzero = (rows) => nonzeroField ? rows.filter(x => (parseFloat(x[nonzeroField]) || 0) > 0) : rows;
    setDrillType(type); if (!refresh) setDrillLoading(true);   // a refresh keeps the rows on screen until the new ones arrive
    try {
      const params = new URLSearchParams();
      // Match the report's attribution: credit calls to Assigned To (else Executive),
      // so drilling into a total returns the same set the total counted.
      if (reportType === "call-summary") {
        Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k === "executive_id" ? "handler_id" : k, v); });
        // the advanced filters apply to the drill-down as well, so the list matches the total
        Object.entries(advParams(advRef.current, callFilterFields(masters, advRef.current))).forEach(([k, v]) => { if (v) params.append(k, v); });
        Object.entries(filterObj).forEach(([k, v]) => { if (v) params.append(k, v); });
        params.append("limit", nonzeroField ? "100000" : "500");
        const r = await api(`/call-logs?${params}`);
        setDrillData(keepNonzero(r.data || []).slice(0, 500));
      } else {
        // Leads: the list endpoint now understands every lead filter, so the server
        // does the filtering. The checks below are kept as a safety net only.
        const adv = advParams(advRef.current, leadFilterFields(masters));
        const serverKeys = ["attended_by_id", "status", "priority", "lead_source", "search",
          "date_from", "date_to", "product_id", "location_id", "assigned_to_id", "sale_type_id",
          "prospect_company", "contact_person", "city", "fu_from", "fu_to", "conv_from", "conv_to", "act_from", "act_to", "assigned_by_id",
          "phone_has", "email_has", "designation_has", "demo_from", "demo_to", "min_value", "min_deal", "min_users", "next_action",
          "lost_reason_has", "lost_reason", "competitor_has", "remarks_has", "referral_client_id", "latest_outcome"];
        Object.entries({ ...filters, ...adv, ...filterObj }).forEach(([k, v]) => {
          if (v && serverKeys.includes(k)) params.append(k, v);
        });
        params.append("limit", "5000");
        const r = await api(`/leads?${params}`);
        let rows = r.data || [];

        const dFrom = adv.date_from || filters.date_from, dTo = adv.date_to || filters.date_to;
        const onlyDate = (v) => v ? toISODate(String(v)) : "";   // local date (slice(0,10) of a UTC timestamp was a day early)
        if (dFrom) rows = rows.filter(x => onlyDate(x.enquiry_date) >= dFrom);
        if (dTo) rows = rows.filter(x => onlyDate(x.enquiry_date) <= dTo);
        if (adv.fu_from) rows = rows.filter(x => onlyDate(x.follow_up_date) >= adv.fu_from);
        if (adv.fu_to) rows = rows.filter(x => onlyDate(x.follow_up_date) <= adv.fu_to);
        if (adv.conv_from) rows = rows.filter(x => onlyDate(x.conversion_date) >= adv.conv_from);
        if (adv.conv_to) rows = rows.filter(x => onlyDate(x.conversion_date) <= adv.conv_to);
        const activityDate = (x) => onlyDate(x.conversion_date || x.updated_at);
        if (adv.act_from) rows = rows.filter(x => activityDate(x) >= adv.act_from);
        if (adv.act_to) rows = rows.filter(x => activityDate(x) <= adv.act_to);
        if (adv.product_id) rows = rows.filter(x => String(x.product_id) === String(adv.product_id));
        if (adv.location_id) rows = rows.filter(x => String(x.location_id) === String(adv.location_id));
        if (adv.sale_type_id) rows = rows.filter(x => String(x.sale_type_id) === String(adv.sale_type_id));
        if (adv.assigned_to_id) rows = rows.filter(x => String(x.assigned_to_id) === String(adv.assigned_to_id));
        const like = (val, term) => String(val || "").toLowerCase().includes(String(term).toLowerCase());
        if (adv.prospect_company) rows = rows.filter(x => like(x.prospect_company, adv.prospect_company));
        if (adv.contact_person) rows = rows.filter(x => like(x.contact_person, adv.contact_person));
        if (adv.city) rows = rows.filter(x => like(x.city, adv.city));
        // whatever the card itself filtered on (status, priority, etc.)
        Object.entries(filterObj).forEach(([k, v]) => {
          if (v && !serverKeys.includes(k)) rows = rows.filter(x => String(x[k]) === String(v));
        });
        setDrillData(keepNonzero(rows).slice(0, 500));
      }
    } catch (e) { console.error(e); }
    setDrillLoading(false);
  };

  const exportDrillData = () => {
    if (drillData.length === 0) return;
    if (reportType === "call-summary") {
      const headers = csvRow(["Ticket#", "Date", "Executive", "Customer", "Product", "License#", "Type", "Start", "End", "Hours", "Status", "Subject"]);
      const rows = drillData.map(r => csvRow([r.ticket_number, formatDateDMY(r.call_date), r.executive_name, r.client_name, r.product_name || "", r.license_number || "", r.call_type_name, formatTime12hr(r.call_start_time), formatTime12hr(r.call_end_time), r.hours_spent || 0, r.call_status, r.regarding || ""])).join("\n");
      downloadCSV(headers + "\n" + rows + "\n", `report_${drillType}_${new Date().toISOString().slice(0,10)}.csv`);
    } else {
      const headers = csvRow(["Enquiry#", "Date", "Company", "Contact", "Source", "Product", "Status", "Priority", "Deal Value", "Executive", "Follow-up Date", "Next Action", "Total Follow-ups"]);
      const rows = drillData.map(r => csvRow([r.enquiry_number, formatDateDMY(r.enquiry_date), r.prospect_company || "", r.contact_person || "", r.lead_source || "", r.product_name || "", r.status, r.priority, r.estimated_deal_value || 0, r.executive_name || "", r.follow_up_date ? formatDateDMY(r.follow_up_date) : "", r.next_action || "", r.total_followups || 0])).join("\n");
      downloadCSV(headers + "\n" + rows + "\n", `report_leads_${drillType}_${new Date().toISOString().slice(0,10)}.csv`);
    }
  };

  // Excel Export - Summary Report
  const exportSummaryExcel = () => {
    if (data.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const groupLabel = groupBy.charAt(0).toUpperCase() + groupBy.slice(1).replace("_", " ");
    let csvContent = "";
    if (reportType === "call-summary") {
      csvContent = `${groupLabel},Total Calls,Total Hours,Completed,Open\n`;
      data.forEach(r => { csvContent += `"${r.group_label}",${r.total_calls},${parseFloat(r.total_hours).toFixed(2)},${r.completed_calls},${r.open_calls}\n`; });
      csvContent += `\n"GRAND TOTAL",${totals.total_calls},${parseFloat(totals.total_hours).toFixed(2)},${totals.completed_calls},${totals.open_calls}\n`;
    } else {
      csvContent = `${groupLabel},Total Leads,Pipeline Value,Converted\n`;
      data.forEach(r => { csvContent += `"${r.group_label}",${r.total_leads},${parseFloat(r.total_pipeline_value).toFixed(2)},${r.converted_count}\n`; });
      csvContent += `\n"GRAND TOTAL",${totals.total_leads},${parseFloat(totals.total_pipeline_value).toFixed(2)},${totals.converted_count}\n`;
    }
    downloadCSV(csvContent, `${reportType}_summary_${new Date().toISOString().slice(0,10)}.csv`);
    setToast({ message: "Summary report exported!", type: "success" });
  };

  // Excel Export - Detailed Call Data
  const exportDetailedExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const advOut = reportType === "call-summary"
        ? advParams(advRef.current, callFilterFields(masters, advRef.current))
        : advParams(advRef.current, leadFilterFields(masters));
      if (reportType === "call-summary" && advOut.executive_id) { advOut.logged_by_id = advOut.executive_id; delete advOut.executive_id; }
      Object.entries(advOut).forEach(([k, v]) => { if (v) params.append(k, v); });
      const endpoint = reportType === "call-summary" ? "/reports/call-export" : "/leads";
      const r = await api(`${endpoint}?${params}&limit=10000`);
      const rows = r.data;
      if (rows.length === 0) { setToast({ message: "No data to export.", type: "warning" }); setExporting(false); return; }

      let csvContent = "";
      if (reportType === "call-summary") {
        const headers = csvRow(["Ticket #", "Log Date", "Call Date", "Executive", "Principal Company", "Customer", "Phone", "Contact Person", "Contact Number", "Email", "Contact Email", "License Number", "Product / Service For", "TDL Type", "Start Time", "End Time", "Hours Spent", "Call Type", "Status", "Location", "Physical Days", "Subscription Expiry", "Billed Through / Partner", "Assigned To", "Next Action Date", "Next Action Time", "Continuation of Ticket", "Contact Person Spoke To", "Release / Version", "Customer Email", "Problem Observed", "Solution Provided", "Subject", "Call Notes"]);
        csvContent = headers + "\n";
        rows.forEach(r => {
          csvContent += csvRow([r.ticket_number, r.log_date ? formatDateDMY(r.log_date) : "", formatDateDMY(r.call_date), r.executive_name || "", r.principal_name || "", r.client_name || "", r.client_phone || "", r.contact_person || "", r.contact_phone || "", r.client_email || "", r.contact_email || "", r.license_number || "", r.product_name || "", r.tdl_type || "", formatTime12hr(r.call_start_time), formatTime12hr(r.call_end_time), r.hours_spent || 0, r.call_type_name || "", r.call_status || "", r.location_name || "", r.physical_days || "", r.subscription_expiry ? formatDateDMY(r.subscription_expiry) : "", r.billed_through || "", r.assigned_to_name || "", r.next_action_date ? formatDateDMY(r.next_action_date) : "", r.next_action_time ? formatTime12hr(String(r.next_action_time).slice(0,5)) : "", r.parent_ticket_number || "", r.contact_person_called || "", r.software_version || "", r.customer_email || r.client_email || "", r.problem_observed || "", r.solution_provided || "", r.regarding || "", r.call_details || ""]) + "\n";
        });
      } else {
        const headers = csvRow(["Enquiry #", "Enquiry Date", "Prospect Company", "Contact Person", "Designation", "Phone", "Email", "City", "Location", "Lead Source", "Product", "No. of Users", "Estimated Deal Value", "Attended By", "Assigned To", "Status", "Priority", "Follow-up Date", "Next Action", "Remarks", "Total Follow-ups", "Conversion Date", "Actual Deal Value", "Lost Reason"]);
        csvContent = headers + "\n";
        rows.forEach(r => {
          csvContent += csvRow([r.enquiry_number, formatDateDMY(r.enquiry_date), r.prospect_company || "", r.contact_person || "", r.designation || "", r.phone || "", r.email || "", r.city || "", r.location_name || "", r.lead_source || "", r.product_name || "", r.number_of_users || "", r.estimated_deal_value || "", r.executive_name || r.attended_by_name || "", r.assigned_to_name || "", r.status || "", r.priority || "", r.follow_up_date ? formatDateDMY(r.follow_up_date) : "", r.next_action || "", r.remarks || "", r.total_followups || 0, r.conversion_date ? formatDateDMY(r.conversion_date) : "", r.actual_deal_value || "", r.lost_reason || ""]) + "\n";
        });
      }
      downloadCSV(csvContent, `${reportType}_detailed_${new Date().toISOString().slice(0,10)}.csv`);
      setToast({ message: `Exported ${rows.length} records!`, type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setExporting(false);
  };

  // PDF Export - Summary
  const todayDMY = (() => { const d = new Date(); return `${d.getDate().toString().padStart(2,"0")}/${(d.getMonth()+1).toString().padStart(2,"0")}/${d.getFullYear()}`; })();
  const logoHtml = companyLogo ? `<img src="${companyLogo}" style="height:60px;margin-right:16px" />` : "";
  const headerHtml = `<div style="display:flex;align-items:center;margin-bottom:10px">${logoHtml}<div><h1 style="color:#1a56db;margin:0;font-size:22px">${companyName || "Call Tracker & CRM Suite"}</h1></div></div>`;

  const exportPDF = () => {
    if (data.length === 0) { setToast({ message: "No data to export.", type: "warning" }); return; }
    const groupLabel = groupBy.charAt(0).toUpperCase() + groupBy.slice(1).replace("_", " ");
    const title = reportType === "call-summary" ? "Call Tracker Summary Report" : "Lead Summary Report";
    const filterDesc = Object.entries(filters).filter(([,v]) => v).map(([k,v]) => `${k}: ${v}`).join(", ") || "All Data";

    let tableRows = "";
    if (reportType === "call-summary") {
      data.forEach(r => {
        tableRows += `<tr><td style="padding:8px;border:1px solid #ddd;font-weight:600">${r.group_label}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${r.total_calls}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${parseFloat(r.total_hours).toFixed(2)}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${r.completed_calls}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${r.open_calls}</td></tr>`;
      });
      tableRows += `<tr style="background:#f0f0f0;font-weight:700"><td style="padding:8px;border:1px solid #ddd">GRAND TOTAL</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${totals.total_calls}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${parseFloat(totals.total_hours).toFixed(2)}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${totals.completed_calls}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${totals.open_calls}</td></tr>`;
    } else {
      data.forEach(r => {
        tableRows += `<tr><td style="padding:8px;border:1px solid #ddd;font-weight:600">${r.group_label}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${r.total_leads}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${formatCurrencyINR(r.total_pipeline_value)}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${r.converted_count}</td></tr>`;
      });
      tableRows += `<tr style="background:#f0f0f0;font-weight:700"><td style="padding:8px;border:1px solid #ddd">GRAND TOTAL</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${totals.total_leads}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${formatCurrencyINR(totals.total_pipeline_value)}</td><td style="padding:8px;border:1px solid #ddd;text-align:right">${totals.converted_count}</td></tr>`;
    }

    const headers = reportType === "call-summary"
      ? `<th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">${groupLabel}</th><th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Total Calls</th><th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Total Hours</th><th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Completed</th><th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Open</th>`
      : `<th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:left">${groupLabel}</th><th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Total Leads</th><th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Pipeline Value</th><th style="padding:8px;border:1px solid #ddd;background:#1a56db;color:#fff;text-align:right">Converted</th>`;

    const html = `<html><head><title>${title}</title></head><body style="font-family:Arial,sans-serif;padding:30px">
      ${headerHtml}
      <h2 style="color:#333;margin:10px 0 5px">${title}</h2>
      <p style="color:#666;font-size:12px">Generated on: ${todayDMY} | Grouped by: ${groupLabel} | Filters: ${filterDesc}</p>
      <hr style="border:1px solid #ddd;margin:15px 0">
      <table style="width:100%;border-collapse:collapse;font-size:12px"><thead><tr>${headers}</tr></thead><tbody>${tableRows}</tbody></table>
      <p style="color:#999;font-size:10px;margin-top:30px;text-align:center">${companyName || "Call Tracker & CRM Suite"} — Report generated on ${todayDMY}</p>
    </body></html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
    setToast({ message: "Summary PDF ready!", type: "success" });
  };

  // Detailed PDF Report
  const exportDetailedPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      Object.entries(advParams(advRef.current, callFilterFields(masters, advRef.current))).forEach(([k, v]) => { if (v) params.append(k, v); });

      if (reportType === "call-summary") {
        const r = await api(`/reports/call-export?${params}&limit=100000`);
        const rows = r.data;
        if (rows.length === 0) { setToast({ message: "No data to export.", type: "warning" }); setExporting(false); return; }

        const tableRows = rows.map(r => `<tr>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.ticket_number || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${formatDateDMY(r.call_date)}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.executive_name || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.client_name || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.product_name || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${formatTime12hr(r.call_start_time)}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${formatTime12hr(r.call_end_time)}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px;text-align:right">${r.hours_spent || 0}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.call_type_name || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.regarding || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.call_status || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.problem_observed || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.solution_provided || ""}</td>
        </tr>`).join("");

        const html = `<html><head><title>Detailed Call Report</title></head><body style="font-family:Arial,sans-serif;padding:20px">
          ${headerHtml}
          <h2 style="color:#333;margin:10px 0 5px">Detailed Call Report</h2>
          <p style="color:#666;font-size:12px">Generated on: ${todayDMY} | Total Records: ${rows.length}</p>
          <hr style="border:1px solid #ddd;margin:10px 0">
          <table style="width:100%;border-collapse:collapse;font-size:11px">
            <thead><tr>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Ticket#</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Date</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Executive</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Customer</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Product</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Start</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">End</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Hours</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Type</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Subject</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Status</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Problem</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Solution</th>
            </tr></thead>
            <tbody>${tableRows}</tbody>
          </table>
          <p style="color:#999;font-size:10px;margin-top:20px;text-align:center">${companyName || "Call Tracker & CRM Suite"} — Report generated on ${todayDMY}</p>
        </body></html>`;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      } else {
        const r = await api(`/leads?${params}&limit=100000`);
        const rows = r.data;
        if (rows.length === 0) { setToast({ message: "No data to export.", type: "warning" }); setExporting(false); return; }

        // Fetch follow-ups for each lead
        const leadFUs = {};
        let maxFU = 0;
        for (const lead of rows) {
          try { const fr = await api(`/lead-followups/lead/${lead.id}`); leadFUs[lead.id] = fr.data || []; if (fr.data.length > maxFU) maxFU = fr.data.length; } catch(e) { leadFUs[lead.id] = []; }
        }

        const fuThHeaders = Array.from({ length: maxFU }, (_, i) => `<th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">FU${i+1} Date</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">FU${i+1} Action</th><th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">FU${i+1} Outcome</th>`).join("");

        const tableRows = rows.map(r => {
          const fus = leadFUs[r.id] || [];
          const fuCells = Array.from({ length: maxFU }, (_, i) => {
            const fu = fus[i];
            if (fu) {
              const dateColor = fu.next_followup_date && new Date(fu.next_followup_date) < new Date() ? '#dc2626' : '#000';
              return `<td style="padding:6px;border:1px solid #ddd;font-size:10px;color:${dateColor}">${formatDateDMY(fu.followup_date)}</td><td style="padding:6px;border:1px solid #ddd;font-size:10px">${fu.next_action || ""}</td><td style="padding:6px;border:1px solid #ddd;font-size:10px">${fu.outcome || ""}</td>`;
            }
            return `<td style="padding:6px;border:1px solid #ddd"></td><td style="padding:6px;border:1px solid #ddd"></td><td style="padding:6px;border:1px solid #ddd"></td>`;
          }).join("");
          return `<tr>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.enquiry_number || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${formatDateDMY(r.enquiry_date)}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.prospect_company || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.contact_person || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.lead_source || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.status || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.priority || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px;text-align:right">${formatCurrencyINR(r.estimated_deal_value)}</td>
          <td style="padding:6px;border:1px solid #ddd;font-size:11px">${r.executive_name || ""}</td>
          ${fuCells}
        </tr>`;
        }).join("");

        const html = `<html><head><title>Detailed Lead Report</title></head><body style="font-family:Arial,sans-serif;padding:20px">
          ${headerHtml}
          <h2 style="color:#333;margin:10px 0 5px">Detailed Lead Report</h2>
          <p style="color:#666;font-size:12px">Generated on: ${todayDMY} | Total Records: ${rows.length}</p>
          <hr style="border:1px solid #ddd;margin:10px 0">
          <table style="width:100%;border-collapse:collapse;font-size:11px">
            <thead><tr>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Enquiry#</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Date</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Company</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Contact</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Source</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Status</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Priority</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Deal Value</th>
              <th style="padding:6px;border:1px solid #ddd;background:#1a56db;color:#fff">Executive</th>
              ${fuThHeaders}
            </tr></thead>
            <tbody>${tableRows}</tbody>
          </table>
          <p style="color:#999;font-size:10px;margin-top:20px;text-align:center">${companyName || "Call Tracker & CRM Suite"} — Report generated on ${todayDMY}</p>
        </body></html>`;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      }
      setToast({ message: "Detailed PDF ready!", type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setExporting(false);
  };

  const callGroupOptions = [
    { value: "", label: "— None (totals only) —" },
    { value: "client", label: "Client" }, { value: "executive", label: "Executive" },
    { value: "product", label: "Product" }, { value: "call_type", label: "Call Type" },
    { value: "month", label: "Month" }, { value: "location", label: "Location" },
    { value: "principal", label: "Principal" },
  ];

  const leadGroupOptions = [
    { value: "status", label: "Status" }, { value: "source", label: "Source" },
    { value: "priority", label: "Priority" }, { value: "executive", label: "Executive" },
    { value: "product", label: "Product" }, { value: "month", label: "Month" },
    { value: "lost_reason", label: "Lost Reason" },
  ];

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Reports & Summary</h1>
        <div style={styles.flexRow}>
          <select style={styles.select} value={reportType} onChange={(e) => { setReportType(e.target.value); setGroupBy(e.target.value === "lead-summary" ? "status" : ""); }}>
            <option value="call-summary">Call Tracker Summary</option>
            <option value="lead-summary">Lead Summary</option>
            <option value="followups">Lead Follow-ups (who did what)</option>
            <option value="scheduled-visits">Scheduled Visits (Detailed)</option>
            <option value="licenses">Licenses &amp; Renewals</option>
            <option value="report-writer">📝 Report Writer (build your own)</option>
          </select>
        </div>
      </div>

      {reportType === "followups" ? (
        <FollowupReport masters={masters} companyLogo={companyLogo} companyName={companyName} />
      ) : reportType === "scheduled-visits" ? (
        <ScheduledVisitsReport masters={masters} companyLogo={companyLogo} companyName={companyName} />
      ) : reportType === "report-writer" ? (
        <ReportWriter masters={masters} companyLogo={companyLogo} companyName={companyName} />
      ) : reportType === "licenses" ? (
        <LicensesReport masters={masters} companyLogo={companyLogo} companyName={companyName} setPendingClient={setPendingClient} setPendingReturn={setPendingReturn} setActiveScreen={setActiveScreen} />
      ) : (
      <>
      <div style={styles.card}>
        {(reportType === "call-summary" || reportType === "scheduled-visits" || reportType === "lead-summary") && (
          <AdvancedFilters masters={masters} screen={reportType === "lead-summary" ? "leads" : "calls"}
            fieldsFn={reportType === "lead-summary" ? leadFilterFields : callFilterFields}
            rows={advRows} setRows={setAdvRows}
            onApply={(r) => { setAdvRows(r); fetchReport(undefined, r); }} />
        )}
        <div style={styles.filterBar}>
          <div><label style={styles.label}>From</label><DateInput value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} /></div>
          <div><label style={styles.label}>To</label><DateInput value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} /></div>
          {reportType === "call-summary" && (
            <>
              <div><label style={styles.label}>Executive</label>
                <select style={styles.select} value={filters.executive_id || ""} onChange={(e) => setFilters({ ...filters, executive_id: e.target.value })}>
                  <option value="">All</option>{masters.executives?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div><label style={styles.label}>Client</label>
                <select style={styles.select} value={filters.client_id || ""} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}>
                  <option value="">All</option>{masters.clients?.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
            </>
          )}
          {/* Search anything — same fields as the Call Tracker / Leads search */}
          <div><label style={styles.label}>Search</label>
            <SearchInput style={{ ...styles.input, minWidth: 190 }} value={filters.search || ""}
              onSearch={(t) => setFilters(f => ({ ...f, search: t }))}
              onEnter={() => repFetchRef.current && repFetchRef.current()}
              placeholder={reportType === "call-summary" ? "Ticket, customer, phone, licence…" : "Enquiry #, company, phone, city…"}
              title={reportType === "call-summary"
                ? "Searches ticket #, customer name / phone / e-mail / GSTIN, executives, contact spoken to, regarding, call details, problem, solution, licence #, version, TDL type, status"
                : "Searches enquiry #, company, contact person, phone, e-mail, city, remarks, competitor info"} />
          </div>
          <div><label style={styles.label}>Group By</label>
            <select style={styles.select} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              {(reportType === "call-summary" ? callGroupOptions : leadGroupOptions).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <button style={styles.btn("primary", "sm")} onClick={() => fetchReport()}>Generate</button>
          <button style={styles.btn("ghost", "sm")} onClick={() => { setFilters({}); fetchReport({}); }}>Clear Filters</button>
        </div>

        {/* Export Buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, padding: "12px 16px", background: "#f8fafc", borderRadius: 8, border: `1px solid ${colors.border}` }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.textLight, alignSelf: "center", marginRight: 8 }}>EXPORT:</span>
          <button style={styles.btn("success", "sm")} onClick={exportSummaryExcel}>📊 Summary to Excel</button>
          <button style={styles.btn("primary", "sm")} onClick={exportDetailedExcel} disabled={exporting}>{exporting ? "Exporting..." : "📋 Detailed to Excel"}</button>
          <button style={{ ...styles.btn("ghost", "sm"), background: "#dc2626", color: "#fff" }} onClick={exportPDF}>📄 Summary to PDF</button>
          <button style={{ ...styles.btn("ghost", "sm"), background: "#9333ea", color: "#fff" }} onClick={exportDetailedPDF} disabled={exporting}>{exporting ? "Exporting..." : "📄 Detailed to PDF"}</button>
        </div>

        {totals && (
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            {reportType === "call-summary" ? (
              <>
                <div style={{ ...styles.statCard(colors.primary), cursor: "pointer" }} onClick={() => handleCardClick("all_calls", {})}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>TOTAL CALLS ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.total_calls}</div></div>
                <div style={{ ...styles.statCard(colors.success), cursor: "pointer" }} onClick={() => handleCardClick("completed", { call_status: "Completed" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>COMPLETED ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.completed_calls}</div></div>
                <div style={{ ...styles.statCard(colors.warning), cursor: "pointer" }} onClick={() => handleCardClick("open", { call_status: "Open" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>OPEN ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.open_calls}</div></div>
                <div style={{ ...styles.statCard("#8b5cf6"), cursor: "pointer" }} onClick={() => handleCardClick("in_progress", { call_status: "In Progress" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>IN PROGRESS ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.in_progress_calls || 0}</div></div>
                <div style={{ ...styles.statCard(colors.danger), cursor: "pointer" }} onClick={() => handleCardClick("escalated", { call_status: "Escalated" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>ESCALATED ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.escalated_calls || 0}</div></div>
                <div style={{ ...styles.statCard("#64748b"), cursor: "pointer" }} onClick={() => handleCardClick("on_hold", { call_status: "On Hold" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>ON HOLD ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.on_hold_calls || 0}</div></div>
                <div style={{ ...styles.statCard("#0ea5e9"), cursor: "pointer" }} title="Click to list the calls with hours" onClick={() => handleCardClick("total_hours", { __nonzero: "hours_spent" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>TOTAL HOURS ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{parseFloat(totals.total_hours).toFixed(1)}</div></div>
              </>
            ) : (
              <>
                <div style={{ ...styles.statCard(colors.primary), cursor: "pointer" }} onClick={() => handleCardClick("all_leads", {})}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>TOTAL LEADS ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.total_leads}</div></div>
                <div style={{ ...styles.statCard(colors.success), cursor: "pointer" }} onClick={() => handleCardClick("converted", { status: "Converted" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>CONVERTED ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.converted_count}</div></div>
                <div style={{ ...styles.statCard(colors.danger), cursor: "pointer" }} onClick={() => handleCardClick("lost", { status: "Lost" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>LOST ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.lost_count || 0}</div></div>
                <div style={{ ...styles.statCard("#f59e0b"), cursor: "pointer" }} onClick={() => handleCardClick("hot", { priority: "Hot" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>HOT LEADS ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.hot_count || 0}</div></div>
                <div style={{ ...styles.statCard("#8b5cf6"), cursor: "pointer" }} onClick={() => handleCardClick("new_leads", { status: "New" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>NEW LEADS ↗</div><div style={{ fontSize: 22, fontWeight: 800 }}>{totals.new_count || 0}</div></div>
                <div style={{ ...styles.statCard("#0ea5e9"), cursor: "pointer" }} title={`${formatCurrencyINR(totals.total_pipeline_value)} — click to list the leads with a deal value`} onClick={() => handleCardClick("pipeline_value", { __nonzero: "estimated_deal_value" })}><div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight }}>PIPELINE VALUE ↗</div><div style={{ fontSize: 22, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{formatINRShort(totals.total_pipeline_value)}</div></div>
              </>
            )}
          </div>
        )}

        {/* Drill-down detail */}
        {drillType && (
          <div id="report-drill" style={{ ...styles.card, marginBottom: 16, border: `2px solid ${colors.primary}`, scrollMarginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{drillType.replace(/_/g, " ").toUpperCase()} — {drillData.length} records</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={styles.btn("success", "sm")} onClick={exportDrillData}>📊 Export</button>
                <button style={styles.btn("ghost", "sm")} onClick={() => { setDrillType(null); setDrillData([]); }}>✕ Close</button>
              </div>
            </div>
            <SortBar sorts={sorts} clearSort={clearSort} labels={{ ticket_number: "Ticket#", call_date: "Date", executive_name: "Executive", client_name: "Customer", contacts: "Contacts", emails: "Email", spoke_to: "Spoke To", product_name: "Product", license_number: "License#", hours_spent: "Hours", call_status: "Status" }} />
            {drillLoading ? <p style={{ color: colors.textLight }}>Loading...</p> : (
              <div style={{ maxHeight: 400, overflowY: "auto" }}>
                <table style={styles.table}>
                  {reportType === "call-summary" ? (
                    <>
                      <thead><tr>
                        {[["Ticket#","ticket_number"],["Date","call_date"],["Executive","executive_name"],["Customer","client_name"],["Contacts","contacts"],["Email","emails"],["Spoke To","spoke_to"],["Product","product_name"],["License#","license_number"],["Start","call_start_time"],["End","call_end_time"],["Hours","hours_spent"],["Status","call_status"]]
                          .map(([h, k]) => <SortTh key={h} label={h} sortKey={k} sorts={sorts} onToggle={toggleSort} style={styles.th} />)}
                      </tr></thead>
                      <tbody>{drillRows.map((r, i) => (
                        <tr key={i} onClick={() => { if (setPendingCall) { rememberReport(); setPendingReturn("reports"); setPendingCall(r); setActiveScreen("call-tracker"); } }} style={{ cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ ...styles.td, fontWeight: 700, color: colors.primary }}>{r.ticket_number}</td>
                          <td style={styles.td}>{formatDateDMY(r.call_date)}</td>
                          <td style={styles.td}>{r.executive_name}</td>
                          <td style={styles.td}>{r.client_name}</td>
                          <td style={styles.td}>{contactNums(r)}</td>
                          <td style={styles.td}>{contactMails(r)}</td>
                          <td style={styles.td}>{r.contact_person_called || contactPersons(r)}</td>
                          <td style={styles.td}>{r.product_name || "—"}</td>
                          <td style={{ ...styles.td, fontSize: 11 }}>{r.license_number || "—"}</td>
                          <td style={styles.td}>{formatTime12hr(r.call_start_time)}</td>
                          <td style={styles.td}>{formatTime12hr(r.call_end_time)}</td>
                          <td style={styles.td}>{r.hours_spent || 0}</td>
                          <td style={styles.td}><StatusBadge status={r.call_status} /></td>
                        </tr>
                      ))}</tbody>
                    </>
                  ) : (
                    <>
                      <thead><tr>
                        <th style={styles.th}>Enquiry#</th><th style={styles.th}>Date</th><th style={styles.th}>Company</th>
                        <th style={styles.th}>Contact</th><th style={styles.th}>Source</th><th style={styles.th}>Product</th>
                        <th style={styles.th}>Deal Value</th><th style={styles.th}>Executive</th><th style={styles.th}>Follow-up Date</th><th style={styles.th}>Next Action</th><th style={styles.th}>Priority</th><th style={styles.th}>Status</th>
                      </tr></thead>
                      <tbody>{drillRows.map((r, i) => (
                        <tr key={i} onClick={() => { if (setPendingLead) { rememberReport(); setPendingReturn("reports"); setPendingLead(r); setActiveScreen("leads"); } }} style={{ cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#eef2ff"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ ...styles.td, fontWeight: 700, color: colors.primary }}>{r.enquiry_number}</td>
                          <td style={styles.td}>{formatDateDMY(r.enquiry_date)}</td>
                          <td style={styles.td}>{r.prospect_company || "—"}</td>
                          <td style={styles.td}>{r.contact_person || "—"}</td>
                          <td style={styles.td}>{r.lead_source || "—"}</td>
                          <td style={styles.td}>{r.product_name || "—"}</td>
                          <td style={styles.td}>{formatCurrencyINR(r.estimated_deal_value)}</td>
                          <td style={styles.td}>{r.executive_name || "—"}</td>
                          <td style={{ ...styles.td, color: r.follow_up_date && new Date(r.follow_up_date) < new Date() ? colors.danger : colors.text, fontWeight: 600 }}>{r.follow_up_date ? formatDateDMY(r.follow_up_date) : "—"}</td>
                          <td style={styles.td}>{r.next_action || "—"}</td>
                          <td style={styles.td}>{r.priority}</td>
                          <td style={styles.td}><StatusBadge status={r.status} /></td>
                        </tr>
                      ))}</tbody>
                    </>
                  )}
                </table>
              </div>
            )}
          </div>
        )}

        {groupBy && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>{groupBy.charAt(0).toUpperCase() + groupBy.slice(1).replace("_", " ")}</th>
              {reportType === "call-summary" ? (
                <>
                  <th style={{ ...styles.th, textAlign: "right" }}>Total Calls</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Total Hours</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Completed</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Open</th>
                </>
              ) : (
                <>
                  <th style={{ ...styles.th, textAlign: "right" }}>Total Leads</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Pipeline Value</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Converted</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const rf = rowDrill(row), rk = rowDrillKey(row);
              return (
              <tr key={i} onClick={() => rf && handleCardClick(rk, rf)}
                title={rf ? "Click to list these records" : "This row can't be listed (no value to filter on)"}
                style={{ cursor: rf ? "pointer" : "default", background: drillType === rk ? "#eef4ff" : undefined }}>
                <td style={{ ...styles.td, fontWeight: 600, color: rf ? colors.primary : undefined }}>{row.group_label || "—"}{rf ? " ↗" : ""}</td>
                {reportType === "call-summary" ? (
                  <>
                    <td style={{ ...styles.td, textAlign: "right" }}>{row.total_calls}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{parseFloat(row.total_hours).toFixed(1)}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{row.completed_calls}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{row.open_calls}</td>
                  </>
                ) : (
                  <>
                    <td style={{ ...styles.td, textAlign: "right" }}>{row.total_leads}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{formatCurrencyINR(row.total_pipeline_value)}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{row.converted_count}</td>
                  </>
                )}
              </tr>
              );
            })}
            {data.length === 0 && <tr><td colSpan={5} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No data for selected filters.</td></tr>}
          </tbody>
        </table>
        )}
      </div>
      </>
      )}
    </div>
  );
};

// ============================================================================
// MAIN APPLICATION
// ============================================================================
// Customer Products & Licenses Section Component
const CustomerProductsSection = ({ editItem, masters, loadMasters }) => {
  const [custProds, setCustProds] = useState([]);
  const [cpForm, setCpForm] = useState({ user_type: "Single User", quantity: 1 });
  const [cpEditId, setCpEditId] = useState(null);   // set = the box below is editing that licence, not adding one
  const [showCpCreate, setShowCpCreate] = useState(false);
  const [quickCreate, setQuickCreate] = useState(null);
  const [toast, setToast] = useState(null);
  const userTypes = ["Single User", "Multi User", "Unlimited", "Per Device", "Enterprise", "Trial", "Others"];
  const catOpts = ["Software License", "Hardware", "Service", "AMC/Support", "Customization", "Training", "Subscription", "Consulting", "Others"].map(d => ({ value: d, label: d }));
  const prodTypeOpts = ["One-time License", "Subscription (Monthly)", "Subscription (Annual)", "Recurring", "Per-user", "Per-transaction", "Others"].map(d => ({ value: d, label: d }));

  const loadCustProds = async () => { try { const r = await api(`/customer-products/customer/${editItem.id}`); setCustProds(r.data); } catch(e) {} };
  useEffect(() => { if (editItem?.id) loadCustProds(); }, [editItem?.id]);

  // ---- License renewal ----
  const [renewCp, setRenewCp] = useState(null);
  const [renewForm, setRenewForm] = useState({});
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const addTerm = (baseStr, term) => {
    const d = baseStr ? new Date(baseStr) : new Date();
    if (term === "Monthly") d.setMonth(d.getMonth() + 1);
    else if (term === "Quarterly") d.setMonth(d.getMonth() + 3);
    else if (term === "Yearly") d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  };
  const computeExpiry = (cp, term, basis) => {
    if (term === "Custom") return "";
    const base = basis === "from_today" ? todayISO() : (cp.subscription_expiry ? String(cp.subscription_expiry).slice(0, 10) : todayISO());
    return addTerm(base, term);
  };
  const openRenew = (cp) => {
    const term = cp.renewal_term || "Yearly";
    const basis = "from_expiry";
    setRenewForm({ term, basis, new_expiry: computeExpiry(cp, term, basis), amount: "", note: "" });
    setRenewCp(cp);
  };
  const setRenewField = (patch) => {
    setRenewForm(f => {
      const next = { ...f, ...patch };
      if (("term" in patch || "basis" in patch) && next.term !== "Custom") next.new_expiry = computeExpiry(renewCp, next.term, next.basis);
      return next;
    });
  };
  const saveRenew = async () => {
    if (!renewForm.new_expiry) { setToast({ message: "Please set the new expiry date.", type: "error" }); return; }
    try {
      await api(`/customer-products/${renewCp.id}/renew`, { method: "POST", body: { new_expiry: renewForm.new_expiry, term: renewForm.term, amount: renewForm.amount || null, note: renewForm.note || null } });
      setRenewCp(null); loadCustProds();
      setToast({ message: "License renewed!", type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };
  const expiryStatus = (d) => {
    if (!d) return null;
    const days = Math.ceil((new Date(String(d).slice(0, 10)) - new Date(new Date().toDateString())) / 86400000);
    if (days < 0) return { label: "Expired", color: colors.danger };
    if (days <= 30) return { label: `Expiring (${days}d)`, color: "#d97706" };
    return { label: "Active", color: colors.success };
  };

  return (
    <div style={{ marginTop: 20 }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Products & Licenses</h3>
        <button style={styles.btn("primary", "sm")} onClick={() => { setCpEditId(null); setCpForm({ user_type: "Single User", quantity: 1 }); setShowCpCreate(true); }}>+ Add Product</button>
      </div>
      {custProds.length > 0 ? (
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Product</th><th style={styles.th}>License #</th><th style={styles.th}>User Type</th>
            <th style={styles.th}>Qty</th><th style={styles.th}>Expiry</th><th style={styles.th}>Actions</th>
          </tr></thead>
          <tbody>
            {custProds.map(cp => (
              <tr key={cp.id}>
                <td style={styles.td}>{cp.product_name}</td>
                <td style={styles.td}>{cp.license_number || "—"}</td>
                <td style={styles.td}>{cp.user_type}</td>
                <td style={styles.td}>{cp.quantity}</td>
                <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{cp.subscription_expiry ? formatDateDMY(cp.subscription_expiry) : "—"}{(() => { const s = expiryStatus(cp.subscription_expiry); return s ? <span style={{ marginLeft: 6, background: s.color + "22", color: s.color, padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{s.label}</span> : null; })()}</td>
                <td style={{ ...styles.td, whiteSpace: "nowrap" }}>   {/* Edit · Renew · Remove on one line */}
                  <button style={{ ...styles.btn("primary", "sm"), marginRight: 6 }} title="Correct this licence's details"
                    onClick={() => {
                      setCpEditId(cp.id);
                      setCpForm({ product_id: cp.product_id ? String(cp.product_id) : "", license_number: cp.license_number || "",
                        user_type: cp.user_type || "Single User", quantity: cp.quantity || 1, notes: cp.notes || "",
                        subscription_expiry: cp.subscription_expiry ? toISODate(cp.subscription_expiry) : "" });
                      setShowCpCreate(true);
                    }}>Edit</button>
                  <button style={{ ...styles.btn("success", "sm"), marginRight: 6 }} onClick={() => openRenew(cp)}>Renew</button>
                  <button style={styles.btn("danger", "sm")} onClick={async () => {
                    if (!window.confirm("Remove this product?")) return;
                    try { await api(`/customer-products/${cp.id}`, { method: "DELETE" }); loadCustProds(); } catch(e) { setToast({ message: e.message, type: "error" }); }
                  }}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p style={{ fontSize: 12, color: colors.textLight, textAlign: "center", padding: 12 }}>No products linked. Click "+ Add Product" to add.</p>}
      {renewCp && (
        <div style={styles.modal} onClick={() => setRenewCp(null)}>
          <div style={{ ...styles.modalContent, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, color: colors.success }}>🔄 Renew License</h3>
            <div style={{ fontSize: 13, color: colors.textLight, marginBottom: 12 }}>
              <b>{renewCp.product_name}</b>{renewCp.license_number ? ` · ${renewCp.license_number}` : ""} — current expiry: <b>{renewCp.subscription_expiry ? formatDateDMY(renewCp.subscription_expiry) : "—"}</b>
            </div>
            <div style={styles.grid(2)}>
              <div><label style={styles.label}>Term</label>
                <select style={styles.select} value={renewForm.term} onChange={e => setRenewField({ term: e.target.value })}>
                  {["Monthly", "Quarterly", "Yearly", "Custom"].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label style={styles.label}>Calculate from</label>
                <select style={styles.select} value={renewForm.basis} disabled={renewForm.term === "Custom"} onChange={e => setRenewField({ basis: e.target.value })}>
                  <option value="from_expiry">Current expiry</option>
                  <option value="from_today">Today (renewal date)</option>
                </select>
              </div>
              <div><label style={styles.label}>New Expiry *</label><DateInput value={renewForm.new_expiry} onChange={v => setRenewForm(f => ({ ...f, new_expiry: v }))} /></div>
              <div><label style={styles.label}>Renewal Amount (₹)</label><input style={styles.input} type="number" value={renewForm.amount || ""} onChange={e => setRenewForm(f => ({ ...f, amount: e.target.value }))} placeholder="e.g. 12000" /></div>
              <div style={{ gridColumn: "1 / -1" }}><label style={styles.label}>Note</label><input style={styles.input} value={renewForm.note || ""} onChange={e => setRenewForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. renewed for 1 year, incl. support" /></div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost", "sm")} onClick={() => setRenewCp(null)}>Cancel</button>
              <button style={styles.btn("success", "sm")} onClick={saveRenew}>Renew</button>
            </div>
          </div>
        </div>
      )}
      {showCpCreate && (
        <div style={{ padding: 16, background: cpEditId ? "#fffbeb" : "#f0f4ff", borderRadius: 8, border: `1px solid ${cpEditId ? "#fcd34d" : colors.primary + "30"}`, marginTop: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: cpEditId ? "#92400e" : colors.primary }}>
            {cpEditId ? "✏ Edit licence" : "Add product / licence"}
            {cpEditId && <span style={{ fontWeight: 400, color: colors.textLight, marginLeft: 8, fontSize: 12 }}>— to record a renewal (with amount and history), use Renew instead</span>}
          </div>
          <div style={styles.grid(3)}>
            <div><label style={styles.label}>Product *</label>
              <SearchableSelect options={masters.products?.map(p => ({ value: p.id.toString(), label: p.name })) || []}
                value={cpForm.product_id || ""} onChange={v => setCpForm({ ...cpForm, product_id: v })} placeholder="Search product..."
                onCreate={() => setQuickCreate(true)} createLabel="Create Product" />
            </div>
            <div><label style={styles.label}>License Number</label><FastText style={styles.input} value={cpForm.license_number || ""} onCommit={(t) => setCpForm(f => ({ ...f, license_number: t }))} /></div>
            <div><label style={styles.label}>User Type</label>
              <select style={styles.select} value={cpForm.user_type || "Single User"} onChange={e => setCpForm({ ...cpForm, user_type: e.target.value })}>
                {userTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={styles.label}>Quantity</label><input style={styles.input} type="number" value={cpForm.quantity || 1} onChange={e => setCpForm({ ...cpForm, quantity: e.target.value })} /></div>
            <div><label style={styles.label}>Subscription Expiry</label><DateInput value={cpForm.subscription_expiry} onChange={v => setCpForm({ ...cpForm, subscription_expiry: v })} /></div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <button style={styles.btn("primary", "sm")} onClick={async () => {
                if (!cpForm.product_id) { setToast({ message: "Select a product.", type: "error" }); return; }
                try {
                  if (cpEditId) {
                    await api(`/customer-products/${cpEditId}`, { method: "PUT", body: {
                      ...cpForm, subscription_expiry: cpForm.subscription_expiry || null, quantity: parseInt(cpForm.quantity) || 1, notes: cpForm.notes || null } });
                    setToast({ message: "Licence updated!", type: "success" });
                  } else {
                    await api("/customer-products", { method: "POST", body: { ...cpForm, client_id: editItem.id } });
                    setToast({ message: "Product added!", type: "success" });
                  }
                  setShowCpCreate(false); setCpEditId(null); loadCustProds();
                } catch(e) { setToast({ message: e.message, type: "error" }); }
              }}>Save</button>
              <button style={styles.btn("ghost", "sm")} onClick={() => { setShowCpCreate(false); setCpEditId(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {quickCreate && (
        <FullCreate title="Product" endpoint="products" fields={[
          { key: "name", label: "Product Name", required: true },
          { key: "category", label: "Category", type: "select", options: catOpts },
          { key: "product_type", label: "Product Type", type: "select", options: prodTypeOpts },
          { key: "description", label: "Description", type: "textarea", fullWidth: true },
          { key: "hsn_sac_code", label: "HSN/SAC Code" },
          { key: "default_gst_rate", label: "GST Rate (%)", type: "number" },
          { key: "unit_price", label: "Unit Price (₹)", type: "number" },
        ]}
          onCreated={(newItem) => { setCpForm({ ...cpForm, product_id: newItem.id?.toString() }); if (loadMasters) loadMasters(); }}
          onClose={() => setQuickCreate(null)} />
      )}
    </div>
  );
};

// Customer License Bulk Import Component
const CustomerLicenseImport = ({ masters, onDone }) => {
  const [show, setShow] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState(null);

  const parseCSVLine = (line) => {
    const result = []; let current = ""; let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQuotes && line[i+1] === '"') { current += '"'; i++; } else { inQuotes = !inQuotes; } }
      else if (ch === ',' && !inQuotes) { result.push(current); current = ""; }
      else { current += ch; }
    }
    result.push(current);
    return result;
  };

  const handleFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      let text = evt.target.result;
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
      text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      const lines = text.split("\n").filter(l => l.trim() && !l.trim().startsWith("#") && !l.trim().toLowerCase().startsWith("sep="));
      if (lines.length < 2) { setToast({ message: "File has no data rows.", type: "error" }); return; }

      // Detect separator
      const firstLine = lines[0];
      const sep = firstLine.includes("\t") ? "\t" : ",";

      const splitLine = (line) => {
        if (sep === "\t") return line.split("\t").map(v => v.replace(/^"|"$/g, "").trim());
        // CSV with possible quoted fields
        const result = []; let current = ""; let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { if (inQuotes && line[i+1] === '"') { current += '"'; i++; } else { inQuotes = !inQuotes; } }
          else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ""; }
          else { current += ch; }
        }
        result.push(current.trim());
        return result;
      };

      const headers = splitLine(lines[0]).map(h => h.toLowerCase().trim());
      const fieldMap = { "company name": "company_name", "customer name": "company_name", "customer id": "customer_id", "product name": "product_name", "license number": "license_number", "user type": "user_type", "quantity": "quantity", "subscription expiry": "subscription_expiry", "subscription expiry (dd/mm/yyyy)": "subscription_expiry" };

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = splitLine(lines[i]);
        if (vals.every(v => !v)) continue;
        const row = {};
        headers.forEach((h, idx) => { const key = fieldMap[h] || h.replace(/\s+/g, "_"); row[key] = vals[idx] || ""; });

        // Skip rows where product is empty
        if (!row.product_name || !row.product_name.trim()) continue;

        // Match customer by ID first, then by name
        let clientMatch = null;
        if (row.customer_id) {
          clientMatch = masters.clients?.find(c => c.id == row.customer_id);
        }
        if (!clientMatch && row.company_name) {
          clientMatch = masters.clients?.find(c => (c.company_name || "").toLowerCase().trim() === (row.company_name || "").toLowerCase().trim());
        }
        const prodMatch = masters.products?.find(p => (p.name || "").toLowerCase().trim() === (row.product_name || "").toLowerCase().trim());

        row._client_id = clientMatch?.id;
        row._client_name = clientMatch?.company_name || row.company_name || row.customer_id;
        row._product_id = prodMatch?.id;
        row._product_name = prodMatch?.name || row.product_name;
        row._status = clientMatch && prodMatch ? "ready" : !clientMatch ? "no_customer" : "no_product";

        // Parse DD/MM/YYYY date
        if (row.subscription_expiry && row.subscription_expiry.includes("/")) {
          const parts = row.subscription_expiry.split("/");
          if (parts.length === 3 && parts[2].length === 4) row.subscription_expiry = `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}`;
        }
        rows.push(row);
      }
      if (rows.length === 0) { setToast({ message: "No license records found. Make sure Product Name column is filled.", type: "error" }); return; }
      setImportData(rows);
      setShow(true);
    };
    reader.readAsText(file, "UTF-8"); e.target.value = "";
  };

  const handleImport = async () => {
    setImporting(true);
    let created = 0, updated = 0, failed = 0;
    for (const row of importData) {
      if (row._status !== "ready") { failed++; continue; }
      try {
        // Check if this customer-product combination exists
        const existing = await api(`/customer-products/customer/${row._client_id}`);
        const existingProd = existing.data?.find(cp => cp.product_id === row._product_id);
        if (existingProd) {
          await api(`/customer-products/${existingProd.id}`, { method: "PUT", body: {
            product_id: row._product_id, license_number: row.license_number || existingProd.license_number,
            subscription_expiry: row.subscription_expiry || existingProd.subscription_expiry,
            user_type: row.user_type || existingProd.user_type, quantity: row.quantity || existingProd.quantity
          }});
          updated++;
        } else {
          await api("/customer-products", { method: "POST", body: {
            client_id: row._client_id, product_id: row._product_id,
            license_number: row.license_number || null, subscription_expiry: row.subscription_expiry || null,
            user_type: row.user_type || "Single User", quantity: row.quantity || 1
          }});
          created++;
        }
      } catch (e) { failed++; }
    }
    setImporting(false); setShow(false); setImportData([]);
    setToast({ message: `License import complete! ${created} created, ${updated} updated, ${failed} failed.`, type: (created + updated) > 0 ? "success" : "error" });
    if (onDone) onDone();
  };

  const blankTemplate = () => {
    const headers = csvRow(["Customer ID", "Customer Name", "Product Name", "License Number", "User Type", "Quantity", "Subscription Expiry (DD/MM/YYYY)"]);
    const sample = csvRow(["1", "Sample Customer Name", "Gold", "ABC123456", "Single User", "1", "31/03/2027"]);
    const clients = masters.clients || [];
    const sortedClients = [...clients].sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
    const rows = sortedClients.map(c => csvRow([c.id, c.company_name || "", "", "", "", "", ""])).join("\n");
    const guide = [
      "# LICENSE IMPORT GUIDE - Delete these # lines and the sample row before importing",
      "# Customer ID: Auto-filled. Do not change.",
      "# Customer Name: Auto-filled for reference. Do not change.",
      "# Product Name: Must match exactly as in Products master (e.g. Gold or Silver)",
      "# License Number: Your software license number",
      "# User Type: Single User / Multi User / Unlimited / Per Device / Enterprise / Trial",
      "# Quantity: Number of licenses (e.g. 1 or 5)",
      "# Subscription Expiry: DD/MM/YYYY format (e.g. 31/03/2027)",
      "# Only fill rows for customers who have products. Empty Product rows are skipped.",
      "# ---- DELETE THIS SAMPLE ROW BELOW BEFORE IMPORTING ----",
    ].join("\n");
    downloadCSV(guide + "\n" + headers + "\n" + sample + "\n" + rows + "\n", "customer_licenses_template.csv");
    setToast({ message: `Template downloaded with sample + ${sortedClients.length} customers!`, type: "success" });
  };

  return (
    <div style={{ display: "inline-flex", gap: 6 }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <button style={styles.btn("ghost", "sm")} onClick={blankTemplate}>📄 License Template</button>
      <label style={{ ...styles.btn("warning", "sm"), cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
        📥 Import Licenses <input type="file" accept=".csv,.txt" onChange={handleFile} style={{ display: "none" }} />
      </label>

      {show && (
        <div style={styles.modal} onClick={() => setShow(false)}>
          <div style={{ ...styles.modalContent, maxWidth: 950 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>Import Customer Licenses — {importData.length} records</h2>
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <div style={{ padding: "8px 16px", background: "#ecfdf5", borderRadius: 8, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: colors.success }}>✓ {importData.filter(r => r._status === "ready").length}</span> Ready
              </div>
              <div style={{ padding: "8px 16px", background: "#fef2f2", borderRadius: 8, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: colors.danger }}>✗ {importData.filter(r => r._status !== "ready").length}</span> Issues (customer or product not found)
              </div>
            </div>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <table style={styles.table}>
                <thead><tr>
                  <th style={styles.th}>Status</th><th style={styles.th}>Customer</th><th style={styles.th}>Product</th>
                  <th style={styles.th}>License #</th><th style={styles.th}>User Type</th><th style={styles.th}>Qty</th><th style={styles.th}>Expiry</th>
                </tr></thead>
                <tbody>
                  {importData.map((row, i) => (
                    <tr key={i} style={{ background: row._status === "ready" ? "#f0fdf4" : "#fef2f2" }}>
                      <td style={styles.td}>{row._status === "ready" ? "✅" : row._status === "no_customer" ? "❌ Customer?" : "❌ Product?"}</td>
                      <td style={{ ...styles.td, color: row._status === "no_customer" ? colors.danger : "inherit", fontWeight: row._status === "no_customer" ? 700 : 400 }}>{row._client_name}</td>
                      <td style={{ ...styles.td, color: row._status === "no_product" ? colors.danger : "inherit", fontWeight: row._status === "no_product" ? 700 : 400 }}>{row._product_name}</td>
                      <td style={styles.td}>{row.license_number || "—"}</td>
                      <td style={styles.td}>{row.user_type || "Single User"}</td>
                      <td style={styles.td}>{row.quantity || 1}</td>
                      <td style={styles.td}>{row.subscription_expiry ? formatDateDMY(row.subscription_expiry) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 11, color: colors.textLight, marginTop: 8 }}>Records marked ❌ will be skipped. Ensure customer and product names match exactly.</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button style={styles.btn("ghost")} onClick={() => setShow(false)}>Cancel <span style={{ fontSize: 9, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
              <button style={styles.btn("primary")} onClick={handleImport} disabled={importing || importData.filter(r => r._status === "ready").length === 0}>
                {importing ? "Importing..." : `Import ${importData.filter(r => r._status === "ready").length} Licenses`} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NAV_ITEMS = [
  { group: "Overview", items: [{ key: "dashboard", label: "Dashboard", icon: "📊", shortcut: "d" }] },
  {
    group: "Operations",
    items: [
      { key: "call-tracker", label: "Call Tracker", icon: "📞", shortcut: "c" },
      { key: "leads", label: "Leads / Enquiries", icon: "🎯", shortcut: "l" },
      { key: "invoices", label: "Quotes", icon: "📄", shortcut: "i" },
      { key: "sale-orders", label: "Sale Orders", icon: "🛒", shortcut: "z" },
      { key: "amc", label: "AMC Contracts", icon: "🛡️", shortcut: "m" },
      { key: "requests", label: "Requests", icon: "💬", shortcut: "q" },
    ],
  },
  {
    group: "Master Data",
    items: [
      { key: "executives", label: "Executives", icon: "👤", shortcut: "e" },
      { key: "principals", label: "Principal Companies", icon: "🏢", shortcut: "p" },
      { key: "clients", label: "Customers", icon: "🤝", shortcut: "k" },
      { key: "products", label: "Products", icon: "📦", shortcut: "o" },
      { key: "locations", label: "Locations", icon: "📍", shortcut: "t" },
      { key: "call-types", label: "Call Types", icon: "📋", shortcut: "y" },
      { key: "problems", label: "Problems", icon: "🛠️", altShift: "P" },
      { key: "groups", label: "Customer Groups", icon: "🏢", altShift: "G" },
      { key: "sale-types", label: "Sale Types", icon: "🏷️", altShift: "T" },
      { key: "designations", label: "Designations", icon: "🏷️", shortcut: "g" },
      { key: "departments", label: "Departments", icon: "🏛️", shortcut: "j" },
      { key: "partners", label: "Partners", icon: "🤝", shortcut: "v" },
      { key: "lead-sources", label: "Lead Sources", icon: "📢", shortcut: "w" },
      { key: "lead-statuses", label: "Lead Statuses", icon: "📊", shortcut: "h" },
      { key: "lost-reasons", label: "Lost Reasons", icon: "❌", altShift: "L" },
      { key: "tdl-types", label: "TDL Types", icon: "🧩", altShift: "D" },
    ],
  },
  {
    group: "Reports",
    items: [{ key: "reports", label: "Reports & Summary", icon: "📈", shortcut: "r" }],
  },
  {
    group: "Settings",
    items: [
      { key: "ticket-config", label: "Number Series", icon: "🔢", altShift: "N" },
      { key: "company-profile", label: "Company Profile", icon: "⚙️", altShift: "C" },
      { key: "users", label: "User Management", icon: "👥", shortcut: "u" },
      { key: "change-password", label: "Change Password", icon: "🔑", altShift: "W" },
      { key: "audit-logs", label: "Audit Trail", icon: "📜", shortcut: "a" },
      { key: "backup", label: "Backup & Restore", icon: "💾", altShift: "B" },
      { key: "email-settings", label: "Email Settings", icon: "✉️", altShift: "E" },
      { key: "email-log", label: "Email Log", icon: "📨", altShift: "M" },
      { key: "voice-settings", label: "Voice Minutes", icon: "🎤", altShift: "V" },
    ],
  },
];

const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Delhi", "Jammu & Kashmir", "Ladakh",
  "Chandigarh", "Puducherry", "Lakshadweep", "Andaman & Nicobar Islands",
  "Dadra & Nagar Haveli and Daman & Diu",
];

// ============================================================================
// NOTIFICATION BELL — Follow-up Reminders
// ============================================================================
const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [snoozed, setSnoozed] = useState({});
  const [tick, setTick] = useState(0); // forces re-eval of due-times between fetches
  const [diag, setDiag] = useState({ calls: 0, leads: 0, matched: 0, error: "", at: "" });
  const notifiedRef = useRef(new Set()); // ids we've already popped a browser alert for
  const timersRef = useRef([]);          // scheduled per-reminder alert timers
  const lastBeepRef = useRef(0);         // throttle overlapping beeps

  // Local (IST) "today" as YYYY-MM-DD — never use toISOString() here, that's UTC
  const localTodayStr = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  };

  // Build the exact local due moment from a date + optional time.
  // Uses the app's toISODate() so DB timezone formatting is handled the same
  // way as the rest of the app. No time set => 00:00 (flashes all day).
  const buildDueAt = (dateStr, timeStr) => {
    const iso = toISODate(dateStr);
    if (!iso) return null;
    const [y, mo, d] = iso.split("-").map(Number);
    const ts = timeStr ? String(timeStr).slice(0, 5) : "00:00";
    const [hh, mm] = ts.split(":").map(Number);
    return new Date(y, (mo || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
  };

  const fetchReminders = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const [callsRes, leadsRes, amcRes] = await Promise.all([
        api("/call-logs?next_actions_due=true&limit=500"),
        api("/leads?limit=500"),
        api("/amc?expiring=true&limit=500").catch(() => ({ data: [] }))
      ]);
      const today = localTodayStr();
      const callsRaw = callsRes.data || [];
      const leadsRaw = leadsRes.data || [];
      const amcRaw = amcRes.data || [];

      // Call reminders — next_action_date is today or overdue
      const callReminders = callsRaw
        .filter(c => c.next_action_date && toISODate(c.next_action_date) <= today && c.call_status !== "Completed")
        .map(c => ({
          id: `call-${c.id}`, type: "call", recordId: c.id,
          title: c.client_name || "Unknown Customer",
          phone: c.customer_phone || "",
          task: c.regarding || "Follow-up call",
          dueDate: c.next_action_date,
          dueTime: c.next_action_time || "",
          dueAt: buildDueAt(c.next_action_date, c.next_action_time),
          overdue: toISODate(c.next_action_date) < today,
          executive: c.executive_name || "",
          ticketNumber: c.ticket_number
        }));

      // Lead reminders — follow_up_date is today or overdue
      const leadReminders = leadsRaw
        .filter(l => l.follow_up_date && toISODate(l.follow_up_date) <= today && !["Converted", "Lost"].includes(l.status))
        .map(l => ({
          id: `lead-${l.id}`, type: "lead", recordId: l.id,
          title: l.prospect_company || "Unknown Prospect",
          phone: l.phone || "",
          task: l.next_action || "Follow-up",
          dueDate: l.follow_up_date,
          dueTime: l.follow_up_time || "",
          dueAt: buildDueAt(l.follow_up_date, l.follow_up_time),
          overdue: toISODate(l.follow_up_date) < today,
          executive: l.executive_name || "",
          enquiryNumber: l.enquiry_number
        }));

      // AMC expiry — informational heads-up (always shown, never beeps)
      const amcReminders = amcRaw.map(a => ({
        id: `amc-${a.id}`, type: "amc", recordId: a.id,
        title: a.client_name || "AMC Customer",
        phone: "",
        task: `AMC expiring${a.product_name ? " (" + a.product_name + ")" : ""}`,
        dueDate: a.end_date,
        dueTime: "",
        dueAt: buildDueAt(a.end_date, ""),
        infoOnly: true,
        executive: a.managed_by_name || "",
        contractNumber: a.contract_number
      }));

      const all = [...callReminders, ...leadReminders, ...amcReminders];
      setReminders(all);
      setDiag({ calls: callsRaw.length, leads: leadsRaw.length, matched: all.length, error: "", at: new Date().toLocaleTimeString() });
    } catch (e) {
      console.error("Reminder fetch error:", e);
      setDiag(d => ({ ...d, error: e.message || "fetch failed", at: new Date().toLocaleTimeString() }));
    }
  };

  // Request browser notification permission + fetch on mount, refetch every 5 min,
  // and refetch immediately whenever a call/lead is saved.
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    fetchReminders();
    const interval = setInterval(fetchReminders, 5 * 60 * 1000);
    const onSave = () => fetchReminders();
    window.addEventListener("reminders-refresh", onSave);
    return () => { clearInterval(interval); window.removeEventListener("reminders-refresh", onSave); };
  }, []);

  // Clock tick every 30s so a reminder set for 2:30 PM surfaces at ~2:30,
  // not up to 5 minutes late — and clear any snoozes that have elapsed.
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
      const now = Date.now();
      setSnoozed(prev => {
        const next = { ...prev };
        let changed = false;
        Object.entries(next).forEach(([id, until]) => {
          if (until <= now) { delete next[id]; changed = true; }
        });
        return changed ? next : prev;
      });
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // A reminder is "due to flash" only once its due moment has passed.
  const isDueNow = (r) => r.dueAt && Date.now() >= r.dueAt.getTime();

  const handleSnooze = (id, minutes) => {
    setSnoozed(prev => ({ ...prev, [id]: Date.now() + minutes * 60 * 1000 }));
  };

  const handleDismiss = (id) => {
    setDismissed(prev => new Set([...prev, id]));
  };

  // Only items whose due moment has arrived, minus dismissed/snoozed.
  // Recomputes on each render; the 30s tick state forces those renders.
  const activeReminders = reminders.filter(r => (r.infoOnly || isDueNow(r)) && !dismissed.has(r.id) && !snoozed[r.id]);
  const count = activeReminders.length;

  // Short two-tone beep using the Web Audio API (works whenever the tab is
  // alive — active, background, or minimized — and needs no OS permission).
  const playBeep = () => {
    const now = Date.now();
    if (now - lastBeepRef.current < 1500) return; // don't stack overlapping beeps
    lastBeepRef.current = now;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      if (ctx.state === "suspended") ctx.resume();
      const beep = (start, freq) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + 0.4);
        o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + 0.42);
      };
      beep(0, 880); beep(0.45, 1047);
      setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1200);
    } catch (e) {}
  };

  // Beep + OS popup for one reminder, once per item per session.
  const fireAlert = (r) => {
    if (!r || notifiedRef.current.has(r.id)) return;
    notifiedRef.current.add(r.id);
    playBeep();
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("CLAS CRM — Follow-up Reminder", {
          body: `${r.title}${r.phone ? " (" + r.phone + ")" : ""} — ${r.task}`,
          icon: "/favicon.ico", tag: r.id, renotify: true
        });
      } catch (e) {}
    }
    setTick(t => t + 1); // refresh badge/list immediately
  };

  // Schedule a precise alarm for each reminder's exact FUTURE due moment.
  // Items already past their time (overdue, earlier today, or no time set) are
  // NOT alerted — they simply sit in the bell list. Only a reminder whose time
  // arrives while you're working will beep + pop up.
  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    const now = Date.now();
    reminders.forEach(r => {
      if (!r.dueAt || r.infoOnly || dismissed.has(r.id) || snoozed[r.id]) return;
      const delay = r.dueAt.getTime() - now;
      if (delay > 0 && delay < 24 * 60 * 60 * 1000) {
        timersRef.current.push(setTimeout(() => fireAlert(r), delay));
      }
      // delay <= 0  => already due/overdue: show in bell only, no flash
    });
    return () => { timersRef.current.forEach(clearTimeout); timersRef.current = []; };
  }, [reminders, dismissed, snoozed]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the user returns to the tab (or it wakes from sleep), resync so any
  // alarm that may have been suspended is recalculated and caught up.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") fetchReminders(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);



  const formatDueDate = (dateStr, timeStr) => {
    if (!dateStr) return "";
    const d = dateStr.slice(0, 10);
    const today = localTodayStr();
    const yparts = new Date(Date.now() - 86400000);
    const yesterday = `${yparts.getFullYear()}-${String(yparts.getMonth() + 1).padStart(2, "0")}-${String(yparts.getDate()).padStart(2, "0")}`;
    let label;
    if (d === today) label = "Today";
    else if (d === yesterday) label = "Yesterday";
    else { const p = d.split("-"); label = `${p[2]}/${p[1]}/${p[0]}`; }
    if (timeStr) {
      const [h, m] = timeStr.slice(0, 5).split(":");
      let hr = parseInt(h); const ap = hr >= 12 ? "PM" : "AM";
      if (hr === 0) hr = 12; else if (hr > 12) hr -= 12;
      label += ` ${hr}:${m} ${ap}`;
    }
    return label;
  };

  return (
    <div style={{ position: "fixed", top: isMobileNow() ? 62 : 12, right: isMobileNow() ? 10 : 20, zIndex: 9999 }}>
      <style>{`
        @keyframes clasBellShake {
          0%,100% { transform: rotate(0deg); }
          15% { transform: rotate(14deg); }
          30% { transform: rotate(-12deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-8deg); }
          75% { transform: rotate(5deg); }
        }
        @keyframes clasBadgePulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.6); }
          70% { box-shadow: 0 0 0 8px rgba(220,38,38,0); }
        }
      `}</style>
      <div style={{ position: "relative", cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <span style={{ fontSize: 24, display: "inline-block", transformOrigin: "50% 0", animation: count > 0 ? "clasBellShake 1s ease-in-out infinite" : "none" }}>🔔</span>
        {count > 0 && (
          <span style={{ position: "absolute", top: -6, right: -8, background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff", animation: "clasBadgePulse 1.2s ease-out infinite" }}>
            {count > 99 ? "99+" : count}
          </span>
        )}
      </div>

      {open && (
        <div style={{ position: "absolute", top: 36, right: 0, width: 380, maxHeight: 500, overflowY: "auto", background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", border: "1px solid #e2e8f0" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>🔔 Follow-up Reminders ({count})</span>
            <button style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#94a3b8" }} onClick={() => setOpen(false)}>✕</button>
          </div>

          <div style={{ padding: "6px 16px", borderBottom: "1px solid #f1f5f9", fontSize: 10, color: diag.error ? "#dc2626" : "#94a3b8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{diag.error ? `Error: ${diag.error}` : `Loaded ${diag.leads} leads, ${diag.calls} calls • ${diag.matched} due/overdue • ${diag.at}`}</span>
            <button style={{ fontSize: 10, color: "#1a56db", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }} onClick={() => fetchReminders()}>Refresh</button>
          </div>

          <div style={{ padding: "6px 16px", borderBottom: "1px solid #f1f5f9", fontSize: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: ("Notification" in window && Notification.permission === "granted") ? "#16a34a" : "#dc2626" }}>
              {!("Notification" in window) ? "Popups not supported by browser"
                : Notification.permission === "granted" ? "✓ Windows popups ON"
                : Notification.permission === "denied" ? "✗ Popups BLOCKED (allow in browser site settings)"
                : "Popups not enabled yet"}
            </span>
            <span>
              {("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") && (
                <button style={{ fontSize: 10, color: "#1a56db", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", marginRight: 10 }}
                  onClick={() => Notification.requestPermission().then(() => setTick(t => t + 1))}>Enable</button>
              )}
              <button style={{ fontSize: 10, color: "#1a56db", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                onClick={() => { playBeep(); if ("Notification" in window && Notification.permission === "granted") { try { new Notification("CLAS CRM — Test", { body: "Reminders are working ✓", icon: "/favicon.ico" }); } catch (e) {} } }}>Test alert</button>
            </span>
          </div>

          <div style={{ padding: "5px 16px", borderBottom: "1px solid #f1f5f9", fontSize: 9.5, color: "#94a3b8" }}>
            🔔 Reminders flash + beep while the CRM tab is open (keep this tab running to receive alerts).
          </div>

          {activeReminders.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              ✅ No pending follow-ups. All caught up!
            </div>
          ) : (
            activeReminders.map(r => (
              <div key={r.id} style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", background: r.type === "amc" ? "#f5f3ff" : r.overdue ? "#fef2f2" : "#fffbeb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
                    {r.type === "call" ? "📞" : r.type === "amc" ? "🛡️" : "💼"} {r.title}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: r.type === "amc" ? "#7c3aed" : r.overdue ? "#dc2626" : "#f59e0b", color: "#fff", flexShrink: 0 }}>
                    {r.type === "amc" ? "AMC" : r.overdue ? "OVERDUE" : "DUE"}
                  </span>
                </div>
                {r.phone && <div style={{ fontSize: 12, color: "#0ea5e9", marginBottom: 2 }}>📱 {r.phone}</div>}
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 2 }}>📝 {r.task}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>
                  {r.type === "call" ? `Ticket: ${r.ticketNumber}` : r.type === "amc" ? `Contract: ${r.contractNumber}` : `Enquiry: ${r.enquiryNumber}`} • {r.executive} • {r.type === "amc" ? "Expires" : "Due"}: {formatDueDate(r.dueDate, r.dueTime)}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <select style={{ fontSize: 11, padding: "3px 6px", borderRadius: 4, border: "1px solid #e2e8f0", color: "#475569", cursor: "pointer" }}
                    onChange={(e) => { if (e.target.value) { handleSnooze(r.id, parseInt(e.target.value)); e.target.value = ""; } }}>
                    <option value="">⏰ Snooze</option>
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="1440">Tomorrow</option>
                  </select>
                  <button style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #e2e8f0", background: "#f1f5f9", color: "#475569", cursor: "pointer" }}
                    onClick={() => handleDismiss(r.id)}>✓ Dismiss</button>
                </div>
              </div>
            ))
          )}

          {activeReminders.length > 0 && (
            <div style={{ padding: "10px 16px", borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
              <button style={{ fontSize: 11, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                onClick={() => { setDismissed(prev => new Set([...prev, ...activeReminders.map(r => r.id)])); }}>Dismiss All</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function App() {
  const [user, setUser] = useState(null);
  const [activeScreen, setActiveScreen] = useState("dashboard");
  const isMobile = useIsMobile();
  useResponsiveStyles();
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { if (!isMobile) setNavOpen(false); }, [isMobile]);

  // Is a form/overlay currently open? (used to show the mobile Close button)
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    if (!isMobile) { setModalOpen(false); return; }
    const check = () => {
      // Any fixed, high z-index, full-screen dimmed layer counts as an open
      // modal/overlay. Browsers normalise colours (rgba(0, 0, 0, .5)) so we
      // strip spaces before comparing.
      const found = Array.from(document.querySelectorAll("div")).some(d => {
        const st = d.style;
        if (st.position !== "fixed") return false;
        const z = parseInt(st.zIndex || "0", 10);
        if (!z || z < 900) return false;
        const bg = ((st.background || "") + (st.backgroundColor || "")).replace(/\s/g, "");
        return bg.includes("rgba(0,0,0");
      });
      setModalOpen(found);
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
    const poll = setInterval(check, 400); // safety net for style-only updates
    return () => { obs.disconnect(); clearInterval(poll); };
  }, [isMobile]);
  const [pendingCall, setPendingCall] = useState(null); // a call row to open in Call Tracker's edit form (from Dashboard drill)
  const [pendingLead, setPendingLead] = useState(null); // a lead row to open in Leads' edit form (from Dashboard drill)
  const [pendingReturn, setPendingReturn] = useState(null); // which screen to return to after editing a drilled record
  const [pendingClient, setPendingClient] = useState(null); // a client id to open in the Customers master (from a license row)
  const [masters, setMasters] = useState({});
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState("");

  const loadCompanyInfo = async () => {
    try {
      const r = await api("/company-profile");
      if (r.data) { setCompanyLogo(r.data.logo_base64); setCompanyName(r.data.company_name || ""); }
    } catch (e) {}
  };

  // Listen for logo updates from Company Profile screen
  useEffect(() => {
    const handler = () => loadCompanyInfo();
    window.addEventListener('logo-updated', handler);
    return () => window.removeEventListener('logo-updated', handler);
  }, []);

  const loadMasters = async () => {
    try {
      // allSettled: one failing master must never blank every dropdown
      const _safe = (r) => (r.status === "fulfilled" ? r.value : { data: [] });
      const [exec, prin, cli, loc, ct, prod, desig, dept, part, lsrc, lstat, probs, serials, groups, saleTypes, lostR, tdlR] = (await Promise.allSettled([
        api("/executives?active_only=true&limit=100000"),
        api("/principals?active_only=true&limit=100000"),
        api("/clients?active_only=true&limit=100000"),
        api("/locations?active_only=true&limit=100000"),
        api("/call-types?active_only=true&limit=100000"),
        api("/products?active_only=true&limit=100000"),
        api("/designations?active_only=true&limit=100000"),
        api("/departments?active_only=true&limit=100000"),
        api("/partners?active_only=true&limit=100000"),
        api("/lead-sources?active_only=true&limit=100000"),
        api("/lead-statuses?active_only=true&limit=100000"),
        api("/call-logs/problems?active_only=true&limit=100000"),
        api("/customer-products/serials"),
        api("/customer-products/groups?active_only=true"),
        api("/customer-products/sale-types?active_only=true"),
        api("/lost-reasons?active_only=true&limit=100000"),
        api("/tdl-types?active_only=true&limit=100000"),
      ])).map(_safe);
      setMasters({
        executives: exec.data, principals: prin.data, clients: cli.data,
        locations: loc.data, callTypes: ct.data, products: prod.data,
        designations: desig.data, departments: dept.data, partners: part.data,
        leadSources: lsrc.data, leadStatuses: lstat.data, problems: probs.data,
        clientSerials: Object.fromEntries((serials.data || []).map(r => [String(r.client_id), (r.serials || []).filter(Boolean)])),
        groups: groups.data || [],
        saleTypes: saleTypes.data || [],
        lostReasons: lostR.data || [],
        tdlTypes: tdlR.data || [],
      });
    } catch (e) {
      console.log("Masters loading deferred - API not connected yet");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      api("/auth/me").then((r) => { setUser(r.data); loadMasters(); loadCompanyInfo(); }).catch(() => localStorage.removeItem("token"));
    }
  }, []);

  // A master was created / changed somewhere (e.g. "Create Designation" inside
  // the Executive form) — reload the dropdown lists so it shows at once.
  useEffect(() => {
    const h = () => loadMasters();
    window.addEventListener("masters-refresh", h);
    return () => window.removeEventListener("masters-refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload masters every time user navigates to a screen that needs them
  useEffect(() => {
    if (user && ["call-tracker", "leads", "reports", "dashboard"].includes(activeScreen)) {
      loadMasters();
    }
  }, [activeScreen, user]);

  const handleLogin = (u) => { setUser(u); setActiveScreen("dashboard"); loadMasters(); loadCompanyInfo(); };

  // Heartbeat — lets an admin see who is actively using the CRM (useful before
  // restarting the server for an update). Silent and failure-tolerant.
  useEffect(() => {
    if (!user) return;
    const ping = () => { api("/call-logs/heartbeat", { method: "POST", body: {} }).catch(() => {}); };
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, [user]);
  const handleLogout = () => { setUser(null); localStorage.removeItem("token"); };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") { document.dispatchEvent(new Event("close-modal")); return; }
      if (!e.altKey) return;

      // Alt+Shift combinations — the plain Alt letters are nearly all taken
      if (e.shiftKey) {
        const shifted = {
          'p': 'problems', 'g': 'groups', 't': 'sale-types',
          'n': 'ticket-config', 'c': 'company-profile', 'b': 'backup',
          'w': 'change-password', 'l': 'lost-reasons', 'd': 'tdl-types',
          'e': 'email-settings', 'm': 'email-log', 'v': 'voice-settings',
        };
        const target = shifted[e.key.toLowerCase()];
        if (target) { e.preventDefault(); setActiveScreen(target); }
        if (e.key.toLowerCase() === 'o') { e.preventDefault(); handleLogout(); }   // sign out
        return;
      }

      const shortcuts = {
        'd': 'dashboard', 'c': 'call-tracker', 'l': 'leads', 'i': 'invoices',
        'r': 'reports', 'q': 'requests', 'e': 'executives', 'p': 'principals',
        'k': 'clients', 'o': 'products', 't': 'locations', 'y': 'call-types',
        'u': 'users', 'a': 'audit-logs', 'z': 'sale-orders',
        'g': 'designations', 'j': 'departments', 'v': 'partners', 'w': 'lead-sources', 'h': 'lead-statuses',
        'm': 'amc',
        'n': null, 's': null
      };
      const key = e.key.toLowerCase();
      if (key in shortcuts) {
        e.preventDefault();
        if (key === 'n') { document.dispatchEvent(new Event("shortcut-add-new")); }
        else if (key === 's') { document.dispatchEvent(new Event("shortcut-save")); }
        else { setActiveScreen(shortcuts[key]); }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (!user) return <LoginScreen onLogin={handleLogin} />;
  // New user / password reset by the admin: choose a password before anything else
  if (user.must_change_password) return <ForcePasswordChange user={user} onLogout={handleLogout}
    onDone={() => setUser({ ...user, must_change_password: false })} />;

  const stateOptions = indianStates.map((s) => ({ value: s, label: s }));

  const renderScreen = () => {
    switch (activeScreen) {
      case "dashboard": return <Dashboard masters={masters} setActiveScreen={setActiveScreen} setPendingCall={setPendingCall} setPendingLead={setPendingLead} setPendingReturn={setPendingReturn} user={user} />;
      case "call-tracker": return <CallTrackerScreen masters={masters} loadMasters={loadMasters} user={user} pendingCall={pendingCall} setPendingCall={setPendingCall} setActiveScreen={setActiveScreen} pendingReturn={pendingReturn} />;
      case "leads": return <LeadsScreen masters={masters} loadMasters={loadMasters} pendingLead={pendingLead} setPendingLead={setPendingLead} setActiveScreen={setActiveScreen} pendingReturn={pendingReturn} user={user} />;
      case "amc": return <AMCScreen masters={masters} loadMasters={loadMasters} companyLogo={companyLogo} companyName={companyName} />;
      case "reports": return <ReportsScreen masters={masters} companyLogo={companyLogo} companyName={companyName} setPendingCall={setPendingCall} setPendingLead={setPendingLead} setPendingReturn={setPendingReturn} setActiveScreen={setActiveScreen} setPendingClient={setPendingClient} />;
      case "executives":
        const desigOps = masters.designations?.map(d => ({ value: d.id.toString(), label: d.name })) || [];
        const deptOps = masters.departments?.map(d => ({ value: d.id.toString(), label: d.name })) || [];
        return <MasterScreen title="Executives" endpoint="executives"
          columns={[{ key: "employee_id", label: "Emp ID" }, { key: "name", label: "Name" }, { key: "designation", label: "Designation" }, { key: "department", label: "Department" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" }]}
          formFields={[
            { key: "name", label: "Full Name", required: true },
            { key: "employee_id", label: "Employee ID", placeholder: "Company internal ID" },
            { key: "designation_id", label: "Designation", type: "searchable_select", options: desigOps, createLabel: "Create Designation", createEndpoint: "designations", createFields: [{ key: "name", label: "Designation Name", required: true }] },
            { key: "department_id", label: "Department", type: "searchable_select", options: deptOps, createLabel: "Create Department", createEndpoint: "departments", createFields: [{ key: "name", label: "Department Name", required: true }] },
            { key: "email", label: "Email", type: "email" },
            { key: "phone", label: "Phone" },
            { key: "alternate_phone", label: "Alternate Phone" },
            { key: "date_of_joining", label: "Date of Joining", type: "date" },
            { key: "address", label: "Address", type: "textarea", fullWidth: true },
          ]} linkConfig={{ principals: masters.principals, clients: masters.clients }} />;
      case "principals":
        const industryOptions = ["IT/Software", "Manufacturing", "Retail", "Healthcare", "FMCG", "Pharma", "Automobile", "Banking/Finance", "Education", "Telecom", "Real Estate", "Logistics", "Agriculture", "Textiles", "E-commerce", "Others"].map(d => ({ value: d, label: d }));
        const compTypeOptions = ["Pvt Ltd", "Ltd (Public)", "LLP", "Partnership", "Proprietorship", "Trust", "Society", "Government", "Others"].map(d => ({ value: d, label: d }));
        const revenueOptions = ["Below 10 Lakhs", "10-50 Lakhs", "50 Lakhs - 1 Crore", "1-5 Crores", "5-25 Crores", "25-100 Crores", "100+ Crores"].map(d => ({ value: d, label: d }));
        const empCountOptions = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"].map(d => ({ value: d, label: d }));
        const payTermOptions = ["Advance", "Net 15", "Net 30", "Net 45", "Net 60", "Net 90", "On Delivery"].map(d => ({ value: d, label: d }));
        return <MasterScreen title="Principal Companies" endpoint="principals"
          columns={[{ key: "id", label: "#" }, { key: "company_name", label: "Company" }, { key: "industry", label: "Industry" }, { key: "city", label: "City" }, { key: "gstin", label: "GSTIN" }, { key: "contact_person", label: "Contact" }]}
          formFields={[
            { key: "company_name", label: "Company Name", required: true },
            { key: "industry", label: "Industry / Sector", type: "select", options: industryOptions },
            { key: "company_type", label: "Company Type", type: "select", options: compTypeOptions },
            { key: "address_line1", label: "Address Line 1" }, { key: "address_line2", label: "Address Line 2" },
            { key: "city", label: "City" }, { key: "state", label: "State", type: "select", options: stateOptions },
            { key: "pincode", label: "Pincode" }, { key: "phone", label: "Phone" },
            { key: "email", label: "Email", type: "email" }, { key: "website", label: "Website" },
            { key: "gst_registration", label: "GST Registration *", type: "select", options: [{ value: "Registered", label: "Registered" }, { value: "Unregistered", label: "Unregistered" }] },
            { key: "gstin", label: "GSTIN (required if Registered)" }, { key: "pan", label: "PAN" }, { key: "cin", label: "CIN" },
            { key: "contact_person", label: "Contact Person" }, { key: "contact_designation", label: "Contact Designation" },
            { key: "contact_phone", label: "Contact Phone" }, { key: "contact_email", label: "Contact Email" },
            { key: "annual_revenue", label: "Annual Revenue Range", type: "select", options: revenueOptions },
            { key: "employee_count", label: "No. of Employees", type: "number" },
            { key: "payment_terms", label: "Payment Terms", type: "select", options: payTermOptions },
            { key: "credit_limit", label: "Credit Limit (INR)", type: "number" },
            { key: "contract_start_date", label: "Contract Start Date", type: "date" },
            { key: "contract_end_date", label: "Contract End Date", type: "date" },
            { key: "notes", label: "Notes", type: "textarea", fullWidth: true },
          ]} />;
      case "clients":
        const cliIndustryOptions = ["IT/Software", "Manufacturing", "Retail", "Healthcare", "FMCG", "Pharma", "Automobile", "Banking/Finance", "Education", "Telecom", "Real Estate", "Logistics", "Agriculture", "Textiles", "E-commerce", "Others"].map(d => ({ value: d, label: d }));
        const cliCompTypeOptions = ["Pvt Ltd", "Ltd (Public)", "LLP", "Partnership", "Proprietorship", "Trust", "Society", "Government", "Others"].map(d => ({ value: d, label: d }));
        const cliRevenueOptions = ["Below 10 Lakhs", "10-50 Lakhs", "50 Lakhs - 1 Crore", "1-5 Crores", "5-25 Crores", "25-100 Crores", "100+ Crores"].map(d => ({ value: d, label: d }));
        const cliEmpCountOptions = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"].map(d => ({ value: d, label: d }));
        const cliPayTermOptions = ["Advance", "Net 15", "Net 30", "Net 45", "Net 60", "Net 90", "On Delivery"].map(d => ({ value: d, label: d }));
        const principalOptions = masters.principals?.map(p => ({ value: p.id.toString(), label: p.company_name })) || [];
        const execOptions = masters.executives?.map(e => ({ value: e.id.toString(), label: e.name })) || [];
        const prodOptions = masters.products?.map(p => ({ value: p.id.toString(), label: p.name })) || [];
        return <MasterScreen title="Customers" endpoint="clients"
          columns={[{ key: "id", label: "#" }, { key: "company_name", label: "Company" }, { key: "industry", label: "Industry" }, { key: "city", label: "City" }, { key: "contact_person", label: "Contact" }, { key: "phone", label: "Phone" }]}
          formFields={[
            { key: "company_name", label: "Company Name", required: true },
            { key: "group_id", label: "Group (sister concerns sharing a licence)", type: "searchable_select", options: masters.groups?.map(g => ({ value: g.id.toString(), label: g.name })) || [], createLabel: "Create Group", createEndpoint: "customer-products/groups" },
            { key: "new_product_id", label: "Product (for the serial below) *", type: "searchable_select", options: masters.products?.map(pr => ({ value: pr.id.toString(), label: pr.name })) || [], newOnly: true },
            { key: "new_license_number", label: "Product Serial / Licence Number *", newOnly: true },
            { key: "new_user_type", label: "User Type (Single / Multi)", type: "select", options: [{ value: "Single User", label: "Single User" }, { value: "Multi User", label: "Multi User" }], newOnly: true },
            { key: "new_subscription_expiry", label: "Subscription / Licence Expiry", type: "date", newOnly: true },
            { key: "parent_principal_id", label: "Principal Company", type: "searchable_select", options: principalOptions },
            { key: "relationship_manager_id", label: "Handled By (Executive)", type: "searchable_select", options: execOptions },
            { key: "contact_person", label: "Contact Person(s) — separate multiple with commas", type: "textarea" },
            { key: "contact_phone", label: "Contact Number(s) — separate multiple with commas", type: "textarea" },
            { key: "phone", label: "Mobile Number(s) — separate multiple with commas", type: "textarea" },
            { key: "email", label: "Email(s) — separate multiple with commas", type: "textarea" },
            { key: "contact_email", label: "Contact Email(s) — separate multiple with commas", type: "textarea" },
            { key: "contact_designation", label: "Contact Designation(s)", type: "textarea" },
            { key: "gst_registration", label: "GST Registration *", type: "select", options: [{ value: "Registered", label: "Registered" }, { value: "Unregistered", label: "Unregistered" }] },
            { key: "gstin", label: "GSTIN (required if Registered)" },
            { key: "industry", label: "Industry / Sector", type: "select", options: cliIndustryOptions },
            { key: "company_type", label: "Company Type", type: "select", options: cliCompTypeOptions },
            { key: "partner_id", label: "Partner / Billed Through", type: "searchable_select", options: masters.partners?.map(p => ({ value: p.id.toString(), label: p.name })) || [], createLabel: "Create Partner", createEndpoint: "partners", createFields: [{ key: "name", label: "Partner Name", required: true }, { key: "phone", label: "Phone" }, { key: "email", label: "Email", type: "email" }] },
            { key: "address_line1", label: "Address Line 1" }, { key: "address_line2", label: "Address Line 2" },
            { key: "city", label: "City" }, { key: "state", label: "State", type: "select", options: stateOptions },
            { key: "pincode", label: "Pincode" }, { key: "phone", label: "Mobile Number(s) — separate multiple with commas", type: "textarea" },
            { key: "email", label: "Email(s) — separate multiple with commas", type: "textarea" }, { key: "website", label: "Website" },
            { key: "gstin", label: "GSTIN" }, { key: "pan", label: "PAN" }, { key: "cin", label: "CIN" },
            { key: "contact_person", label: "Contact Person(s) — separate multiple with commas", type: "textarea" }, { key: "contact_designation", label: "Contact Designation(s)", type: "textarea" },
            { key: "contact_phone", label: "Contact Number(s) — separate multiple with commas", type: "textarea" }, { key: "contact_email", label: "Contact Email(s) — separate multiple with commas", type: "textarea" },
            { key: "annual_revenue", label: "Annual Revenue Range", type: "select", options: cliRevenueOptions },
            { key: "employee_count", label: "No. of Employees", type: "number" },
            { key: "payment_terms", label: "Payment Terms", type: "select", options: cliPayTermOptions },
            { key: "credit_limit", label: "Credit Limit (INR)", type: "number" },
            { key: "notes", label: "Notes", type: "textarea", fullWidth: true },
          ]}
          renderExtra={(editItem) => <CustomerProductsSection editItem={editItem} masters={masters} loadMasters={loadMasters} />}
          extraButtons={<CustomerLicenseImport masters={masters} onDone={loadMasters} />}
          pendingOpenId={pendingClient} setPendingOpen={setPendingClient} pendingReturn={pendingReturn} setActiveScreen={setActiveScreen} />;
      case "products":
        const catOptions = ["Software License", "Hardware", "Service", "AMC/Support", "Customization", "Training", "Subscription", "Consulting", "Others"].map(d => ({ value: d, label: d }));
        const prodTypeOptions = ["One-time License", "Subscription (Monthly)", "Subscription (Annual)", "Recurring", "Per-user", "Per-transaction", "Others"].map(d => ({ value: d, label: d }));
        return <MasterScreen title="Products" endpoint="products"
          columns={[{ key: "code", label: "ID" }, { key: "name", label: "Product" }, { key: "category", label: "Category" }, { key: "hsn_sac_code", label: "HSN/SAC" }, { key: "default_gst_rate", label: "GST %", render: (v) => `${v}%` }, { key: "unit_price", label: "Unit Price", render: (v) => formatCurrencyINR(v) }]}
          formFields={[
            { key: "name", label: "Product Name", required: true },
            { key: "category", label: "Category", type: "select", options: catOptions },
            { key: "product_type", label: "Product Type", type: "select", options: prodTypeOptions },
            { key: "description", label: "Description", type: "textarea", fullWidth: true },
            { key: "hsn_sac_code", label: "HSN/SAC Code" },
            { key: "default_gst_rate", label: "Default GST Rate (%)", type: "number" },
            { key: "unit_price", label: "Unit Price (₹)", type: "number" },
            { key: "validity_months", label: "Validity (Months)", type: "number" },
            { key: "vendor", label: "Vendor / Manufacturer" },
          ]} />;
      case "locations":
        const regionOptions = ["North", "South", "East", "West", "Central", "North-East"].map(d => ({ value: d, label: d }));
        return <MasterScreen title="Locations" endpoint="locations"
          columns={[{ key: "code", label: "ID" }, { key: "name", label: "Location" }, { key: "state", label: "State" }, { key: "region", label: "Region" }]}
          formFields={[
            { key: "name", label: "Location Name", required: true },
            { key: "state", label: "State", type: "select", options: stateOptions },
            { key: "region", label: "Region", type: "select", options: regionOptions },
            { key: "pincode", label: "Pincode" },
            { key: "country", label: "Country" },
          ]} />;
      case "call-types":
        return <MasterScreen title="Call Types" endpoint="call-types"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Call Type Name" }]}
          formFields={[{ key: "name", label: "Call Type Name", required: true }]} />;
      case "sale-types":
        return <MasterScreen title="Sale Types" endpoint="customer-products/sale-types"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Sale Type" }, { key: "needs_serial", label: "Creates a licence?" }]}
          formFields={[
            { key: "name", label: "Sale Type", required: true },
            { key: "needs_serial", label: "Creates a new licence (asks for product + serial)", type: "select", options: [{ value: "true", label: "Yes" }, { value: "false", label: "No" }] },
            { key: "description", label: "Description", type: "textarea", fullWidth: true },
          ]} />;
      case "groups":
        return <MasterScreen title="Customer Groups" endpoint="customer-products/groups"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Group Name" }, { key: "description", label: "Description" }]}
          formFields={[
            { key: "name", label: "Group Name", required: true },
            { key: "description", label: "Description", type: "textarea", fullWidth: true },
          ]} />;
      case "problems":
        return <MasterScreen title="Problems" endpoint="call-logs/problems"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Problem" }, { key: "category", label: "Category" }]}
          formFields={[
            { key: "name", label: "Problem (short description)", required: true },
            { key: "category", label: "Category" },
            { key: "description", label: "Notes", type: "textarea", fullWidth: true },
          ]} />;
      case "designations":
        return <MasterScreen title="Designations" endpoint="designations"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Designation" }]}
          formFields={[{ key: "name", label: "Designation Name", required: true }]} />;
      case "departments":
        return <MasterScreen title="Departments" endpoint="departments"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Department" }]}
          formFields={[{ key: "name", label: "Department Name", required: true }]} />;
      case "partners":
        return <MasterScreen title="Partners / Billed Through" endpoint="partners"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Partner Name" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }]}
          formFields={[
            { key: "name", label: "Partner Name", required: true },
            { key: "phone", label: "Phone" },
            { key: "email", label: "Email", type: "email" },
            { key: "notes", label: "Notes", type: "textarea", fullWidth: true },
          ]} />;
      case "lead-sources":
        return <MasterScreen title="Lead Sources" endpoint="lead-sources"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Source Name" }]}
          formFields={[{ key: "name", label: "Source Name", required: true }]} />;
      case "lead-statuses":
        return <MasterScreen title="Lead Statuses" endpoint="lead-statuses"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Status Name" }]}
          formFields={[{ key: "name", label: "Status Name", required: true }]} />;
      case "email-settings": return <EmailSettingsScreen />;
      case "email-log": return <EmailLogScreen />;
      case "voice-settings": return <VoiceSettingsScreen />;
      case "tdl-types":
        return <MasterScreen title="TDL Types" endpoint="tdl-types"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "TDL Type" }]}
          formFields={[{ key: "name", label: "TDL Type", required: true }]} />;
      case "lost-reasons":
        return <MasterScreen title="Lost Reasons" endpoint="lost-reasons"
          columns={[{ key: "id", label: "#" }, { key: "name", label: "Reason" }]}
          formFields={[{ key: "name", label: "Reason", required: true }]} />;
      case "ticket-config":
        return <TicketConfigScreen />;
      case "company-profile":
        return <CompanyProfileScreen />;
      case "users":
        return <UserManagementScreen masters={masters} />;
      case "audit-logs":
        return <AuditLogsScreen />;
      case "change-password":
        return <ChangePasswordScreen />;
      case "backup":
        return <BackupScreen />;
      case "requests":
        return <RequestsScreen />;
      case "sale-orders":
        return <SaleOrdersScreen masters={masters} />;
      default:
        return <div style={{ padding: 40, textAlign: "center", color: colors.textLight }}>Screen under construction</div>;
    }
  };

  return (
    <AuthContext.Provider value={{ user }}>
      <div style={styles.page}>
        {/* Mobile: floating Close button — does exactly what Escape does on desktop,
            including the unsaved-changes prompt. Only shown while a form/overlay is open. */}
        {isMobile && modalOpen && (
          <button onClick={() => document.dispatchEvent(new Event("close-modal"))} aria-label="Close"
            style={{ position: "fixed", top: 10, right: 10, zIndex: 100000, background: "#dc2626", color: "#fff",
              border: "none", borderRadius: 22, padding: "9px 16px", fontSize: 14, fontWeight: 700,
              boxShadow: "0 3px 12px rgba(0,0,0,0.35)", cursor: "pointer" }}>✕ Close</button>
        )}

        {/* Mobile top bar with hamburger */}
        {isMobile && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 52, background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "0 12px", zIndex: 120, boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
            <button onClick={() => setNavOpen(true)} aria-label="Menu"
              style={{ background: "transparent", border: "1px solid #334155", color: "#fff", borderRadius: 8, width: 38, height: 34, fontSize: 18, lineHeight: 1, cursor: "pointer" }}>☰</button>
            {companyLogo
              ? <img src={companyLogo} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: "contain", background: "#fff" }} />
              : <div style={{ width: 26, height: 26, borderRadius: 6, background: colors.primary, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>CT</div>}
            <div style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {NAV_ITEMS.flatMap(g => g.items).find(i => i.key === activeScreen)?.label || companyName || "CLAS CRM"}
            </div>
          </div>
        )}

        {/* Backdrop behind the slide-in menu */}
        {isMobile && navOpen && (
          <div onClick={() => setNavOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 110 }} />
        )}

        {/* Sidebar */}
        <div style={{ ...styles.sidebar, ...(isMobile ? { width: 260, zIndex: 130, transform: navOpen ? "translateX(0)" : "translateX(-100%)" } : {}) }}>
          <div style={styles.sidebarHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {companyLogo ? (
                <img src={companyLogo} alt="Logo" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain", background: "#fff" }} />
              ) : (
                <div style={{ width: 32, height: 32, borderRadius: 8, background: colors.primary, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800 }}>CT</div>
              )}
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{companyName || "Call Tracker"}</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>& CRM Suite</div>
              </div>
            </div>
          </div>
          {NAV_ITEMS.map((group) => (
            <div key={group.group} style={styles.navGroup}>
              <div style={styles.navLabel}>{group.group}</div>
              {group.items.map((item) => (
                <div key={item.key} style={styles.navItem(activeScreen === item.key)} onClick={() => { setActiveScreen(item.key); setNavOpen(false); }}>
                  <span>{item.icon}</span>
                  <span>{item.shortcut ? <ShortcutLabel text={item.label} shortcutChar={item.shortcut} /> : item.label}</span>
                  {item.shortcut && <span style={{ marginLeft: "auto", fontSize: 9, color: "#64748b", background: "#1e293b", padding: "1px 5px", borderRadius: 3 }}>Alt+{item.shortcut.toUpperCase()}</span>}
                  {item.altShift && <span style={{ marginLeft: "auto", fontSize: 9, color: "#64748b", background: "#1e293b", padding: "1px 5px", borderRadius: 3 }}>Alt+⇧{item.altShift}</span>}
                </div>
              ))}
            </div>
          ))}
          <div style={{ padding: 16, borderTop: "1px solid #1e293b", marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>{user.full_name}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginBottom: 12 }}>{user.role?.toUpperCase()}</div>
            <button style={{ ...styles.btn("ghost", "sm"), width: "100%", justifyContent: "center", background: "#1e293b", color: "#94a3b8" }} onClick={handleLogout}>Sign Out <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+⇧O)</span></button>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.main}>
          <NotificationBell />
          {/* key: every screen starts fresh. Without it, going from one master list to
              another (e.g. Lead Statuses → Lost Reasons) reused the same list, and if the
              new one failed to load, the old rows stayed on screen. */}
          <Fragment key={activeScreen}>{renderScreen()}</Fragment>
        </div>
      </div>
    </AuthContext.Provider>
  );
}

// ============================================================================
// USER MANAGEMENT SCREEN
// ============================================================================
function UserManagementScreen({ masters }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ role: "executive" });
  const [permissions, setPermissions] = useState({});
  const [resetPwd, setResetPwd] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [toast, setToast] = useState(null);

  const moduleLabels = { call_logs: "Call Tracker", leads: "Leads / Enquiries", masters: "Master Data", invoices: "Quotes" };
  const actionLabels = { read: "View", write: "Create", update: "Edit", delete: "Delete" };
  const defaultPerms = { call_logs: { read: true, write: true, update: false, delete: false }, leads: { read: true, write: true, update: false, delete: false }, masters: { read: true, write: false, update: false, delete: false }, invoices: { read: true, write: false, update: false, delete: false } };

  const fetchUsers = async () => { try { const r = await api("/users"); setUsers(r.data); } catch (e) { setToast({ message: e.message, type: "error" }); } };
  useEffect(() => { fetchUsers(); }, []);

  const handleSave = async () => {
    try {
      if (editItem) { await api(`/users/${editItem.id}`, { method: "PUT", body: form }); setToast({ message: "User updated!", type: "success" }); }
      else { if (!form.password) { setToast({ message: "Password is required.", type: "error" }); return; } await api("/auth/register", { method: "POST", body: form }); setToast({ message: "User created!", type: "success" }); }
      setShowForm(false); setEditItem(null); setForm({ role: "executive" }); fetchUsers();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const openPermissions = (u) => { setEditItem(u); setPermissions(u.permissions || defaultPerms); setShowPermissions(true); };
  const togglePerm = (mod, act) => { setPermissions(p => ({ ...p, [mod]: { ...p[mod], [act]: !p[mod]?.[act] } })); };
  const savePermissions = async () => { try { await api(`/users/${editItem.id}`, { method: "PUT", body: { ...editItem, permissions } }); setToast({ message: "Permissions saved!", type: "success" }); setShowPermissions(false); fetchUsers(); } catch (e) { setToast({ message: e.message, type: "error" }); } };
  const openResetPwd = (u) => { setEditItem(u); setResetPwd(""); setShowResetPwd(true); };
  const handleResetPwd = async () => { if (!resetPwd || resetPwd.length < 6) { setToast({ message: "Min 6 characters.", type: "error" }); return; } try { await api(`/users/${editItem.id}/reset-password`, { method: "PUT", body: { new_password: resetPwd } }); setToast({ message: `Password reset for ${editItem.full_name}!`, type: "success" }); setShowResetPwd(false); } catch (e) { setToast({ message: e.message, type: "error" }); } };

  const chk = (on) => ({ width: 22, height: 22, borderRadius: 4, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: on ? colors.primary : "#fff", border: `2px solid ${on ? colors.primary : colors.border}`, color: "#fff", fontSize: 13, fontWeight: 800 });

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}><h1 style={styles.h1}>User Management</h1>
        <button style={styles.btn("primary")} onClick={() => { setEditItem(null); setForm({ role: "executive" }); setShowForm(true); }}>+ Add User</button>
      </div>
      <div style={styles.card}>
        <div style={{ marginBottom: 12 }}>
          <input style={{ ...styles.input, minWidth: 280, maxWidth: 360 }} placeholder="🔍 Search by name, username, email, role, phone..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape" && userSearch) { e.stopPropagation(); setUserSearch(""); } }} />
          {userSearch && <span style={{ marginLeft: 10, fontSize: 12, color: colors.textLight }}>{users.filter(u => [u.full_name, u.username, u.email, u.role, u.phone].some(v => (v || "").toString().toLowerCase().includes(userSearch.toLowerCase()))).length} match(es)</span>}
        </div>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", maxWidth: "100%" }}>
        <table style={{ ...styles.table, minWidth: 900 }}><thead><tr>
          <th style={styles.th}>Name</th><th style={styles.th}>Username</th><th style={styles.th}>Email</th>
          <th style={styles.th}>Role</th><th style={styles.th}>Status</th><th style={styles.th}>Actions</th>
        </tr></thead><tbody>
          {users.filter(u => !userSearch || [u.full_name, u.username, u.email, u.role, u.phone].some(v => (v || "").toString().toLowerCase().includes(userSearch.toLowerCase()))).map(u => (
            <tr key={u.id} style={{ opacity: u.is_active === false ? 0.5 : 1 }}>
              <td style={styles.td}>{u.full_name}</td><td style={styles.td}>{u.username}</td><td style={styles.td}>{u.email}</td>
              <td style={styles.td}>
                <span style={{
                  background: (u.role === "admin" ? colors.danger : u.role === "manager" ? "#d97706" : colors.primary) + "22",
                  color: u.role === "admin" ? colors.danger : u.role === "manager" ? "#b45309" : colors.primary,
                  padding: "2px 10px", borderRadius: 10, fontSize: 11.5, fontWeight: 700, textTransform: "capitalize", whiteSpace: "nowrap"
                }}>{u.role}</span>
              </td>
              <td style={styles.td}><StatusBadge status={u.is_active !== false ? "Active" : "Inactive"} /></td>
              <td style={styles.td}><div style={styles.flexRow}>
                <button style={styles.btn("primary", "sm")} onClick={() => { setEditItem(u); setForm({ full_name: u.full_name, username: u.username, email: u.email, phone: u.phone, role: u.role, is_active: u.is_active, executive_id: u.executive_id?.toString() || "" }); setShowForm(true); }}>Edit</button>
                {u.role !== 'admin' && <button style={styles.btn(u.is_active !== false ? "warning" : "success", "sm")} onClick={async () => {
                  const action = u.is_active !== false ? "Deactivate" : "Activate";
                  if (!window.confirm(`${action} user "${u.full_name}"?${u.is_active !== false ? " This user will not be able to login." : ""}`)) return;
                  try {
                    await api(`/users/${u.id}`, { method: "PUT", body: { ...u, is_active: u.is_active === false } });
                    setToast({ message: `User "${u.full_name}" ${action.toLowerCase()}d!`, type: "success" }); fetchUsers();
                  } catch(e) { setToast({ message: e.message, type: "error" }); }
                }}>{u.is_active !== false ? "Deactivate" : "Activate"}</button>}
                <button style={styles.btn("ghost", "sm")} onClick={() => openPermissions(u)}>Permissions</button>
                <button style={styles.btn("ghost", "sm")} onClick={() => openResetPwd(u)}>Reset Pwd</button>
                {u.role !== 'admin' && <button style={styles.btn("danger", "sm")} onClick={async () => {
                  if (!window.confirm(`⚠️ Delete user "${u.full_name}"?\n\nIf this user has linked data, deletion will be blocked.`)) return;
                  try {
                    await api(`/users/${u.id}`, { method: "DELETE" });
                    setToast({ message: `User "${u.full_name}" deleted!`, type: "success" }); fetchUsers();
                  } catch(e) {
                    setToast({ message: `Cannot delete "${u.full_name}" — user has linked data. Deactivate instead.`, type: "error" });
                  }
                }}>Delete</button>}
              </div></td>
            </tr>
          ))}
          {users.length === 0 && <tr><td colSpan={6} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No users found.</td></tr>}
        </tbody></table>
        </div>
      </div>

      {showForm && (<div style={styles.modal} onClick={() => setShowForm(false)}>
        <div style={{ ...styles.modalContent, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>{editItem ? "Edit User" : "Add User"}</h2>
          <div style={styles.grid(2)}>
            <div><label style={styles.label}>Full Name *</label><FastText style={styles.input} value={form.full_name || ""} onCommit={(t) => setForm(f => ({ ...f, full_name: t }))} /></div>
            <div><label style={styles.label}>Username *</label><FastText style={styles.input} value={form.username || ""} onCommit={(t) => setForm(f => ({ ...f, username: t }))} disabled={!!editItem} /></div>
            <div><label style={styles.label}>Email *</label><FastText style={styles.input} type="email" value={form.email || ""} onCommit={(t) => setForm(f => ({ ...f, email: t }))} /></div>
            <div><label style={styles.label}>Phone</label><FastText style={styles.input} value={form.phone || ""} onCommit={(t) => setForm(f => ({ ...f, phone: t }))} /></div>
            {!editItem && <div><label style={styles.label}>Password *</label><FastText style={styles.input} type="password" value={form.password || ""} onCommit={(t) => setForm(f => ({ ...f, password: t }))} placeholder="Min 6 characters" /></div>}
            <div><label style={styles.label}>Role *</label><select style={styles.select} value={form.role || "executive"} onChange={e => setForm({ ...form, role: e.target.value })}><option value="admin">Admin</option><option value="manager">Manager</option><option value="executive">Executive</option></select></div>
            <div><label style={styles.label}>Linked Executive</label>
              <select style={styles.select} value={form.executive_id || ""} onChange={e => setForm({ ...form, executive_id: e.target.value })}>
                <option value="">— None —</option>
                {masters.executives?.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
              </select>
            </div>
            {editItem && <div><label style={styles.label}>Status</label><select style={styles.select} value={form.is_active !== false ? "true" : "false"} onChange={e => setForm({ ...form, is_active: e.target.value === "true" })}><option value="true">Active</option><option value="false">Inactive</option></select></div>}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
            <button style={styles.btn("ghost")} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={styles.btn("primary")} onClick={handleSave}>{editItem ? "Update" : "Create User"}</button>
          </div>
        </div>
      </div>)}

      {showPermissions && (<div style={styles.modal} onClick={() => setShowPermissions(false)}>
        <div style={{ ...styles.modalContent, maxWidth: 650 }} onClick={e => e.stopPropagation()}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>Permissions — {editItem?.full_name}</h2>
          <p style={{ fontSize: 12, color: colors.textLight, margin: "0 0 16px" }}>Click checkboxes to enable/disable access for each module.</p>
          <table style={{ ...styles.table, fontSize: 13 }}><thead><tr>
            <th style={{ ...styles.th, width: 180 }}>Module</th>
            {Object.keys(actionLabels).map(a => <th key={a} style={{ ...styles.th, textAlign: "center", width: 90 }}>{actionLabels[a]}</th>)}
          </tr></thead><tbody>
            {Object.keys(moduleLabels).map(mod => (
              <tr key={mod}><td style={{ ...styles.td, fontWeight: 600 }}>{moduleLabels[mod]}</td>
                {Object.keys(actionLabels).map(act => (
                  <td key={act} style={{ ...styles.td, textAlign: "center" }}>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <div style={chk(permissions[mod]?.[act])} onClick={() => togglePerm(mod, act)}>{permissions[mod]?.[act] ? "✓" : ""}</div>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody></table>
          <div style={{ marginTop: 16, padding: "12px 16px", background: "#f8fafc", borderRadius: 8, border: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Quick Presets:</div>
            <div style={styles.flexRow}>
              <button style={styles.btn("primary", "sm")} onClick={() => setPermissions({ call_logs: { read: true, write: true, update: true, delete: true }, leads: { read: true, write: true, update: true, delete: true }, masters: { read: true, write: true, update: true, delete: true }, invoices: { read: true, write: true, update: true, delete: true } })}>Full Access</button>
              <button style={styles.btn("success", "sm")} onClick={() => setPermissions({ call_logs: { read: true, write: true, update: true, delete: false }, leads: { read: true, write: true, update: true, delete: false }, masters: { read: true, write: true, update: true, delete: false }, invoices: { read: true, write: true, update: true, delete: false } })}>Manager</button>
              <button style={styles.btn("warning", "sm")} onClick={() => setPermissions({ call_logs: { read: true, write: true, update: false, delete: false }, leads: { read: true, write: true, update: false, delete: false }, masters: { read: true, write: false, update: false, delete: false }, invoices: { read: true, write: false, update: false, delete: false } })}>Executive</button>
              <button style={styles.btn("ghost", "sm")} onClick={() => setPermissions({ call_logs: { read: true, write: false, update: false, delete: false }, leads: { read: true, write: false, update: false, delete: false }, masters: { read: true, write: false, update: false, delete: false }, invoices: { read: true, write: false, update: false, delete: false } })}>Read Only</button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
            <button style={styles.btn("ghost")} onClick={() => setShowPermissions(false)}>Cancel</button>
            <button style={styles.btn("primary")} onClick={savePermissions}>Save Permissions</button>
          </div>
        </div>
      </div>)}

      {showResetPwd && (<div style={styles.modal} onClick={() => setShowResetPwd(false)}>
        <div style={{ ...styles.modalContent, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>Reset Password — {editItem?.full_name}</h2>
          <p style={{ fontSize: 12, color: colors.textLight, margin: "0 0 16px" }}>Set a new temporary password. User can change it after logging in.</p>
          <div><label style={styles.label}>New Password *</label><input style={styles.input} type="password" value={resetPwd} onChange={e => setResetPwd(e.target.value)} placeholder="Min 6 characters" /></div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
            <button style={styles.btn("ghost")} onClick={() => setShowResetPwd(false)}>Cancel</button>
            <button style={styles.btn("danger")} onClick={handleResetPwd}>Reset Password</button>
          </div>
        </div>
      </div>)}
    </div>
  );
}

// ============================================================================
// TICKET CONFIG SCREEN
// ============================================================================
function TicketConfigScreen() {
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState({ config_type: "ticket" });
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchConfigs = async () => {
    try { const r = await api("/ticket-configurations"); setConfigs(r.data); } catch (e) { setToast({ message: e.message, type: "error" }); }
  };
  useEffect(() => { fetchConfigs(); }, []);

  const handleSave = async () => {
    try {
      await api("/ticket-configurations", { method: "POST", body: form });
      setToast({ message: "Number series created!", type: "success" });
      setShowForm(false); setForm({ config_type: "ticket" }); fetchConfigs();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // Alt+S — save the number series
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.shiftKey && e.key.toLowerCase() === "s") { e.preventDefault(); handleSave(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });


  const typeLabels = { ticket: "Ticket", enquiry: "Enquiry", invoice: "Invoice" };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Number Series Configuration</h1>
        <button style={styles.btn("primary")} onClick={() => setShowForm(true)}>+ Add New Series</button>
      </div>
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Type</th><th style={styles.th}>Starting #</th><th style={styles.th}>Current #</th>
              <th style={styles.th}>Effective From</th><th style={styles.th}>Effective To</th><th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {configs.map((c) => (
              <tr key={c.id}>
                <td style={{ ...styles.td, fontWeight: 600 }}>{typeLabels[c.config_type]}</td>
                <td style={styles.td}>{c.starting_number}</td>
                <td style={styles.td}>{c.current_number}</td>
                <td style={styles.td}>{formatDateDMY(c.effective_from)}</td>
                <td style={styles.td}>{c.effective_to ? formatDateDMY(c.effective_to) : "Open"}</td>
                <td style={styles.td}><StatusBadge status={c.is_active ? "Active" : "Inactive"} /></td>
              </tr>
            ))}
            {configs.length === 0 && <tr><td colSpan={6} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No number series configured. Add one to start.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={styles.modal} onClick={() => setShowForm(false)}>
          <div style={{ ...styles.modalContent, maxWidth: 450 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>New Number Series</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><label style={styles.label}>Series Type *</label>
                <select style={styles.select} value={form.config_type} onChange={(e) => setForm({ ...form, config_type: e.target.value })}>
                  <option value="ticket">Ticket Number</option>
                  <option value="enquiry">Enquiry Number</option>
                  <option value="invoice">Invoice Number</option>
                </select>
              </div>
              <div><label style={styles.label}>Starting Number *</label>
                <input style={styles.input} type="number" value={form.starting_number || ""} onChange={(e) => setForm({ ...form, starting_number: parseInt(e.target.value) })} placeholder="e.g. 1001" />
              </div>
              <div><label style={styles.label}>Effective From Date *</label>
                <DateInput value={form.effective_from} onChange={(v) => setForm({ ...form, effective_from: v })} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
              <button style={styles.btn("ghost")} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={styles.btn("primary")} onClick={handleSave}>Create Series</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPANY PROFILE SCREEN
// ============================================================================
function CompanyProfileScreen() {
  const [form, setForm] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api("/company-profile").then((r) => { if (r.data) setForm(r.data); }).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      await api("/company-profile", { method: "POST", body: form });
      setToast({ message: "Company profile saved!", type: "success" });
      // Update logo in sidebar by dispatching custom event
      window.dispatchEvent(new Event('logo-updated'));
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  // Alt+S — save the company profile
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.shiftKey && e.key.toLowerCase() === "s") { e.preventDefault(); handleSave(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });


  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.match('image.*')) { setToast({ message: "Please select an image file (PNG, JPG).", type: "error" }); return; }
    if (file.size > 2 * 1024 * 1024) { setToast({ message: "Image must be less than 2MB.", type: "error" }); return; }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setForm({ ...form, logo_base64: evt.target.result });
      setToast({ message: "Logo loaded! Click 'Save Changes' to apply.", type: "success" });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setForm({ ...form, logo_base64: null });
    setToast({ message: "Logo removed. Click 'Save Changes' to apply.", type: "success" });
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Company Profile</h1>
        <button style={styles.btn("primary")} onClick={handleSave}>Save Changes</button>
      </div>

      {/* Logo Section */}
      <div style={{ ...styles.card, display: "flex", alignItems: "center", gap: 24, marginBottom: 0 }}>
        <div style={{ width: 80, height: 80, borderRadius: 12, border: `2px dashed ${colors.border}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
          {form.logo_base64 ? (
            <img src={form.logo_base64} alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <span style={{ fontSize: 28, color: colors.textLight }}>🏢</span>
          )}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Company Logo</div>
          <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 8 }}>PNG or JPG, max 2MB. Displayed in sidebar and login screen.</div>
          <div style={styles.flexRow}>
            <label style={{ ...styles.btn("primary", "sm"), cursor: "pointer" }}>
              Upload Logo <input type="file" accept="image/png,image/jpeg,image/gif" onChange={handleLogoUpload} style={{ display: "none" }} />
            </label>
            {form.logo_base64 && <button style={styles.btn("danger", "sm")} onClick={removeLogo}>Remove Logo</button>}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 16 }}>Business Details</h3>
        <div style={styles.grid(3)}>
          <div><label style={styles.label}>Company Name *</label><FastText style={styles.input} value={form.company_name || ""} onCommit={(t) => setForm(f => ({ ...f, company_name: t }))} /></div>
          <div><label style={styles.label}>Phone</label><FastText style={styles.input} value={form.phone || ""} onCommit={(t) => setForm(f => ({ ...f, phone: t }))} /></div>
          <div><label style={styles.label}>Email</label><FastText style={styles.input} value={form.email || ""} onCommit={(t) => setForm(f => ({ ...f, email: t }))} /></div>
          <div><label style={styles.label}>Address Line 1</label><FastText style={styles.input} value={form.address_line1 || ""} onCommit={(t) => setForm(f => ({ ...f, address_line1: t }))} /></div>
          <div><label style={styles.label}>Address Line 2</label><FastText style={styles.input} value={form.address_line2 || ""} onCommit={(t) => setForm(f => ({ ...f, address_line2: t }))} /></div>
          <div><label style={styles.label}>City</label><FastText style={styles.input} value={form.city || ""} onCommit={(t) => setForm(f => ({ ...f, city: t }))} /></div>
          <div><label style={styles.label}>State</label><FastText style={styles.input} value={form.state || ""} onCommit={(t) => setForm(f => ({ ...f, state: t }))} /></div>
          <div><label style={styles.label}>Pincode</label><FastText style={styles.input} value={form.pincode || ""} onCommit={(t) => setForm(f => ({ ...f, pincode: t }))} /></div>
          <div><label style={styles.label}>Website</label><FastText style={styles.input} value={form.website || ""} onCommit={(t) => setForm(f => ({ ...f, website: t }))} /></div>
        </div>

        <h3 style={{ fontSize: 15, fontWeight: 700, marginTop: 28, marginBottom: 16 }}>Statutory Details</h3>
        <div style={styles.grid(3)}>
          <div><label style={styles.label}>GSTIN</label><FastText style={styles.input} value={form.gstin || ""} onCommit={(t) => setForm(f => ({ ...f, gstin: t }))} maxLength={15} placeholder="29ABCDE1234F1Z5" /></div>
          <div><label style={styles.label}>PAN</label><FastText style={styles.input} value={form.pan || ""} onCommit={(t) => setForm(f => ({ ...f, pan: t }))} maxLength={10} /></div>
          <div><label style={styles.label}>CIN</label><FastText style={styles.input} value={form.cin || ""} onCommit={(t) => setForm(f => ({ ...f, cin: t }))} /></div>
        </div>

        <h3 style={{ fontSize: 15, fontWeight: 700, marginTop: 28, marginBottom: 16 }}>Bank Details (for Invoices)</h3>
        <div style={styles.grid(3)}>
          <div><label style={styles.label}>Bank Name</label><FastText style={styles.input} value={form.bank_name || ""} onCommit={(t) => setForm(f => ({ ...f, bank_name: t }))} /></div>
          <div><label style={styles.label}>Account Number</label><FastText style={styles.input} value={form.bank_account_number || ""} onCommit={(t) => setForm(f => ({ ...f, bank_account_number: t }))} /></div>
          <div><label style={styles.label}>IFSC Code</label><FastText style={styles.input} value={form.bank_ifsc || ""} onCommit={(t) => setForm(f => ({ ...f, bank_ifsc: t }))} /></div>
          <div><label style={styles.label}>Branch</label><FastText style={styles.input} value={form.bank_branch || ""} onCommit={(t) => setForm(f => ({ ...f, bank_branch: t }))} /></div>
        </div>

        <h3 style={{ fontSize: 15, fontWeight: 700, marginTop: 28, marginBottom: 16 }}>Invoice Settings</h3>
        <div style={styles.grid(1)}>
          <div><label style={styles.label}>Invoice Terms & Conditions</label><FastText multiline style={{ ...styles.input, minHeight: 80 }} value={form.invoice_terms || ""} onCommit={(t) => setForm(f => ({ ...f, invoice_terms: t }))} /></div>
          <div><label style={styles.label}>Invoice Footer</label><FastText multiline style={{ ...styles.input, minHeight: 60 }} value={form.invoice_footer || ""} onCommit={(t) => setForm(f => ({ ...f, invoice_footer: t }))} /></div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SALE ORDERS SCREEN
// ============================================================================
function SaleOrdersScreen({ masters }) {
  const [qc, setQc] = useState(null);   // quick-create from a dropdown
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ status: "Pending", items: [{ quantity: 1, unit_price: 0, gst_rate: 18 }] });
  const [toast, setToast] = useState(null);

  const fetchData = useCallback(async (page = 1) => {
    try {
      const r = await api(`/sale-orders?page=${page}&limit=50`);
      setData(r.data); setPagination(r.pagination);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keyboard shortcuts for Sale Orders
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && showForm) { setShowForm(false); }
      if (e.altKey && e.key.toLowerCase() === "s" && showForm) { e.preventDefault(); handleSave(); }
    };
    document.addEventListener("close-modal", () => setShowForm(false));
    document.addEventListener("shortcut-add-new", () => { setEditItem(null); setForm({ status: "Pending", so_date: new Date().toISOString().slice(0, 10), items: [{ quantity: 1, unit_price: 0, gst_rate: 18 }] }); setShowForm(true); });
    window.addEventListener("keydown", handleKey);
    return () => { window.removeEventListener("keydown", handleKey); };
  }, [showForm]);

  const handleSave = async () => {
    try {
      if (editItem) {
        await api(`/sale-orders/${editItem.id}`, { method: "PUT", body: form });
      } else {
        await api("/sale-orders", { method: "POST", body: form });
      }
      setToast({ message: editItem ? "Updated!" : "Sale Order created!", type: "success" });
      setShowForm(false); setEditItem(null); setForm({ status: "Pending", items: [{ quantity: 1, unit_price: 0, gst_rate: 18 }] }); fetchData();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const addItem = () => setForm({ ...form, items: [...(form.items || []), { quantity: 1, unit_price: 0, gst_rate: 18 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, key, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: val };
    setForm({ ...form, items });
  };

  const calcTotal = () => {
    let sub = 0;
    (form.items || []).forEach(item => { sub += (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0); });
    return sub;
  };

  const soStatuses = ["Pending", "Confirmed", "In Progress", "Delivered", "Cancelled"];

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Sale Orders</h1>
        <button style={styles.btn("primary")} onClick={() => { setEditItem(null); setForm({ status: "Pending", so_date: new Date().toISOString().slice(0, 10), items: [{ quantity: 1, unit_price: 0, gst_rate: 18 }] }); setShowForm(true); }}>+ New Sale Order <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+N)</span></button>
      </div>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>SO #</th><th style={styles.th}>Date</th><th style={styles.th}>Customer</th>
            <th style={styles.th}>PO #</th><th style={styles.th}>Total</th><th style={styles.th}>Status</th><th style={styles.th}>Actions</th>
          </tr></thead>
          <tbody>
            {data.map(row => (
              <tr key={row.id}>
                <td style={{ ...styles.td, fontWeight: 700 }}>{row.so_number}</td>
                <td style={styles.td}>{formatDateDMY(row.so_date)}</td>
                <td style={styles.td}>{row.client_name}</td>
                <td style={styles.td}>{row.customer_po_number || "—"}</td>
                <td style={styles.td}>{formatCurrencyINR(row.grand_total)}</td>
                <td style={styles.td}><StatusBadge status={row.status} /></td>
                <td style={styles.td}>
                  <button style={styles.btn("primary", "sm")} onClick={async () => {
                    try {
                      const r = await api(`/sale-orders/${row.id}`);
                      setEditItem(r.data); setForm(r.data); setShowForm(true);
                    } catch (e) { setToast({ message: e.message, type: "error" }); }
                  }}>Edit</button>
                </td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={7} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No sale orders found.</td></tr>}
          </tbody>
        </table>
        <Pagination pagination={pagination} onPageChange={fetchData} />
      </div>

      {showForm && (
        <div style={styles.modal} onClick={() => setShowForm(false)}>
          <div style={{ ...styles.modalContent, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>{editItem ? `Edit SO #${editItem.so_number}` : "New Sale Order"}</h2>
            <div style={styles.grid(3)}>
              <div><label style={styles.label}>SO Date *</label><DateInput value={form.so_date} onChange={v => setForm({ ...form, so_date: v })} /></div>
              <div><label style={styles.label}>Customer *</label>
                <SearchableSelect options={masters.clients?.map(c => { const sn = masters.clientSerials?.[c.id.toString()] || []; return { value: c.id.toString(), label: sn.length ? `${c.company_name} — ${sn.join(", ")}` : c.company_name }; }) || []}
                  value={form.client_id || ""} onChange={v => setForm({ ...form, client_id: v })} placeholder="Search customer..."
                  onCreate={() => setQc({ kind: "customer", apply: id => setForm(f => ({ ...f, client_id: id })) })} createLabel="Create Customer" />
                <QuickCreateModal qc={qc} masters={masters} onClose={() => setQc(null)} />
              </div>
              <div><label style={styles.label}>Status</label>
                <select style={styles.select} value={form.status || "Pending"} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {soStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label style={styles.label}>Customer PO Number</label><FastText style={styles.input} value={form.customer_po_number || ""} onCommit={(t) => setForm(f => ({ ...f, customer_po_number: t }))} placeholder="Purchase Order #" /></div>
              <div><label style={styles.label}>PO Date</label><DateInput value={form.po_date} onChange={v => setForm({ ...form, po_date: v })} /></div>
              <div><label style={styles.label}>Expected Delivery</label><DateInput value={form.expected_delivery_date} onChange={v => setForm({ ...form, expected_delivery_date: v })} /></div>
            </div>

            {/* Line Items */}
            <div style={{ marginTop: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Line Items</h3>
                <button style={styles.btn("ghost", "sm")} onClick={addItem}>+ Add Item</button>
              </div>
              <table style={styles.table}>
                <thead><tr>
                  <th style={styles.th}>Product</th><th style={styles.th}>Description</th><th style={styles.th}>Qty</th>
                  <th style={styles.th}>Unit Price</th><th style={styles.th}>GST %</th><th style={styles.th}>Amount</th><th style={styles.th}></th>
                </tr></thead>
                <tbody>
                  {(form.items || []).map((item, idx) => {
                    const amt = (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0);
                    return (
                      <tr key={idx}>
                        <td style={styles.td}>
                          <select style={{ ...styles.select, width: 150 }} value={item.product_id || ""} onChange={e => {
                            const prod = masters.products?.find(p => p.id == e.target.value);
                            updateItem(idx, "product_id", e.target.value);
                            if (prod) { updateItem(idx, "unit_price", prod.unit_price); updateItem(idx, "gst_rate", prod.default_gst_rate); updateItem(idx, "hsn_sac_code", prod.hsn_sac_code); }
                          }}>
                            <option value="">Select...</option>
                            {masters.products?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </td>
                        <td style={styles.td}><input style={{ ...styles.input, width: 150 }} value={item.description || ""} onChange={e => updateItem(idx, "description", e.target.value)} /></td>
                        <td style={styles.td}><input style={{ ...styles.input, width: 60 }} type="number" value={item.quantity || ""} onChange={e => updateItem(idx, "quantity", e.target.value)} /></td>
                        <td style={styles.td}><input style={{ ...styles.input, width: 100 }} type="number" value={item.unit_price || ""} onChange={e => updateItem(idx, "unit_price", e.target.value)} /></td>
                        <td style={styles.td}><input style={{ ...styles.input, width: 60 }} type="number" value={item.gst_rate || ""} onChange={e => updateItem(idx, "gst_rate", e.target.value)} /></td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{formatCurrencyINR(amt)}</td>
                        <td style={styles.td}><button style={styles.btn("danger", "sm")} onClick={() => removeItem(idx)}>✕</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ textAlign: "right", marginTop: 8, fontSize: 16, fontWeight: 700 }}>Subtotal: {formatCurrencyINR(calcTotal())}</div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Notes</label>
              <FastText multiline style={{ ...styles.input, minHeight: 60 }} value={form.notes || ""} onCommit={(t) => setForm(f => ({ ...f, notes: t }))} />
            </div>

            {editItem?.quote_number && <div style={{ marginTop: 8, fontSize: 12, color: colors.textLight }}>Created from Quote: {editItem.quote_number}</div>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
              <button style={styles.btn("ghost")} onClick={() => setShowForm(false)}>Cancel <span style={{ fontSize: 9, color: colors.textLight, display: isMobileNow() ? "none" : "inline" }}>(Esc)</span></button>
              <button style={styles.btn("primary")} onClick={handleSave}>{editItem ? "Update" : "Create Sale Order"} <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+S)</span></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// AUDIT LOGS SCREEN
// ============================================================================
// BACKUP & RESTORE SCREEN
function BackupScreen() {
  const [backups, setBackups] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadBackups = async () => {
    try { const r = await api("/backup/list"); setBackups(r.data); } catch (e) { console.error(e); }
  };

  useEffect(() => { loadBackups(); }, []);

  const createBackup = async () => {
    setLoading(true);
    try {
      const r = await api("/backup/create", { method: "POST" });
      setToast({ message: r.message, type: "success" });
      loadBackups();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  };

  // Alt+N — create a database backup
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.shiftKey && e.key.toLowerCase() === "n") { e.preventDefault(); createBackup(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });


  const exportCSV = async () => {
    setLoading(true);
    try {
      const r = await api("/backup/export-csv", { method: "POST" });
      setToast({ message: r.message, type: "success" });
      loadBackups();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  };

  const downloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/backup/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      setToast({ message: `Downloading ${filename}...`, type: "success" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Delete backup "${filename}"? This cannot be undone.`)) return;
    try {
      await api(`/backup/${filename}`, { method: "DELETE" });
      setToast({ message: "Backup deleted.", type: "success" });
      loadBackups();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const cleanupOld = async () => {
    if (!window.confirm("Delete all backups older than 30 days?")) return;
    try {
      const r = await api("/backup/cleanup", { method: "POST", body: { days: 30 } });
      setToast({ message: r.message, type: "success" });
      loadBackups();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>💾 Backup & Restore</h1>
      </div>

      {/* Action Buttons */}
      <div style={{ ...styles.card, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
        <button style={{ ...styles.btn("primary"), padding: "12px 24px", fontSize: 14 }} onClick={createBackup} disabled={loading}>
          {loading ? "⏳ Processing..." : <>📦 Create Database Backup (.sql) <span style={{ fontSize: 9, opacity: 0.7, display: isMobileNow() ? "none" : "inline" }}>(Alt+N)</span></>}
        </button>
        <button style={{ ...styles.btn("success"), padding: "12px 24px", fontSize: 14 }} onClick={exportCSV} disabled={loading}>
          {loading ? "⏳ Processing..." : "📊 Export All Data to CSV"}
        </button>
        <button style={{ ...styles.btn("warning"), padding: "12px 24px", fontSize: 14 }} onClick={cleanupOld}>
          🗑️ Cleanup Old (30+ days)
        </button>
      </div>

      {/* Info */}
      <div style={{ ...styles.card, background: "#f0f4ff", border: `1px solid ${colors.primary}` }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0, color: colors.primary }}>ℹ️ Backup Information</h3>
        <p style={{ fontSize: 12, marginBottom: 8 }}><strong>Database Backup (.sql):</strong> Complete database dump including all tables, data, and structure. Use this for full recovery.</p>
        <p style={{ fontSize: 12, marginBottom: 8 }}><strong>CSV Export:</strong> All tables exported as individual CSV files in a folder. Can be opened in Excel for viewing.</p>
        <p style={{ fontSize: 12, marginBottom: 8 }}><strong>Backup Location:</strong> C:\Users\srvadmin\Desktop\Call Log\backups\</p>
        <p style={{ fontSize: 12, marginBottom: 8 }}><strong>To Restore:</strong> Open Command Prompt and run:</p>
        <div style={{ background: "#1e293b", color: "#e2e8f0", padding: 12, borderRadius: 8, fontSize: 12, fontFamily: "monospace" }}>
          psql -U postgres -d call_tracker_db &lt; "C:\Users\srvadmin\Desktop\Call Log\backups\crm_backup_XXXXXXXX.sql"
        </div>
      </div>

      {/* Backup List */}
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>📁 Existing Backups ({backups.length})</h3>
        {backups.length > 0 ? (
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Filename</th>
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Actions</th>
            </tr></thead>
            <tbody>
              {backups.map((b, i) => (
                <tr key={i}>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{b.name}</td>
                  <td style={styles.td}>{b.size}</td>
                  <td style={styles.td}>{new Date(b.date).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {b.name.endsWith(".sql") && <button style={styles.btn("primary", "sm")} onClick={() => downloadBackup(b.name)}>⬇️ Download</button>}
                      <button style={styles.btn("danger", "sm")} onClick={() => deleteBackup(b.name)}>🗑️ Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p style={{ fontSize: 12, color: colors.textLight, textAlign: "center", padding: 20 }}>No backups found. Click "Create Database Backup" to create your first backup.</p>}
      </div>
    </div>
  );
}

function AuditLogsScreen() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({});

  const fetchData = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api(`/audit-logs?${params}`);
      setData(r.data); setPagination(r.pagination);
    } catch (e) { console.error(e); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div>
      <h1 style={styles.h1}>Audit Trail</h1>
      <div style={styles.card}>
        <div style={styles.filterBar}>
          <div><label style={styles.label}>Table</label>
            <select style={styles.select} value={filters.table_name || ""} onChange={(e) => setFilters({ ...filters, table_name: e.target.value })}>
              <option value="">All</option>
              {["users", "executives", "principals", "clients", "products", "locations", "call_types", "call_logs", "leads", "proforma_invoices", "company_profile"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>Action</label>
            <select style={styles.select} value={filters.action || ""} onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
              <option value="">All</option>{["CREATE", "UPDATE", "DELETE"].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div><label style={styles.label}>From</label><DateInput value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} /></div>
          <div><label style={styles.label}>To</label><DateInput value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} /></div>
          <button style={styles.btn("ghost", "sm")} onClick={() => setFilters({})}>Clear</button>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date/Time</th><th style={styles.th}>User</th><th style={styles.th}>Action</th>
              <th style={styles.th}>Table</th><th style={styles.th}>Record ID</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.id}>
                <td style={styles.td}>{new Date(row.changed_at).toLocaleString("en-IN")}</td>
                <td style={styles.td}>{row.changed_by_name || "System"}</td>
                <td style={styles.td}><StatusBadge status={row.action === "CREATE" ? "New" : row.action === "UPDATE" ? "In Progress" : "Lost"} /></td>
                <td style={styles.td}>{row.table_name}</td>
                <td style={styles.td}>{row.record_id}</td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={5} style={{ ...styles.td, textAlign: "center", color: colors.textLight }}>No audit logs found.</td></tr>}
          </tbody>
        </table>
        <Pagination pagination={pagination} onPageChange={fetchData} />
      </div>
    </div>
  );
}

// ============================================================================
// CHANGE PASSWORD SCREEN
// ============================================================================
function ChangePasswordScreen() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.current_password) { setToast({ message: "Current password is required.", type: "error" }); return; }
    if (!form.new_password) { setToast({ message: "New password is required.", type: "error" }); return; }
    if (form.new_password.length < 8) { setToast({ message: "Password must be at least 8 characters.", type: "error" }); return; }
    if (form.new_password !== form.confirm_password) { setToast({ message: "Passwords do not match.", type: "error" }); return; }

    setLoading(true);
    try {
      await api("/auth/change-password", { method: "PUT", body: { current_password: form.current_password, new_password: form.new_password } });
      setToast({ message: "Password changed successfully!", type: "success" });
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) { setToast({ message: e.message, type: "error" }); }
    setLoading(false);
  };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <h1 style={styles.h1}>Change Password</h1>
      <div style={{ ...styles.card, maxWidth: 450 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div><label style={styles.label}>Current Password *</label>
            <input style={styles.input} type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} placeholder="Enter current password" />
          </div>
          <div><label style={styles.label}>New Password *</label>
            <input style={styles.input} type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="Min 6 characters" />
          </div>
          <div><label style={styles.label}>Confirm New Password *</label>
            <input style={styles.input} type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} placeholder="Re-enter new password" />
          </div>
          <button style={{ ...styles.btn("primary"), justifyContent: "center" }} onClick={handleSubmit} disabled={loading}>
            {loading ? "Changing..." : "Change Password"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// REQUESTS / CHAT SCREEN
// ============================================================================
function RequestsScreen() {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ subject: "", message: "", priority: "Normal" });
  const [replyText, setReplyText] = useState("");
  const [toast, setToast] = useState(null);

  const fetchRequests = async () => {
    try { const r = await api("/requests"); setRequests(r.data); } catch (e) { console.error(e); }
  };

  const openRequest = async (id) => {
    try {
      const r = await api(`/requests/${id}`);
      setSelectedReq(r.data);
      setMessages(r.data.messages || []);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const handleNewRequest = async () => {
    if (!newForm.subject || !newForm.message) { setToast({ message: "Subject and message are required.", type: "error" }); return; }
    try {
      await api("/requests", { method: "POST", body: newForm });
      setToast({ message: "Request sent!", type: "success" });
      setShowNew(false); setNewForm({ subject: "", message: "", priority: "Normal" }); fetchRequests();
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const handleReply = async () => {
    if (!replyText) return;
    try {
      await api(`/requests/${selectedReq.id}/reply`, { method: "POST", body: { message: replyText } });
      setReplyText("");
      openRequest(selectedReq.id);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api(`/requests/${id}/status`, { method: "PATCH", body: { status } });
      setToast({ message: `Status changed to ${status}`, type: "success" });
      fetchRequests(); if (selectedReq?.id === id) openRequest(id);
    } catch (e) { setToast({ message: e.message, type: "error" }); }
  };

  useEffect(() => { fetchRequests(); }, []);

  const statusColors = { New: colors.primary, "In Progress": colors.warning, Resolved: colors.success, Rejected: colors.danger };

  return (
    <div>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <div style={styles.topbar}>
        <h1 style={styles.h1}>Requests</h1>
        <button style={styles.btn("primary")} onClick={() => setShowNew(true)}>+ New Request</button>
      </div>

      <div style={{ display: "flex", gap: 16, height: "calc(100vh - 150px)" }}>
        {/* Request List */}
        <div style={{ width: 380, flexShrink: 0, background: "#fff", borderRadius: 10, border: `1px solid ${colors.border}`, overflowY: "auto" }}>
          {requests.length === 0 && <p style={{ padding: 20, textAlign: "center", color: colors.textLight, fontSize: 13 }}>No requests yet.</p>}
          {requests.map(r => (
            <div key={r.id} onClick={() => openRequest(r.id)}
              style={{ padding: "14px 16px", borderBottom: `1px solid ${colors.border}`, cursor: "pointer",
                background: selectedReq?.id === r.id ? colors.primaryLight : ((!r.is_read_by_admin && user?.role === 'admin') || (!r.is_read_by_sender && user?.role !== 'admin') ? "#fffbeb" : "transparent") }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{r.subject}</span>
                <span style={{ ...styles.badge(statusColors[r.status] || colors.secondary), fontSize: 10 }}>{r.status}</span>
              </div>
              <div style={{ fontSize: 11, color: colors.textLight, marginTop: 4 }}>
                {r.sender_name} · {new Date(r.created_at).toLocaleDateString("en-IN")} · {r.message_count} messages
              </div>
            </div>
          ))}
        </div>

        {/* Chat / Detail Panel */}
        <div style={{ flex: 1, background: "#fff", borderRadius: 10, border: `1px solid ${colors.border}`, display: "flex", flexDirection: "column" }}>
          {selectedReq ? (
            <>
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{selectedReq.subject}</h3>
                  <div style={{ fontSize: 11, color: colors.textLight, marginTop: 2 }}>From: {selectedReq.sender_name} · {new Date(selectedReq.created_at).toLocaleString("en-IN")}</div>
                </div>
                {user?.role === 'admin' && (
                  <div style={styles.flexRow}>
                    {["New", "In Progress", "Resolved", "Rejected"].map(s => (
                      <button key={s} style={{ ...styles.btn(selectedReq.status === s ? "primary" : "ghost", "sm"), fontSize: 10 }} onClick={() => handleStatusChange(selectedReq.id, s)}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
                {messages.map((m, i) => (
                  <div key={i} style={{ marginBottom: 12, display: "flex", flexDirection: "column", alignItems: m.sender_role === "admin" ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "75%", padding: "10px 14px", borderRadius: 12, background: m.sender_role === "admin" ? "#e8eefb" : "#f1f5f9",
                      borderBottomRightRadius: m.sender_role === "admin" ? 2 : 12, borderBottomLeftRadius: m.sender_role === "admin" ? 12 : 2 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: colors.textLight, marginBottom: 4 }}>{m.sender_name} · {m.sender_role}</div>
                      <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{m.message}</div>
                      <div style={{ fontSize: 9, color: colors.textLight, marginTop: 4, textAlign: "right" }}>{new Date(m.created_at).toLocaleString("en-IN")}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: "12px 20px", borderTop: `1px solid ${colors.border}`, display: "flex", gap: 8 }}>
                <input style={{ ...styles.input, flex: 1 }} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type your reply..."
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(); } }} />
                <button style={styles.btn("primary")} onClick={handleReply}>Send</button>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: colors.textLight, fontSize: 14 }}>
              Select a request to view the conversation
            </div>
          )}
        </div>
      </div>

      {/* New Request Modal */}
      {showNew && (
        <div style={styles.modal} onClick={() => setShowNew(false)}>
          <div style={{ ...styles.modalContent, maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0 }}>New Request</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div><label style={styles.label}>Subject *</label>
                <input style={styles.input} value={newForm.subject} onChange={(e) => setNewForm({ ...newForm, subject: e.target.value })} placeholder="e.g., Need to edit Call #1015" />
              </div>
              <div><label style={styles.label}>Priority</label>
                <select style={styles.select} value={newForm.priority} onChange={(e) => setNewForm({ ...newForm, priority: e.target.value })}>
                  <option value="Low">Low</option><option value="Normal">Normal</option>
                  <option value="High">High</option><option value="Urgent">Urgent</option>
                </select>
              </div>
              <div><label style={styles.label}>Message *</label>
                <textarea style={{ ...styles.input, minHeight: 100 }} value={newForm.message} onChange={(e) => setNewForm({ ...newForm, message: e.target.value })}
                  placeholder="Describe your request in detail..." />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, ...(isMobileNow() ? { position: "sticky", bottom: 0, background: "#fff", padding: "10px 0 2px", borderTop: `1px solid ${colors.border}`, marginLeft: -14, marginRight: -14, paddingLeft: 14, paddingRight: 14, zIndex: 5 } : {}) }}>
              <button style={styles.btn("ghost")} onClick={() => setShowNew(false)}>Cancel</button>
              <button style={styles.btn("primary")} onClick={handleNewRequest}>Send Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}